import { getGuideline, LaserGuideline } from "../data/LaserGuidelines";
import { DEFAULT_BAD_LEAFLETS } from "../data/badNewsItems";
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";

import DermBodyMap from "./DermBodyMap";

import {
  Settings,
  FileText,
  User as UserIcon,
  Activity, Globe, Loader2,
  Calendar,
  Send,
  Camera,
  Layers,
  Sparkles,
  ClipboardList,
  FolderOpen,
  ChevronRight,
  Plus,
  Trash,
  Info,
  CheckCircle,
  RefreshCw,
  Printer,
  History, // Added History icon
  X,
  BookOpen,
  Clock,
  Lock,
  Unlock,
  Pill,
  Video,
  Edit,
  Download,
  Upload,
  ExternalLink,
  UserPlus,
  Mic,
  PhoneOff,
  Search,
  DollarSign,
  Building2,
  Save,
  Star,
  Users,
} from "lucide-react";
import {
  Patient,
  Appointment,
  LaserTherapyEntry,
  ClinicalRecord,
  Order,
  Vitals,
  MedicalHistory,
} from "../types";
import { usePatient } from "../context/PatientContext";
import GoogleCalendarSync from "./GoogleCalendarSync";
import GoogleMeetButton from "./GoogleMeetButton";
import AisNotepad from "./AisNotepad";
import ClinicalProfessorBar from "./ClinicalProfessorBar";
import DataBackupPanel from "./DataBackupPanel";
import VoiceDictation from "./VoiceDictation";
import PatientAnalytics from "./PatientAnalytics";
import StatsBanner from "./StatsBanner";
import PatientEhrPrintPreview from "./PatientEhrPrintPreview";
import SendWhatsAppButton from "./SendWhatsAppButton";
import SaveToDriveButton from "./SaveToDriveButton";
import VitalsChart from "./VitalsChart";
import PediatricDevelopment from "./PediatricDevelopment";
import BmiTrendChart from "./BmiTrendChart";
import {
  bnfCatalog,
  labCatalog,
  xrayCatalog,
  diseaseCatalog,
  fullInvestigationCatalog,
  iraqiTradeNamesMap,
} from "../data/catalogs";
import { SeriousDrugInteraction, SERIOUS_DRUG_INTERACTIONS } from "../data/interactions";
import { connectSocket } from "../utils/socket";
import { getWorkspaceAccessToken, appendDataToSheet } from "../lib/firebase";
import ReferralGenerator from "./ReferralGenerator";

const INJECTION_ZONES = [
  { id: "glabella", label: "Glabella", top: "25%", left: "50%" },
  {
    id: "left_zygomatic",
    label: "Left Zygomatic Arch",
    top: "40%",
    left: "25%",
  },
  {
    id: "right_zygomatic",
    label: "Right Zygomatic Arch",
    top: "40%",
    left: "75%",
  },
  {
    id: "nasolabial_left",
    label: "Left Nasolabial Fold",
    top: "55%",
    left: "38%",
  },
  {
    id: "nasolabial_right",
    label: "Right Nasolabial Fold",
    top: "55%",
    left: "62%",
  },
  { id: "lips", label: "Lips (Body/Border)", top: "68%", left: "50%" },
  { id: "chin", label: "Chin / Mental Crease", top: "85%", left: "50%" },
];

const DebouncedInput = ({
  value,
  onChange,
  placeholder,
  className,
  dir,
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  className?: string;
  dir?: string;
}) => {
  const [localValue, setLocalValue] = useState(value);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (!isInternalChange.current) {
      setLocalValue(value);
    }
    isInternalChange.current = false;
  }, [value]);

  useEffect(() => {
    const handler = setTimeout(() => {
      onChange(localValue);
    }, 300);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValue]);

  return (
    <input
      type="text"
      placeholder={placeholder}
      value={localValue}
      onChange={(e) => {
        isInternalChange.current = true;
        setLocalValue(e.target.value);
      }}
      className={className}
      dir={dir}
    />
  );
};

const NICE_GUIDELINES = [
  {
    disease: "Acne Vulgaris",
    management:
      "Topical retinoid ± benzoyl peroxide ➔ Add oral antibiotic (Lymecycline/Doxycycline) max 3m ➔ Refer for oral Isotretinoin if severe.",
  },
  {
    disease: "Atopic Eczema",
    management:
      "Emollients frequently ➔ Mild topical corticosteroid ➔ Moderate for flares ➔ Refer if poorly controlled or systemic symptoms.",
  },
  {
    disease: "Psoriasis",
    management:
      "Regular emollients ➔ Potent topical corticosteroid OD + Vit D analogue OD for 4 wks ➔ Refer for phototherapy/systemics.",
  },
  {
    disease: "Fungal (Tinea/Nails)",
    management:
      "Terbinafine 1% cream ➔ Oral Terbinafine 250mg OD if nails (6-12 wks). Check LFTs before oral therapy.",
  },
  {
    disease: "Genital (Bacterial Vaginosis)",
    management:
      "Oral Metronidazole 400mg BD x 7 days OR 2g stat, OR intravaginal clindamycin/metronidazole. Avoid alcohol.",
  },
  {
    disease: "Hypertension",
    management:
      "Step 1: ACEi/ARB (A) or CCB (C). Step 2: A+C. Step 3: A+C+D(Thiazide-like). Step 4: Add Spironolactone.",
  },
  {
    disease: "Type 2 Diabetes",
    management:
      "HbA1c > 48: Lifestyle + Metformin ➔ Add SGLT2i/DPP-4i/Sulfonylurea based on CV risk profile.",
  },
  {
    disease: "Asthma",
    management:
      "SABA PRN + Low-dose ICS (MART protocol preferred) ➔ Step up to Moderate-dose ICS-LABA.",
  },
  {
    disease: "Rosacea",
    management:
      "Avoid triggers. Mild: Topical metronidazole/azelaic acid. Mod-Severe: Oral tetracycline (Oxytetracycline/Doxycycline) for 6-12 wks.",
  },
  {
    disease: "Seborrhoeic Dermatitis",
    management:
      "Scalp: Ketoconazole/Selenium sulphide shampoo. Face/body: Topical ketoconazole 2% cream ± mild topical corticosteroid.",
  },
  {
    disease: "Alopecia Areata",
    management:
      "Wait and see (many resolve spontaneously). Potent topical corticosteroids. Refer to dermatology for intralesional steroids/immunotherapy if severe.",
  },
  {
    disease: "Urticaria",
    management:
      "Identify/avoid triggers. Non-sedating antihistamines (Cetirizine/Loratadine/Fexofenadine) up to 4x standard dose. Short course oral steroids for severe flares.",
  },
  {
    disease: "Scabies",
    management:
      "Permethrin 5% cream (apply whole body, leave for 8-12h). Malathion 0.5% liquid. Treat all household contacts simultaneously. Wash clothes/bedding at 50°C+.",
  },
  {
    disease: "Impetigo",
    management:
      "Localized: Hydrogen peroxide 1% cream or topical Fusidic acid. Widespread/systemically unwell: Oral Flucloxacillin (or clarithromycin if penicillin allergic).",
  },
  {
    disease: "Cellulitis",
    management:
      "Draw around erythema. Oral Flucloxacillin 500mg QDS (or Clarithromycin/Erythromycin if allergic). Elevate limb. Refer if systemic signs/sepsis.",
  },
  {
    disease: "Viral Warts",
    management:
      "Topical salicylic acid daily (e.g., Duofilm/Occlusal) for up to 12 wks. Cryotherapy. Explain spontaneous resolution often occurs in children.",
  },
  {
    disease: "Melasma",
    management:
      "Strict sun protection/SPF 50. Avoid hormonal triggers (COCP). First-line active: Hydroquinone 4% ± topical retinoids (e.g. Pigmanorm/Kligman's formula). Refer if resistant.",
  },
  {
    disease: "Actinic Keratosis",
    management:
      "Sun protection. Emollients. First-line directed: 5-Fluorouracil (Efudix), Imiquimod, or Diclofenac gel. Cryotherapy for isolated thick lesions.",
  },
  {
    disease: "Contact Dermatitis",
    management:
      "Identify and avoid allergen/irritant (patch testing if severe). Emollients + potent topical corticosteroids for active flares.",
  },
  {
    disease: "Pityriasis Rosea",
    management:
      "Self-limiting (6-12 weeks). Emollients and topical antipruritics (calamine). Mild/moderate topical corticosteroids if very itchy. Consider phototherapy if severe.",
  },
  {
    disease: "Pityriasis Versicolor",
    management:
      "Topical antifungal (Ketoconazole shampoo, wash off after 3-5 mins). Itraconazole 200mg OD for 7 days if extensive/resistant.",
  },
  {
    disease: "Vitiligo",
    management:
      "Sun protection to prevent sunburn and minimize contrast. Potent topical corticosteroids (max 1-2 months) or topical calcineurin inhibitors (Tacrolimus). Refer for phototherapy.",
  },
  {
    disease: "Lichen Planus",
    management:
      "Potent topical corticosteroids. Menthol in aqueous cream for pruritus. Systemic corticosteroids or immunosuppressants for extensive/severe disease.",
  },
  {
    disease: "Hyperhidrosis",
    management:
      "First-line: Aluminum chloride hexahydrate 20% solution. Second-line: Iontophoresis, botulinum toxin injections, or oral anticholinergics (oxybutynin).",
  },
  {
    disease: "Venous Leg Ulcers",
    management:
      "Exclude arterial disease (ABPI > 0.8). Compression therapy. Emollients for surrounding skin. Topical steroids for associated venous eczema.",
  },
  {
    disease: "Chlamydia",
    management:
      "Doxycycline 100mg BD for 7 days. Alternative: Azithromycin 1g stat then 500mg OD for 2 days. Treat partners.",
  },
  {
    disease: "Gonorrhoea",
    management:
      "Ceftriaxone 1g IM stat. Add Doxycycline 100mg BD for 7 days if chlamydia not excluded. Test of cure recommended.",
  },
  {
    disease: "Syphilis (Primary/Secondary)",
    management:
      "Benzathine penicillin 2.4 MU IM stat. Alternative (if allergic): Doxycycline 100mg BD for 14 days.",
  },
  {
    disease: "Genital Herpes",
    management:
      "First episode: Aciclovir 400mg TDS for 5 days. Recurrent: Aciclovir 400mg TDS for 3-5 days or suppressive therapy.",
  },
  {
    disease: "Anogenital Warts",
    management:
      "Topical Podophyllotoxin (if soft/non-keratinised) or Imiquimod 5%. Cryotherapy / excision for keratinised/resistant warts.",
  },
  {
    disease: "Trichomoniasis",
    management:
      "Metronidazole 400mg BD for 5-7 days OR 2g stat. Treat partners simultaneously.",
  },
  {
    disease: "Pubic Lice / Crabs",
    management:
      "Permethrin 5% cream or Malathion 0.5% aqueous liquid. Apply to whole body, wash off after 12 hours. Repeat after 7 days.",
  },
  {
    disease: "Candidiasis (Thrush)",
    management:
      "Topical Clotrimazole cream/pessary OR Oral Fluconazole 150mg stat. Ensure partner checked if symptomatic.",
  },
  {
    disease: "Pelvic Inflammatory Disease (PID)",
    management:
      "Ceftriaxone 1g IM stat + Doxycycline 100mg BD for 14 days + Metronidazole 400mg BD for 14 days.",
  },
  {
    disease: "Molluscum Contagiosum",
    management:
      "Self-limiting in immunocompetent. Can use Cryotherapy, topical podophyllotoxin, or potassium hydroxide 5% if required/severe.",
  },
  {
    disease: "Lymphogranuloma Venereum (LGV)",
    management:
      "Doxycycline 100mg BD for 21 days. Erythromycin 500mg QDS for 21 days as alternative.",
  },
  {
    disease: "Chancroid",
    management:
      "Azithromycin 1g oral stat, OR Ceftriaxone 250mg IM stat, OR Ciprofloxacin 500mg BD for 3 days.",
  },
  {
    disease: "Donovanosis",
    management:
      "Azithromycin 1g weekly OR 500mg daily for at least 3 weeks and until all lesions have completely healed.",
  },
  {
    disease: "Gastro-oesophageal Reflux Disease (GORD)",
    management:
      "Lifestyle advice ➔ Full dose PPI for 4-8 weeks ➔ Maintenance low dose PPI if needed. Refer if red flag symptoms (dysphagia, weight loss).",
  },
  {
    disease: "Peptic Ulcer Disease (GI)",
    management:
      "H. pylori eradication (PPI + Amoxicillin + Clarithromycin for 7 days) if positive. If negative, PPI for 4-8 weeks.",
  },
  {
    disease: "Helicobacter Pylori Eradication (GI)",
    management:
      "First-line: PPI BD + Amoxicillin 1g BD + Clarithromycin 500mg BD (or Metronidazole 400mg BD) for 7 days. Penicillin allergic: PPI BD + Clarithromycin 500mg BD + Metronidazole 400mg BD.",
  },
  {
    disease: "Irritable Bowel Syndrome (IBS)",
    management:
      "First-line: Antispasmodics (Mebeverine), laxatives for constipation, loperamide for diarrhea. Second-line: TCA (Amitriptyline) or SSRI.",
  },
  {
    disease: "Inflammatory Bowel Disease (Crohn's/UC flares)",
    management:
      "Mild-mod UC: topical/oral aminosalicylates. Severe: Oral corticosteroids. Refer to specialist.",
  },
  {
    disease: "Acute Diarrhea / Gastroenteritis (GI)",
    management:
      "Fluid replacement/ORS. Loperamide for symptomatic relief. Antibiotics ONLY if systemically unwell or persistent (Ciprofloxacin).",
  },
  {
    disease: "Constipation (GI)",
    management:
      "Lifestyle/dietary fibre ➔ Bulk-forming laxative (Ispaghula) ➔ Osmotic (Macrogol/Lactulose) ➔ Stimulant (Senna).",
  },
  {
    disease: "Acute Otitis Media (ENT)",
    management:
      "Analgesia first line. Consider 3-day delayed antibiotic prescription (Amoxicillin) if symptoms not improving. Immediate antibiotics if systemically unwell or high risk.",
  },
  {
    disease: "Acute Sinusitis (ENT)",
    management:
      "Analgesia, nasal saline. No antibiotics if < 10 days. If >10 days or severe: High-dose nasal corticosteroid. Backup: Phenoxymethylpenicillin for 5 days.",
  },
  {
    disease: "Acute Sore Throat / Tonsillitis (ENT)",
    management:
      "Centor/FeverPAIN score. Score 0-2: no antibiotics. Score 3+: consider delayed or immediate Phenoxymethylpenicillin (or Clarithromycin/Erythromycin) for 5-10 days.",
  },
  {
    disease: "Otitis Externa (ENT)",
    management:
      "Topical acetic acid or topical antibiotic ± steroid (e.g., Neomycin/Dexamethasone). Oral flucloxacillin if spreading cellulitis.",
  },
  {
    disease: "Allergic Rhinitis (ENT)",
    management:
      "Allergen avoidance, oral non-sedating antihistamines, intranasal corticosteroids (e.g., Mometasone/Fluticasone).",
  },
  {
    disease: "Bacterial Conjunctivitis (Ophthalmology)",
    management:
      "Often self-limiting (clean with cooled boiled water). Chloramphenicol 0.5% drops if severe or not clearing.",
  },
  {
    disease: "Dry Eye Syndrome (Ophthalmology)",
    management:
      "Artificial tears (Hypromellose), lubricating ointments at night.",
  },
  {
    disease: "Allergic Conjunctivitis (Ophthalmology)",
    management:
      "Cool compresses, topical antihistamines (Olopatadine) or mast cell stabilisers (Sodium cromoglicate).",
  },
  {
    disease: "Blepharitis (Ophthalmology)",
    management:
      "Eyelid hygiene (warm compresses, baby shampoo). Topical antibiotic ointment (Chloramphenicol) for severe flares.",
  },
  {
    disease: "Glaucoma (Open-angle) (Ophthalmology)",
    management:
      "Prostaglandin analogues (Latanoprost) first-line. Second-line: Beta-blockers (Timolol). Refer to ophthalmology.",
  },
  {
    disease: "Heart Failure (Cardiovascular)",
    management:
      "Assess stroke risk. ACEi/ARB + Beta-blocker ➔ Add MRA (Spironolactone/Eplerenone) ➔ Add SGLT2i. Loop diuretics for fluid overload symptoms.",
  },
  {
    disease: "Angina Pectoris (Stable) (Cardiovascular)",
    management:
      "Sublingual GTN for relief. First-line prevention: Beta-blocker OR Calcium Channel Blocker. Secondary prevention: Aspirin 75mg + Statin.",
  },
  {
    disease: "Atrial Fibrillation (Cardiovascular)",
    management:
      "Rate control (Beta-blocker or Diltiazem). Anticoagulation (DOAC e.g. Apixaban or Warfarin) based on CHA2DS2-VASc and ORBIT scores.",
  },
  {
    disease: "Acute Coronary Syndrome (ACS) Initial Mgt",
    management:
      "Aspirin 300mg stat, Ticagrelor 300mg stat, GTN, Oxygen (if SpO2 < 94%), Morphine + Metoclopramide. Transfer to PCI.",
  },
  {
    disease: "Dyslipidaemia / Primary Prevention (Cardiovascular)",
    management:
      "QRISK3 > 10%. Atorvastatin 20mg ON. (Secondary Prevention: Atorvastatin 80mg ON).",
  },
  {
    disease: "Lower Urinary Tract Infection (Infectious Disease)",
    management:
      "Women: Nitrofurantoin or Trimethoprim (3 days). Men: Nitrofurantoin or Trimethoprim (7 days). Send MSU if pregnant, male, or treatment failure.",
  },
  {
    disease: "Recurrent UTI (Infectious Disease)",
    management:
      "Preventative: behavioral advice, D-mannose, or cranberry. Post-menopausal: topical vaginal estrogen. Prophylaxis: single-dose post-coital or daily low-dose Trimethoprim/Nitrofurantoin (review at 6 months).",
  },
  {
    disease: "Upper Respiratory Tract Infection (Common Cold)",
    management:
      "Reassurance, paracetamol, fluids. Do NOT prescribe antibiotics.",
  },
  {
    disease: "Community-Acquired Pneumonia (CAP) (Infectious Disease)",
    management:
      "CRB-65 score 0: Amoxicillin (or Doxycycline/Clarithromycin) for 5 days. High severity: co-amoxiclav + clarithromycin.",
  },
  {
    disease: "Acute Pyelonephritis / Upper UTI (Infectious Disease)",
    management:
      "Cefalexin, Co-amoxiclav, or Ciprofloxacin for 7-10 days. Refer to hospital if systemically very unwell.",
  },
  {
    disease: "Influenza (Infectious Disease)",
    management:
      "Oseltamivir 75mg BD for 5 days if in at-risk group and within 48h of onset. Uncomplicated: symptomatic management.",
  },
  {
    disease: "Herpes Zoster / Shingles (Infectious Disease)",
    management:
      "Aciclovir 800mg 5x daily for 7 days if starting within 72 hours of rash onset, especially >50 years old.",
  },
  {
    disease: "Acute Pain Management",
    management:
      "Step 1: Paracetamol/NSAIDs. Step 2: Weak opioid (e.g., Codeine). Step 3: Strong opioid (e.g., Morphine). Reassess regularly and step down when possible.",
  },
  {
    disease: "Neuropathic Pain",
    management:
      "First-line: Amitriptyline, Duloxetine, Gabapentin, or Pregabalin. Do NOT use standard analgesics alone.",
  },
  {
    disease: "Osteoarthritis",
    management:
      "Topical NSAIDs (e.g., Ibuprofen gel), Paracetamol, Oral NSAIDs (with PPI), intra-articular steroid injections.",
  },
  {
    disease: "Migraine (Acute)",
    management:
      "Oral NSAID or Triptan (e.g. Sumatriptan) + antiemetic (Metoclopramide) at onset.",
  },
  {
    disease: "Migraine (Prophylaxis)",
    management:
      "Propranolol, Topiramate, or Amitriptyline if >4 attacks/month or severe.",
  },
];

interface PhysicianDashboardProps {
  user: { email: string; name: string; role: string };
  apiToken: string;
}


const COMMON_CHRONIC_DISEASES = [
  "Hypertension", "Diabetes Mellitus Type 1", "Diabetes Mellitus Type 2", 
  "Asthma", "Chronic Obstructive Pulmonary Disease (COPD)", 
  "Coronary Artery Disease (CAD)", "Heart Failure", "Chronic Kidney Disease (CKD)", 
  "Rheumatoid Arthritis", "Osteoarthritis", "Hypothyroidism", "Hyperthyroidism",
  "Epilepsy", "Depression", "Anxiety Disorder", "Bipolar Disorder",
  "Schizophrenia", "Alzheimer's Disease", "Parkinson's Disease", 
  "Gastroesophageal Reflux Disease (GERD)", "Peptic Ulcer Disease", 
  "Inflammatory Bowel Disease (Crohn's/UC)", "Migraine", "Multiple Sclerosis",
  "Psoriasis", "Systemic Lupus Erythematosus (SLE)", "HIV/AIDS", 
  "Hepatitis B", "Hepatitis C", "Tuberculosis", "Gout", "Atrial Fibrillation",
  "Deep Vein Thrombosis (DVT)", "Pulmonary Embolism (PE)", "Hyperlipidemia",
  "Anemia", "Glaucoma"
];

const smartLabSuggestions: Record<string, string[]> = {
  "diabetes": ["HbA1c", "Fasting Blood Sugar (FBS)", "Random Blood Sugar (RBS)", "Lipid Profile", "Creatinine", "Urine Microalbumin"],
  "hypertension": ["ECG", "Lipid Profile", "Creatinine", "Urine Analysis", "CBC", "Electrolytes (Na, K, Cl)"],
  "anemia": ["CBC", "Iron Profile", "Vitamin B12", "Folic Acid", "Peripheral Blood Smear", "Reticulocyte Count"],
  "thyroid": ["TSH", "Free T3", "Free T4", "Anti-TPO", "Thyroid Ultrasound"],
  "pregnancy": ["CBC", "Blood Group & Rh", "Urine Analysis", "VDRL", "HbsAg", "HIV", "Rubella IgG", "OGTT"],
  "kidney": ["Urea", "Creatinine", "Uric Acid", "Urine Analysis", "Electrolytes (Na, K, Cl)", "Renal Ultrasound"],
  "liver": ["AST", "ALT", "ALP", "Bilirubin", "Albumin", "PT/INR", "HCV Ab", "HBsAg", "Liver Ultrasound"],
  "lipid": ["Total Cholesterol", "HDL", "LDL", "Triglycerides", "VLDL"],
  "gout": ["Uric Acid", "Creatinine", "Urine Analysis"],
  "asthma": ["IgE Level", "CBC (Eosinophils)", "Chest X-Ray"],
  "alopecia": ["CBC", "Iron Profile", "Vitamin D", "Vitamin B12", "TSH", "Free T4", "Testosterone", "DHEAS", "Zinc"],
  "hairfall": ["CBC", "Iron Profile", "Vitamin D", "Vitamin B12", "TSH", "Free T4", "Zinc", "Magnesium"],
  "bone profile": ["Calcium", "Phosphorus", "Alkaline Phosphatase (ALP)", "Vitamin D", "PTH", "Uric Acid"],
  "coagulation profile": ["PT", "INR", "aPTT", "Bleeding Time", "Fibrinogen", "D-Dimer", "CBC"],
  "joints pain": ["CBC", "ESR", "CRP", "Rheumatoid Factor (RF)", "Anti-CCP", "Uric Acid", "ANA"],
  "erectile dysfunction": ["Testosterone (Free & Total)", "Prolactin", "TSH", "HbA1c", "Lipid Profile", "PSA"],
  "infertility male": ["Semen Analysis", "Testosterone (Free & Total)", "FSH", "LH", "Prolactin"],
  "infertility female": ["FSH", "LH", "Prolactin", "TSH", "AMH", "Estradiol (E2)", "Progesterone"],
  "pcos": ["Testosterone (Free & Total)", "FSH", "LH", "Prolactin", "DHEAS", "Insulin (Fasting)", "HbA1c", "Pelvic Ultrasound"],
  "hormones": ["FSH", "LH", "Prolactin", "TSH", "Testosterone", "Estradiol (E2)", "Progesterone", "Cortisol"],
  "psoriasis": ["CBC", "ESR", "CRP", "Uric Acid", "Lipid Profile", "Liver Function Tests (LFTs)"],
  "seborrheic dermatitis": ["Vitamin D", "Zinc", "Vitamin B12", "Fasting Blood Sugar (FBS)"],
  "weakness": ["CBC", "Iron Profile", "Vitamin B12", "Vitamin D", "TSH", "Fasting Blood Sugar (FBS)", "Electrolytes (Na, K, Cl)"],
  "vitamins": ["Vitamin D", "Vitamin B12", "Folic Acid", "Vitamin B6", "Vitamin A", "Vitamin E", "Zinc", "Magnesium"],
  "genetics": ["Karyotyping", "Microarray", "Specific Gene Sequencing", "HLA-B27"],
  "celiac disease": ["Anti-tTG IgA", "Total IgA", "Endomysial Antibodies (EMA)", "CBC", "Iron Profile", "Vitamin D"],
  "cancer": ["CBC", "ESR", "CRP", "LDH", "CEA", "CA 125", "CA 19-9", "CA 15-3", "PSA", "AFP"],
  "breast cancer": ["CA 15-3", "CEA", "CBC", "Liver Function Tests (LFTs)", "Calcium", "Mammogram / Breast Ultrasound"],
  "inflammatory bowel disease": ["CBC", "ESR", "CRP", "Fecal Calprotectin", "Stool Routine", "Iron Profile", "Vitamin B12"],
  "seborrhic dermatitis": ["Vitamin D", "Zinc", "Vitamin B12", "Fasting Blood Sugar (FBS)"],
  "genital diseases": ["VDRL (Syphilis)", "HIV 1&2", "HBsAg", "HCV Ab", "HSV 1/2 Antibodies", "Urine Analysis", "Chlamydia / Gonorrhea PCR"],
  "hepatitis": ["Liver Function Tests (LFTs)", "HBsAg", "HCV Ab", "HAV IgM", "Bilirubin (Direct & Total)", "PT/INR", "Abdominal Ultrasound"],
  "heptitis": ["Liver Function Tests (LFTs)", "HBsAg", "HCV Ab", "HAV IgM", "Bilirubin (Direct & Total)", "PT/INR", "Abdominal Ultrasound"],
  "vaginal disease": ["High Vaginal Swab (HVS) Culture", "Urine Analysis", "Pap Smear", "Chlamydia / Gonorrhea PCR", "Wet Mount (Trichomonas/Yeast)"],
  "venereal diseases": ["VDRL (Syphilis)", "HIV 1&2", "HBsAg", "HCV Ab", "HSV 1/2 Antibodies", "Urine Analysis", "Chlamydia / Gonorrhea PCR"],
  "bilogic treatment": ["CBC", "Liver Function Tests (LFTs)", "Creatinine", "HBsAg", "HCV Ab", "Quantiferon-TB Gold", "HIV 1&2"],
  "biologic treatment": ["CBC", "Liver Function Tests (LFTs)", "Creatinine", "HBsAg", "HCV Ab", "Quantiferon-TB Gold", "HIV 1&2"],
  "isoteritonion treatemnt": ["Liver Function Tests (LFTs)", "Lipid Profile (Triglycerides)", "CBC", "Pregnancy Test (Serum/Urine)"],
  "isotretinoin": ["Liver Function Tests (LFTs)", "Lipid Profile (Triglycerides)", "CBC", "Pregnancy Test (Serum/Urine)"]
};

export default function PhysicianDashboard({
  user,
  apiToken,
}: PhysicianDashboardProps) {
  const [whiteLabel, setWhiteLabel] = useState<any>(null);

  useEffect(() => {
    const saved = localStorage.getItem("clinic_white_label_settings");
    if (saved) {
      try {
        setWhiteLabel(JSON.parse(saved));
      } catch (e) {}
    }

    // Fetch from central API
    fetch("/api/clinic/profile")
      .then(res => res.json())
      .then(data => {
        if (data && Object.keys(data).length > 0) {
          setWhiteLabel(data);
          localStorage.setItem("clinic_white_label_settings", JSON.stringify(data));
        }
      })
      .catch(err => console.error("Failed to fetch clinic profile:", err));
  }, []);
  
  const [patients, setPatients] = useState<Patient[]>([]);
  const { selectedPatient, setSelectedPatient } = usePatient();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [tab, setTab] = useState<
    | "gp"
    | "derma"
    | "surgery"
    | "pharmacy"
    | "nursing"
    | "lab"
    | "history"
    | "timeline"
    | "followup"
    | "med_profile"
    | "documents"
  >("gp");
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [notesRecord, setNotesRecord] = useState<ClinicalRecord[]>([]);
  const [latestVitals, setLatestVitals] = useState<Vitals | null>(null);
  const [patientDocuments, setPatientDocuments] = useState<any[]>([]);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any | null>(null);
  const [vitalsHistory, setVitalsHistory] = useState<Vitals[]>([]);
  const [labHistory, setLabHistory] = useState<any[]>([]);
  const [showLaserProtocol, setShowLaserProtocol] = useState(false);

  // CLINICAL FEATURE: Secure manual Telegram dispatch state & helper function
  // Expert Commentary: Tracks Telegram invitation/reminders push statuses to avoid network lockups or double clicks on physician screens.
  const [tgStatus, setTgStatus] = useState<{
    [patientId: string]: "loading" | "success" | "error" | null;
  }>({});
  const handleSendTelegram = async (patientId: string) => {
    if (!patientId) return;
    setTgStatus((prev) => ({ ...prev, [patientId]: "loading" }));
    try {
      const res = await fetch(`/api/patients/${patientId}/notify-telegram`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${apiToken}`,
          "Content-Type": "application/json",
        },
      });
      if (res.ok) {
        setTgStatus((prev) => ({ ...prev, [patientId]: "success" }));
        setTimeout(() => {
          setTgStatus((prev) => ({ ...prev, [patientId]: null }));
        }, 3000);
      } else {
        setTgStatus((prev) => ({ ...prev, [patientId]: "error" }));
        setTimeout(() => {
          setTgStatus((prev) => ({ ...prev, [patientId]: null }));
        }, 3000);
      }
    } catch (err) {
      console.error(
        "[Clinical Tech Warning] Failed to manual dispatch telegram alert in PhysicianDashboard:",
        err,
      );
      setTgStatus((prev) => ({ ...prev, [patientId]: "error" }));
      setTimeout(() => {
        setTgStatus((prev) => ({ ...prev, [patientId]: null }));
      }, 3000);
    }
  };

  // --- Automated Google Sheets Sync ---
  useEffect(() => {
    const runAutoSync = async () => {
      const spreadsheetId = localStorage.getItem("SPREADSHEET_ID");
      if (!spreadsheetId) {
        console.log(
          "Automatic Google Sheets sync check: Needs config (Spreadsheet ID).",
        );
        return;
      }

      const accessToken = getWorkspaceAccessToken();
      if (!accessToken) return;

      const last = localStorage.getItem("lastSheetSync");
      const now = new Date();
      if (last) {
        const lastDate = new Date(last);
        const diffDays =
          (now.getTime() - lastDate.getTime()) / (1000 * 3600 * 24);
        if (diffDays < 5) return;
      }

      // Patient data format: Name, Phone, Age, Gender, Last Visit
      const dataToSync = patients.map((p) => [
        p.name,
        p.phone,
        p.age,
        p.gender,
        (p as any).lastVisit || "",
      ]);

      const result = await appendDataToSheet(
        accessToken,
        spreadsheetId,
        "Sheet1!A1",
        dataToSync,
      );
      if (result.success) {
        localStorage.setItem("lastSheetSync", now.toISOString());
        console.log("Successfully synced patient data to Sheets.");
      } else {
        console.error("Sync failed:", result.error);
      }
    };
    runAutoSync();
  }, [patients]);

  // Online Status
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Medical History fields
  const [medicalHistory, setMedicalHistory] = useState<MedicalHistory | null>(
    null,
  );
  const [historyLoading, setHistoryLoading] = useState(false);
  const [newSurgery, setNewSurgery] = useState({
    procedure: "",
    year: "",
    notes: "",
  });
  const [newCondition, setNewCondition] = useState({
    condition: "",
    diagnosedYear: "",
    status: "ACTIVE" as "ACTIVE" | "RESOLVED" | "MANAGED",
    notes: "",
    tempRx: "",
    rxList: [] as string[],
  });
  const [newAllergy, setNewAllergy] = useState({
    allergen: "",
    severity: "MILD" as const,
    reaction: "",
  });
  const [newChronicMedication, setNewChronicMedication] = useState({ name: "", dose: "", frequency: "" });

  const [cpoeSearchTerm, setCpoeSearchTerm] = useState("");

  const clinicalCatalog: Record<
    "LAB" | "X_RAY" | "PHARMACY" | "NURSING",
    string[]
  > = {
    PHARMACY: Array.from(new Set([
      ...bnfCatalog.map((item) => item.name),
      ...Object.values(iraqiTradeNamesMap).flat()
    ])),
    LAB: Array.from(new Set([...labCatalog, ...fullInvestigationCatalog.filter(i => (i as any).dept === "LAB").map(i => i.name)])),
    X_RAY: Array.from(new Set([...xrayCatalog, ...fullInvestigationCatalog.filter(i => (i as any).dept === "X_RAY").map(i => i.name)])),
    NURSING: [
      "Wound Dressing (Aseptic)",
      "IV Fluids (Normal Saline 500ml)",
      "IM Injection (Vitamin B12)",
      "IV Cannulation",
      "ECG Recording",
      "Blood Sugar Monitoring",
      "Nebulizer Setting",
      "Cryotherapy (Standard)",
      "Subcutaneous Injection",
      "Catheterization",
      "Oxygen Therapy",
      "Suture Removal",
    ],
  };

  const getDrugInteractions = (selectedDrug: string) => {
    if (!medicalHistory) return [];
    const alerts: string[] = [];
    const drug = selectedDrug.toLowerCase();

    // Simple mock logic for demonstration
    if (drug.includes("ibuprofen")) {
      if (
        medicalHistory.chronicConditions.some((c) =>
          (c.condition || "").toLowerCase().includes("asthma"),
        )
      ) {
        alerts.push(
          "Contraindication Alert: NSAIDs (Ibuprofen) may worsen existing asthma.",
        );
      }
    }
    if (drug.includes("metformin")) {
      if (
        medicalHistory.chronicConditions.some(
          (c) =>
            (c.condition || "").toLowerCase().includes("kidney") ||
            (c.condition || "").toLowerCase().includes("renal"),
        )
      ) {
        alerts.push(
          "Warning: Metformin requires dosage adjustment in renal impairment.",
        );
      }
    }
    if (drug.includes("amoxicillin") || drug.includes("penicillin")) {
      if (
        medicalHistory.allergies?.some((a) =>
          (a.allergen || "").toLowerCase().includes("penicillin"),
        )
      ) {
        alerts.push(
          "CRITICAL ALLERGY ALERT: Patient has a documented allergy to Penicillin.",
        );
      }
    }
    return alerts;
  };

  /**
   * CLINICAL SAFETY: Scans the specified batch of medicines plus any currently input/drafted drug
   * to check for serious or critical drug-drug interactions defined in SERIOUS_DRUG_INTERACTIONS catalog.
   */
  const getDrugToDrugInteractions = (
    currentList: { name: string }[],
    pendingInput?: string,
  ) => {
    try {
      const detected: {
        drugA: string;
        drugB: string;
        severity: "SERIOUS" | "CRITICAL";
        mechanism: string;
        action: string;
      }[] = [];
      const drugs = currentList.map((p) => p.name.toLowerCase().trim());
      if (medicalHistory?.chronicMedications) { drugs.push(...medicalHistory.chronicMedications.map((m) => m.name.toLowerCase().trim())); }
      if (pendingInput && pendingInput.trim()) {
        drugs.push(pendingInput.toLowerCase().trim());
      }

      for (const rule of SERIOUS_DRUG_INTERACTIONS) {
        // Find indices of drugs matching rule criteria
        const matchAIndex = drugs.findIndex((d) => d.includes(rule.drugA));
        const matchBIndex = drugs.findIndex((d) => d.includes(rule.drugB));

        // Ensure they are two distinct drugs in the list
        if (
          matchAIndex !== -1 &&
          matchBIndex !== -1 &&
          matchAIndex !== matchBIndex
        ) {
          // Resolve original names for friendly user feedback
          const findOriginalName = (index: number) => {
            if (index < currentList.length) {
              return currentList[index].name;
            }
            return pendingInput || "";
          };

          const nameA = findOriginalName(matchAIndex);
          const nameB = findOriginalName(matchBIndex);

          // Avoid duplicate pairs in report
          const alreadyLogged = detected.some(
            (d) =>
              (d.drugA.toLowerCase() === nameA.toLowerCase() &&
                d.drugB.toLowerCase() === nameB.toLowerCase()) ||
              (d.drugA.toLowerCase() === nameB.toLowerCase() &&
                d.drugB.toLowerCase() === nameA.toLowerCase()),
          );

          if (!alreadyLogged) {
            detected.push({
              drugA: nameA,
              drugB: nameB,
              severity: rule.severity,
              mechanism: rule.mechanism,
              action: rule.action,
            });
          }
        }
      }
      return detected;
    } catch (e) {
      console.error("[Clinical Safety Alert Error]", e);
      return [];
    }
  };

  // GP fields
  const [gpNotes, setGpNotes] = useState("");
  const [issuedDocs, setIssuedDocs] = useState({ report: false, sickLeave: false, receipt: false });
  const [showEmbeddedVideo, setShowEmbeddedVideo] = useState(false);

  // SOAP Notes states
  const [soapSubjective, setSoapSubjective] = useState("");
  const [soapObjective, setSoapObjective] = useState("");
  const [familyHistory, setFamilyHistory] = useState("");
  const [socialHistory, setSocialHistory] = useState("");
  const [vitalTemp, setVitalTemp] = useState("");
  const [vitalPulse, setVitalPulse] = useState("");
  const [vitalBP, setVitalBP] = useState("");
  const [vitalWeight, setVitalWeight] = useState("");
  
  // Smart Triage Import State
  const [importedTriageAppts, setImportedTriageAppts] = useState<string[]>([]);
  const [vitalHeight, setVitalHeight] = useState("");
  const [vitalBMI, setVitalBMI] = useState("");

  useEffect(() => {
    if (vitalWeight && vitalHeight) {
      const w = parseFloat(vitalWeight);
      const h = parseFloat(vitalHeight) / 100;
      if (w > 0 && h > 0) {
        setVitalBMI((w / (h * h)).toFixed(1));
      } else {
        setVitalBMI("");
      }
    } else {
      setVitalBMI("");
    }
  }, [vitalWeight, vitalHeight]);
  const [soapAssessment, setSoapAssessment] = useState("");
  const [soapPlan, setSoapPlan] = useState("");
  const [icd10, setIcd10] = useState("");
  const [prescriptions, setPrescriptions] = useState<
    { name: string; dose: string; frequency: string; duration: string }[]
  >([]);

  // Laser Therapy Fields
  const [laserLogs, setLaserLogs] = useState<LaserTherapyEntry[]>([]);
  const [laserFormData, setLaserFormData] = useState({
    treatmentArea: "",
    laserType: "Alexandrite 755nm",
    spotSize: "12",
    fluence: "10",
    pulseDuration: "3.0",
    cooling: "Cryogen 30/30",
    passes: "1",
  });
  const [skinType, setSkinType] = useState("Type III");
  const [dermPins, setDermPins] = useState([]);
  const [dermPhotos, setDermPhotos] = useState([]);

  // AI Scanner fields
  const [skinImageBase64, setSkinImageBase64] = useState<string>("");
  const [lesionDuration, setLesionDuration] = useState("3 months");
  const [aiAnalysisResult, setAiAnalysisResult] = useState<any>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  // Minor surgery fields
  const [minorSurgeryNotes, setMinorSurgeryNotes] = useState("");

  useEffect(() => {
    const bc = new BroadcastChannel('iq_doctor_calculators');
    bc.onmessage = (event) => {
      if (event.data.type === 'ADD_NOTE') {
        setGpNotes(prev => prev ? prev + '\n\n---\n' + event.data.text : event.data.text);
      }
    };
    return () => bc.close();
  }, []);

  useEffect(() => {
    let animationFrameId: number;
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
    if (!link) return;

    if (aiLoading) {
      const img = new Image();
      img.src = "/logo.png";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 32;
        canvas.height = 32;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        let angle = 0;

        const animate = () => {
          angle += 0.15;
          ctx.clearRect(0, 0, 32, 32);
          ctx.translate(16, 16);
          ctx.rotate(angle);
          ctx.drawImage(img, -16, -16, 32, 32);
          ctx.rotate(-angle);
          ctx.translate(-16, -16);
          link.href = canvas.toDataURL("image/png");

          // Throttle updates slightly to avoid browser dropping frames on favicon
          setTimeout(() => {
            animationFrameId = requestAnimationFrame(animate);
          }, 50);
        };
        animate();
      };
    } else {
      link.href = "/logo.png";
    }

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (!aiLoading) link.href = "/logo.png"; // Only restore if we are abandoning while not loading
    };
  }, [aiLoading]);

  // CPOE fields
  const [cpoeDept, setCpoeDept] = useState<
    "LAB" | "X_RAY" | "PHARMACY" | "NURSING"
  >("NURSING");
  const [cpoeInstruction, setCpoeInstruction] = useState("");
  const [cpoeNurseInput, setCpoeNurseInput] = useState("");
  const [cpoeInvInput, setCpoeInvInput] = useState("");
  const [investigations, setInvestigations] = useState<
    { name: string; dept: "LAB" | "X_RAY" }[]
  >([]);
    const [cpoeDrugInput, setCpoeDrugInput] = useState("");

  const [globalCheckResult, setGlobalCheckResult] = useState<any[] | null>(null);
  const [showGlobalCheckModal, setShowGlobalCheckModal] = useState(false);

  const [isDrugInputFocused, setIsDrugInputFocused] = useState(false);

  const [cpoeDrugDose, setCpoeDrugDose] = useState("");
  const [cpoeDrugFreq, setCpoeDrugFreq] = useState("");
  const [cpoeDrugDuration, setCpoeDrugDuration] = useState("");
  const [cpoeInvDept, setCpoeInvDept] = useState<"LAB" | "X_RAY">("LAB");


  const [dispatchedOrders, setDispatchedOrders] = useState<Order[]>([]);
  const [successMsg, setSuccessMsg] = useState("");
  const [submitFailed, setSubmitFailed] = useState(false);
  const [isSavingEhr, setIsSavingEhr] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [isAiDiagnosing, setIsAiDiagnosing] = useState(false);
  const [aiSuggestedDiagnoses, setAiSuggestedDiagnoses] = useState<any[]>([]);
  const [aiDiagnosisReasoning, setAiDiagnosisReasoning] = useState("");
  const [aiAnalysisError, setAiAnalysisError] = useState<string | null>(null);

  // Quick Register New Patient States (Additive Only)
  const [regName, setRegName] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regDob, setRegDob] = useState("");
  const [regNationalId, setRegNationalId] = useState("");
  const [regGender, setRegGender] = useState<"MALE" | "FEMALE" | "">("");
  const [regAge, setRegAge] = useState("");
  const [regBloodType, setRegBloodType] = useState("");
  const [regAllergies, setRegAllergies] = useState("");

  /* Expert Commentary: Automatically updates calculated age when date of birth changes via birth diary calendar selection */
  useEffect(() => {
    try {
      if (regDob) {
        const birthDate = new Date(regDob);
        if (!isNaN(birthDate.getTime())) {
          const today = new Date();
          let calculatedAge = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birthDate.getDate())
          ) {
            calculatedAge--;
          }
          setRegAge(Math.max(0, calculatedAge).toString());
        }
      }
    } catch (error) {
      console.error(
        "Clinical System Alert: Failed to auto-calculate register age metrics.",
        error,
      );
    }
  }, [regDob]);
  const [regDept, setRegDept] = useState<"GP" | "DERMATOLOGY">("GP");
  const [isRegistering, setIsRegistering] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  const [printDocType, setPrintDocType] = useState<
    | "RECORD"
    | "REFERRAL"
    | "CONSENT"
    | "CONSENT_PRIVATE_EXAM"
    | "PATIENT_REPORT"
    | "PRESCRIPTION"
    | "INVESTIGATION"
    | "VISIT_ORDERS"
    | "LEAFLET_ACNE"
    | "LEAFLET_ECZEMA"
    | "LEAFLET_PSORIASIS"
    | "MEDICATION_PROFILE"
    | "BILL_10000"
    | "SICK_LEAVE"
  >("RECORD");
  const [selectedOrderToPrint, setSelectedOrderToPrint] =
    useState<Order | null>(null);
  const [showDrugInfoModal, setShowDrugInfoModal] = useState<any | null>(null);
  const [activeRxDatabase, setActiveRxDatabase] = useState<
    "bnf" | "drugsdotcom" | "emc"
  >("bnf");
  const [bnfSearchText, setBnfSearchText] = useState("");
  const [bnfSelectedCategory, setBnfSelectedCategory] = useState("All");
  const [invSearchText, setInvSearchText] = useState("");
  const [invSelectedCategory, setInvSelectedCategory] = useState("All");

  // Custom Leaflet fields
  const [customLeafletQuery, setCustomLeafletQuery] = useState("");
  const [doctorInstructions, setDoctorInstructions] = useState("");
  const [customLeafletLoading, setCustomLeafletLoading] = useState(false);
  const [customLeafletResult, setCustomLeafletResult] = useState<string | null>(
    () => localStorage.getItem("default_patient_leaflet") || null,
  );
  const [leafletTemplates, setLeafletTemplates] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem("leaflet_templates") || "{}"); } catch(e) { return {}; }
  });

  useEffect(() => {
    fetch("/api/leaflet-templates", { headers: { Authorization: `Bearer ${apiToken}` } })
      .then(res => res.json())
      .then(data => {
        if (data && typeof data === 'object') {
          setLeafletTemplates(prev => {
            const merged = { ...prev, ...data };
            localStorage.setItem("leaflet_templates", JSON.stringify(merged));
            return merged;
          });
        }
      }).catch(console.error);
  }, [apiToken]);

  // Admin Module fields
  const [isAdminSettingsOpen, setIsAdminSettingsOpen] = useState(false);
  const [isClinicSettingsOpen, setIsClinicSettingsOpen] = useState(false);
  const [modalHeroImages, setModalHeroImages] = useState<string[]>([]);
  const [heroBannerEnabled, setHeroBannerEnabled] = useState(false);
  const [clinicNewsEnabled, setClinicNewsEnabled] = useState(false);
  const [clinicNewsItems, setClinicNewsItems] = useState<any[]>([]);
  const [modalClinicLogo, setModalClinicLogo] = useState<string>("");
  const [modalDoctorSignature, setModalDoctorSignature] = useState<string>("");
  const [modalClinicStamp, setModalClinicStamp] = useState<string>("");

  useEffect(() => {
    if (isClinicSettingsOpen && whiteLabel) {
      setModalHeroImages(whiteLabel.heroBannerImages || []);
      setHeroBannerEnabled(whiteLabel.heroBannerEnabled || false);
      setClinicNewsEnabled(whiteLabel.clinicNewsEnabled || false);
      setClinicNewsItems(whiteLabel.clinicNewsItems || DEFAULT_BAD_LEAFLETS);
      setModalClinicLogo(whiteLabel.clinicLogo || "");
      setModalDoctorSignature(whiteLabel.doctorSignature || "");
      setModalClinicStamp(whiteLabel.clinicStamp || "");
    }
  }, [isClinicSettingsOpen, whiteLabel]);
  const [targetUserEmail, setTargetUserEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [adminSettingLoading, setAdminSettingLoading] = useState(false);
  const [adminUsersList, setAdminUsersList] = useState<
    { name: string; email: string; role: string }[]
  >([]);

  useEffect(() => {
    if (isAdminSettingsOpen && user.role === "SUPER_ADMIN") {
      fetch("/api/admin/users", {
        headers: {
          Authorization: `Bearer ${apiToken}`,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.users) {
            setAdminUsersList(data.users);
          }
        })
        .catch((err) => console.error("Failed to load admin users:", err));
    }
  }, [isAdminSettingsOpen, user.role, apiToken]);

  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  const [customRecordDate, setCustomRecordDate] = useState<string>("");
  const [isRecordLocked, setIsRecordLocked] = useState(false);
  const [adminEditOverride, setAdminEditOverride] = useState(false);
  const [urgentOnlineConsults, setUrgentOnlineConsults] = useState<any[]>([]);

  // Clinical Online Consultation State Variables (Additive)
  const [showOnlineConsultRooms, setShowOnlineConsultRooms] = useState(false);
  const [activeOnlineConsult, setActiveOnlineConsult] = useState<any | null>(
    null,
  );
  const [liveDocuments, setLiveDocuments] = useState<
    { name: string; url: string; sender: string }[]
  >([]);
  const [customMeetUrl, setCustomMeetUrl] = useState("");
  const [isSavingMeetUrl, setIsSavingMeetUrl] = useState(false);

  // AI/Clinical Analysis
  const [isQiAnalyzing, setIsQiAnalyzing] = useState(false);
  const [qiAnalysisResult, setQiAnalysisResult] = useState<any>(null);
  const [isQiPopupOpen, setIsQiPopupOpen] = useState(false);
  const [gpDiagnoses, setGpDiagnoses] = useState<
    { disorder: string; ICD10: string }[]
  >([]);
  const [qiAnalysisError, setQiAnalysisError] = useState<string | null>(null);
  const [isApplyingQiResult, setIsApplyingQiResult] = useState(false);
  const [patientQueueFilter, setPatientQueueFilter] = useState<
    "PENDING" | "INCOMPLETE" | "COMPLETED" | "ALL"
  >("PENDING");
  const [selectedQueueDate, setSelectedQueueDate] = useState(() => {
    try {
      const tzDate = new Date(
        new Date().toLocaleString("en-US", { timeZone: "Asia/Baghdad" }),
      );
      return `${tzDate.getFullYear()}-${String(tzDate.getMonth() + 1).padStart(2, "0")}-${String(tzDate.getDate()).padStart(2, "0")}`;
    } catch {
      return new Date().toISOString().split("T")[0];
    }
  });
  const [patientSearchQuery, setPatientSearchQuery] = useState("");
  const [quickSearchStr, setQuickSearchStr] = useState("");
  const [consultTimer, setConsultTimer] = useState(600); // 10 minutes in seconds
  const [timerIsRunning, setTimerIsRunning] = useState(true);
  const [diagnosisSearchStr, setDiagnosisSearchStr] = useState("");
  const [guidelineSearchTerm, setGuidelineSearchTerm] = useState("");

  const [favoriteDiagnoses, setFavoriteDiagnoses] = useState<any[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("iqDoctorFavDiagnoses") || "[]");
    } catch {
      return [];
    }
  });

  const toggleFavoriteDiagnosis = (item: any) => {
    setFavoriteDiagnoses(prev => {
      const isFav = prev.find(p => p.name === item.name);
      const next = isFav ? prev.filter(p => p.name !== item.name) : [...prev, item];
      localStorage.setItem("iqDoctorFavDiagnoses", JSON.stringify(next));
      return next;
    });
  };

  const universalSearchData = [
    ...diseaseCatalog.map((d) => ({
      type: "DIAGNOSIS" as const,
      name: d.disorder,
      subText: d.ICD10,
      original: d,
    })),
    ...fullInvestigationCatalog.map((inv) => ({
      type: "INVESTIGATION" as const,
      name: inv.name,
      subText: `${inv.category} (${inv.dept})`,
      original: inv,
    })),
    ...bnfCatalog.map((drug) => ({
      type: "MEDICATION" as const,
      name: drug.name,
      subText: `${drug.category || "Medication"} - ${drug.defaultDose || ""} ${drug.defaultFreq || ""}`,
      original: drug,
    })),
    ...Object.entries(iraqiTradeNamesMap).flatMap(([sciName, tradeNames]) => 
      tradeNames.map(trade => ({
        type: "MEDICATION" as const,
        name: trade,
        subText: `اسم تجاري للدواء العلمي: ${sciName}`,
        original: { name: trade, defaultDose: "حسب الإرشادات", defaultFreq: "OD", defaultDuration: "7 days" }
      }))
    ),
  ];

  // Loaded presets
  const sampleSkinLesions = [
    {
      name: "Symmetric Scaly Plaque (Psoriasis Candidate)",
      url: "https://images.unsplash.com/photo-1608248597279-f99d160bfcbc?q=80&w=300&auto=format&fit=crop",
      promptBase64:
        "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD/2wBDAPX2...", // fallback
    },
    {
      name: "Mild Erythema (Eczema Candidate)",
      url: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?q=80&w=300&auto=format&fit=crop",
    },
  ];

  // Fetch data
  useEffect(() => {
    fetchPatients();
    fetchAppointments();
    fetchOrders();

    const fetchSettings = () => {
      fetch("/api/settings", {
        headers: { Authorization: `Bearer ${apiToken}` },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.adminEditOverride !== undefined)
            setAdminEditOverride(data.adminEditOverride);
        })
        .catch(console.error);
    };
    fetchSettings();

    const fetchOnline = () => {
      fetch("/api/online-consultations", {
        headers: { Authorization: `Bearer ${apiToken}` },
      })
        .then((res) => res.json())
        .then((data) =>
          setUrgentOnlineConsults(
            data.filter((c: any) => c.status === "WAITING"),
          ),
        )
        .catch(console.error);
    };
    fetchOnline();
    const interval = setInterval(fetchOnline, 5000);

    const socket = connectSocket();
    socket.on("newOnlineConsultation", () => {
      fetchOnline();
    });
    socket.on("liveDocumentTransferred", (doc: any) => {
      if (doc.sender !== "العيادة (الطبيب)") {
        setLiveDocuments((prev) => [...prev, doc]);
      }
    });

    socket.on("databaseSynchronised", () => {
      fetchPatients();
      fetchAppointments();
      fetchOrders();
      fetchOnline();
    });

    return () => {
      clearInterval(interval);
      socket.disconnect();
    };
  }, [apiToken]);

  useEffect(() => {
    if (selectedPatient) {
      setCustomRecordDate(prev => prev ? prev : new Date().toISOString());
    } else {
      setCustomRecordDate("");
    }
  }, [selectedPatient]);


  const prevPatientId = useRef<string | null>(null);
  const nextTargetApptDate = useRef<string | null>(null);

  const loadTargetRecordIntoForm = (targetRecord: any) => {
    if (!targetRecord) return;
    setEditingRecordId(targetRecord.id);
    setCustomRecordDate(targetRecord.createdAt || "");
    const relatedAppt = appointments.find((a: any) => a.patientId === targetRecord.patientId && a.dateTime === targetRecord.createdAt);
    setIsRecordLocked(!(relatedAppt && relatedAppt.status === "IN_PROGRESS"));
    setGpNotes(targetRecord.gpNotes || "");
    setSoapSubjective(targetRecord.soapSubjective || "");
    setSoapObjective(targetRecord.soapObjective || "");
    setSoapAssessment(targetRecord.soapAssessment || "");
    setSoapPlan(targetRecord.soapPlan || "");
    setFamilyHistory(targetRecord.familyHistory === "N/A" ? "" : (targetRecord.familyHistory || ""));
    setSocialHistory(targetRecord.socialHistory === "N/A" ? "" : (targetRecord.socialHistory || ""));
    setVitalTemp(targetRecord.vitalTemp || "");
    setVitalPulse(targetRecord.vitalPulse || "");
    setVitalBP(targetRecord.vitalBP || "");
    setVitalWeight(targetRecord.vitalWeight || "");
    setVitalHeight(targetRecord.vitalHeight || "");
    setVitalBMI(targetRecord.vitalBMI || "");
    setIcd10(targetRecord.icd10Codes || "");
    try {
      if (targetRecord.prescriptions) {
        setPrescriptions(JSON.parse(targetRecord.prescriptions));
      } else {
        setPrescriptions([]);
      }
    } catch {
      setPrescriptions([]);
    }
    setMinorSurgeryNotes(targetRecord.minorSurgery || "");
    try {
      if (targetRecord.printableDocs) {
        const pd = JSON.parse(targetRecord.printableDocs);
        if (pd && pd.issuedDocs) {
          setIssuedDocs(pd.issuedDocs);
        } else {
          setIssuedDocs({ report: false, sickLeave: false, receipt: false });
        }
        // Restore receipt/bill data to localStorage so finance sync works on re-save
        const restoreKey = targetRecord.id || targetRecord.patientId;
        if (pd.billPrice) localStorage.setItem(`bill_price_${restoreKey}`, pd.billPrice);
        if (pd.billPriceWords) localStorage.setItem(`bill_price_words_${restoreKey}`, pd.billPriceWords);
        if (pd.billCategory) localStorage.setItem(`bill_category_${restoreKey}`, pd.billCategory);
        if (pd.receiptName) localStorage.setItem(`receipt_name_${restoreKey}`, pd.receiptName);
        if (pd.reportTitle) localStorage.setItem(`report_title_${restoreKey}`, pd.reportTitle);
        if (pd.reportSub) localStorage.setItem(`report_sub_${restoreKey}`, pd.reportSub);
        if (pd.reportInstructionsHeader) localStorage.setItem(`report_instr_hdr_${restoreKey}`, pd.reportInstructionsHeader);
        if (pd.reportInstructions) localStorage.setItem(`report_instr_${restoreKey}`, pd.reportInstructions);
        if (pd.referralFindingsHeader) localStorage.setItem(`referral_findings_hdr_${restoreKey}`, pd.referralFindingsHeader);
        if (pd.referralFindings) localStorage.setItem(`referral_findings_${restoreKey}`, pd.referralFindings);
        if (pd.referralAdditionalNotes) localStorage.setItem(`referral_add_notes_${restoreKey}`, pd.referralAdditionalNotes);
        if (pd.recordClinicalNotes) localStorage.setItem(`rec_clinical_notes_${restoreKey}`, pd.recordClinicalNotes);
      } else {
        setIssuedDocs({ report: false, sickLeave: false, receipt: false });
      }
    } catch {
      setIssuedDocs({ report: false, sickLeave: false, receipt: false });
    }
    try {
      if (targetRecord.faceMapJson)
        setLaserLogs(JSON.parse(targetRecord.faceMapJson));
    } catch {
      setLaserLogs([]);
    }
    try {
      if (targetRecord.dermaData) {
        const dd = JSON.parse(targetRecord.dermaData);
        setDermPins(dd.pins || []);
        setDermPhotos(dd.photos || []);
      } else {
        setDermPins([]);
        setDermPhotos([]);
      }
    } catch {
      setDermPins([]);
      setDermPhotos([]);
    }
    try {
      if (targetRecord.aiMetadata)
        setAiAnalysisResult(JSON.parse(targetRecord.aiMetadata));
    } catch {
      setAiAnalysisResult(null);
    }
  };

  useEffect(() => {
    if (selectedPatient) {
      // Only reset the clinician's notes draft if we have actually navigated to a DIFFERENT patient record.
      // This prevents data loss if a background sync (via websocket) refreshes the patient object reference but the ID remains the same.
      const isNewPatient = selectedPatient.id !== prevPatientId.current;

      fetchPatientDocuments(selectedPatient.id);
      fetchPatientVitals(selectedPatient.id);
      fetchPatientClinicalRecords(selectedPatient.id);
      fetchPatientMedicalHistory(selectedPatient.id);

      if (isNewPatient) {
        // EXPERT COMMENTARY: Automatically upgrade any PENDING appointment for this patient today to IN_PROGRESS
        const todayStr = new Date().toISOString().split("T")[0];
        const todaysAppt = appointments.find((a: any) => a.patientId === selectedPatient.id && a.status === "PENDING" && a.dateTime.startsWith(todayStr));
        if (todaysAppt) {
           fetch(`/api/appointments/${todaysAppt.id}/status`, {
             method: "POST",
             headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
             body: JSON.stringify({ status: "IN_PROGRESS", openedAt: new Date().toISOString() })
           }).then(() => fetchAppointments()).catch(console.error);
        }

        // Clinical Data Protection: Only clear draft inputs when a new patient is loaded from the sidebar
        setGpNotes(""); setIssuedDocs({ report: false, sickLeave: false, receipt: false });
        setSoapSubjective("");
        setSoapObjective("");
        setSoapAssessment("");
        setSoapPlan("");
        setFamilyHistory("");
        setSocialHistory("");
        setMedicalHistory(null);
        setIcd10("");
        setVitalTemp("");
        setVitalPulse("");
        setVitalBP("");
        setVitalWeight("");
        setVitalHeight("");
        setVitalBMI("");
        setPrescriptions([]);
        setLaserLogs([]);
        setMinorSurgeryNotes("");
        setAiAnalysisResult(null);
        setSkinImageBase64("");
        setNewSurgery({ procedure: "", year: "", notes: "" });
        setNewCondition({
          condition: "",
          diagnosedYear: "",
          status: "ACTIVE",
          notes: "",
        });
        setGpDiagnoses([]);
        setCustomLeafletQuery("");
        setDoctorInstructions("");
        setCustomLeafletResult(localStorage.getItem("default_patient_leaflet") || null);
        setEditingRecordId(null);
        setCustomRecordDate("");
        setIsRecordLocked(false);
        setLaserLogs([]);
        setLaserFormData({
          treatmentArea: "",
          laserType: "Alexandrite 755nm",
          spotSize: "12",
          fluence: "10",
          pulseDuration: "3.0",
          cooling: "Cryogen 30/30",
          passes: "1",
        });
        setSkinType("Type III");
        setDermPins([]);
        setDermPhotos([]);
      }

      // Update the tracking ref for the next sync cycle
      prevPatientId.current = selectedPatient.id;
    } else {
      prevPatientId.current = null;
    }
  }, [selectedPatient?.id]);

  const fetchPatients = async () => {
    try {
      const res = await fetch("/api/patients", {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const data = await res.json();
      if (res.ok) {
        setPatients(data);
        // Under USER INTENT to prevent Layla Hassan from always preloading we do NOT auto-select data[0]
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleQiDoctorSuggest = async () => {
    if (!selectedPatient) return;
    setIsQiAnalyzing(true);
    setQiAnalysisResult(null);
    try {
      let age = "Unknown";
      if (selectedPatient.dob) {
        age = (
          new Date().getFullYear() - new Date(selectedPatient.dob).getFullYear()
        ).toString();
      }

      console.log(
        `[Clinical AI Assistant] Dispatching diagnostic analysis for patient: ${selectedPatient.id}`,
      );

      const res = await fetch("/api/clinical/qi-doctor-suggest", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          soapSubjective: (soapSubjective || "").trim(),
          soapObjective: (soapObjective || "").trim(),
          soapAssessment: (soapAssessment || "").trim(),
          soapPlan: (soapPlan || "").trim(),
          gpNotes,
          patientId: selectedPatient.id,
          age,
          sex: selectedPatient.gender || "Unknown",
          vitals: latestVitals,
          lesionDuration:
            typeof lesionDuration !== "undefined" ? lesionDuration : "Unknown",
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to fetch suggestions");

      setQiAnalysisResult(data);
    } catch (err: any) {
      console.error("[Clinical AI Error] Failure in Qi-Doctor Suggest:", err);
      // We could set an error state here if needed
    } finally {
      setIsQiAnalyzing(false);
    }
  };

  const handleSuggestDiagnosis = async () => {
    if (!selectedPatient) return;
    setIsAiDiagnosing(true);
    setAiSuggestedDiagnoses([]);
    setAiDiagnosisReasoning("");
    setAiAnalysisError(null);
    try {
      let age = "Unknown";
      if (selectedPatient.dob) {
        age = (
          new Date().getFullYear() - new Date(selectedPatient.dob).getFullYear()
        ).toString();
      }
      const res = await fetch("/api/clinical/suggest-diagnosis", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          gpNotes,
          soapSubjective: (soapSubjective || "").trim(),
          soapObjective: (soapObjective || "").trim(),
          soapAssessment: (soapAssessment || "").trim(),
          soapPlan: (soapPlan || "").trim(),
          patientId: selectedPatient.id,
          age,
          sex: selectedPatient.gender || "Unknown",
          vitals: latestVitals,
        }),
      });
      const data = await res.json();
      if (!res.ok)
        throw new Error(data.error || "Failed to fetch AI suggestions");
      setAiSuggestedDiagnoses(data.suggestedDiagnoses || []);
      setAiDiagnosisReasoning(data.reasoning || "");
    } catch (err: any) {
      setAiAnalysisError(
        err.message || "An unexpected error occurred during AI suggestion.",
      );
    } finally {
      setIsAiDiagnosing(false);
    }
  };

  useEffect(() => {
    if (selectedPatient) {
      setConsultTimer(600);
      setTimerIsRunning(true);
    }
  }, [selectedPatient?.id]);

  useEffect(() => {
    if (!timerIsRunning || !selectedPatient) return;
    const interval = setInterval(() => {
      setConsultTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [timerIsRunning, !!selectedPatient]);

  const handleSelectUniversalSuggestion = (item: {
    type: "DIAGNOSIS" | "INVESTIGATION" | "MEDICATION";
    name: string;
    subText: string;
    original: any;
  }) => {
    if (item.type === "DIAGNOSIS") {
      if (!gpDiagnoses.some((d) => d.disorder === item.name)) {
        setGpDiagnoses((prev) => [
          ...prev,
          { disorder: item.name, ICD10: item.original.ICD10 || "ICD-10" },
        ]);
      }
      setSoapAssessment((prev) => (prev ? prev + ", " + item.name : item.name));
      setIcd10((prev) =>
        prev
          ? prev + ", " + (item.original.ICD10 || "ICD-10")
          : item.original.ICD10 || "ICD-10",
      );
      triggerSuccessToast(`Added Diagnosis: ${item.name}`);
    } else if (item.type === "INVESTIGATION") {
      const deptVal = item.original.dept || "LAB";
      if (!investigations.some((i) => i.name === item.name)) {
        setInvestigations((prev) => [
          ...prev,
          { name: item.name, dept: deptVal },
        ]);
      }
      triggerSuccessToast(`Added Test: ${item.name} to Active Requests`);
    } else if (item.type === "MEDICATION") {
      if (!prescriptions.some((p) => p.name === item.name)) {
        setPrescriptions((prev) => [
          ...prev,
          {
            name: item.name,
            dose: item.original.defaultDose || "As directed",
            frequency: item.original.defaultFreq || "OD (Once daily)",
            duration: item.original.defaultDuration || "7 days",
          },
        ]);
      }
      triggerSuccessToast(
        `Added Medication: ${item.name} to Draft Prescription`,
      );
    }
  };

  const handleAcceptQiSuggestedAnalysis = () => {
    if (!qiAnalysisResult) return;
    setIsApplyingQiResult(true);

    try {
      const suggestedDisorder = qiAnalysisResult.mostAccurateDiagnosis;
      if (suggestedDisorder && suggestedDisorder !== "I don't know.") {
        const alreadyExists = gpDiagnoses.some(
          (d) => d.disorder.toLowerCase() === suggestedDisorder.toLowerCase(),
        );
        if (!alreadyExists) {
          setGpDiagnoses((prev) => [
            ...prev,
            { disorder: suggestedDisorder, ICD10: "M99.9" },
          ]);
        }
      }

      let updatedAssessment = soapAssessment ? soapAssessment.trim() : "";
      if (suggestedDisorder && suggestedDisorder !== "I don't know.") {
        if (updatedAssessment) updatedAssessment += "\n\n";
        updatedAssessment += `[Clinical Advisor Assessment (Accepted)]: ${suggestedDisorder}`;
      }

      if (
        qiAnalysisResult.differentialDiagnosis &&
        qiAnalysisResult.differentialDiagnosis.length > 0
      ) {
        updatedAssessment +=
          `\nDifferential Diagnosis Spectrum:\n` +
          qiAnalysisResult.differentialDiagnosis
            .map(
              (diff: any) =>
                `- ${diff.disorder} (Suspect Probability: ${diff.probability})`,
            )
            .join("\n");
      }
      setSoapAssessment(updatedAssessment);

      let updatedPlan = soapPlan ? soapPlan.trim() : "";
      if (
        qiAnalysisResult.differentialDiagnosis &&
        qiAnalysisResult.differentialDiagnosis.length > 0
      ) {
        if (updatedPlan) updatedPlan += "\n\n";
        updatedPlan += `[Clinical Advisor Management Protocols (Accepted)]:\n`;
        qiAnalysisResult.differentialDiagnosis.forEach((diff: any) => {
          if (diff.recommendedManagement) {
            updatedPlan += `- For suspected ${diff.disorder}: ${diff.recommendedManagement}\n`;
          }
        });
      }
      setSoapPlan(updatedPlan);

      const currentInvestigations = [...investigations];
      if (
        qiAnalysisResult.immediateInvestigations &&
        qiAnalysisResult.immediateInvestigations.length > 0
      ) {
        qiAnalysisResult.immediateInvestigations.forEach((testName: string) => {
          if (
            testName &&
            testName !== "No immediate labs designated." &&
            testName !==
              "No immediate labs designated. Check general care guidelines."
          ) {
            const isDuplicate = currentInvestigations.some(
              (inv) => inv.name.toLowerCase() === testName.toLowerCase(),
            );
            if (!isDuplicate) {
              const isRadiology =
                /x-ray|cxr|ultrasound|scan|mri|ct|echo|radiology|imaging|mammogram/i.test(
                  testName,
                );
              currentInvestigations.push({
                name: testName,
                dept: isRadiology ? "X_RAY" : "LAB",
              });
            }
          }
        });
        setInvestigations(currentInvestigations);
      }

      setSuccessMsg("✓ Clinical Analysis applied successfully!");
      setTimeout(() => setSuccessMsg(""), 4500);
    } catch (applyErr: any) {
      console.error(
        "[Clinical Application Error] Failed to write suggestion data safely:",
        applyErr,
      );
    } finally {
      setIsApplyingQiResult(false);
    }
  };

  const endPhysicianCall = async (completeCompletely: boolean = false) => {
    if (activeOnlineConsult) {
      const finalStatus = completeCompletely ? "COMPLETED" : "WAITING";
      await fetch(
        `/api/online-consultations/${activeOnlineConsult.id}/status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiToken}`,
          },
          body: JSON.stringify({ status: finalStatus }),
        },
      ).catch(console.error);
    }
    setActiveOnlineConsult(null);
    setShowEmbeddedVideo(false);
  };

  const saveCustomMeetUrl = async (newUrl: string) => {
    if (!activeOnlineConsult) return;
    const sanitizedUrl = newUrl.trim();
    if (!sanitizedUrl.startsWith("https://meet.google.com/")) {
      return;
    }

    setIsSavingMeetUrl(true);
    try {
      const response = await fetch(
        `/api/online-consultations/${activeOnlineConsult.id}/meet-url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiToken}`,
          },
          body: JSON.stringify({ meetUrl: sanitizedUrl }),
        },
      );
      if (response.ok) {
        setActiveOnlineConsult((prev: any) =>
          prev ? { ...prev, meetUrl: sanitizedUrl } : null,
        );
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setIsSavingMeetUrl(false);
    }
  };

  const handleLiveDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeOnlineConsult) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const newDoc = {
        name: file.name,
        url: reader.result as string,
        sender: "العيادة (الطبيب)",
        consultationId: activeOnlineConsult.id.toString(),
      };
      const socket = connectSocket();
      socket.emit("liveDocumentTransfer", newDoc);
      setLiveDocuments((prev) => [...prev, newDoc]);
    };
    reader.readAsDataURL(file);
  };

  // Helper to normalize Arabic characters for robust comparison to avoid double registrations
  const normalizeArabic = (str: string) => {
    if (!str) return "";
    return str
      .trim()
      .toLowerCase()
      .replace(/[أإآا]/g, "ا")
      .replace(/ة/g, "ه")
      .replace(/ى/g, "ي")
      .replace(/[\s-]/g, "");
  };

  const handlePhysicianOneClickCheckIn = async (p: Patient) => {
    try {
      // Calculate updated age based on DOB
      let calculatedAge = p.age || 35;
      if (p.dob) {
        const birthDate = new Date(p.dob);
        if (!isNaN(birthDate.getTime())) {
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birthDate.getDate())
          ) {
            age--;
          }
          calculatedAge = Math.max(0, age);
        }
      }

      // 1. Update patient age via PUT /api/patients/:id
      const updateRes = await fetch(`/api/patients/${p.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({ age: calculatedAge }),
      });

      let updatedPatient = p;
      if (updateRes.ok) {
        updatedPatient = await updateRes.json();
        setPatients((prev) =>
          prev.map((item) => (item.id === p.id ? updatedPatient : item)),
        );
      }

      // 2. Automated waiting list check-in
      const apptRes = await fetch("/api/appointments", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          patientId: updatedPatient.id,
          physicianId: user.email,
          dateTime: new Date().toISOString(),
          department: regDept,
        }),
      });

      if (apptRes.ok) {
        const apptData = await apptRes.json();
        setAppointments((prev) => [...prev, apptData]);

        // Reset registration fields
        setRegName("");
        setRegPhone("");
        setRegDob("");
        setRegNationalId("");
        setRegGender("");
        setRegAge("");
        setRegBloodType("");
        setRegAllergies("");

        // Select the existing patient to open workspace EHR
        nextTargetApptDate.current = "NEW:" + apptData.dateTime;
        setSelectedPatient(updatedPatient);
        triggerSuccessToast(
          `تم تسجيل زيارة جديدة وتحديث عمر المريض "${updatedPatient.name}" إلى ${calculatedAge} بنجاح!`,
        );
      }
    } catch (err) {
      console.error(
        "Clinical System Error: One-click check-in failed inside Physician Dashboard:",
        err,
      );
    }
  };

  /**
   * QUICK REGISTRATION IN CLINIC SYSTEM (ADDITIVE ROUTINE)
   * Registers a new patient with required demographics (Name, Phone, DOB) and checking them in
   * automatically. Once created, launches their EHR workstation session instantly.
   */
  const handleQuickRegisterPatient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regName.trim() || !regPhone.trim() || !regDob) {
      alert(
        "الرجاء ملء حقول الاسم والهاتف وتاريخ الميلاد الأساسية لإنشاء ملف المريض.",
      );
      return;
    }

    setIsRegistering(true);
    try {
      // 1. Submit patient registration to server
      const res = await fetch("/api/patients", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          name: regName.trim(),
          phone: regPhone.trim(),
          dob: regDob,
          nationalId: regNationalId.trim(),
          gender: regGender || "MALE",
          age: regAge
            ? parseInt(regAge)
            : new Date().getFullYear() - new Date(regDob).getFullYear(),
          bloodType: regBloodType.trim(),
          allergies: regAllergies
            ? regAllergies
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [],
        }),
      });

      const newPatient = await res.json();
      if (!res.ok) {
        throw new Error(
          newPatient.error || "خطأ أثناء محاولة تسجيل المريض الجديد.",
        );
      }

      // 2. Add to active array
      setPatients((prev) => [...prev, newPatient]);

      // 3. Register a corresponding checked-in waiting Row appointment for them to appear on the side panel
      const apptRes = await fetch("/api/appointments", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          patientId: newPatient.id,
          physicianId: user.email,
          dateTime: new Date().toISOString(),
          department: regDept,
        }),
      });
      if (apptRes.ok) {
        const apptData = await apptRes.json();
        setAppointments((prev) => [...prev, apptData]);
        nextTargetApptDate.current = "NEW:" + apptData.dateTime;
      }

      // 4. Reset intake form controls
      setRegName("");
      setRegPhone("");
      setRegDob("");
      setRegNationalId("");
      setRegGender("");
      setRegAge("");
      setRegBloodType("");
      setRegAllergies("");

      // 5. Instantly load the brand-new patient's professional clinical workspace chart / EHR!
      setSelectedPatient(newPatient);
      triggerSuccessToast(
        `تم تسجيل المريض "${newPatient.name}" بنجاح وفتح ملف الاستشارة الحالي.`,
      );
    } catch (err: any) {
      console.error(err);
      alert(err.message || "حدث خطأ غير متوقع أثناء تسجيل المريض.");
    } finally {
      setIsRegistering(false);
    }
  };

  const fetchAppointments = async () => {
    try {
      const res = await fetch("/api/appointments", {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const data = await res.json();
      if (res.ok) setAppointments(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await fetch("/api/orders", {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const data = await res.json();
      if (res.ok) setDispatchedOrders(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPatientDocuments = async (pId: string) => {
    try {
      const res = await fetch(`/api/documents/${pId}`, {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const data = await res.json();
      if (res.ok && Array.isArray(data)) {
        setPatientDocuments(data);
      } else {
        setPatientDocuments([]);
      }
    } catch (err) {
      console.error("Failed to fetch documents", err);
      setPatientDocuments([]);
    }
  };

  useEffect(() => {
    const handleRefreshDocs = () => {
      if (selectedPatient) {
        fetchPatientDocuments(selectedPatient.id);
      }
    };
    window.addEventListener("refreshPatientDocuments", handleRefreshDocs);
    return () => window.removeEventListener("refreshPatientDocuments", handleRefreshDocs);
  }, [selectedPatient, apiToken]);

  const fetchPatientVitals = async (pId: string) => {
    try {
      const res = await fetch(`/api/patients/${pId}/vitals`, {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const data = await res.json();
      if (res.ok && Array.isArray(data)) {
        setVitalsHistory(data);
        setLatestVitals(data.length > 0 ? data[data.length - 1] : null);
      } else {
        setVitalsHistory([]);
        setLatestVitals(null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPatientMedicalHistory = async (pId: string) => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`/api/patients/${pId}/medical-history`, {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const data = await res.json();
      if (res.ok) {
        setMedicalHistory(data);
        setFamilyHistory(data.familyHistory || "");
        setSocialHistory(data.socialHistory || "");
      } else {
        setMedicalHistory(null);
        setFamilyHistory("");
        setSocialHistory("");
      }
    } catch (err) {
      console.error(err);
      setMedicalHistory(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleUpdateMedicalHistory = async (updated: MedicalHistory) => {
    if (!selectedPatient) return;
    const finalUpdate = {
      ...updated,
      familyHistory: updated.familyHistory !== undefined ? updated.familyHistory : medicalHistory?.familyHistory || "",
      socialHistory: updated.socialHistory !== undefined ? updated.socialHistory : medicalHistory?.socialHistory || "",
    };
    try {
      const res = await fetch(
        `/api/patients/${selectedPatient.id}/medical-history`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiToken}`,
          },
          body: JSON.stringify(finalUpdate),
        },
      );
      const data = await res.json();
      if (res.ok) {
        setMedicalHistory(data);
        setFamilyHistory(data.familyHistory || "");
        setSocialHistory(data.socialHistory || "");
        triggerSuccessToast("Medical history record updated successfully.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPatientClinicalRecords = async (pId: string) => {
    try {
      const res = await fetch(`/api/clinical/records/${pId}`, {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const data = await res.json();
      if (res.ok) {
        setNotesRecord(data);

        const getDbDateStr = (dStr: string) => {
          const d = new Date(dStr);
          const tzDate = new Date(
            d.toLocaleString("en-US", { timeZone: "Asia/Baghdad" }),
          );
          return `${tzDate.getFullYear()}-${String(tzDate.getMonth() + 1).padStart(2, "0")}-${String(tzDate.getDate()).padStart(2, "0")}`;
        };

        if (nextTargetApptDate.current === "FORCE_LATEST") {
          const matchRecord = [...data].sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )[0];
          if (matchRecord) {
            loadTargetRecordIntoForm(matchRecord);
          } else {
            const fallbackRecord = [...data].sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
            if (fallbackRecord) {
                                                setSoapObjective("");
                                                setSoapAssessment("");
                                                setSoapPlan("");
                                                setIcd10("");
                                                setPrescriptions([]);
                                                try { setLaserLogs([]); } catch(e){}
                                                try { setMinorSurgeryNotes(""); } catch(e){}
                                                try { setAiAnalysisResult(null); } catch(e){}
                                                try { setGpDiagnoses([]); } catch(e){}
                                                try { setCustomLeafletResult(null); } catch(e){}
                                                try { setDoctorInstructions(""); } catch(e){}
                                                try { setSkinType(""); } catch(e){}
                                              }
          }
          nextTargetApptDate.current = null;
        } else if (nextTargetApptDate.current) {
          let rawDate = nextTargetApptDate.current;
          let isForcedNew = false;
          if (rawDate.startsWith("NEW:")) {
             isForcedNew = true;
             rawDate = rawDate.substring(4);
          }
          const targetStr = rawDate; // Exact time match required
          const matchRecord = data.find((r: any) => {
            if (!r.createdAt) return false;
            return r.createdAt === targetStr;
          });
          if (matchRecord) {
            loadTargetRecordIntoForm(matchRecord);
          } else {
            // EXPERT COMMENTARY: Check if this is an active pending booking. If so, open a blank consultation. Otherwise load the latest record.
            const hasPending = appointments?.some((a: any) => a.patientId === pId && (a.status === "PENDING" || a.status === "IN_PROGRESS") && a.dateTime === targetStr);
            if (isForcedNew || hasPending) {
              setEditingRecordId(null);
              setIsRecordLocked(false);
              setCustomRecordDate(rawDate);
              setGpNotes(""); setIssuedDocs({ report: false, sickLeave: false, receipt: false });
              setSoapSubjective("");
              setSoapObjective("");
              setSoapAssessment("");
              setSoapPlan("");
              setIcd10("");
              setPrescriptions([]);
              setSkinType("");
              setNewCondition({
                condition: "",
                diagnosedYear: "",
                status: "ACTIVE",
                notes: "",
              });
              setGpDiagnoses([]);
              setDoctorInstructions("");
              setCustomLeafletResult(localStorage.getItem("default_patient_leaflet") || null);
              setLaserLogs([]);
              setMinorSurgeryNotes("");
              setAiAnalysisResult(null);
            } else {
              setEditingRecordId(null);
              setIsRecordLocked(true);
              setCustomRecordDate(rawDate);
              setGpNotes(""); setIssuedDocs({ report: false, sickLeave: false, receipt: false });
              setSoapSubjective("");
              setSoapObjective("");
              setSoapAssessment("");
              setSoapPlan("");
              setIcd10("");
              setPrescriptions([]);
              setSkinType("");
              setNewCondition({
                condition: "",
                diagnosedYear: "",
                status: "ACTIVE",
                notes: "",
              });
              setGpDiagnoses([]);
              setDoctorInstructions("");
              setCustomLeafletResult(localStorage.getItem("default_patient_leaflet") || null);
              setLaserLogs([]);
              setMinorSurgeryNotes("");
              setAiAnalysisResult(null);
            }
          }
          nextTargetApptDate.current = null;
        } else {
          // By user request, ALWAYS load the absolute latest consultation (FORCE_LATEST logic), 
          // never open a blank empty consultation by default!
          const matchRecord = [...data].sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )[0];

          if (matchRecord) {
            loadTargetRecordIntoForm(matchRecord);
          } else {
            // Patient has never had any consultation, default to a completely new, blank form.
            setEditingRecordId(null);
            setIsRecordLocked(false);
            setGpNotes(""); setIssuedDocs({ report: false, sickLeave: false, receipt: false });
            setSoapSubjective("");
            setSoapObjective("");
            setSoapAssessment("");
            setSoapPlan("");
            setIcd10("");
            setPrescriptions([]);
            setSkinType("");
            setNewCondition({
              condition: "",
              diagnosedYear: "",
              status: "ACTIVE",
              notes: "",
            });
            setGpDiagnoses([]);
            setDoctorInstructions("");
            setCustomLeafletResult(localStorage.getItem("default_patient_leaflet") || null);
            setLaserLogs([]);
            setLaserFormData({
              treatmentArea: "",
              laserType: "Alexandrite 755nm",
              spotSize: "12",
              fluence: "10",
              pulseDuration: "3.0",
              cooling: "Cryogen 30/30",
              passes: "1",
            });
            setMinorSurgeryNotes("");
            setAiAnalysisResult(null);
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Generic Dispatch CPOE Order
  const handleDispatchSpecificOrder = async (
    dept: "LAB" | "X_RAY" | "PHARMACY" | "NURSING",
    instruction: string,
    resetFn: () => void,
  ) => {
    if (!selectedPatient || !instruction) return;
    try {
      const res = await fetch("/api/orders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          patientId: selectedPatient.id,
          physicianId: user.email,
          department: dept,
          instruction: instruction,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setDispatchedOrders([data, ...dispatchedOrders]);
        resetFn();
        triggerSuccessToast(`${dept} Order dispatched successfully!`);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Dispatch CPOE Order (Legacy for form)
  const handleDispatchOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    handleDispatchSpecificOrder(cpoeDept, cpoeInstruction, () =>
      setCpoeInstruction(""),
    );
  };

  // Trigger Gemini dermatological scan via Server-side proxy
  const triggerAiAnalysis = async (customBase64?: string) => {
    const imgData = customBase64 || skinImageBase64;
    if (!imgData || !selectedPatient) return;

    setAiLoading(true);
    setAiError("");
    setAiAnalysisResult(null);

    // Auto calculate age from simple ISO string if available
    let age = "36";
    if (selectedPatient.dob) {
      const birth = new Date(selectedPatient.dob).getFullYear();
      age = (new Date().getFullYear() - birth).toString();
    }

    try {
      const res = await fetch("/api/clinical/analyze", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          image: imgData,
          age,
          sex: "Female",
          duration: lesionDuration,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          data.error || "Gemini evaluated but returned failed analysis status",
        );
      }

      setAiAnalysisResult(data);
    } catch (err: any) {
      setAiError(
        err.message ||
          "An error occurred with Vertex AI Decision Support. Re-trying...",
      );
    } finally {
      setAiLoading(false);
    }
  };

  const triggerQiDermatologyAnalysis = async (customBase64?: string) => {
    const imgData = customBase64 || skinImageBase64;
    if (!imgData || !selectedPatient) return;

    setIsQiAnalyzing(true);
    setQiAnalysisResult(null);
    setQiAnalysisError(null);

    // Auto calculate age from simple ISO string if available
    let age = "Unknown";
    if (selectedPatient.dob) {
      const birth = new Date(selectedPatient.dob).getFullYear();
      age = (new Date().getFullYear() - birth).toString();
    }

    try {
      const res = await fetch("/api/clinical/qi-doctor-suggest", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          image: imgData,
          age,
          sex: "Female",
          lesionDuration: lesionDuration,
          soapSubjective:
            soapSubjective ||
            "Specialist dermatology review request via clinical scanner module.",
          soapObjective:
            soapObjective ||
            "Skin lesion photo submitted for specialist evaluation.",
          soapAssessment:
            soapAssessment || "Undergoing dermatologist evaluation.",
          soapPlan: soapPlan || "Formulate definitive dermo care workflow.",
          vitals: latestVitals,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          data.error || "Qi-Doctor professor analysis returned failed status",
        );
      }

      setQiAnalysisResult(data);
    } catch (err: any) {
      setQiAnalysisError(
        err.message ||
          "An unexpected error occurred during Qi-Doctor evaluation.",
      );
    } finally {
      setIsQiAnalyzing(false);
    }
  };

  const handleGenerateLeaflet = async () => {
    const hasDiagnoses = gpDiagnoses.length > 0;
    if (
      !customLeafletQuery.trim() &&
      !doctorInstructions.trim() &&
      !hasDiagnoses
    )
      return;
    setCustomLeafletLoading(true);
    setCustomLeafletResult(null);
    try {
      const res = await fetch("/api/clinical/generate-patient-instructions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          condition: customLeafletQuery,
          doctorInstructions: doctorInstructions,
          diagnoses: gpDiagnoses.map((d) => d.disorder),
          department: tab === "derma" ? "Dermatology" : "GP",
        }),
      });
      const data = await res.json();
      if (data.leaflet) setCustomLeafletResult(data.leaflet);
    } catch (err) {
      console.error(err);
    } finally {
      setCustomLeafletLoading(false);
    }
  };

  const handleAdminChangePassword = async () => {
    if (!targetUserEmail.trim()) {
      alert("Please provide the target user email address.");
      return;
    }
    if (!newPassword.trim() && !newUsername.trim()) {
      alert(
        "Please provide at least a new username or a new password to modify.",
      );
      return;
    }
    setAdminSettingLoading(true);
    try {
      const res = await fetch("/api/admin/change-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          targetEmail: targetUserEmail,
          newPassword: newPassword.trim() || undefined,
          newUsername: newUsername.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        triggerSuccessToast(
          data.message || "User credentials successfully changed.",
        );
        setIsAdminSettingsOpen(false);
        setTargetUserEmail("");
        setNewPassword("");
        setNewUsername("");
      } else {
        alert(data.error || "Failed to update user credentials.");
      }
    } catch (err) {
      console.error(err);
      alert("Network error.");
    } finally {
      setAdminSettingLoading(false);
    }
  };

  /**
   * CLINICAL AUDITING: SECURE DATABASE snap-shot export handler
   * Provides full offline data persistence, backup retention, and recovery capabilities.
   * Prompts a file download of the entire clinical database schema.
   */
  const handleExportBackup = async () => {
    try {
      setAdminSettingLoading(true);
      const res = await fetch("/api/admin/backup", {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      if (!res.ok) {
        throw new Error("Failed to export clinical backup payload.");
      }
      const data = await res.json();

      // Serialize database payload to offline JSON format for 5+ years archiving
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `iq_doctor_clinical_backup_${new Date().toISOString().split("T")[0]}.json`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      triggerSuccessToast("EHR Archive Backup Exported Successfully!");
    } catch (e: any) {
      console.error(e);
      alert("خطأ أثناء تحضير وتحميل النسخة الاحتياطية للسجلات الطبية.");
    } finally {
      setAdminSettingLoading(false);
    }
  };

  /**
   * CLINICAL INGESTION: SECURE database restoration handler
   * Reconstructs patients list, visits, appointments, and medical records from an exported file.
   */
  const handleImportBackup = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        setAdminSettingLoading(true);
        const text = e.target?.result as string;
        const parsed = JSON.parse(text);

        // Standard Clinical Verification for safety and structural consistency
        const dbPayload = parsed.database || parsed;
        if (
          !dbPayload ||
          !Array.isArray(dbPayload.patients) ||
          !Array.isArray(dbPayload.clinicalRecords)
        ) {
          alert(
            "فشلت عملية التحقق: ملف النسخة الاحتياطية لا يحتوي على سجلات المرضى والزيارات المعتمدة.",
          );
          return;
        }

        const confirmRestore = window.confirm(
          `تحذير أمني سريري: هل أنت متأكد من رغبتك في استعادة السجلات السريرية؟ سيتم تحديث قاعدة البيانات الحالية لتشمل ${dbPayload.patients.length} مريض و ${dbPayload.clinicalRecords.length} سجل طبي للزيارات.`,
        );
        if (!confirmRestore) return;

        const res = await fetch("/api/admin/restore", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiToken}`,
          },
          body: JSON.stringify({ database: dbPayload }),
        });

        const resData = await res.json();
        if (res.ok) {
          triggerSuccessToast("تمت استعادة السجلات والملفات الطبية بنجاح!");
          // Reload datasets in the active dashboard view safely
          await fetchPatients();
          await fetchAppointments();
          await fetchOrders();
          if (selectedPatient) {
            await fetchPatientDocuments(selectedPatient.id);
            await fetchPatientVitals(selectedPatient.id);
            await fetchPatientMedicalHistory(selectedPatient.id);
            await fetchPatientClinicalRecords(selectedPatient.id);
          }
        } else {
          alert(
            resData.error || "خطأ أثناء محاولة دمج السجلات السريرية المستوردة.",
          );
        }
      } catch (err) {
        console.error(err);
        alert(
          "خطأ في قراءة ملف السجلات الطبية. يرجى التأكد من اختيار ملف مدعوم وبصيغة JSON صحيحة.",
        );
      } finally {
        setAdminSettingLoading(false);
        // Reset file input target value
        event.target.value = "";
      }
    };
    reader.readAsText(file);
  };

  // Save the full EHR Patient Consultation Report (Clinical Record)
  const handleSaveEhrReport = async (
    forceSave: boolean = false,
    forceSaveAsNew: boolean = false,
  ) => {
    if (!selectedPatient) return;

    const isEditing = editingRecordId && !forceSaveAsNew;

    // Clinical Security: If editing an old historical record, pop up a confirmation alert
    if (isEditing && !forceSave) {
      const originalRec = notesRecord.find((r) => r.id === editingRecordId);
      const hoursOld = originalRec ? (new Date().getTime() - new Date(originalRec.createdAt).getTime()) / (1000 * 60 * 60) : 0;
      if (hoursOld > 12) {
        const confirmOverwrite = window.confirm(
          "تنبيه أمان سريري: هل أنت متأكد من رغبتك في تعديل السجل السريري الحالي؟ سيؤدي حفظ التغييرات إلى تعديل البيانات الطبية التاريخية المخزنة للمريض.\n\nClinical Security Warning: Are you sure you want to overwrite this existing clinical record? This will modify historical patient data.",
        );
        if (!confirmOverwrite) {
          return; // Safe abort: do not touch database or send API request
        }
      }
    }

    if (
      !forceSave &&
      (!soapSubjective.trim() ||
        !soapObjective.trim() ||
        (!soapAssessment.trim() && !icd10.trim() && gpDiagnoses.length === 0))
    ) {
      setSubmitFailed(true);
      alert(
        "Incomplete EHR Record: You must complete the Subjective, Objective, and Diagnosis sections before submitting.",
      );
      return;
    }

    setSubmitFailed(false);
    setIsSavingEhr(true);
    setJustSaved(false);
    try {
      // EXPERT COMMENTARY: Automatically dispatch any pending draft/CPOE/investigation entries before completing/saving so that they are securely persisted as official clinic orders.
      if (investigations.length > 0) {
        const labs = investigations.filter((i) => i.dept === "LAB");
        const xrays = investigations.filter((i) => i.dept === "X_RAY");
        if (labs.length > 0) {
          await handleDispatchSpecificOrder(
            "LAB",
            labs.map((l, idx) => `${idx + 1}. ${l.name}`).join("\n"),
            () => {},
          );
        }
        if (xrays.length > 0) {
          await handleDispatchSpecificOrder(
            "X_RAY",
            xrays.map((x, idx) => `${idx + 1}. ${x.name}`).join("\n"),
            () => {},
          );
        }
        setInvestigations([]);
      }
      if (cpoeNurseInput && cpoeNurseInput.trim()) {
        await handleDispatchSpecificOrder(
          "NURSING",
          cpoeNurseInput.trim(),
          () => setCpoeNurseInput(""),
        );
      }
      if (cpoeInstruction && cpoeInstruction.trim()) {
        await handleDispatchSpecificOrder(
          cpoeDept,
          cpoeInstruction.trim(),
          () => setCpoeInstruction(""),
        );
      }

      const compiledDiagnoses =
        gpDiagnoses.length > 0
          ? `\n\n` +
            gpDiagnoses
              .map((d) => `- ${d.disorder} (ICD-10: ${d.ICD10})`)
              .join("\n")
          : "";

      const docKey = isEditing ? editingRecordId : selectedPatient.id;
      const printableDocsPayload = {
        reportTitle: localStorage.getItem(`report_title_${docKey}`),
        reportSub: localStorage.getItem(`report_sub_${docKey}`),
        reportInstructionsHeader: localStorage.getItem(`report_instr_hdr_${docKey}`),
        reportInstructions: localStorage.getItem(`report_instr_${docKey}`),
        billPrice: localStorage.getItem(`bill_price_${docKey}`),
        billPriceWords: localStorage.getItem(`bill_price_words_${docKey}`),
        billCategory: localStorage.getItem(`bill_category_${docKey}`),
        receiptName: localStorage.getItem(`receipt_name_${docKey}`),
        referralFindingsHeader: localStorage.getItem(`referral_findings_hdr_${docKey}`),
        referralFindings: localStorage.getItem(`referral_findings_${docKey}`),
        referralAdditionalNotes: localStorage.getItem(`referral_add_notes_${docKey}`),
        recordClinicalNotes: localStorage.getItem(`rec_clinical_notes_${docKey}`),
        issuedDocs: issuedDocs,
      };

      const bodyPayload = {
        patientId: selectedPatient.id,
        physicianId: user.email,
        physicianName: user.name,
        gpNotes: gpNotes + compiledDiagnoses,
        familyHistory: familyHistory || "N/A",
        socialHistory: socialHistory || "N/A",
        vitalTemp: vitalTemp,
        vitalPulse: vitalPulse,
        vitalBP: vitalBP,
        vitalWeight: vitalWeight,
        vitalHeight: vitalHeight,
        vitalBMI: vitalBMI,
        soapSubjective: soapSubjective || "N/A",
        soapObjective: soapObjective || "N/A",
        soapAssessment: soapAssessment || "N/A",
        soapPlan: soapPlan || "N/A",
        icd10Codes: icd10,
        prescriptions: JSON.stringify(prescriptions),
        dermaData: JSON.stringify({ skinType, pins: dermPins, photos: dermPhotos }),
        faceMapJson: JSON.stringify(laserLogs),
        minorSurgery: minorSurgeryNotes,
        aiMetadata: aiAnalysisResult ? JSON.stringify(aiAnalysisResult) : "",
        printableDocs: JSON.stringify(printableDocsPayload),
        forceSaveAsNew,
        createdAt: forceSaveAsNew ? new Date().toISOString() : (customRecordDate || undefined),
      };

      const res = await fetch(
        isEditing
          ? `/api/clinical/records/${editingRecordId}`
          : "/api/clinical/records",
        {
          method: isEditing ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiToken}`,
          },
          body: JSON.stringify(bodyPayload),
        },
      );

      const data = await res.json();
      if (res.ok) {
        const originalRecord = isEditing ? notesRecord.find((r: any) => r.id === editingRecordId) : null;
        const vitalsChanged = !originalRecord || 
          originalRecord.vitalTemp !== vitalTemp ||
          originalRecord.vitalPulse !== vitalPulse ||
          originalRecord.vitalBP !== vitalBP ||
          originalRecord.vitalWeight !== vitalWeight ||
          originalRecord.vitalHeight !== vitalHeight;

        if (vitalsChanged && (vitalTemp || vitalPulse || vitalBP || vitalWeight || vitalHeight)) {
          try {
            await fetch("/api/vitals", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${apiToken}`,
              },
              body: JSON.stringify({
                patientId: selectedPatient.id,
                temp: vitalTemp,
                pulse: vitalPulse,
                bloodPressure: vitalBP,
                weight: vitalWeight,
                height: vitalHeight,
                bmi: vitalBMI,
                recordedBy: user.name,
                chiefComplaint: "GP Consultation Auto-Vitals",
              }),
            });
            await fetchPatientVitals(selectedPatient.id);
          } catch (err) {
            console.error("Failed to save GP vitals", err);
          }
        }

        const updatedMH: MedicalHistory = {
          ...(medicalHistory || { patientId: selectedPatient.id, surgeries: [], chronicConditions: [], allergies: [] }),
          familyHistory: familyHistory,
          socialHistory: socialHistory,
        };
        await handleUpdateMedicalHistory(updatedMH);

        // --- Auto-Sync Clinical Receipt to Finance Ledger ---
        if (issuedDocs.receipt) {
          try {
            const docKey = isEditing ? editingRecordId : selectedPatient.id;
            // Prevent double-syncing if the user just printed the receipt
            const syncKey = `receipt_synced_${docKey}`;
            const lastSync = localStorage.getItem(syncKey);
            const now = Date.now();
            
            // Only sync if we haven't synced a receipt for this patient in the last 2 minutes
            if (!lastSync) {
              const priceStr = localStorage.getItem(`bill_price_${docKey}`);
              const categoryStr = localStorage.getItem(`bill_category_${docKey}`);
              const receiptName = localStorage.getItem(`receipt_name_${docKey}`);
              const numPrice = priceStr ? parseInt(priceStr.replace(/\D/g, ''), 10) : 10000;
              
              await fetch("/api/finance/income", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${apiToken}`,
                },
                body: JSON.stringify({
                  amount: numPrice,
                  category: categoryStr || "Consultation Fee",
                  description: `${categoryStr || "Consultation"} - Auto Synced Record (${receiptName || selectedPatient.name})`,
                  patientId: selectedPatient.id
                }),
              });
              
              localStorage.setItem(syncKey, now.toString());
            }
          } catch (err) {
            console.error("Failed to auto-sync clinical receipt", err);
          }
        }
        // --------------------------------------------------

        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 3000);
        if (isEditing) {
          setNotesRecord(
            notesRecord.map((r: any) => (r.id === data.id ? data : r)),
          );
          setIsRecordLocked(true);
          triggerSuccessToast(
            "Clinical transaction successfully updated in Postgres SQL ledger!",
          );
        } else {
          setNotesRecord([...notesRecord, data]);
          setEditingRecordId(data.id);
          setIsRecordLocked(true);
          triggerSuccessToast(
            "Completed clinical transaction logged to Postgres SQL ledger!",
          );
        }
        fetchAppointments(); // Update status queue
      } else {
        alert(data.error || "Failed to save record.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSavingEhr(false);
    }
  };

  const triggerSuccessToast = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 4000);
  };

  // Additive: Secure Clinical state modifier to change patient appointment status
  const updateAppointmentStatus = async (
    apptId: string,
    targetStatus: "PENDING" | "COMPLETED",
    setOpened: boolean,
  ) => {
    try {
      const res = await fetch(`/api/appointments/${apptId}/status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({ status: targetStatus, openedAt: setOpened ? new Date().toISOString() : undefined }),
      });
      if (res.ok) {
        await fetchAppointments();
        triggerSuccessToast("تم تحديث حالة موعد المريض بنجاح");
      } else {
        const errorData = await res.json();
        alert(errorData.error || "خطأ طبي في تحديث الحالة");
      }
    } catch (err) {
      console.error("[Clinical State Sync Error]: ", err);
      alert("حدث خطأ طارئ أثناء الاتصال بالخادم.");
    }
  };

  // Click handlers for face zones in Interactive 2D Face Map
  const handleAddLaserLog = (e: React.FormEvent) => {
    e.preventDefault();
    if (!laserFormData.treatmentArea) return;

    const newRecord: LaserTherapyEntry = {
      treatmentArea: laserFormData.treatmentArea,
      laserType: laserFormData.laserType,
      spotSize: laserFormData.spotSize,
      fluence: laserFormData.fluence,
      pulseDuration: laserFormData.pulseDuration,
      cooling: laserFormData.cooling,
      passes: parseInt(laserFormData.passes) || 1,
    };

    setLaserLogs([...laserLogs, newRecord]);

    // Reset area
    setLaserFormData({ ...laserFormData, treatmentArea: "" });
  };

  const handleSaveSession = async () => {
    await handleSaveEhrReport();
    alert("Clinical record securely saved.");
  };

  const removeLaserLog = (index: number) => {
    setLaserLogs(laserLogs.filter((_, i) => i !== index));
  };

  // Convert uploaded image to Base64
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Str = reader.result as string;
        setSkinImageBase64(base64Str);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-6">
      {/* Toast Alert */}
      {successMsg && (
        <div className="fixed top-6 right-6 z-50 bg-[#0F172A] text-white font-medium text-xs px-5 py-4 rounded-xl shadow-[0_20px_50px_rgba(16,185,129,0.3)] border border-[#1E293B] border-l-4 border-l-[#10B981] flex items-center gap-3 animate-in slide-in-from-top-4 sm:slide-in-from-right-4 duration-300 max-w-sm">
          <div className="bg-[#10B981]/10 p-1.5 rounded-lg shrink-0">
            <CheckCircle className="w-4 h-4 text-[#10B981] animate-pulse" />
          </div>
          <div className="flex flex-col text-right">
            <span className="font-bold text-[#E2E8F0] tracking-wide">
              نم إدراج المعاملة بنجاح • Clinical Entry Saved
            </span>
            <span className="text-[10px] text-[#94A3B8] mt-0.5">
              {successMsg}
            </span>
          </div>
        </div>
      )}

      {/* Header Panel */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="bg-[#F8FAFC] text-[#64748B] font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border border-[#E2E8F0]">
              {user.role} Privilege Level
            </span>
            <span
              className={`font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border flex items-center gap-1 ${
                isOnline
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-rose-50 text-rose-700 border-rose-200 animate-pulse"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${isOnline ? "bg-emerald-500" : "bg-rose-500"}`}
              ></span>
              {isOnline ? "System Online" : "Offline - Connection Lost"}
            </span>
            {user.role === "SUPER_ADMIN" && (
              <div className="flex gap-2">
                <button
                  onClick={() => setIsClinicSettingsOpen(true)}
                  className="bg-indigo-600 text-slate-100 hover:bg-indigo-500 font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border border-indigo-700 cursor-pointer transition select-none flex items-center gap-1"
                >
                  <Settings className="w-3 h-3" /> Clinic Profile
                </button>
                <button
                  onClick={() => setIsAdminSettingsOpen(true)}
                  className="bg-slate-800 text-slate-100 hover:bg-slate-700 font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border border-slate-900 cursor-pointer transition select-none flex items-center gap-1"
                >
                  <Lock className="w-3 h-3" /> Admin Settings
                </button>
                <button
                  onClick={() => window.open("/#reception", "_blank")}
                  className="bg-amber-600 text-amber-50 hover:bg-amber-500 font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border border-amber-700 cursor-pointer transition select-none flex items-center gap-1"
                >
                  <Users className="w-3 h-3" /> الاستعلامات
                </button>
                <button
                  onClick={() => window.open("/#finance", "_blank")}
                  className="bg-emerald-700 text-emerald-50 hover:bg-emerald-600 font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border border-emerald-800 cursor-pointer transition select-none flex items-center gap-1"
                >
                  <DollarSign className="w-3 h-3" /> الحسابات والمالية
                </button>
                <button
                  onClick={() => window.open("/#calculators", "_blank")}
                  className="bg-cyan-700 text-cyan-50 hover:bg-cyan-600 font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border border-cyan-800 cursor-pointer transition select-none flex items-center gap-1"
                >
                  <Activity className="w-3 h-3" /> الحاسبات الطبية
                </button>
              </div>
            )}
          </div>
          <h1 className="text-base font-bold text-[#1E293B] mt-2">
            Physician EHR & Aesthetics Lounge
          </h1>
          <p className="text-[#64748B] text-xs">
            Logged in as {whiteLabel?.doctorNameEn || user.name || "Doctor Name"} • {whiteLabel?.clinicNameEn || "Dr. Watheq Al-Chalabi Clinic"}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2.5 w-full md:w-auto mt-3 md:mt-0">
          {selectedPatient && (
            <div className="flex flex-wrap justify-end gap-1.5 bg-slate-50 p-1.5 rounded-lg border border-slate-200 max-w-3xl w-full">
              <button
                onClick={() => {
                  setPrintDocType("RECORD");
                  setPrintOpen(true);
                }}
                className="bg-white hover:bg-slate-100 text-slate-700 font-bold text-[9px] uppercase tracking-wider py-1.5 px-2 rounded-md shadow-sm border border-slate-200 transition cursor-pointer flex items-center gap-1 select-none"
              >
                <Printer className="w-3 h-3" />
                Report (تقرير)
              </button>
              <button
                onClick={() => {
                  setPrintDocType("PATIENT_REPORT");
                  setPrintOpen(true);
                  setIssuedDocs(prev => ({...prev, report: true}));
                }}
                className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-[9px] uppercase tracking-wider py-1.5 px-2 rounded-md shadow-sm border border-indigo-200 transition cursor-pointer flex items-center gap-1 select-none"
              >
                <Printer className="w-3 h-3" />
                Patient Report
              </button>
              <button
                onClick={() => {
                  setPrintDocType("REFERRAL");
                  setPrintOpen(true);
                }}
                className="bg-white hover:bg-slate-100 text-slate-700 font-bold text-[9px] uppercase tracking-wider py-1.5 px-2 rounded-md shadow-sm border border-slate-200 transition cursor-pointer flex items-center gap-1 select-none"
              >
                <Printer className="w-3 h-3" />
                Referral
              </button>
              <button
                onClick={() => {
                  setPrintDocType("CONSENT");
                  setPrintOpen(true);
                }}
                className="bg-white hover:bg-slate-100 text-slate-700 font-bold text-[9px] uppercase tracking-wider py-1.5 px-2 rounded-md shadow-sm border border-slate-200 transition cursor-pointer flex items-center gap-1 select-none"
              >
                <Printer className="w-3 h-3" />
                موافقة طبية
              </button>
              <button
                onClick={() => {
                  setPrintDocType("SICK_LEAVE");
                  setPrintOpen(true);
                  setIssuedDocs(prev => ({...prev, sickLeave: true}));
                }}
                className="bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-[9px] uppercase tracking-wider py-1.5 px-2 rounded-md shadow-sm border border-rose-200 transition cursor-pointer flex items-center gap-1 select-none"
              >
                <Printer className="w-3 h-3" />
                Sick Leave
              </button>
              <button
                onClick={() => {
                  setPrintDocType("CONSENT_PRIVATE_EXAM");
                  setPrintOpen(true);
                }}
                className="bg-orange-50 hover:bg-orange-100 text-orange-700 font-bold text-[9px] uppercase tracking-wider py-1.5 px-2 rounded-md shadow-sm border border-orange-200 transition cursor-pointer flex items-center gap-1 select-none"
              >
                <Printer className="w-3 h-3" />
                موافقة فحص خاص
              </button>
              <button
                onClick={async () => {
                  setPrintDocType("BILL_10000");
                  setPrintOpen(true);
                  setIssuedDocs(prev => ({...prev, receipt: true}));

                  // Immediate finance sync on receipt print
                  if (selectedPatient) {
                    const docKey = editingRecordId || selectedPatient.id;
                    const syncKey = `receipt_synced_${docKey}`;
                    const lastSync = localStorage.getItem(syncKey);
                    const now = Date.now();
                    if (!lastSync) {
                      const priceStr = localStorage.getItem(`bill_price_${docKey}`);
                      const categoryStr = localStorage.getItem(`bill_category_${docKey}`);
                      const receiptName = localStorage.getItem(`receipt_name_${docKey}`);
                      const numPrice = priceStr ? parseInt(priceStr.replace(/\D/g, ''), 10) : 10000;
                      try {
                        await fetch("/api/finance/income", {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${apiToken}`,
                          },
                          body: JSON.stringify({
                            amount: numPrice,
                            category: categoryStr || "Consultation Fee",
                            description: `${categoryStr || "Consultation"} - Receipt Print (${receiptName || selectedPatient.name})`,
                            patientId: selectedPatient.id,
                            patientName: receiptName || selectedPatient.name
                          }),
                        });
                        localStorage.setItem(syncKey, now.toString());
                      } catch (err) {
                        console.error("Failed to sync receipt to finance", err);
                      }
                    }
                  }
                }}
                className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold text-[9px] uppercase tracking-wider py-1.5 px-2 rounded-md shadow-sm border border-emerald-200 transition cursor-pointer flex items-center gap-1 select-none"
              >
                <Printer className="w-3 h-3" />
                Receipt (وصل)
              </button>
              <button
                id="print-isolated-rx-btn"
                onClick={() => {
                  try {
                    setPrintDocType("PRESCRIPTION");
                    setSelectedOrderToPrint(null);
                    setPrintOpen(true);
                  } catch (err: any) {
                    console.error(
                      "[EHR Failure] Standing Rx document prep blocked: ",
                      err,
                    );
                  }
                }}
                className="bg-purple-50 hover:bg-purple-100 text-purple-700 font-bold text-[9px] uppercase tracking-wider py-1.5 px-2 rounded-md shadow-sm border border-purple-200 transition cursor-pointer flex items-center gap-1 select-none"
              >
                <Printer className="w-3 h-3" />
                Rx Script
              </button>
              <button
                id="print-isolated-lab-btn"
                onClick={() => {
                  try {
                    setPrintDocType("INVESTIGATION");
                    setSelectedOrderToPrint(null);
                    setPrintOpen(true);
                  } catch (err: any) {
                    console.error(
                      "[EHR Failure] Standing laboratory document prep blocked: ",
                      err,
                    );
                  }
                }}
                className="bg-teal-50 hover:bg-teal-100 text-teal-700 font-bold text-[9px] uppercase tracking-wider py-1.5 px-2 rounded-md shadow-sm border border-teal-200 transition cursor-pointer flex items-center gap-1 select-none"
              >
                <Printer className="w-3 h-3" />
                Lab Req
              </button>
            </div>
          )}
          <button
            className={`font-semibold text-xs py-2 px-5 rounded-md shadow-md transition-all duration-300 flex items-center justify-center gap-2 select-none cursor-pointer ${
              justSaved
                ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-[0_0_15px_rgba(16,185,129,0.35)] scale-[1.03]"
                : isSavingEhr
                  ? "bg-slate-700 text-slate-200 cursor-not-allowed"
                  : "bg-[#0F172A] hover:bg-slate-800 text-white"
            }`}
            disabled={isSavingEhr || isRecordLocked}
            onClick={() => handleSaveEhrReport()}
          >
            {isSavingEhr ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin text-white" />
                <span>Saving Ledger...</span>
              </>
            ) : justSaved ? (
              <>
                <CheckCircle className="w-3.5 h-3.5 text-white animate-bounce" />
                <span>✓ EHR Session Committed!</span>
              </>
            ) : (
              <>
                <Send className="w-3.5 h-3.5" />
                <span>Commit & Sign EHR Session</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Dr. Chatbot Appointment Alerts Banner */}
      {(() => {
        const chatbotAppts = appointments.filter(
          (a) =>
            (a as any).chatbotBooked &&
            a.status === "PENDING",
        );
        if (chatbotAppts.length === 0) return null;
        return (
          <div className="mb-4 bg-indigo-50/70 border border-indigo-100 p-2.5 rounded-lg flex flex-col gap-2">
            <div className="flex items-center gap-1.5 px-1">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-600"></span>
              </span>
              <h4 className="text-[10px] font-bold text-indigo-900 uppercase tracking-wider flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5 text-indigo-600 shrink-0" />{" "}
                تنبيه: حجوزات من روبوت المحادثة • Dr. Chatbot Alerts
              </h4>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1.5">
              {chatbotAppts.map((appt) => {
                const patient = patients.find((p) => p.id === appt.patientId);
                return (
                  <div
                    key={appt.id}
                    className="bg-white border border-indigo-100/60 rounded-md p-1.5 flex flex-col gap-0.5 shadow-xs transition-all hover:border-indigo-200"
                  >
                    <div className="flex justify-between items-center border-b border-indigo-50 pb-1 mb-0.5">
                      <span className="font-bold text-slate-800 text-[10px] truncate pr-1">
                        {patient ? patient.name : "مريض جديد"}
                      </span>
                      <span className="text-[7px] font-bold text-indigo-700 uppercase bg-indigo-50 px-1 py-[1px] rounded whitespace-nowrap">
                        GP Chatbot
                      </span>
                    </div>
                    <div className="text-[9px] text-slate-600 font-semibold flex items-center gap-1 truncate">
                      🗓{" "}
                      {new Date(appt.dateTime).toLocaleString("ar-IQ", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </div>
                    {patient && (
                      <div className="text-[8.5px] text-slate-500 font-mono flex items-center gap-1">
                        📞 {patient.phone}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Summary Statistics Widget */}
      {user.role === "SUPER_ADMIN" && <StatsBanner apiToken={apiToken} />}

      {/* Online Consultation Notifications */}
      {urgentOnlineConsults.length > 0 &&
        (user.role === "SUPER_ADMIN" || user.role === "PHYSICIAN") && (
          <div className="bg-gradient-to-r from-red-600 to-rose-500 rounded-lg p-4 shadow-md text-white mb-6 border border-red-400 animate-pulse">
            <div className="flex items-center gap-3">
              <span className="bg-white/20 p-2 rounded-full">
                <Video className="w-5 h-5 text-white" />
              </span>
              <div className="flex-1">
                <h3 className="font-bold text-lg leading-tight">
                  طلب استشارة مرئية عاجلة!
                </h3>
                <p className="text-red-50 text-xs">
                  يوجد {urgentOnlineConsults.length} مريض بانتظار الانضمام إلى
                  مكالمة الفيديو. يرجى التوجه لغرفة المحادثة فوراً.
                </p>
              </div>
              <button
                onClick={() => setShowOnlineConsultRooms(true)}
                className="bg-white text-red-600 font-bold px-4 py-2 rounded-md shadow-sm text-xs hover:bg-slate-50 transition cursor-pointer"
              >
                الدخول للعيادة
              </button>
            </div>
          </div>
        )}

      <div
        className={
          selectedPatient
            ? "grid grid-cols-1 lg:grid-cols-4 gap-6"
            : "space-y-6"
        }
      >
        {/* Left/Main column: Patients queue & Notepad */}
        <div
          className={
            selectedPatient
              ? "lg:col-span-1 space-y-4"
              : "grid grid-cols-1 lg:grid-cols-3 gap-6"
          }
        >
          {!selectedPatient && (
            <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-xl p-5 shadow-sm">
              <h3 className="font-bold text-[#1E293B] text-xs mb-3 flex items-center justify-between uppercase tracking-wider text-[#64748B]">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#64748B]" />
                  Check-In Row
                </div>
              </h3>

              {/* صندوق البحث السريع عن المرضى المراجعين للعيادة */}
              <div className="mb-3">
                <DebouncedInput
                  placeholder="🔍 ابحث عن اسم المريض أو رقم الهاتف..."
                  value={patientSearchQuery}
                  onChange={(val) => setPatientSearchQuery(val)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5 text-xs text-slate-800 font-bold focus:ring-1 focus:ring-indigo-500 focus:outline-none placeholder-slate-400"
                  dir="rtl"
                />
              </div>

              {/* Date filter selector for specific single day only */}
              <div
                className="mb-3 flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2 text-right"
                dir="rtl"
              >
                <label className="text-[10px] font-bold text-slate-600 flex items-center gap-1">
                  📅 تاريخ المراجعة النشط:
                </label>
                <input
                  type="date"
                  value={selectedQueueDate}
                  onChange={(e) => setSelectedQueueDate(e.target.value)}
                  className="bg-white border border-slate-300 rounded px-2.5 py-1 text-xs font-mono font-bold text-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div className="flex bg-slate-100 p-1 rounded-md mb-3 gap-1">
                {(["PENDING", "INCOMPLETE", "COMPLETED", "ALL"] as const).map(
                  (f) => (
                    <button
                      key={f}
                      onClick={() => setPatientQueueFilter(f)}
                      className={`flex-1 text-[8.5px] font-bold py-1.5 px-0.5 rounded uppercase tracking-tighter transition leading-none text-center ${patientQueueFilter === f ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700"}`}
                    >
                      {f === "PENDING"
                        ? "في الانتظار (Waiting)"
                        : f === "INCOMPLETE"
                          ? "قيد الإجراء / معلق (In Progress)"
                          : f === "COMPLETED"
                            ? "مكتمل (Completed)"
                            : "الكل (All)"}
                    </button>
                  ),
                )}
              </div>

              <div
                className={`${selectedPatient ? "space-y-1.5 max-h-[250px]" : "grid grid-cols-1 md:grid-cols-2 gap-2.5 max-h-[380px] items-start"} overflow-y-auto pr-1`}
              >
                {(() => {
                  const getApptDate = (dStr: string) => {
                    try {
                      // Convert to Baghdad timezone before date comparison
                      const baghdadISO = new Date(dStr).toLocaleString("sv-SE", { timeZone: "Asia/Baghdad" });
                      return baghdadISO.replace(" ", "T");
                    } catch {
                      return dStr;
                    }
                  };

                  const currentDayAppts = appointments.filter(
                    (a) =>
                      getApptDate(a.dateTime).split("T")[0] === selectedQueueDate,
                  );
                  const isSearching = !!patientSearchQuery.trim();
                  const normalizedQuery = isSearching ? normalizeArabic(patientSearchQuery).toLowerCase() : "";

                  return patients
                    .filter((p) => {
                      const dayAppts = currentDayAppts;

                      // If there is an active search query, filter across patients (ignores selected date)
                      if (isSearching) {
                        const query = normalizedQuery;
                        const pName = normalizeArabic(p.name).toLowerCase();
                        const natId = p.nationalId
                          ? String(p.nationalId).toLowerCase()
                          : "";
                        return (
                          pName.includes(query) ||
                          (p.phone && p.phone.includes(query)) ||
                          natId.includes(query)
                        );
                      }

                      const isPending = dayAppts.some(
                        (a) =>
                          a.patientId === p.id &&
                          (a.status === "PENDING" || a.status === "WAITING"),
                      );
                      const isIncomplete = dayAppts.some(
                        (a) =>
                          a.patientId === p.id &&
                          a.status === "IN_PROGRESS",
                      );
                      const isCompleted = dayAppts.some(
                        (a) => a.patientId === p.id && a.status === "COMPLETED",
                      );

                      if (patientQueueFilter === "PENDING") return isPending;
                      if (patientQueueFilter === "INCOMPLETE")
                        return isIncomplete;
                      if (patientQueueFilter === "COMPLETED")
                        return isCompleted;
                      // For "ALL": Return true ONLY if patient has an appointment on the selected date to match visit date
                      return dayAppts.some((a) => a.patientId === p.id);
                    })
                    .map((p) => {
                      const dayAppts = currentDayAppts;
                      const matchingAppt = dayAppts.find(
                        (a) => a.patientId === p.id && (a.status === "PENDING" || a.status === "WAITING"),
                      );
                      const completedAppt = dayAppts.find(
                        (a) => a.patientId === p.id && a.status === "COMPLETED",
                      );
                      const allPatientAppts = appointments
                        .filter((a) => a.patientId === p.id)
                        .sort(
                          (a, b) =>
                            new Date(b.dateTime).getTime() -
                            new Date(a.dateTime).getTime(),
                        );

                      return (
                        <div
                          key={p.id}
                          onClick={async () => {
                            const targetAppt = matchingAppt || dayAppts.find(a => a.patientId === p.id && a.status === "IN_PROGRESS");
                            if (targetAppt) {
                              if (targetAppt.status === "PENDING" || targetAppt.status === "WAITING") {
                                await updateAppointmentStatus(targetAppt.id, "IN_PROGRESS", true);
                              }
                              nextTargetApptDate.current = targetAppt.dateTime;
                            } else {
                              nextTargetApptDate.current = "FORCE_LATEST";
                            }
                            setSelectedPatient(p);
                          }}
                          className={`p-2.5 rounded-md border cursor-pointer transition flex items-start justify-between bg-white border-[#E2E8F0] hover:bg-[#F8FAFC] h-[165px] overflow-y-auto`}
                        >
                          <div className="flex-1 min-w-0 pr-2">
                            <h4 className="font-bold text-[#1E293B] text-xs flex items-center justify-between gap-1.5 flex-wrap">
                              <span className="flex items-center gap-1.5 truncate">
                                <span>{p.name}</span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSendTelegram(p.id);
                                  }}
                                  disabled={tgStatus[p.id] === "loading"}
                                  className={`p-0.5 rounded transition shrink-0 inline-flex items-center justify-center ${
                                    tgStatus[p.id] === "loading"
                                      ? "animate-pulse text-slate-400 bg-slate-50"
                                      : tgStatus[p.id] === "success"
                                        ? "text-emerald-600 bg-emerald-50"
                                        : tgStatus[p.id] === "error"
                                          ? "text-rose-600 bg-rose-50"
                                          : "text-sky-500 hover:text-sky-600 hover:bg-sky-50"
                                  }`}
                                  title="إرسال إشعار تليجرام"
                                >
                                  {tgStatus[p.id] === "loading" ? (
                                    <span className="text-[10px]">⏳</span>
                                  ) : tgStatus[p.id] === "success" ? (
                                    <span className="text-[10px]">✅</span>
                                  ) : tgStatus[p.id] === "error" ? (
                                    <span className="text-[10px]">⚠️</span>
                                  ) : (
                                    <svg
                                      className="w-3.5 h-3.5 fill-current"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.02-1.96 1.24-5.54 3.65-.52.36-.99.53-1.41.52-.46-.01-1.35-.26-2.01-.48-.81-.27-1.46-.42-1.4-.88.03-.24.37-.48 1.02-.73 3.98-1.73 6.64-2.87 7.99-3.41 3.8-1.55 4.59-1.82 5.1-.1.11.24.18.52.2.82z" />
                                    </svg>
                                  )}
                                </button>
                              </span>
                              {(matchingAppt || completedAppt) && (
                                <span className="text-[9px] text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded font-mono shrink-0 font-bold">
                                  {(() => {
                                    const targetAppt =
                                      matchingAppt || completedAppt;
                                    if (!targetAppt) return "";
                                    const d = new Date(targetAppt.dateTime);
                                    return `${d.getMonth() + 1}/${d.getDate()} - ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
                                  })()}
                                </span>
                              )}
                            </h4>
                            <p className="text-[10px] text-[#64748B] font-mono mt-0.5">
                              {p.phone || (() => {
                                if (!p.name) return "بدون رقم هاتف";
                                const parts = p.name.trim().split(" ");
                                if (parts.length < 2) return "بدون رقم هاتف";
                                const lastName = parts[parts.length - 1];
                                const familyMember = patients.find(other => other.id !== p.id && other.name && other.name.trim().endsWith(lastName) && other.phone);
                                return familyMember ? `(مرتبط بـ ${familyMember.phone})` : "بدون رقم هاتف";
                              })()}
                            </p>
                            {(() => {
                              if (!p.name) return null;
                              const parts = p.name.trim().split(" ");
                              if (parts.length < 2) return null;
                              const lastName = parts[parts.length - 1];
                              const familyMembersList = patients.filter(other => other.id !== p.id && other.name && other.name.trim().endsWith(lastName));
                              if (familyMembersList.length === 0) return null;
                              return (
                                <div className="w-full overflow-hidden bg-blue-50/50 border border-blue-100/50 rounded-sm text-[9px] text-blue-700 py-0.5 mt-1 relative flex items-center h-[18px]" style={{ direction: "rtl" }}>
                                  <div className="w-max whitespace-nowrap animate-marquee flex items-center gap-3 px-2">
                                    <span className="font-bold">👨‍👩‍👧‍👦 العائلة المسجلة:</span>
                                    {familyMembersList.map((fm, idx) => (
                                      <span key={idx}>• {fm.name}</span>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}
                            {(matchingAppt?.reason || completedAppt?.reason) && (
                              <p className="text-[9.5px] text-rose-700 bg-rose-50 border border-rose-100 px-1.5 py-1 rounded mt-1.5 font-bold line-clamp-2" title={(matchingAppt?.reason || completedAppt?.reason)?.split("--- AI Triage ---")[0].trim()}>
                                ⚠️ الشكوى: {(matchingAppt?.reason || completedAppt?.reason)?.split("--- AI Triage ---")[0].trim()}
                              </p>
                            )}
                            <div className="mt-2 flex flex-col gap-1.5 w-full">
                              {allPatientAppts.length > 2 ? (
                                <details
                                  className="group"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <summary className="text-[10px] font-bold text-slate-600 cursor-pointer bg-slate-100 hover:bg-slate-200 px-2 py-1.5 rounded select-none list-none flex items-center justify-between border border-slate-200 relative z-10">
                                    <span>
                                      عرض جميع المواعيد السابقة (
                                      {allPatientAppts.length})
                                    </span>
                                    <span className="group-open:rotate-180 transition-transform">
                                      ▼
                                    </span>
                                  </summary>
                                  <div className="mt-1 flex flex-col gap-1.5 max-h-[150px] overflow-y-auto pl-1 pr-1 pb-1 relative z-20">
                                    {allPatientAppts.map((appt) => (
                                      <div
                                        key={appt.id}
                                          onClick={async (e) => {
                                            e.stopPropagation();

                                            if (appt.status === "PENDING") {
                                              await updateAppointmentStatus(appt.id, "IN_PROGRESS", true);
                                            }

                                          nextTargetApptDate.current =
                                            appt.dateTime;

                                          if (p.id === selectedPatient?.id) {
                                            const targetDateStr = appt.dateTime;
                                            const matchRecord =
                                              notesRecord.find((r) => {
                                                if (!r.createdAt) return false;
                                                return (
                                                  r.createdAt ===
                                                  targetDateStr
                                                );
                                              });
                                            if (matchRecord) {
                                              loadTargetRecordIntoForm(
                                                matchRecord,
                                              );
                                            } else {
                                              if (appt.status === "PENDING") {
                                                setEditingRecordId(null);
                                                setIsRecordLocked(false);
                                                setCustomRecordDate(""); // Will trigger real-time capture hook
                                                setGpNotes(""); setIssuedDocs({ report: false, sickLeave: false, receipt: false });
                                                setSoapSubjective("");
                                                setSoapObjective("");
                                                setSoapAssessment("");
                                                setSoapPlan("");
                                                setIcd10("");
                                                setPrescriptions([]);
                                                try { setLaserLogs([]); } catch(e){}
                                                try { setMinorSurgeryNotes(""); } catch(e){}
                                                try { setAiAnalysisResult(null); } catch(e){}
                                                try { setGpDiagnoses([]); } catch(e){}
                                                try { setCustomLeafletResult(null); } catch(e){}
                                                try { setDoctorInstructions(""); } catch(e){}
                                                try { setSkinType(""); } catch(e){}
                                              } else {
                                                setEditingRecordId(null);
                                                setIsRecordLocked(true);
                                                setCustomRecordDate(""); // Will trigger real-time capture hook
                                                setGpNotes(""); setIssuedDocs({ report: false, sickLeave: false, receipt: false });
                                                setSoapSubjective("");
                                                setSoapObjective("");
                                                setSoapAssessment("");
                                                setSoapPlan("");
                                                setIcd10("");
                                                setPrescriptions([]);
                                                try { setLaserLogs([]); } catch(e){}
                                                try { setMinorSurgeryNotes(""); } catch(e){}
                                                try { setAiAnalysisResult(null); } catch(e){}
                                                try { setGpDiagnoses([]); } catch(e){}
                                                try { setCustomLeafletResult(null); } catch(e){}
                                                try { setDoctorInstructions(""); } catch(e){}
                                                try { setSkinType(""); } catch(e){}
                                              }
                                            }
                                          }

                                          setSelectedPatient(p);
                                          setTab("gp");
                                        }}
                                        className={`text-[9.5px] px-2 py-1 rounded-md font-bold inline-flex items-center gap-1.5 border transition cursor-pointer ${
                                          appt.status === "PENDING" || appt.status === "IN_PROGRESS"
                                            ? "bg-[#EEF2F6] hover:bg-[#E2E8F0] text-[#1E293B] border-slate-200"
                                            : appt.status === "COMPLETED"
                                              ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100"
                                              : appt.status === "MISSED"
                                                ? "bg-rose-50 text-rose-700 border-rose-100 hover:bg-rose-100"
                                                : "bg-slate-50 text-slate-600 border-slate-100 hover:bg-slate-100"
                                        }`}
                                        dir="rtl"
                                      >
                                        <span>
                                          {appt.status === "IN_PROGRESS"
                                            ? "⏳"
                                            : appt.status === "PENDING"
                                              ? "⏰"
                                              : appt.status === "COMPLETED"
                                                ? "✅"
                                                : appt.status === "MISSED"
                                                  ? "❌"
                                                  : "📅"}
                                        </span>
                                        <span>
                                          الموعد:{" "}
                                          {new Date(
                                            appt.dateTime,
                                          ).toLocaleString("ar-IQ", {
                                            year: "numeric",
                                            month: "numeric",
                                            day: "numeric",
                                            hour: "numeric",
                                            minute: "2-digit",
                                          })}{" "}
                                          (
                                          {appt.status === "IN_PROGRESS"
                                            ? "معلّق"
                                            : appt.status === "PENDING"
                                              ? "في الانتظار"
                                              : appt.status === "COMPLETED"
                                                ? "اكتمل"
                                                : appt.status === "MISSED"
                                                  ? "فائت"
                                                  : appt.status}
                                          )
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              ) : (
                                <>
                                  {allPatientAppts.map((appt) => (
                                      <div
                                        key={appt.id}
                                          onClick={async (e) => {
                                            e.stopPropagation();

                                            if (appt.status === "PENDING") {
                                              await updateAppointmentStatus(appt.id, "IN_PROGRESS", true);
                                            }

                                          nextTargetApptDate.current =
                                            appt.dateTime;

                                          if (p.id === selectedPatient?.id) {
                                            const targetDateStr = appt.dateTime;
                                            const matchRecord =
                                              notesRecord.find((r) => {
                                                if (!r.createdAt) return false;
                                                return (
                                                  r.createdAt ===
                                                  targetDateStr
                                                );
                                              });
                                            if (matchRecord) {
                                              loadTargetRecordIntoForm(
                                                matchRecord,
                                              );
                                            } else {
                                              if (appt.status === "PENDING") {
                                                setEditingRecordId(null);
                                                setIsRecordLocked(false);
                                                setCustomRecordDate(""); // Will trigger real-time capture hook
                                                setGpNotes(""); setIssuedDocs({ report: false, sickLeave: false, receipt: false });
                                                setSoapSubjective("");
                                                setSoapObjective("");
                                                setSoapAssessment("");
                                                setSoapPlan("");
                                                setIcd10("");
                                                setPrescriptions([]);
                                                try { setLaserLogs([]); } catch(e){}
                                                try { setMinorSurgeryNotes(""); } catch(e){}
                                                try { setAiAnalysisResult(null); } catch(e){}
                                                try { setGpDiagnoses([]); } catch(e){}
                                                try { setCustomLeafletResult(null); } catch(e){}
                                                try { setDoctorInstructions(""); } catch(e){}
                                                try { setSkinType(""); } catch(e){}
                                              } else {
                                                setEditingRecordId(null);
                                                setIsRecordLocked(true);
                                                setCustomRecordDate(""); // Will trigger real-time capture hook
                                                setGpNotes(""); setIssuedDocs({ report: false, sickLeave: false, receipt: false });
                                                setSoapSubjective("");
                                                setSoapObjective("");
                                                setSoapAssessment("");
                                                setSoapPlan("");
                                                setIcd10("");
                                                setPrescriptions([]);
                                                try { setLaserLogs([]); } catch(e){}
                                                try { setMinorSurgeryNotes(""); } catch(e){}
                                                try { setAiAnalysisResult(null); } catch(e){}
                                                try { setGpDiagnoses([]); } catch(e){}
                                                try { setCustomLeafletResult(null); } catch(e){}
                                                try { setDoctorInstructions(""); } catch(e){}
                                                try { setSkinType(""); } catch(e){}
                                              }
                                            }
                                          }

                                          setSelectedPatient(p);
                                          setTab("gp");
                                        }}
                                        className={`text-[9.5px] px-2 py-1 rounded-md font-bold inline-flex items-center gap-1.5 border transition cursor-pointer ${
                                          appt.status === "PENDING" || appt.status === "IN_PROGRESS"
                                            ? "bg-[#EEF2F6] hover:bg-[#E2E8F0] text-[#1E293B] border-slate-200"
                                            : appt.status === "COMPLETED"
                                              ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100"
                                              : appt.status === "MISSED"
                                                ? "bg-rose-50 text-rose-700 border-rose-100 hover:bg-rose-100"
                                                : "bg-slate-50 text-slate-600 border-slate-100 hover:bg-slate-100"
                                        }`}
                                        dir="rtl"
                                      >
                                        <span>
                                          {appt.status === "IN_PROGRESS"
                                            ? "⏳"
                                            : appt.status === "PENDING"
                                              ? "⏰"
                                              : appt.status === "COMPLETED"
                                                ? "✅"
                                                : appt.status === "MISSED"
                                                  ? "❌"
                                                  : "📅"}
                                        </span>
                                        <span>
                                          الموعد:{" "}
                                          {new Date(
                                            appt.dateTime,
                                          ).toLocaleString("ar-IQ", {
                                            year: "numeric",
                                            month: "numeric",
                                            day: "numeric",
                                            hour: "numeric",
                                            minute: "2-digit",
                                          })}{" "}
                                          (
                                          {appt.status === "IN_PROGRESS"
                                            ? "معلّق"
                                            : appt.status === "PENDING"
                                              ? "في الانتظار"
                                              : appt.status === "COMPLETED"
                                                ? "اكتمل"
                                                : appt.status === "MISSED"
                                                  ? "فائت"
                                                  : appt.status}
                                          )
                                        </span>
                                      </div>
                                    ))}
                                </>
                              )}
                            </div>
                          </div>
                          {matchingAppt ? (
                            matchingAppt.status === "IN_PROGRESS" ? (
                              <span className="bg-amber-100 text-amber-900 font-bold text-[8px] uppercase px-1.5 py-0.5 rounded border border-amber-300">
                                Pending / معلّق ({matchingAppt.department})
                              </span>
                            ) : (
                              <span className="bg-indigo-50 text-indigo-700 font-bold text-[8px] uppercase px-1.5 py-0.5 rounded border border-indigo-200">
                                Waiting / إنتظار ({matchingAppt.department})
                              </span>
                            )
                          ) : completedAppt ? (
                            <span className="bg-emerald-50 text-emerald-700 font-bold text-[8px] uppercase px-1.5 py-0.5 rounded border border-emerald-200">
                              Completed
                            </span>
                          ) : null}
                        </div>
                      );
                    });
                })()}
              </div>
            </div>
          )}

          {/* Quick Demographics Cards */}
          {selectedPatient && (
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 shadow-sm relative overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider text-[#64748B]">
                  Demographics Summary
                </h3>
                <button
                  onClick={async () => {
                    // EXPERT COMMENTARY: Save the clinical EHR patient consultation record before closing the active workspace to prevent any data loss.
                    try {
                      await handleSaveEhrReport(true);
                    } catch (error) {
                      console.error("[Clinical Save Error on Close]: ", error);
                    }
                    // EXPERT COMMENTARY: Close the open workspace for the current patient.
                    setSelectedPatient(null);
                  }}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition"
                >
                  Close Record
                </button>
              </div>

              <div className="space-y-2.5 text-xs">
                <div>
                  <span className="text-[#64748B] block font-semibold text-[9px] uppercase tracking-wider">
                    Official Name
                  </span>
                  <span className="text-[#1E293B] font-bold">
                    {selectedPatient.name}
                  </span>
                </div>
                <div>
                  <span className="text-[#64748B] block font-semibold text-[9px] uppercase tracking-wider">
                    Contact Number
                  </span>
                  <span className="text-[#1E293B] font-mono">
                    {selectedPatient.phone}
                  </span>
                </div>
                <div>
                  <span className="text-[#64748B] block font-semibold text-[9px] uppercase tracking-wider">
                    Birth Date
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-[#1E293B] font-mono">
                      {selectedPatient.dob} (
                      {(() => {
                        if (!selectedPatient.dob) return "-";
                        const b = new Date(selectedPatient.dob);
                        const n = new Date();
                        let m = (n.getFullYear() - b.getFullYear()) * 12 + (n.getMonth() - b.getMonth());
                        if (n.getDate() < b.getDate()) m--;
                        if (m < 24) return Math.max(0, m) + " Months";
                        return Math.floor(m / 12) + " Y.O.";
                      })()}
                      )
                    </span>
                    <button
                      onClick={async () => {
                        const newAgeOrDob = window.prompt("للحفظ، أدخل العمر رقماً (مثال: 30) أو تاريخ الميلاد كاملاً (مثال: 1994-05-20):", selectedPatient.dob);
                        if (!newAgeOrDob) return;
                        
                        let newDob = newAgeOrDob;
                        if (!newAgeOrDob.includes("-")) {
                          const ageNum = parseInt(newAgeOrDob);
                          if (!isNaN(ageNum)) {
                            newDob = `${new Date().getFullYear() - ageNum}-01-01`;
                          }
                        }
                        
                        try {
                          const res = await fetch(`/api/patients/${selectedPatient.id}`, {
                            method: "PUT",
                            headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
                            body: JSON.stringify({ dob: newDob })
                          });
                          if (res.ok) {
                            const updatedPatient = await res.json();
                            setSelectedPatient(updatedPatient);
                          } else {
                            alert("حدث خطأ أثناء التحديث.");
                          }
                        } catch (e) {
                          console.error(e);
                        }
                      }}
                      className="text-slate-400 hover:text-indigo-600 transition-colors"
                      title="تعديل العمر / تاريخ الميلاد"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                    </button>
                  </div>
                </div>
                <div>
                  <span className="text-[#64748B] block font-semibold text-[9px] uppercase tracking-wider mt-2">
                    Email Address
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-[#1E293B] font-mono">
                      {selectedPatient.email || "غير متوفر"}
                    </span>
                    <button
                      onClick={async () => {
                        const newEmail = window.prompt("أدخل البريد الإلكتروني الجديد:", selectedPatient.email || "");
                        if (newEmail === null) return;
                        
                        try {
                          const res = await fetch(`/api/patients/${selectedPatient.id}`, {
                            method: "PUT",
                            headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
                            body: JSON.stringify({ email: newEmail.trim() })
                          });
                          if (res.ok) {
                            const updatedPatient = await res.json();
                            setSelectedPatient(updatedPatient);
                          } else {
                            alert("حدث خطأ أثناء التحديث.");
                          }
                        } catch (e) {
                          console.error(e);
                        }
                      }}
                      className="text-slate-400 hover:text-indigo-600 transition-colors"
                      title="تعديل البريد الإلكتروني"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                    </button>
                  </div>
                </div>
                <div>
                  <span className="text-[#64748B] block font-semibold text-[9px] uppercase tracking-wider mt-2">
                    National ID
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-[#1E293B] font-mono">
                      {selectedPatient.nationalId || "غير متوفر"}
                    </span>
                    <button
                      onClick={async () => {
                        const newNationalId = window.prompt("أدخل رقم البطاقة الموحدة الجديد:", selectedPatient.nationalId || "");
                        if (newNationalId === null) return;
                        
                        try {
                          const res = await fetch(`/api/patients/${selectedPatient.id}`, {
                            method: "PUT",
                            headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
                            body: JSON.stringify({ nationalId: newNationalId.trim() })
                          });
                          if (res.ok) {
                            const updatedPatient = await res.json();
                            setSelectedPatient(updatedPatient);
                          } else {
                            alert("حدث خطأ أثناء التحديث.");
                          }
                        } catch (e) {
                          console.error(e);
                        }
                      }}
                      className="text-slate-400 hover:text-indigo-600 transition-colors"
                      title="تعديل البطاقة الموحدة"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                    </button>
                  </div>
                </div>
                <div>
                  <span className="text-[#64748B] block font-semibold text-[9px] uppercase tracking-wider mt-2">
                    Gender
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-[#1E293B] font-bold">
                      {selectedPatient.gender === "MALE" ? "ذكر" : selectedPatient.gender === "FEMALE" ? "أنثى" : selectedPatient.gender || "غير محدد"}
                    </span>
                    <button
                      onClick={async () => {
                        const newGender = window.prompt("أدخل الجنس (MALE أو FEMALE):", selectedPatient.gender || "");
                        if (newGender === null) return;
                        
                        const normalizedGender = newGender.trim().toUpperCase();
                        if (normalizedGender !== "MALE" && normalizedGender !== "FEMALE") {
                          alert("يرجى إدخال MALE أو FEMALE فقط.");
                          return;
                        }

                        try {
                          const res = await fetch(`/api/patients/${selectedPatient.id}`, {
                            method: "PUT",
                            headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
                            body: JSON.stringify({ gender: normalizedGender })
                          });
                          if (res.ok) {
                            const updatedPatient = await res.json();
                            setSelectedPatient(updatedPatient);
                          } else {
                            alert("حدث خطأ أثناء التحديث.");
                          }
                        } catch (e) {
                          console.error(e);
                        }
                      }}
                      className="text-slate-400 hover:text-indigo-600 transition-colors"
                      title="تعديل الجنس"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                    </button>
                  </div>
                </div>
                {(appointments.filter((a) => a.patientId === selectedPatient.id)
                  .length > 0 ||
                  notesRecord.length > 0) && (
                  <div>
                    <span className="text-[#64748B] block font-semibold text-[9px] uppercase tracking-wider mt-2">
                      Appointments & Consultations History
                    </span>
                    <select
                      className="mt-1 w-full text-[10px] font-mono border border-slate-200 rounded p-1 bg-slate-50 text-slate-700 outline-none focus:border-indigo-500 cursor-pointer"
                      value={editingRecordId ? `record_${editingRecordId}` : (customRecordDate ? `appt_${customRecordDate}` : "")}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!val) {
                          setEditingRecordId(null);
                          setIsRecordLocked(true);
                          setGpNotes(""); setIssuedDocs({ report: false, sickLeave: false, receipt: false });
                          setSoapSubjective("");
                          setSoapObjective("");
                          setSoapAssessment("");
                          setSoapPlan("");
                          setIcd10("");
                          setPrescriptions([]);
                          setLaserLogs([]);
                          setMinorSurgeryNotes("");
                          setAiAnalysisResult(null);
                          setGpDiagnoses([]);
                          setFamilyHistory(medicalHistory?.familyHistory || "");
                          setSocialHistory(medicalHistory?.socialHistory || "");
                          return;
                        }

                        if (val.startsWith("record_")) {
                          const targetId = val.replace("record_", "");
                          const matchRecord = notesRecord.find(
                            (r: any) => r.id === targetId,
                          );
                          if (matchRecord) {
                            loadTargetRecordIntoForm(matchRecord);
                          }
                        } else if (val.startsWith("appt_")) {
                          const targetDate = val.replace("appt_", "");
                          nextTargetApptDate.current = targetDate;
                          
                          const targetStr = targetDate;
                          const matchRecord = notesRecord.find((r) => { if (!r.createdAt) return false; return r.createdAt === targetStr; });
                          
                          if (matchRecord) {
                             loadTargetRecordIntoForm(matchRecord);
                          } else {
                             const matchingAppt = appointments.find((a: any) => a.patientId === selectedPatient?.id && a.dateTime === targetDate);
                             
                             if (matchingAppt && (matchingAppt.status === "PENDING" || matchingAppt.status === "WAITING" || matchingAppt.status === "IN_PROGRESS")) {
                                                setEditingRecordId(null);
                                                setIsRecordLocked(false);
                                                setCustomRecordDate(targetDate);
                                                setGpNotes(""); setIssuedDocs({ report: false, sickLeave: false, receipt: false });
                                                setSoapSubjective("");
                                                setSoapObjective("");
                                                setSoapAssessment("");
                                                setSoapPlan("");
                                                setIcd10("");
                                                setPrescriptions([]);
                                                try { setLaserLogs([]); } catch(e){}
                                                try { setMinorSurgeryNotes(""); } catch(e){}
                                                try { setAiAnalysisResult(null); } catch(e){}
                                                try { setGpDiagnoses([]); } catch(e){}
                                                try { setCustomLeafletResult(null); } catch(e){}
                                                try { setDoctorInstructions(""); } catch(e){}
                                                try { setSkinType(""); } catch(e){}
                             } else {
                                                setEditingRecordId(null);
                                                setIsRecordLocked(true);
                                                setCustomRecordDate(targetDate);
                                                setGpNotes(""); setIssuedDocs({ report: false, sickLeave: false, receipt: false });
                                                setSoapSubjective("");
                                                setSoapObjective("");
                                                setSoapAssessment("");
                                                setSoapPlan("");
                                                setIcd10("");
                                                setPrescriptions([]);
                                                try { setLaserLogs([]); } catch(e){}
                                                try { setMinorSurgeryNotes(""); } catch(e){}
                                                try { setAiAnalysisResult(null); } catch(e){}
                                                try { setGpDiagnoses([]); } catch(e){}
                                                try { setCustomLeafletResult(null); } catch(e){}
                                                try { setDoctorInstructions(""); } catch(e){}
                                                try { setSkinType(""); } catch(e){}
                             }
                          }
                        }
                      }}
                    >
                      {(() => {
                        const allOptions: {
                          id: string;
                          label: string;
                          date: Date;
                        }[] = [];

                        const linkedApptDates = new Set<string>();

                        notesRecord.forEach((r: any) => {
                          let icons = "";
                          try {
                            if (r.printableDocs) {
                              const pd = JSON.parse(r.printableDocs);
                              if (pd && pd.issuedDocs) {
                                if (pd.issuedDocs.report) icons += " 📄";
                                if (pd.issuedDocs.sickLeave) icons += " 🛏️";
                                if (pd.issuedDocs.receipt) icons += " 🧾";
                              }
                            }
                          } catch(e) {}
                          
                          const recordDate = new Date(r.createdAt);
                          const matchingAppt = appointments.find((a) => {
                            if (a.patientId !== selectedPatient.id) return false;
                            if (a.dateTime === r.createdAt) return true;
                            if (linkedApptDates.has(a.dateTime)) return false;
                            const apptDate = new Date(a.dateTime);
                            const isSameDay = recordDate.toDateString() === apptDate.toDateString();
                            const diffHours = Math.abs(recordDate.getTime() - apptDate.getTime()) / (1000 * 60 * 60);
                            return isSameDay || diffHours <= 12;
                          });
                          
                          if (matchingAppt) {
                            linkedApptDates.add(matchingAppt.dateTime);
                            allOptions.push({
                              id: `record_${r.id}`,
                              date: new Date(r.createdAt),
                              label: `📝 Consult + 📅 Appt: ${new Date(r.createdAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })} | ${matchingAppt.status}${icons}`,
                            });
                          } else {
                            allOptions.push({
                              id: `record_${r.id}`,
                              date: new Date(r.createdAt),
                              label: `📝 Consult: ${new Date(r.createdAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}${icons}`,
                            });
                          }
                        });

                        appointments
                          .filter((a) => a.patientId === selectedPatient.id)
                          .filter((a) => !linkedApptDates.has(a.dateTime))
                          .forEach((a) => {
                            allOptions.push({
                              id: `appt_${a.dateTime}`,
                              date: new Date(a.dateTime),
                              label: `📅 Appt:   ${new Date(a.dateTime).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })} | ${a.status}`,
                            });
                          });

                        const uniqueIds = new Set();
                        return allOptions
                          .filter(opt => {
                            if (uniqueIds.has(opt.id)) return false;
                            uniqueIds.add(opt.id);
                            return true;
                          })
                          .sort((a, b) => b.date.getTime() - a.date.getTime())
                          .map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.label}
                            </option>
                          ));
                      })()}
                    </select>
                  </div>
                )}
              </div>

              {/* Consultation Status Selection Panel */}
              {(() => {
                const patientAppts = appointments
                  .filter((a) => a.patientId === selectedPatient.id)
                  .sort(
                    (a, b) =>
                      new Date(b.dateTime).getTime() -
                      new Date(a.dateTime).getTime(),
                  );

                const getLocalYYYYMMDD = (dStr: string) => {
                  try {
                    const d = new Date(dStr);
                    const tzDate = new Date(
                      d.toLocaleString("en-US", { timeZone: "Asia/Baghdad" }),
                    );
                    return `${tzDate.getFullYear()}-${String(tzDate.getMonth() + 1).padStart(2, "0")}-${String(tzDate.getDate()).padStart(2, "0")}`;
                  } catch {
                    return dStr.split("T")[0];
                  }
                };

                // EXPERT COMMENTARY: Prefer finding today's appointment or an active/pending appointment, fallback to latest
                const currentAppt =
                  patientAppts.find(
                    (a) => getLocalYYYYMMDD(a.dateTime) === selectedQueueDate,
                  ) ||
                  patientAppts.find((a) => a.status === "PENDING" || a.status === "IN_PROGRESS") ||
                  patientAppts[0];
                if (!currentAppt) return null;

                return (
                  <div
                    className="mt-4 pt-4 border-t border-[#E2E8F0]"
                    dir="rtl"
                  >
                    <span className="text-[#64748B] block font-bold text-[9px] uppercase tracking-wider mb-2 text-right">
                      Consultation Status / حالة المريض
                    </span>
                    <div className="flex flex-col gap-2">
                      <div
                        className="flex items-center justify-between text-xs mb-1"
                        dir="ltr"
                      >
                        <span className="text-slate-500 font-medium">
                          Current Status:
                        </span>
                        <button
                          onClick={async () => {
                            if (currentAppt.status !== "COMPLETED") {
                              try {
                                await handleSaveEhrReport(true);
                              } catch (error) {
                                console.error(
                                  "[Clinical Record Save Error]: ",
                                  error,
                                );
                              }
                              try {
                                await updateAppointmentStatus(
                                  currentAppt.id,
                                  "COMPLETED",
                                  false,
                                );
                              } catch (error) {
                                console.error(
                                  "[Clinical Status Sync Error]: ",
                                  error,
                                );
                              }
                            }
                          }}
                          type="button"
                          className="hover:opacity-80 transition-opacity cursor-pointer group"
                          title="Click to complete and close session"
                        >
                          {currentAppt.status === "COMPLETED" ? (
                            <span className="bg-emerald-50 text-emerald-700 font-extrabold px-2 py-0.5 rounded border border-emerald-200 group-hover:bg-emerald-100 transition-colors">
                              Completed (مكتمل)
                            </span>
                          ) : currentAppt.status === "IN_PROGRESS" ? (
                            <span className="bg-amber-50 text-amber-900 font-extrabold px-2 py-0.5 rounded border border-amber-300 group-hover:bg-amber-100 transition-colors">
                              In Progress (قيد الإجراء)
                            </span>
                          ) : (
                            <span className="bg-indigo-50 text-indigo-700 font-extrabold px-2 py-0.5 rounded border border-indigo-200 group-hover:bg-indigo-100 transition-colors">
                              Waiting Room (في الإنتظار)
                            </span>
                          )}
                        </button>
                      </div>

                      <div className={`grid gap-1.5 mt-1 grid-cols-2`}>
                          <button
                            onClick={async () => {
                              if (currentAppt.status === "COMPLETED") {
                                // First click on completed consultation: unlock it without closing
                                try {
                                  await updateAppointmentStatus(
                                    currentAppt.id,
                                    "IN_PROGRESS",
                                    true,
                                  );
                                  // Explicitly unlock the record for editing
                                  setIsRecordLocked(false);
                                  // Deliberately NOT calling setSelectedPatient(null) to keep it open
                                } catch (error) {
                                  console.error(
                                    "[Clinical Status Sync Error]: ",
                                    error,
                                  );
                                }
                              } else {
                                // Second click (already unlocked): save, update status, and CLOSE CARD to see waiting room
                                try {
                                  await handleSaveEhrReport(true);
                                } catch (error) {
                                  console.error(
                                    "[Clinical Record Save Error]: ",
                                    error,
                                  );
                                }
                                try {
                                  await updateAppointmentStatus(
                                    currentAppt.id,
                                    "IN_PROGRESS",
                                    true,
                                  );
                                  setSelectedPatient(null);
                                } catch (error) {
                                  console.error(
                                    "[Clinical Status Sync Error]: ",
                                    error,
                                  );
                                }
                              }
                            }}
                            className={`text-[9.5px] font-black py-1 px-0.5 rounded uppercase transition border text-center bg-white text-amber-600 border-amber-200 hover:bg-amber-50`}
                          >
                            {currentAppt.status === "COMPLETED" ? "Unlock" : "Hold"}
                            <span className="block text-[8px] font-normal leading-tight">
                              {currentAppt.status === "COMPLETED" ? "تعديل" : "تعليق"}
                            </span>
                          </button>
                        <button
                          onClick={async () => {
                            // EXPERT COMMENTARY: Mark the patient's appointment as COMPLETED.
                            try {
                              await handleSaveEhrReport(true);
                            } catch (error) {
                              console.error(
                                "[Clinical Record Save Error]: ",
                                error,
                              );
                            }
                            try {
                              await updateAppointmentStatus(
                                currentAppt.id,
                                "COMPLETED",
                                false,
                              );
                              setSelectedPatient(null);
                            } catch (error) {
                              console.error(
                                "[Clinical Status Sync Error]: ",
                                error,
                              );
                            }
                          }}
                          className={`text-[9.5px] font-black py-1 px-0.5 rounded uppercase transition border text-center ${
                            currentAppt.status === "COMPLETED"
                              ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                              : "bg-white text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                          }`}
                        >
                          Complete
                          <span className="block text-[8px] font-normal leading-tight">
                            مكتمل
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}


            </div>
          )}

          {selectedPatient ? (
            <>
              <ClinicalProfessorBar
                apiToken={apiToken}
                patient={selectedPatient}
                clinicalRecords={notesRecord}
                patientDocuments={patientDocuments}
              />
              <GoogleCalendarSync
                appointments={appointments}
                patients={patients}
                apiToken={apiToken}
              />
              <GoogleMeetButton
                patient={selectedPatient}
                onStartConsultation={(generatedMeetUrl) => {
                  // EXPERT CLINICAL COMMENTARY:
                  // Initiates a secure live telemedicine consultation for the selected patient
                  // by mapping patient demographics and the newly generated Google Meet URL
                  // directly into the active interactive physician video consultation stream states.
                  try {
                    setActiveOnlineConsult({
                      id: selectedPatient.id,
                      name: selectedPatient.name,
                      phone: selectedPatient.phone,
                      age: selectedPatient.dob
                        ? (
                            new Date().getFullYear() -
                            new Date(selectedPatient.dob).getFullYear()
                          ).toString()
                        : "",
                      nationalId: selectedPatient.nationalId || "",
                      meetUrl: generatedMeetUrl,
                      symptoms:
                        latestVitals?.chiefComplaint ||
                        "حالة حضورية مجدولة بالعيادة تم تحويلها لاستشارة مرئية مباشرة",
                    });
                    setCustomMeetUrl(generatedMeetUrl);
                  } catch (err: any) {
                    console.error(
                      "[Clinical Telemedicine Initiation Fail]:",
                      err,
                    );
                  }
                }}
              />
              <AisNotepad
                appointments={appointments}
                patients={patients}
                apiToken={apiToken}
                setSelectedPatient={setSelectedPatient}
              />
            </>
          ) : (
            <div className="lg:col-span-1 space-y-4">
              <ClinicalProfessorBar apiToken={apiToken} />
              <AisNotepad
                appointments={appointments}
                patients={patients}
                apiToken={apiToken}
                setSelectedPatient={setSelectedPatient}
              />
              <GoogleCalendarSync
                appointments={appointments}
                patients={patients}
                apiToken={apiToken}
              />
              <GoogleMeetButton />
            </div>
          )}
        </div>

        {/* Right column: EHR Workstation with Tabbed Interface */}
        <div className={selectedPatient ? "lg:col-span-3 space-y-6" : "w-full"}>
          {selectedPatient ? (
            <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden">
              {/* Clinical Appointment Adherence Countdown Timer */}
              <div className="bg-indigo-900 text-white px-4 py-3 flex items-center justify-between shadow-sm border-b border-indigo-950 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-2.5 h-2.5 rounded-full ${consultTimer <= 60 ? "bg-rose-500 animate-pulse" : consultTimer <= 120 ? "bg-amber-400" : "bg-emerald-400"}`}
                  />
                  <div>
                    <span className="text-[10px] text-indigo-200 font-extrabold uppercase tracking-wider block leading-none">
                      Active Consultation Session
                    </span>
                    <span className="text-xs font-bold text-white flex items-center gap-2 mt-0.5">
                      Adherence Tracker for:{" "}
                      <span className="underline decoration-indigo-300 font-extrabold">
                        {selectedPatient.name}
                      </span>
                      <button 
                        onClick={() => setShowAnalytics(true)} 
                        title="View Clinical Analytics & Trends"
                        className="bg-indigo-600 hover:bg-indigo-500 text-white text-[9px] px-2 py-0.5 rounded shadow-sm border border-indigo-400 flex items-center gap-1 transition ml-2 cursor-pointer"
                      >
                         📊 Analytics
                      </button>
                    </span>
                  </div>
                </div>

                {/* Real-time Clock Graphic and Controls */}
                <div className="flex items-center gap-2.5">
                  <div
                    className={`px-2.5 py-1 bg-indigo-900/60 rounded-md border flex items-center gap-2 font-mono text-sm font-extrabold ${
                      consultTimer <= 60
                        ? "border-rose-500 text-rose-400 animate-pulse bg-rose-950/20"
                        : consultTimer <= 120
                          ? "border-amber-500 text-amber-400"
                          : "border-indigo-800 text-indigo-100"
                    }`}
                  >
                    {/* SVG Timer Clock Icon */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2.5}
                      stroke="currentColor"
                      className={`w-3.5 h-3.5 ${timerIsRunning && consultTimer > 0 ? "animate-spin" : ""}`}
                      style={{
                        animationDuration: consultTimer <= 60 ? "1s" : "3s",
                      }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>
                      {Math.floor(consultTimer / 60)}:
                      {(consultTimer % 60).toString().padStart(2, "0")}
                    </span>
                  </div>

                  {/* Buttons */}
                  <div className="flex items-center gap-1">
                    {/* Play / Pause */}
                    <button
                      type="button"
                      onClick={() => setTimerIsRunning(!timerIsRunning)}
                      className="p-1 px-2 bg-indigo-800 hover:bg-indigo-700 active:bg-indigo-950 rounded-md border border-indigo-750 text-[10px] font-bold transition flex items-center gap-1 cursor-pointer"
                      title={timerIsRunning ? "Pause Timer" : "Start Timer"}
                    >
                      {timerIsRunning ? (
                        <>
                          <span className="w-1.5 h-1.5 bg-amber-400 rounded-sm inline-block" />
                          <span>إيقاف</span>
                        </>
                      ) : (
                        <>
                          <span className="border-t-[3.5px] border-t-transparent border-b-[3.5px] border-b-transparent border-l-[5.5px] border-l-emerald-400 inline-block" />
                          <span>تشغيل</span>
                        </>
                      )}
                    </button>

                    {/* Add 2 min */}
                    <button
                      type="button"
                      onClick={() => setConsultTimer((prev) => prev + 120)}
                      className="p-1 px-1.5 bg-indigo-800 hover:bg-indigo-700 active:bg-indigo-950 rounded-md border border-indigo-750 text-[9.5px] font-bold transition cursor-pointer"
                      title="Add 2 Minutes"
                    >
                      +2 د
                    </button>

                    {/* Add 5 min */}
                    <button
                      type="button"
                      onClick={() => setConsultTimer((prev) => prev + 300)}
                      className="p-1 px-1.5 bg-indigo-800 hover:bg-indigo-700 active:bg-indigo-950 rounded-md border border-indigo-750 text-[9.5px] font-bold transition cursor-pointer"
                      title="Add 5 Minutes"
                    >
                      +5 د
                    </button>

                    {/* Reset button */}
                    <button
                      type="button"
                      onClick={() => {
                        setConsultTimer(600);
                        setTimerIsRunning(true);
                      }}
                      className="p-1 px-1.5 bg-indigo-950 hover:bg-indigo-900 active:bg-black rounded-md border border-indigo-800 text-[9.5px] font-bold transition text-indigo-300 hover:text-white cursor-pointer"
                      title="Reset to 10 min"
                    >
                      إعادة
                    </button>
                  </div>
                </div>
              </div>

              {/* Always Visible Medical Alerts Bar */}
              <div className="bg-slate-50 border-b border-[#E2E8F0] p-3 text-[11px]">
                <div className="flex gap-4 items-start">
                  <div className="flex-1 space-y-1">
                    <span className="font-bold text-slate-700 uppercase tracking-wider text-[10px] flex items-center gap-1">
                      <Activity className="w-3 h-3 text-emerald-600" /> Chronic
                      Conditions & Meds:
                    </span>
                    {medicalHistory?.chronicConditions &&
                    medicalHistory.chronicConditions.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {medicalHistory.chronicConditions.map((cc) => (
                          <span
                            key={cc.id}
                            className="bg-white border border-slate-200 px-2 py-0.5 rounded text-slate-700 font-medium"
                          >
                            {cc.condition}{" "}
                            {cc.notes ? (
                              <span className="text-slate-400 font-normal">
                                ({cc.notes})
                              </span>
                            ) : null}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="text-slate-400 italic text-[10px]">
                        None registered.
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-1">
                    <span className="font-bold text-slate-700 uppercase tracking-wider text-[10px] flex items-center gap-1">
                      <Info className="w-3 h-3 text-rose-600" /> Drug Allergies:
                    </span>
                    {medicalHistory?.allergies &&
                    medicalHistory.allergies.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {medicalHistory.allergies.map((a) => (
                          <span
                            key={a.id}
                            className="bg-rose-50 border border-rose-200 text-rose-700 px-2 py-0.5 rounded font-bold"
                          >
                            {a.allergen} ({a.severity})
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="text-slate-400 italic text-[10px]">
                        No known drug allergies.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Tab headers */}
              <div className="flex border-b flex-wrap border-[#E2E8F0]">
                {(
                  [
                    { id: "gp", label: "GP Consultation", icon: FileText },
                    { id: "alaa_triage", label: "Alaa Triage", icon: Sparkles },
                    user.role === "ADMIN" || user.role === "SUPER_ADMIN" || user.role === "PHYSICIAN"
                      ? { id: "derma", label: "Dermatology", icon: Sparkles }
                      : null,
                    { id: "surgery", label: "Surgery Log", icon: Layers },
                    { id: "pharmacy", label: "Pharmacy RX", icon: Activity },
                    { id: "med_profile", label: "Med Profile", icon: Pill },
                    { id: "lab", label: "Lab/X-Ray", icon: ClipboardList },
                    { id: "nursing", label: "Nurse Request", icon: Info },
                    { id: "history", label: "Medical History", icon: History },
                    { id: "timeline", label: "Timeline View", icon: Clock },
                    { id: "followup", label: "Follow-up", icon: BookOpen },
                    { id: "documents", label: "المرفقات الطبية", icon: FolderOpen },
                  ].filter(Boolean) as {
                    id: string;
                    label: string;
                    icon: any;
                  }[]
                ).map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTab(t.id as any)}
                      className={`flex-1 py-3 px-2 flex items-center justify-center gap-1.5 cursor-pointer font-bold text-xs border-b-2 transition ${
                        tab === t.id
                          ? "border-[#0F172A] text-[#0F172A] bg-[#F8FAFC]"
                          : "border-transparent text-[#64748B] hover:text-[#1E293B]"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{t.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Tab Content Panels */}
              <div className="p-5">
                {/* 1.5 ALAA TRIAGE TAB */}
                {tab === "alaa_triage" && (() => {
                  let currentAppt = null;
                  if (editingRecordId) {
                  if (editingRecordId.startsWith("appt_")) {
                  currentAppt = appointments.find((a: any) => a.id === editingRecordId.replace("appt_", ""));
                  } else if (editingRecordId.startsWith("record_")) {
                  const recordId = editingRecordId.replace("record_", "");
                  const record = notesRecord.find((r: any) => r.id === recordId);
                  if (record && record.appointmentId) {
                  currentAppt = appointments.find((a: any) => a.id === record.appointmentId);
                  } else if (record && record.createdAt) {
                  currentAppt = appointments.find((a: any) => a.dateTime === record.createdAt && a.patientId === selectedPatient?.id);
                  }
                  } else {
                  const record = notesRecord.find((r: any) => r.id === editingRecordId);
                  if (record && record.appointmentId) {
                  currentAppt = appointments.find((a: any) => a.id === record.appointmentId);
                  }
                  }
                  } else if (customRecordDate) {
                  currentAppt = appointments.find((a: any) => a.dateTime === customRecordDate && a.patientId === selectedPatient?.id);
                  }
                  
                  if (!currentAppt) {
                  const patientAppts = appointments.filter((a: any) => a.patientId === selectedPatient?.id);
                  const activeAppts = patientAppts.filter((a: any) => a.status === "IN_PROGRESS" || a.status === "PENDING" || a.status === "WAITING").sort((a: any, b: any) => new Date(b.createdAt || "").getTime() - new Date(a.createdAt || "").getTime());
                  currentAppt = activeAppts[0] || patientAppts.sort((a: any, b: any) => new Date(b.createdAt || "").getTime() - new Date(a.createdAt || "").getTime())[0];
                  }
                  const alaaTriage = currentAppt?.alaaTriage;
                  const alaaTriageText = currentAppt?.reason?.includes("--- AI Triage ---")
                    ? currentAppt.reason.split("--- AI Triage ---")[1].trim()
                    : null;
                    
                  const SectionCard = ({ title, content, icon: Icon }: { title: string, content: string | null | undefined, icon: any }) => {
                    if (!content) return null;
                    return (
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm flex flex-col gap-2">
                        <div className="flex items-center gap-2 text-indigo-900 border-b border-indigo-50 pb-2">
                          <Icon className="w-4 h-4 text-indigo-500" />
                          <span className="font-bold text-[11px] uppercase tracking-wider">{title}</span>
                        </div>
                        <pre className="text-xs text-slate-800 font-medium leading-relaxed whitespace-pre-wrap font-sans text-left" dir="ltr">
                          {content}
                        </pre>
                      </div>
                    );
                  };

                  return (
                    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                      <div className="flex items-center justify-between border-b border-indigo-100 pb-2">
                        <h3 className="text-sm font-black text-indigo-900 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-indigo-600" /> Alaa AI Triage & Medical History
                        </h3>
                        {currentAppt && (
                          <div className="flex items-center gap-2 text-[11px] font-bold text-indigo-700 bg-indigo-50/80 px-2.5 py-1 rounded-md border border-indigo-100 shadow-sm" dir="ltr">
                            {currentAppt.dateTime && (
                              <span className="flex items-center gap-1.5">
                                <span>📅</span>
                                {currentAppt.dateTime}
                              </span>
                            )}
                            {currentAppt.time && (
                              <span className="flex items-center gap-1.5 ml-1 pl-2 border-l border-indigo-200">
                                <span>⌚</span>
                                {currentAppt.time}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {alaaTriage ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-indigo-50/30 p-5 rounded-2xl border border-indigo-100">
                          <div className="md:col-span-2">
                            <SectionCard title="Present Complaint (SOCRATES)" content={alaaTriage.socrates} icon={Activity} />
                          </div>
                          <SectionCard title="Medical & Surgical History" content={alaaTriage.medicalHistory} icon={History} />
                          <SectionCard title="Drug History & Allergies" content={alaaTriage.drugHistory} icon={Pill} />
                          <SectionCard title="Family History" content={alaaTriage.familyHistory} icon={UserIcon} />
                          <SectionCard title="Social History" content={alaaTriage.socialHistory} icon={Info} />
                        </div>
                      ) : alaaTriageText ? (
                        <div className="bg-indigo-50/50 p-5 rounded-2xl border border-indigo-100 shadow-inner">
                          <pre className="text-xs text-indigo-950 font-bold leading-relaxed whitespace-pre-wrap font-sans text-right" dir="rtl">
                            {alaaTriageText}
                          </pre>
                        </div>
                      ) : (
                        <div className="bg-indigo-50/50 p-5 rounded-2xl border border-indigo-100 shadow-inner">
                          <div className="text-center py-10">
                            <Sparkles className="w-10 h-10 text-indigo-200 mx-auto mb-3" />
                            <p className="text-indigo-400 font-bold text-xs italic">لا يوجد تاريخ مرضي مسجل بواسطة الذكاء الاصطناعي (آلاء) لهذه الجلسة حتى الآن.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* 1. GP CONSULTATION TAB */}
                {tab === "gp" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h3 className="text-sm font-bold text-[#1E293B]">
                          Primary General Practice Assessment
                        </h3>
                        {editingRecordId && !isRecordLocked && (
                          <span className="text-[10px] bg-amber-100 text-amber-700 font-bold px-2 py-0.5 rounded uppercase flex items-center gap-1 shadow-sm border border-amber-200">
                            Editing Past Record
                          </span>
                        )}

                        {(() => {
                          let currentAppt = null;
                          if (editingRecordId) {
                          if (editingRecordId.startsWith("appt_")) {
                          currentAppt = appointments.find((a: any) => a.id === editingRecordId.replace("appt_", ""));
                          } else if (editingRecordId.startsWith("record_")) {
                          const recordId = editingRecordId.replace("record_", "");
                          const record = notesRecord.find((r: any) => r.id === recordId);
                          if (record && record.appointmentId) {
                          currentAppt = appointments.find((a: any) => a.id === record.appointmentId);
                          } else if (record && record.createdAt) {
                          currentAppt = appointments.find((a: any) => a.dateTime === record.createdAt && a.patientId === selectedPatient?.id);
                          }
                          } else {
                          const record = notesRecord.find((r: any) => r.id === editingRecordId);
                          if (record && record.appointmentId) {
                          currentAppt = appointments.find((a: any) => a.id === record.appointmentId);
                          }
                          }
                          } else if (customRecordDate) {
                          currentAppt = appointments.find((a: any) => a.dateTime === customRecordDate && a.patientId === selectedPatient?.id);
                          }
                          
                          if (!currentAppt) {
                          const patientAppts = appointments.filter((a: any) => a.patientId === selectedPatient?.id);
                          const activeAppts = patientAppts.filter((a: any) => a.status === "IN_PROGRESS" || a.status === "PENDING" || a.status === "WAITING").sort((a: any, b: any) => new Date(b.createdAt || "").getTime() - new Date(a.createdAt || "").getTime());
                          currentAppt = activeAppts[0] || patientAppts.sort((a: any, b: any) => new Date(b.createdAt || "").getTime() - new Date(a.createdAt || "").getTime())[0];
                          }
                          const alaaTriage = currentAppt?.alaaTriage;
                          const alaaTriageText = currentAppt?.reason?.includes("--- AI Triage ---") 
                            ? currentAppt.reason.split("--- AI Triage ---")[1].trim() 
                            : null;
                          const isAlreadyImported = currentAppt ? importedTriageAppts.includes(currentAppt.id) : false;
                          
                          if ((alaaTriage || alaaTriageText) && !isRecordLocked && !isAlreadyImported) {
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (alaaTriage) {
                                    // --- Structured Import ---
                                    let combinedSubjective = "";
                                    if (alaaTriage.socrates) combinedSubjective += "Present Complaint (SOCRATES):\n" + alaaTriage.socrates;
                                    if (alaaTriage.medicalHistory) combinedSubjective += (combinedSubjective ? "\n\n" : "") + "Past Medical & Surgical History:\n" + alaaTriage.medicalHistory;
                                    if (alaaTriage.drugHistory) combinedSubjective += (combinedSubjective ? "\n\n" : "") + "Drug History & Allergies:\n" + alaaTriage.drugHistory;
                                    if (alaaTriage.familyHistory) combinedSubjective += (combinedSubjective ? "\n\n" : "") + "Family History:\n" + alaaTriage.familyHistory;
                                    if (alaaTriage.socialHistory) combinedSubjective += (combinedSubjective ? "\n\n" : "") + "Social History:\n" + alaaTriage.socialHistory;

                                    setSoapSubjective((prev) => prev ? prev + "\n\n--- AI Triage Summary ---\n" + combinedSubjective : combinedSubjective);
                                  } else if (alaaTriageText) {
                                    // --- Fallback Text Parser ---
                                    const lines = alaaTriageText.split('\n');
                                    let currentSection = 'subjective'; // default
                                    let socratesText: string[] = [];
                                    let medicalText: string[] = [];
                                    let familyText: string[] = [];
                                    let socialText: string[] = [];
                                    let allergyText: string[] = [];

                                    lines.forEach(line => {
                                        const lowerLine = line.toLowerCase();
                                        if (lowerLine.includes('socrates') || lowerLine.includes('الشكوى') || lowerLine.includes('الرئيسية')) {
                                            currentSection = 'subjective';
                                        } else if (lowerLine.includes('medical history') || lowerLine.includes('التاريخ المرضي') || lowerLine.includes('امراض مزمنة') || lowerLine.includes('أمراض مزمنة')) {
                                            currentSection = 'medical';
                                        } else if (lowerLine.includes('family history') || lowerLine.includes('التاريخ العائلي')) {
                                            currentSection = 'family';
                                        } else if (lowerLine.includes('social history') || lowerLine.includes('التاريخ الاجتماعي')) {
                                            currentSection = 'social';
                                        } else if (lowerLine.includes('allerg') || lowerLine.includes('حساسية')) {
                                            currentSection = 'allergy';
                                        } else {
                                            if (line.trim() !== '') {
                                                if (currentSection === 'subjective') socratesText.push(line.trim());
                                                else if (currentSection === 'medical') medicalText.push(line.trim());
                                                else if (currentSection === 'family') familyText.push(line.trim());
                                                else if (currentSection === 'social') socialText.push(line.trim());
                                                else if (currentSection === 'allergy') allergyText.push(line.trim());
                                            }
                                        }
                                    });

                                    // --- Distribution ---
                                    if (socratesText.length) {
                                        const combinedSocrates = socratesText.join('\n');
                                        setSoapSubjective((prev) => prev ? prev + "\n\n" + combinedSocrates : combinedSocrates);
                                    }
                                    if (familyText.length) {
                                        setFamilyHistory((prev) => prev ? prev + "\n" + familyText.join('\n') : familyText.join('\n'));
                                    }
                                    if (socialText.length) {
                                        setSocialHistory((prev) => prev ? prev + "\n" + socialText.join('\n') : socialText.join('\n'));
                                    }
                                    
                                    let newGpNotes = gpNotes;
                                    if (medicalText.length) newGpNotes += (newGpNotes ? "\n\n" : "") + "Past Medical History:\n" + medicalText.join('\n');
                                    if (allergyText.length) newGpNotes += (newGpNotes ? "\n\n" : "") + "Drug Allergies:\n" + allergyText.join('\n');
                                    if (medicalText.length || allergyText.length) setGpNotes(newGpNotes);
                                  }

                                  if (currentAppt) {
                                    setImportedTriageAppts(prev => [...prev, currentAppt.id]);
                                  }
                                  triggerSuccessToast("تم استيراد وتوزيع التاريخ المرضي بذكاء في أماكنه المخصصة!");
                                }}
                                className="ml-2 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white font-extrabold text-[10px] px-3 py-1 rounded-full shadow-sm transition-all transform hover:scale-105 flex items-center gap-1.5 cursor-pointer"
                              >
                                <Sparkles className="w-3 h-3" />
                                استيراد ملخص آلاء (Smart Parse)
                              </button>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      <span className="text-[9px] bg-[#F8FAFC] border border-[#E2E8F0] px-2 py-0.5 rounded-full text-[#64748B] font-mono">
                        MD Node Sync
                      </span>
                    </div>

                    <div className="space-y-6">
                        {/* Vital Signs Box */}
                        <div className="bg-white border-2 border-indigo-50 rounded-xl overflow-hidden shadow-sm mb-6">
                          <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-100 flex items-center justify-between">
                            <label className="text-[11px] font-bold text-indigo-900 uppercase tracking-widest flex items-center gap-1.5 mb-0">
                              <Activity className="w-4 h-4 text-indigo-500" />
                              Vital Signs
                            </label>
                          </div>
                          <div className="p-4 grid grid-cols-2 md:grid-cols-6 gap-4">
                            <div className="space-y-1.5">
                              <label className="text-[10px] font-bold text-slate-600 uppercase">Temp (°C)</label>
                              <input type="number" step="0.1" value={vitalTemp} onChange={(e) => setVitalTemp(e.target.value)} disabled={isRecordLocked} className="w-full bg-white border border-slate-200 shadow-sm rounded-lg px-3 py-2 font-medium text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition" placeholder="37.0" />
                            </div>
                            <div className="space-y-1.5">
                              <label className="text-[10px] font-bold text-slate-600 uppercase">Pulse (bpm)</label>
                              <input type="number" value={vitalPulse} onChange={(e) => setVitalPulse(e.target.value)} disabled={isRecordLocked} className="w-full bg-white border border-slate-200 shadow-sm rounded-lg px-3 py-2 font-medium text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition" placeholder="80" />
                            </div>
                            <div className="space-y-1.5">
                              <label className="text-[10px] font-bold text-slate-600 uppercase">BP (mmHg)</label>
                              <input type="text" value={vitalBP} onChange={(e) => setVitalBP(e.target.value)} disabled={isRecordLocked} className="w-full bg-white border border-slate-200 shadow-sm rounded-lg px-3 py-2 font-medium text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition" placeholder="120/80" />
                            </div>
                            <div className="space-y-1.5">
                              <label className="text-[10px] font-bold text-slate-600 uppercase">Weight (kg)</label>
                              <input type="number" step="0.1" value={vitalWeight} onChange={(e) => setVitalWeight(e.target.value)} disabled={isRecordLocked} className="w-full bg-white border border-slate-200 shadow-sm rounded-lg px-3 py-2 font-medium text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition" placeholder="70" />
                            </div>
                            <div className="space-y-1.5">
                              <label className="text-[10px] font-bold text-slate-600 uppercase">Height (cm)</label>
                              <input type="number" step="1" value={vitalHeight} onChange={(e) => setVitalHeight(e.target.value)} disabled={isRecordLocked} className="w-full bg-white border border-slate-200 shadow-sm rounded-lg px-3 py-2 font-medium text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition" placeholder="170" />
                            </div>
                            <div className="space-y-1.5">
                              <label className="text-[10px] font-bold text-slate-600 uppercase">BMI</label>
                              <input type="text" readOnly value={vitalBMI} className="w-full bg-slate-50 border border-slate-200 shadow-sm rounded-lg px-3 py-2 font-bold text-xs text-slate-700 outline-none" placeholder="Auto" />
                            </div>
                          </div>
                        </div>

                      <div className="bg-white border-2 border-indigo-50 rounded-xl overflow-hidden shadow-sm">
                        <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-100 flex items-center justify-between">
                          <label className="text-[11px] font-bold text-indigo-900 uppercase tracking-widest flex items-center gap-1.5 mb-0">
                            <FileText className="w-4 h-4 text-indigo-500" />
                            Structured SOAP Notes
                          </label>
                          <span className="text-[9px] bg-white text-indigo-500 px-2 py-0.5 rounded-full font-mono shadow-sm">
                            EHR STANDARD
                          </span>
                        </div>

                        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-5 bg-slate-50/50">
                          <div className="space-y-1.5">
                            <label className="flex items-center justify-between text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                              <span className="flex items-center gap-1.5">
                                Subjective
                                <VoiceDictation onResult={(t) => setSoapSubjective(prev => prev ? prev + " " + t : t)} disabled={isRecordLocked} />
                              </span>
                              <span className="text-slate-400 font-normal">
                                Chief complaint, HPI
                              </span>
                            </label>
                            <textarea
                              value={soapSubjective}
                              onChange={(e) =>
                                setSoapSubjective(e.target.value)
                              }
                              dir="auto"
                              placeholder="Patient's symptoms and history as described..."
                              className={`w-full h-[240px] resize-none overflow-y-auto bg-white border ${submitFailed && !soapSubjective.trim() ? "border-red-500 ring-1 ring-red-500/30" : "border-[#E2E8F0]"} shadow-sm rounded-lg p-3 font-medium text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition duration-200`}
                              disabled={isRecordLocked}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <label className="flex items-center justify-between text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                              <span className="flex items-center gap-1.5">
                                Objective
                                <VoiceDictation onResult={(t) => setSoapObjective(prev => prev ? prev + " " + t : t)} disabled={isRecordLocked} />
                                <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSoapObjective(soapObjective ? soapObjective + "\nChest: Clear, Heart: S1S2 Normal, Abdomen: Soft & non-tender" : "Chest: Clear, Heart: S1S2 Normal, Abdomen: Soft & non-tender"); }} disabled={isRecordLocked} className="ml-2 text-[8px] bg-indigo-50 hover:bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded transition font-bold tracking-wide shadow-sm" title="Insert Normal Physical Examination">
                                  + NORMAL EXAM
                                </button>
                                <select 
                                  className="ml-2 text-[8px] bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded transition font-bold tracking-wide shadow-sm cursor-pointer outline-none text-center"
                                  title="Insert Dermoscopy Macros"
                                  value=""
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if(val) {
                                      setSoapObjective(prev => prev ? prev + "\n" + val : val);
                                      e.target.value = "";
                                    }
                                  }}
                                  disabled={isRecordLocked}
                                >
                                  <option value="" disabled>+ DERMOSCOPY</option>
                                  <option value="Dermoscopy: Symmetrical pigment network, regular dots/globules, uniform color. No atypical features. Compatible with benign melanocytic nevus.">Benign Nevus</option>
                                  <option value="Dermoscopy: Comedo-like openings, milia-like cysts, sharp demarcation, and hairpin vessels. Compatible with Seborrheic Keratosis.">Seborrheic Keratosis</option>
                                  <option value="Dermoscopy: Absence of pigment network, presence of arborizing vessels, ulceration, and blue-gray ovoid nests. Suspicious for BCC.">BCC</option>
                                  <option value="Dermoscopy: Asymmetrical pigment network, atypical dots/globules, blue-white veil, and multiple colors. Highly suspicious for Melanoma, biopsy recommended.">Melanoma</option>
                                  <option value="Trichoscopy: Hair shaft diameter diversity >20%, yellow dots, and thin vellus hairs in the frontal area. Compatible with AGA.">AGA</option>
                                  <option value="Trichoscopy: Exclamation mark hairs, yellow dots, black dots, and broken hairs. Compatible with Alopecia Areata.">Alopecia Areata</option>
                                  <option value="Dermoscopy: Delta wing sign (jet with contrail sign) present, representing the mite and its burrow. Compatible with Scabies.">Scabies</option>
                                  <option value="Dermoscopy: Multiple thrombosed capillaries (red/black dots or loops) surrounded by a whitish halo. Compatible with Viral Wart.">Viral Warts</option>
                                  <option value="Dermoscopy: Wickham striae (white crossing lines) with dotted vessels at the periphery. Compatible with Lichen Planus.">Lichen Planus</option>
                                  <option value="Dermoscopy: Regularly distributed dotted vessels on a light red background with diffuse white scales. Compatible with Psoriasis.">Psoriasis</option>
                                  <option value="Dermoscopy: Central umbilication with polylobular white-yellowish amorphous structures and peripheral crown vessels. Compatible with Molluscum.">Molluscum</option>
                                </select>
                              </span>
                              <span className="text-slate-400 font-normal">
                                Vitals, Exam
                              </span>
                            </label>
                            <textarea
                              value={soapObjective}
                              onChange={(e) => setSoapObjective(e.target.value)}
                              dir="auto"
                              placeholder="Clinical findings, imaging results, vital signs..."
                              className={`w-full h-[240px] resize-none overflow-y-auto bg-white border ${submitFailed && !soapObjective.trim() ? "border-red-500 ring-1 ring-red-500/30" : "border-[#E2E8F0]"} shadow-sm rounded-lg p-3 font-medium text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition duration-200`}
                              disabled={isRecordLocked}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <label className="flex items-center justify-between text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                              <span className="flex items-center gap-1.5">
                                Assessment
                                <VoiceDictation onResult={(t) => setSoapAssessment(prev => prev ? prev + " " + t : t)} disabled={isRecordLocked} />
                              </span>
                              <span className="text-slate-400 font-normal">
                                Diagnosis / ICD-10
                              </span>
                            </label>
                            <textarea
                              value={soapAssessment}
                              onChange={(e) =>
                                setSoapAssessment(e.target.value)
                              }
                              dir="auto"
                              placeholder="Primary and secondary diagnosis..."
                              className={`w-full h-[140px] resize-none overflow-y-auto bg-white border ${submitFailed && !soapAssessment.trim() && !icd10.trim() && gpDiagnoses.length === 0 ? "border-red-500 ring-1 ring-red-500/30" : "border-[#E2E8F0]"} shadow-sm rounded-lg p-3 font-medium text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition duration-200 mb-2`}
                              disabled={isRecordLocked}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <label className="flex items-center justify-between text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                              <span className="flex items-center gap-1.5">
                                Plan
                                <VoiceDictation onResult={(t) => setSoapPlan(prev => prev ? prev + " " + t : t)} disabled={isRecordLocked} />
                              </span>
                              <span className="text-slate-400 font-normal">
                                Treatment
                              </span>
                            </label>
                            <textarea
                              rows={4}
                              value={soapPlan}
                              onChange={(e) => setSoapPlan(e.target.value)}
                              dir="auto"
                              placeholder="Treatment prescribed, follow-up timelines..."
                              className="w-full bg-white border border-[#E2E8F0] shadow-sm rounded-lg p-3 font-medium text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition duration-200 h-[104px]"
                              disabled={isRecordLocked}
                            />
                          </div>
                        </div>

                        {/* Qi-Doctor Clinical AI Triage Support Module (Dual Dermatology & General Clinical Logic) REMOVED */}
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400"></span>
                        Family History
                      </label>
                      <textarea
                        rows={1}
                        value={familyHistory}
                        onChange={(e) => setFamilyHistory(e.target.value)}
                        dir="auto"
                        placeholder="Any relevant family history..."
                        className="w-full bg-white border border-[#E2E8F0] rounded-md p-3 font-medium text-xs outline-none focus:border-[#0F172A] duration-150 mb-4"
                        disabled={isRecordLocked}
                      />

                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400"></span>
                        Social History
                      </label>
                      <textarea
                        rows={1}
                        value={socialHistory}
                        onChange={(e) => setSocialHistory(e.target.value)}
                        dir="auto"
                        placeholder="Smoking, alcohol, occupation, lifestyle..."
                        className="w-full bg-white border border-[#E2E8F0] rounded-md p-3 font-medium text-xs outline-none focus:border-[#0F172A] duration-150 mb-6"
                        disabled={isRecordLocked}
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                          PROFESSIONAL DIAGNOSIS
                        </label>
                        {!isRecordLocked && (
                          <button
                            type="button"
                            onClick={() => setGpNotes("")}
                            className="text-rose-500 hover:text-rose-700 transition cursor-pointer flex items-center gap-1 bg-rose-50 hover:bg-rose-100 px-2 py-1 rounded shadow-sm border border-rose-100"
                            title="Clear Diagnosis"
                          >
                            <Trash className="w-3.5 h-3.5" />
                            <span className="text-[9px] font-bold">Clear</span>
                          </button>
                        )}
                      </div>
                      <textarea
                        rows={5}
                        value={gpNotes}
                        onChange={(e) => setGpNotes(e.target.value)}
                        dir="auto"
                        placeholder="Miscellaneous unstructured notes..."
                        className="w-full bg-white border border-[#E2E8F0] rounded-md p-3 font-medium text-xs outline-none focus:border-[#0F172A] duration-150"
                        disabled={isRecordLocked}
                      />
                    </div>

                    {/* --- Diagnosis Search & AI Suggestion --- */}
                    <div className="mt-6 bg-[#F8FAFC] border border-[#E2E8F0] p-4 rounded-xl space-y-4">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                          <Layers className="w-4 h-4 text-indigo-500" />{" "}
                          Clinical Diagnosis Directory
                        </h4>
                        <button
                          type="button"
                          onClick={handleSuggestDiagnosis}
                          disabled={isAiDiagnosing || isRecordLocked}
                          className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold text-[10px] uppercase px-3 py-1.5 rounded flex items-center gap-1.5 transition shadow-sm cursor-pointer ml-auto"
                        >
                          {isAiDiagnosing ? (
                            <>
                              <RefreshCw className="w-3 h-3 animate-spin" />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-3 h-3" />
                              AI Suggest Diagnosis
                            </>
                          )}
                        </button>
                      </div>

                      {/* AI Suggestions Results */}
                      {aiSuggestedDiagnoses.length > 0 && (
                        <div className="bg-white border border-indigo-100 rounded-lg p-3 space-y-3 shadow-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold text-indigo-950 uppercase tracking-wider flex items-center gap-1">
                              <Sparkles className="w-3 h-3 text-indigo-500" />
                              AI Suggested Differential Diagnoses
                            </span>
                            <button
                              onClick={() => setAiSuggestedDiagnoses([])}
                              className="text-slate-400 hover:text-slate-600"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="grid grid-cols-1 gap-2">
                            {aiSuggestedDiagnoses.map((d, i) => (
                              <div
                                key={i}
                                className="bg-slate-50 border border-slate-100 rounded p-2.5 space-y-1"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] font-black text-slate-900">
                                    {d.disorder}
                                  </span>
                                  <div className="flex gap-1">
                                    {d.ICD10 && (
                                      <span className="text-[8px] bg-indigo-50 text-indigo-600 font-bold px-1.5 py-0.5 rounded uppercase">
                                        {d.ICD10}
                                      </span>
                                    )}
                                    <button
                                      onClick={() => {
                                        if (
                                          !gpDiagnoses.some(
                                            (existing) =>
                                              existing.disorder === d.disorder,
                                          )
                                        ) {
                                          setGpDiagnoses((prev) => [
                                            ...prev,
                                            {
                                              disorder: d.disorder,
                                              ICD10: d.ICD10 || "ICD-10",
                                            },
                                          ]);
                                        }
                                        if (d.recommendedManagement) {
                                          setSoapPlan((prev) =>
                                            prev
                                              ? prev +
                                                "\n\n" +
                                                d.recommendedManagement
                                              : d.recommendedManagement,
                                          );
                                        }
                                        triggerSuccessToast(
                                          `Added: ${d.disorder}`,
                                        );
                                      }}
                                      className="text-[9px] bg-emerald-600 text-white font-bold px-1.5 py-0.5 rounded hover:bg-emerald-700 transition"
                                    >
                                      Add to Record
                                    </button>
                                  </div>
                                </div>
                                {d.reasoning && (
                                  <p className="text-[10px] text-slate-600 font-medium leading-relaxed italic">
                                    "{d.reasoning}"
                                  </p>
                                )}
                                {d.probability && (
                                  <span className="text-[8px] text-indigo-500 font-bold uppercase tracking-widest">
                                    Confidence: {d.probability}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                          {aiDiagnosisReasoning && (
                            <div className="mt-2 pt-2 border-t border-slate-100">
                              <p className="text-[9.5px] text-slate-500 font-medium italic leading-relaxed">
                                <span className="font-bold text-slate-700">
                                  Professor Reasoning:
                                </span>{" "}
                                {aiDiagnosisReasoning}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {aiAnalysisError && (
                        <div className="bg-rose-50 border border-rose-100 text-rose-700 p-2.5 rounded text-[10px] font-bold">
                          Error: {aiAnalysisError}
                        </div>
                      )}


                      {/* Manual Search Box */}
                      <div className="relative">
                        <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                          Search Trusted Disease Directory (Min 1 char)
                        </label>
                        <input
                          type="text"
                          value={diagnosisSearchStr}
                          onChange={(e) =>
                            setDiagnosisSearchStr(e.target.value)
                          }
                          placeholder="Type disease to search..."
                          className="w-full bg-white border border-slate-200 text-xs px-2.5 py-2 rounded-md outline-none focus:border-indigo-500"
                        />
                        {diagnosisSearchStr.length >= 1 && (
                          <div className="absolute z-10 w-full bg-white border border-slate-200 shadow-xl rounded-md mt-1 max-h-64 overflow-y-auto">
                            {(() => {
                              const searchLower = diagnosisSearchStr.toLowerCase();
                              const matchedFavs = favoriteDiagnoses.filter((fav) =>
                                fav.type === "DIAGNOSIS" &&
                                (fav.name.toLowerCase().includes(searchLower) ||
                                 (fav.subText || "").toLowerCase().includes(searchLower))
                              ).map(fav => ({ ...fav, isFav: true }));

                              const matchedDirectory = universalSearchData.filter(
                                (item) =>
                                  item.type === "DIAGNOSIS" &&
                                  (item.name.toLowerCase().includes(searchLower) ||
                                    (item.subText || "").toLowerCase().includes(searchLower)) &&
                                  !matchedFavs.some(fav => fav.name === item.name)
                              ).map(item => ({ ...item, isFav: false }));

                              const matched = [...matchedFavs, ...matchedDirectory];

                              if (matched.length === 0) {
                                return (
                                  <div className="p-3 text-xs text-slate-400 text-center">
                                    No results found in clinical directory.
                                  </div>
                                );
                              }
                              return matched.slice(0, 50).map((item, idx) => (
                                <div
                                  key={idx}
                                  className={`px-3 py-2 text-xs hover:bg-slate-50 cursor-pointer border-b last:border-0 border-slate-100 flex justify-between items-center text-right ${item.isFav ? 'bg-amber-50/30' : ''}`}
                                  onClick={() => {
                                    handleSelectUniversalSuggestion(item);
                                    setDiagnosisSearchStr("");
                                  }}
                                >
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleFavoriteDiagnosis(item);
                                      }}
                                      className="text-slate-300 hover:text-amber-400 transition"
                                      title="Toggle Favorite"
                                    >
                                      <Star className={`w-4 h-4 ${favoriteDiagnoses.find(f => f.name === item.name) ? "fill-amber-400 text-amber-400" : ""}`} />
                                    </button>
                                    <span
                                      className={`text-[8px] uppercase font-bold px-1.5 py-0.5 rounded ${
                                        item.type === "DIAGNOSIS"
                                          ? "bg-orange-50 text-orange-600"
                                          : item.type === "INVESTIGATION"
                                            ? "bg-teal-50 text-teal-600"
                                            : "bg-purple-50 text-purple-600"
                                      }`}
                                    >
                                      {item.type}
                                    </span>
                                    <span className="font-semibold text-slate-700 flex items-center gap-1">
                                      {item.name}
                                    </span>
                                  </div>
                                  <span className="text-[10px] font-mono text-slate-400">
                                    {item.subText}
                                  </span>
                                </div>
                              ));
                            })()}
                          </div>
                        )}
                      </div>

                      {/* Selected Diagnoses/Plans */}
                      {gpDiagnoses.length > 0 && (
                        <div className="space-y-1 mt-2">
                          <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                            Selected Clinical Entities
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {gpDiagnoses.map((d, i) => (
                              <span
                                key={i}
                                className="text-[10px] bg-slate-800 text-white px-2 py-1 rounded flex items-center gap-1.5"
                              >
                                {d.disorder}{" "}
                                <span className="text-slate-400 font-mono text-[9px]">
                                  ({d.ICD10})
                                </span>
                                <button
                                  onClick={() =>
                                    setGpDiagnoses(
                                      gpDiagnoses.filter((_, idx) => idx !== i),
                                    )
                                  }
                                  className="text-slate-400 hover:text-rose-400 ml-1"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* --- Quick Add Chronic Meds / Allergies (Visible for Next Visits) --- */}
                    <div className="mt-4 bg-[#F8FAFC] border border-[#E2E8F0] p-4 rounded-xl space-y-4">
                      <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                        <Activity className="w-4 h-4 text-emerald-500" />{" "}
                        Continuous Care Updates (For Next Visits)
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Quick Add Chronic */}
                        <div className="space-y-2">
                          <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider">
                            Quick Add Chronic Disease / Med
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="e.g. Hypertension Mgmt"
                              value={newCondition.condition}
                              onChange={(e) =>
                                setNewCondition({
                                  ...newCondition,
                                  condition: e.target.value,
                                })
                              }
                              className="flex-1 bg-white border border-slate-200 text-xs px-2.5 py-1.5 rounded-md outline-none focus:border-emerald-500"
                            />
                            <button
                              onClick={() => {
                                if (!newCondition.condition) return;
                                const updatedHistory: MedicalHistory = {
                                  patientId: selectedPatient.id,
                                  surgeries: medicalHistory?.surgeries || [],
                                  allergies: medicalHistory?.allergies || [],
                                  chronicConditions: [
                                    ...(medicalHistory?.chronicConditions ||
                                      []),
                                    {
                                      id: "cc_" + Date.now().toString(36),
                                      condition: newCondition.condition,
                                      diagnosedYear: new Date()
                                        .getFullYear()
                                        .toString(),
                                      status: "ACTIVE",
                                      notes: "Added from GP Consult",
                                    },
                                  ],
                                };
                                handleUpdateMedicalHistory(updatedHistory);
                                setNewCondition({
                                  condition: "",
                                  diagnosedYear: "",
                                  status: "ACTIVE",
                                  notes: "",
                                });
                              }}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-[10px] uppercase px-3 py-1.5 rounded-md transition cursor-pointer"
                            >
                              Save
                            </button>
                          </div>
                        </div>

                        {/* Quick Add Allergy */}
                        <div className="space-y-2">
                          <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider">
                            Quick Add Patient Allergy
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="e.g. Penicillin"
                              value={newAllergy.allergen}
                              onChange={(e) =>
                                setNewAllergy({
                                  ...newAllergy,
                                  allergen: e.target.value,
                                })
                              }
                              className="flex-1 bg-white border border-slate-200 text-xs px-2.5 py-1.5 rounded-md outline-none focus:border-rose-500"
                            />
                            <button
                              onClick={() => {
                                if (!newAllergy.allergen) return;
                                const updatedHistory: MedicalHistory = {
                                  patientId: selectedPatient.id,
                                  surgeries: medicalHistory?.surgeries || [],
                                  chronicConditions:
                                    medicalHistory?.chronicConditions || [],
                                  allergies: [
                                    ...(medicalHistory?.allergies || []),
                                    {
                                      id: "al_" + Date.now().toString(36),
                                      allergen: newAllergy.allergen,
                                      severity: "SEVERE",
                                      reaction: "Added from GP Consult",
                                    },
                                  ],
                                };
                                handleUpdateMedicalHistory(updatedHistory);
                                setNewAllergy({
                                  allergen: "",
                                  severity: "MILD",
                                  reaction: "",
                                });
                              }}
                              className="bg-rose-600 hover:bg-rose-700 text-white font-semibold text-[10px] uppercase px-3 py-1.5 rounded-md transition cursor-pointer"
                            >
                              Add Alert
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {submitFailed &&
                      (!soapSubjective.trim() ||
                        !soapObjective.trim() ||
                        (!soapAssessment.trim() &&
                          !icd10.trim() &&
                          gpDiagnoses.length === 0)) && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-semibold rounded-md flex items-center gap-2">
                          <span className="shrink-0 w-4 h-4 flex items-center justify-center rounded-full bg-red-100 text-red-600">
                            !
                          </span>
                          Please complete Subjective, Objective, and
                          Diagnosis/Assessment fields highlighted above.
                        </div>
                      )}

                    <div className="mt-4 p-3.5 bg-slate-50 border border-[#E2E8F0] rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3 font-sans">
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-[#1E293B] flex items-center gap-1.5 font-sans">
                          📅 Consultation Date & Time / وقت فتح الاستشارة
                        </span>
                        <span className="text-[10px] text-slate-500 mt-0.5 font-sans">
                          This is the exact locked time this consultation was started. (Read-Only)
                        </span>
                      </div>
                      <input
                        type="datetime-local"
                        value={(() => {
                          const d = customRecordDate ? new Date(customRecordDate) : new Date();
                          const pad = (n: number) => String(n).padStart(2, "0");
                          return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                        })()}
                        readOnly={true}
                        disabled={true}
                        className="bg-gray-100 border border-[#CBD5E1] rounded-md px-3 py-1.5 text-xs text-slate-500 focus:outline-none max-w-xs cursor-not-allowed select-none font-bold opacity-80"
                      />
                    </div>

                    <div className="flex gap-4 mt-4 mb-2 p-3 bg-slate-50 border border-slate-200 rounded-lg max-w-xl">
                      <span className="text-xs font-bold text-slate-700 mr-2 flex items-center">Issued Documents:</span>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={issuedDocs.report} onChange={(e) => setIssuedDocs({...issuedDocs, report: e.target.checked})} className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4 cursor-pointer" />
                        <span className="text-xs font-semibold text-slate-600 flex items-center gap-1">📄 Report</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={issuedDocs.sickLeave} onChange={(e) => setIssuedDocs({...issuedDocs, sickLeave: e.target.checked})} className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4 cursor-pointer" />
                        <span className="text-xs font-semibold text-slate-600 flex items-center gap-1">🛏️ Sick Leave</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={issuedDocs.receipt} onChange={(e) => setIssuedDocs({...issuedDocs, receipt: e.target.checked})} className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4 cursor-pointer" />
                        <span className="text-xs font-semibold text-slate-600 flex items-center gap-1">🧾 Receipt</span>
                      </label>
                    </div>

                    <button
                      disabled={isSavingEhr || isRecordLocked}
                      onClick={() => handleSaveEhrReport()}
                      className={`mt-3 font-semibold text-xs py-2 px-4 rounded-md shadow-sm transition-all duration-300 flex items-center gap-2 select-none cursor-pointer ${
                        justSaved
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-[0_0_12px_rgba(16,185,129,0.3)] scale-[1.02]"
                          : isSavingEhr
                            ? "bg-slate-700 text-slate-200 cursor-not-allowed"
                            : "bg-[#0F172A] hover:bg-slate-800 text-white"
                      }`}
                    >
                      {isSavingEhr ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 animate-spin text-white" />
                          <span>Saving GP Record...</span>
                        </>
                      ) : justSaved ? (
                        <>
                          <CheckCircle className="w-3.5 h-3.5 text-white animate-bounce" />
                          <span>✓ Saved!</span>
                        </>
                      ) : (
                        "Submit GP Record"
                      )}
                    </button>
                  </div>
                )}

                {/* 2. DERMATOLOGY & LASER THERAPY TAB */}
                {tab === "derma" && (
                  <div className="space-y-6">
                    <div className="p-6 bg-slate-50 border border-[#E2E8F0] rounded-xl font-sans">
                      <div className="w-full bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <h2 className="text-sm font-extrabold mb-4 text-gray-800">
                          Laser Therapy Documentation
                        </h2>

                        <div className="grid grid-cols-2 gap-6 mb-6">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700">
                              Patient Skin Type (Fitzpatrick Scale)
                            </label>
                            <select
                              className="mt-1 block w-full p-2 border border-gray-300 rounded-md text-slate-800 font-medium bg-white text-xs outline-none"
                              value={skinType}
                              onChange={(e) => setSkinType(e.target.value)}
                            >
                              <option value="Type I">
                                Type I (Light, Pale White)
                              </option>
                              <option value="Type II">
                                Type II (White, Fair)
                              </option>
                              <option value="Type III">
                                Type III (Medium, White to Olive)
                              </option>
                              <option value="Type IV">
                                Type IV (Olive, Moderate Brown)
                              </option>
                              <option value="Type V">
                                Type V (Brown, Dark Brown)
                              </option>
                              <option value="Type VI">
                                Type VI (Very Dark Brown to Black)
                              </option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-700">
                              Treatment Plan Notes
                            </label>
                            <input
                              type="text"
                              value={soapPlan}
                              onChange={(e) => setSoapPlan(e.target.value)}
                              placeholder="e.g. 6 sessions planned, 4 weeks apart"
                              className="mt-1 block w-full p-2 border border-gray-300 rounded-md text-slate-800 font-medium bg-white text-xs outline-none"
                            />
                          </div>
                        </div>

                        <div className="border-t border-gray-200 pt-6">
                          <h3 className="text-xs font-bold text-gray-800 mb-3 uppercase tracking-wide">
                            Record New Treatment Area
                          </h3>
                          <form
                            onSubmit={handleAddLaserLog}
                            className="grid grid-cols-4 gap-4"
                          >
                            <div className="col-span-2">
                              <label className="block text-xs font-semibold text-gray-700">
                                Treatment Area
                              </label>
                              <input
                                required
                                type="text"
                                placeholder="e.g. Upper Lip, Full Face, Lower Legs"
                                className="mt-1 block w-full p-2 border border-gray-300 rounded-md text-slate-800 font-medium bg-white text-xs outline-none"
                                value={laserFormData.treatmentArea}
                                onChange={(e) =>
                                  setLaserFormData({
                                    ...laserFormData,
                                    treatmentArea: e.target.value,
                                  })
                                }
                              />
                            </div>

                            <div className="col-span-2">
                              <label className="block text-xs font-semibold text-gray-700">
                                Laser Type
                              </label>
                              <select
                                className="mt-1 block w-full p-2 border border-gray-300 rounded-md text-slate-800 font-medium bg-white text-xs outline-none"
                                value={laserFormData.laserType}
                                onChange={(e) =>
                                  setLaserFormData({
                                    ...laserFormData,
                                    laserType: e.target.value,
                                  })
                                }
                              >
                                <option value="Alexandrite 755nm">
                                  Alexandrite 755nm
                                </option>
                                <option value="Nd:YAG 1064nm">
                                  Nd:YAG 1064nm
                                </option>
                                <option value="Diode 810nm">Diode 810nm</option>
                                <option value="IPL (Intense Pulsed Light)">
                                  IPL
                                </option>
                                <option value="CO2 Fractional">
                                  CO2 Fractional
                                </option>
                                <option value="Erbium YAG">Erbium YAG</option>
                              </select>
                            </div>

                            <div className="col-span-4 mb-2">
                              {(() => {
                                const guideline = getGuideline(laserFormData.laserType, skinType);
                                if (!guideline) return null;
                                return (
                                  <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-3">
                                    <div className="flex justify-between items-center mb-2">
                                      <button 
                                        type="button"
                                        onClick={() => setShowLaserProtocol(!showLaserProtocol)}
                                        className="text-xs font-bold text-blue-700 flex items-center gap-1 hover:text-blue-900"
                                      >
                                        📚 Clinical Protocol
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setLaserFormData({
                                            ...laserFormData,
                                            fluence: guideline.fluence.split('-')[0],
                                            spotSize: guideline.spotSize.split('-')[0],
                                            pulseDuration: guideline.pulseDuration.split('-')[0],
                                            cooling: guideline.cooling,
                                            passes: guideline.passes
                                          });
                                        }}
                                        className="text-[10px] bg-blue-600 text-white px-2 py-1 rounded shadow-sm hover:bg-blue-700 transition"
                                      >
                                        🪄 Auto-Fill from Guidelines
                                      </button>
                                    </div>
                                    
                                    {showLaserProtocol && (
                                      <div className="text-[11px] text-gray-700 mt-2 pt-2 border-t border-blue-100 grid grid-cols-2 gap-4">
                                        <div>
                                          <p className="font-bold text-red-600 mb-1">🛑 Contraindications</p>
                                          <ul className="list-disc pl-4 space-y-0.5">
                                            {guideline.contraindications.map((c, i) => <li key={i}>{c}</li>)}
                                          </ul>
                                        </div>
                                        <div>
                                          <p className="font-bold text-indigo-600 mb-1">⏳ Treatment Plan</p>
                                          <p>{guideline.sessions}</p>
                                        </div>
                                        <div>
                                          <p className="font-bold text-green-700 mb-1">📋 Pre-Care</p>
                                          <ul className="list-disc pl-4 space-y-0.5">
                                            {guideline.preCare.map((c, i) => <li key={i}>{c}</li>)}
                                          </ul>
                                        </div>
                                        <div>
                                          <p className="font-bold text-emerald-600 mb-1">✨ Post-Care</p>
                                          <ul className="list-disc pl-4 space-y-0.5">
                                            {guideline.postCare.map((c, i) => <li key={i}>{c}</li>)}
                                          </ul>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-gray-700">
                                Spot Size (mm)
                              </label>
                              <input
                                required
                                type="number"
                                step="0.5"
                                className="mt-1 block w-full p-2 border border-gray-300 rounded-md text-slate-800 font-medium bg-white text-xs outline-none"
                                value={laserFormData.spotSize}
                                onChange={(e) =>
                                  setLaserFormData({
                                    ...laserFormData,
                                    spotSize: e.target.value,
                                  })
                                }
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-gray-700">
                                Fluence / Joules
                              </label>
                              <input
                                required
                                type="number"
                                step="1"
                                className="mt-1 block w-full p-2 border border-gray-300 rounded-md text-slate-800 font-medium bg-white text-xs outline-none"
                                value={laserFormData.fluence}
                                onChange={(e) =>
                                  setLaserFormData({
                                    ...laserFormData,
                                    fluence: e.target.value,
                                  })
                                }
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-gray-700">
                                Pulse Duration (ms)
                              </label>
                              <input
                                required
                                type="number"
                                step="0.5"
                                className="mt-1 block w-full p-2 border border-gray-300 rounded-md text-slate-800 font-medium bg-white text-xs outline-none"
                                value={laserFormData.pulseDuration}
                                onChange={(e) =>
                                  setLaserFormData({
                                    ...laserFormData,
                                    pulseDuration: e.target.value,
                                  })
                                }
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-gray-700">
                                Cooling / Passes
                              </label>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  className="mt-1 block w-2/3 p-2 border border-gray-300 rounded-md text-slate-800 font-medium bg-white text-xs outline-none"
                                  value={laserFormData.cooling}
                                  onChange={(e) =>
                                    setLaserFormData({
                                      ...laserFormData,
                                      cooling: e.target.value,
                                    })
                                  }
                                />
                                <input
                                  type="number"
                                  min="1"
                                  className="mt-1 block w-1/3 p-2 border border-gray-300 rounded-md text-slate-800 font-medium bg-white text-xs outline-none"
                                  value={laserFormData.passes}
                                  onChange={(e) =>
                                    setLaserFormData({
                                      ...laserFormData,
                                      passes: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </div>

                            <div className="col-span-4 mt-2">
                              <button
                                type="submit"
                                disabled={!laserFormData.treatmentArea}
                                className="bg-[#0F172A] text-white py-2 px-6 rounded-md hover:bg-slate-800 font-bold shadow-sm transition disabled:bg-gray-200 disabled:text-gray-400 text-xs cursor-pointer w-full"
                              >
                                + Add Laser Treatment Session Log
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                      <DermBodyMap isRecordLocked={isRecordLocked} pins={dermPins} onChange={(newPins) => setDermPins(newPins)} photos={dermPhotos} onPhotosChange={(p) => setDermPhotos(p)} />

                      <div className="mt-8 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <h2 className="text-sm font-extrabold mb-4 text-gray-800">
                          Laser Treatment Session Records
                        </h2>
                        <div className="border border-gray-200 rounded overflow-hidden">
                          {laserLogs.length === 0 ? (
                            <p className="text-gray-500 text-xs text-center py-6 font-medium bg-gray-50">
                              No treatment areas recorded yet.
                            </p>
                          ) : (
                            <table className="w-full text-left text-xs bg-white">
                              <thead className="bg-[#F8FAFC] text-[#64748B] border-b border-[#E2E8F0]">
                                <tr>
                                  <th className="px-4 py-2 font-bold uppercase tracking-wider">
                                    Area
                                  </th>
                                  <th className="px-4 py-2 font-bold uppercase tracking-wider">
                                    Laser
                                  </th>
                                  <th className="px-4 py-2 font-bold uppercase tracking-wider">
                                    Fluence
                                  </th>
                                  <th className="px-4 py-2 font-bold uppercase tracking-wider">
                                    Settings
                                  </th>
                                  <th className="px-4 py-2"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {laserLogs.map((log, index) => (
                                  <tr
                                    key={index}
                                    className="border-b border-[#E2E8F0] last:border-0 hover:bg-slate-50"
                                  >
                                    <td className="px-4 py-2 font-semibold text-slate-800">
                                      {log.treatmentArea}
                                    </td>
                                    <td className="px-4 py-2 text-slate-600 font-mono">
                                      {log.laserType}
                                    </td>
                                    <td className="px-4 py-2 text-slate-800 font-bold">
                                      {log.fluence} J/cm²
                                    </td>
                                    <td className="px-4 py-2 text-slate-600">
                                      Spot: {log.spotSize}mm • Pulse:{" "}
                                      {log.pulseDuration}ms
                                      <br />
                                      <span className="text-[10px] text-slate-400">
                                        Cooling: {log.cooling} • Passes:{" "}
                                        {log.passes}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                      <button
                                        type="button"
                                        onClick={() => removeLaserLog(index)}
                                        className="text-[#64748B] hover:text-rose-500 cursor-pointer p-0.5 transition"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>

                        <div className="flex justify-end pt-4 mt-4 border-t border-gray-200">
                          <button
                            onClick={handleSaveSession}
                            disabled={laserLogs.length === 0}
                            className="bg-emerald-600 text-white py-2 px-6 rounded-md hover:bg-emerald-700 font-bold shadow-sm transition disabled:bg-gray-200 disabled:text-gray-400 text-xs cursor-pointer"
                          >
                            Sign & Save Laser Session to EHR
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* AI Clinical Diagnostic Scanner (Gemini VLM model integration) */}
                    <div className="border border-[#E2E8F0] rounded-lg p-4 bg-white shadow-sm">
                      <h4 className="font-bold text-[#1E293B] text-xs mb-2 flex items-center gap-1.5 uppercase tracking-wider">
                        <Camera className="w-3.5 h-3.5 text-[#0F172A]" />
                        Gemini AI Dermatological Scanner (CDSS)
                      </h4>
                      <p className="text-[10.5px] text-[#64748B] leading-relaxed mb-3">
                        Upload high-resolution clinical skin lesion photos. The
                        GCloud Vertex CDSS module evaluates morphological
                        structures.
                      </p>

                      <div className="space-y-3">
                        <div className="flex gap-2 text-[10px]">
                          <div className="flex-1 flex flex-col">
                            <span className="font-semibold text-[#64748B]">
                              Duration (metadata)
                            </span>
                            <input
                              type="text"
                              value={lesionDuration}
                              onChange={(e) =>
                                setLesionDuration(e.target.value)
                              }
                              className="bg-white border border-[#E2E8F0] rounded-md p-1.5 mt-1 outline-none text-[#1E293B]"
                            />
                          </div>
                          <div className="flex-1 flex flex-col justify-end">
                            <label className="bg-[#F8FAFC] hover:bg-slate-100 border border-[#E2E8F0] text-[#64748B] font-semibold cursor-pointer text-center p-1.5 rounded-md border-dashed text-xs">
                              Choose Photo File
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                              />
                            </label>
                          </div>
                        </div>

                        {/* Preselected Presets for Fast Testing */}
                        <div className="flex flex-col gap-1 pt-1.5 border-t border-[#E2E8F0]">
                          <span className="text-[9px] text-[#64748B] font-bold uppercase">
                            Or Choose Seeding Sample:
                          </span>
                          <div className="flex gap-1.5">
                            {sampleSkinLesions.map((sample, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={async () => {
                                  const mockBase =
                                    "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQE...";
                                  setSkinImageBase64(mockBase);
                                  triggerAiAnalysis(mockBase);
                                }}
                                className="bg-[#F8FAFC] hover:bg-slate-100 text-[#1E293B] text-[10px] font-semibold px-2 py-1.5 rounded border border-[#E2E8F0] cursor-pointer"
                              >
                                {sample.name}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Image preview state */}
                        {skinImageBase64 && (
                          <div className="bg-[#F8FAFC] p-2.5 rounded-md border border-[#E2E8F0] flex items-center gap-3">
                            <div className="h-10 w-10 bg-slate-100 rounded-md overflow-hidden flex-shrink-0 border border-[#E2E8F0]">
                              <img
                                src="/index.html"
                                referrerPolicy="no-referrer"
                                alt="clinical preview"
                                className="h-full w-full object-cover"
                              />
                            </div>
                            <div className="flex-1 text-[10px]">
                              <span className="text-[#64748B] block">
                                Lesion duration: {lesionDuration}
                              </span>
                              {aiLoading ? (
                                <strong className="text-[#0F172A] block animate-pulse">
                                  Running AI CDSS Model...
                                </strong>
                              ) : isQiAnalyzing ? (
                                <strong className="text-indigo-600 font-extrabold block animate-pulse flex items-center gap-1">
                                  <Sparkles className="w-3 h-3 text-indigo-500 animate-spin" />
                                  QI-Doctor Evaluating...
                                </strong>
                              ) : (
                                <strong className="text-[#64748B] block">
                                  Diagnostic baseline prepared
                                </strong>
                              )}
                            </div>
                            {!aiLoading && !isQiAnalyzing && (
                              <div className="flex gap-1.5 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => triggerAiAnalysis()}
                                  className="bg-[#0F172A] hover:bg-slate-800 text-white font-semibold text-[10px] py-1.5 px-3 rounded-md cursor-pointer transition-colors"
                                >
                                  Analyze (Standard)
                                </button>
                                <button
                                  type="button"
                                  id="qi-doctor-derma-btn"
                                  onClick={() => triggerQiDermatologyAnalysis()}
                                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-[10px] py-1.5 px-3 rounded-md cursor-pointer transition-colors flex items-center gap-1 shadow-sm"
                                >
                                  <Sparkles className="w-3 h-3 text-white shrink-0" />
                                  <span>Qi-Doctor Professor</span>
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* AI Error */}
                        {aiError && (
                          <div className="bg-rose-50 border border-rose-200 p-2 text-[10px] text-rose-700 rounded-md">
                            {aiError}
                          </div>
                        )}

                        {/* Decoded structured response block */}
                        {aiAnalysisResult && (
                          <div className="bg-[#0F172A] text-[#E2E8F0] rounded-md p-4 text-[11px] font-mono space-y-3 max-h-[380px] overflow-y-auto border border-indigo-500/30 shadow-[0_4px_24px_rgba(0,0,0,0.5)]">
                            <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                              <div className="flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                <span className="text-white font-bold uppercase text-[9px]">
                                  GCloud CDSS Derm Scanner Output
                                </span>
                              </div>
                              <span className="text-slate-400 font-bold text-[8.5px] bg-slate-800 px-1.5 py-0.5 rounded border border-slate-700">
                                Quality Valid:{" "}
                                {aiAnalysisResult.imageQualityValid
                                  ? "PASS"
                                  : "FAIL"}
                              </span>
                            </div>

                            <div>
                              <strong className="text-white text-[10px] block mb-0.5">
                                Morphology Analysis:
                              </strong>
                              <p className="text-slate-300 text-[10.5px]">
                                {
                                  aiAnalysisResult.morphologicalAnalysis
                                    ?.primaryLesion
                                }{" "}
                                (Secondary:{" "}
                                {
                                  aiAnalysisResult.morphologicalAnalysis
                                    ?.secondaryChanges
                                }
                                )
                              </p>
                            </div>

                            <div className="space-y-2">
                              <strong className="text-white text-[10px] block border-b border-slate-800 pb-0.5">
                                Differential Diagnoses (Grounded on DermNetNZ):
                              </strong>
                              {aiAnalysisResult.differentialDiagnosis?.map(
                                (d: any, i: number) => (
                                  <div
                                    key={i}
                                    className="bg-slate-900 border border-slate-800 p-2.5 rounded-md space-y-1.5"
                                  >
                                    <div className="flex justify-between items-center font-bold text-white text-[10.5px]">
                                      <span className="text-indigo-300 font-sans tracking-tight">
                                        {d.condition}
                                      </span>
                                      <span className="text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded text-[9px] font-mono">
                                        {d.probability}
                                      </span>
                                    </div>
                                    <p className="text-slate-400 text-[9.5px] font-sans leading-relaxed">
                                      {d.reasoning}
                                    </p>

                                    {d.clinicalEvidence && (
                                      <div className="bg-slate-950/60 p-1.5 rounded border border-slate-800/60 text-[9.2px] text-emerald-400/90 font-sans">
                                        <span className="font-bold text-slate-500 block text-[8px] uppercase tracking-wider mb-0.5">
                                          Standard Diagnostic Marker:
                                        </span>
                                        {d.clinicalEvidence}
                                      </div>
                                    )}

                                    {d.dermnetUrl && (
                                      <div className="pt-1 flex">
                                        <a
                                          href={d.dermnetUrl}
                                          target="_blank"
                                          referrerPolicy="no-referrer"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1 text-[8.5px] font-bold text-emerald-400 hover:text-emerald-300 transition-colors uppercase tracking-wider bg-emerald-500/10 hover:bg-emerald-500/20 px-2 py-0.5 rounded-full border border-emerald-500/30"
                                        >
                                          <span>Verify on DermNet NZ</span>
                                          <ExternalLink className="w-2.5 h-2.5" />
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                ),
                              )}
                            </div>

                            <div className="border-t border-slate-800 pt-2 text-rose-400 text-[9.5px]">
                              <strong className="uppercase text-[8px] tracking-wider text-rose-500">
                                System Warnings / Red Flags:
                              </strong>
                              <p className="mt-0.5 leading-relaxed font-sans text-slate-300 text-[10px]">
                                {aiAnalysisResult.redFlags}
                              </p>
                            </div>

                            {/* TRUSTED GROUNDING EXTERNAL SITE LINKS FOR VERIFIABILITY */}
                            {aiAnalysisResult.trustedGroundingSources &&
                              aiAnalysisResult.trustedGroundingSources.length >
                                0 && (
                                <div className="border-t border-slate-800 pt-2 space-y-1.5">
                                  <strong className="text-white text-[9px] uppercase tracking-wider block">
                                    Grounded Registry Sources (Zero
                                    Hallucination):
                                  </strong>
                                  <div className="flex flex-col gap-1">
                                    {aiAnalysisResult.trustedGroundingSources.map(
                                      (src: any, idx: number) => (
                                        <a
                                          key={idx}
                                          href={src.url}
                                          target="_blank"
                                          referrerPolicy="no-referrer"
                                          rel="noopener noreferrer"
                                          className="bg-slate-900/80 hover:bg-slate-900 border border-slate-800 p-1.5 rounded flex items-center justify-between group transition-all"
                                        >
                                          <div className="flex items-center gap-1.5 overflow-hidden">
                                            <span className="w-1 h-1 rounded-full bg-indigo-500"></span>
                                            <span className="text-slate-300 text-[9px] font-sans truncate group-hover:text-indigo-300">
                                              {src.title}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-1 text-slate-500 group-hover:text-indigo-400">
                                            <span className="text-[8px] font-mono font-bold uppercase">
                                              Open
                                            </span>
                                            <ExternalLink className="w-2.5 h-2.5" />
                                          </div>
                                        </a>
                                      ),
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>
                        )}

                        {/* Qi-Doctor Dermatology Specialty Evaluation Card */}
                        {qiAnalysisResult && (
                          <div className="bg-white border border-indigo-200 rounded-lg p-4 text-[10.5px] space-y-3.5 shadow-md">
                            <div className="flex items-center justify-between border-b border-indigo-50 pb-2 flex-wrap gap-2 text-left">
                              <div className="flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></span>
                                <span className="text-indigo-950 font-black uppercase text-[9px] tracking-wider flex items-center gap-1">
                                  <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                                  Qi-Doctor Specialty Professor Dermatology
                                  Clinical Output
                                </span>
                              </div>
                              <span className="text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded text-[8px] font-bold border border-emerald-100">
                                Verified Medical Sources Only (Zero
                                Hallucination)
                              </span>
                            </div>

                            {qiAnalysisResult.hasSuggestion === false ? (
                              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-amber-800 text-[10px] font-bold text-left">
                                {qiAnalysisResult.message || "I don't know."}
                              </div>
                            ) : (
                              <>
                                <div className="bg-slate-50 border border-slate-100 p-2.5 rounded text-[10px] font-sans leading-relaxed text-slate-700 text-left">
                                  <strong className="text-slate-950 block mb-0.5 uppercase tracking-wide text-[8.5px]">
                                    Specialist Professor Clinical Commentary:
                                  </strong>
                                  <div className="whitespace-pre-line font-semibold leading-relaxed text-[10.5px]">
                                    {qiAnalysisResult.professorAnalysis}
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 text-left">
                                  <div className="bg-emerald-50/40 border border-emerald-150 p-2.5 rounded space-y-1">
                                    <strong className="text-emerald-900 text-[8.5px] uppercase tracking-wider block">
                                      Most Accurate Suspect:
                                    </strong>
                                    <span className="text-emerald-950 font-extrabold text-[11px] block">
                                      {qiAnalysisResult.mostAccurateDiagnosis}
                                    </span>
                                  </div>
                                  <div className="bg-rose-50/40 border border-rose-150 p-2.5 rounded space-y-1">
                                    <strong className="text-rose-900 text-[8.5px] uppercase tracking-wider block font-black">
                                      Urgent Investigations Required:
                                    </strong>
                                    <ul className="list-disc list-inside text-[9.5px] text-slate-800 font-bold space-y-0.5">
                                      {qiAnalysisResult.immediateInvestigations &&
                                      qiAnalysisResult.immediateInvestigations
                                        .length > 0 ? (
                                        qiAnalysisResult.immediateInvestigations.map(
                                          (inv: string, i: number) => (
                                            <li key={i}>{inv}</li>
                                          ),
                                        )
                                      ) : (
                                        <li className="text-slate-400 font-normal">
                                          None specified.
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                </div>

                                <div className="space-y-2 text-left">
                                  <strong className="text-slate-400 text-[8px] uppercase tracking-wider block font-bold">
                                    Clinical Specialty Differentials (Grounded
                                    Reference):
                                  </strong>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {qiAnalysisResult.differentialDiagnosis?.map(
                                      (d: any, i: number) => (
                                        <div
                                          key={i}
                                          className="bg-slate-50/60 border border-slate-200 p-2.5 rounded space-y-1.5 text-[10px]"
                                        >
                                          <div className="flex justify-between items-center font-bold">
                                            <span className="text-indigo-950 font-black">
                                              {d.disorder || d.condition}
                                            </span>
                                            <span className="text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded text-[9px] font-mono shrink-0">
                                              {d.probability}
                                            </span>
                                          </div>
                                          <p className="text-slate-600 font-semibold text-[9.5px] leading-relaxed">
                                            {d.reasoning}
                                          </p>
                                          {d.sourceUrl && (
                                            <div className="pt-0.5 flex">
                                              <a
                                                href={d.sourceUrl}
                                                target="_blank"
                                                referrerPolicy="no-referrer"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 text-[8.5px] font-bold text-indigo-600 hover:text-indigo-800 transition-colors uppercase tracking-wider bg-indigo-50 px-1.5 py-0.5 rounded"
                                              >
                                                <BookOpen className="w-3 h-3 text-indigo-500 shrink-0" />
                                                <span>
                                                  Check:{" "}
                                                  {d.sourceTitle ||
                                                    "Official Reference Manual"}
                                                </span>
                                              </a>
                                            </div>
                                          )}
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>

                                {qiAnalysisResult.sources &&
                                  qiAnalysisResult.sources.length > 0 && (
                                    <div className="border-t border-slate-100 pt-2.5 flex flex-wrap items-center gap-2 text-left">
                                      <strong className="text-slate-400 text-[8px] uppercase tracking-wider">
                                        Citations:
                                      </strong>
                                      {qiAnalysisResult.sources.map(
                                        (src: any, i: number) => (
                                          <a
                                            key={i}
                                            href={src.url}
                                            target="_blank"
                                            referrerPolicy="no-referrer"
                                            rel="noopener noreferrer"
                                            className="text-[9px] text-slate-500 hover:text-slate-800 transition-colors bg-slate-100 duration-100 hover:bg-slate-200 px-2 py-0.5 rounded flex items-center gap-1 font-semibold"
                                          >
                                            <ExternalLink className="w-2.5 h-2.5 text-slate-400" />
                                            <span>{src.title}</span>
                                          </a>
                                        ),
                                      )}
                                    </div>
                                  )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      disabled={isSavingEhr || isRecordLocked}
                      onClick={() => handleSaveEhrReport()}
                      className={`mt-3 font-semibold text-xs py-2 px-4 rounded-md shadow-sm transition-all duration-300 flex items-center gap-2 select-none cursor-pointer ${
                        justSaved
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-[0_0_12px_rgba(16,185,129,0.3)] scale-[1.02]"
                          : isSavingEhr
                            ? "bg-slate-700 text-slate-200 cursor-not-allowed"
                            : "bg-[#0F172A] hover:bg-slate-800 text-white"
                      }`}
                    >
                      {isSavingEhr ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 animate-spin text-white" />
                          <span>Saving Dermatology Record...</span>
                        </>
                      ) : justSaved ? (
                        <>
                          <CheckCircle className="w-3.5 h-3.5 text-white animate-bounce" />
                          <span>✓ Saved!</span>
                        </>
                      ) : (
                        "Submit Dermatology Record"
                      )}
                    </button>
                  </div>
                )}

                {/* 3. MINOR SURGERY LOG TAB */}
                {tab === "surgery" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-[#1E293B]">
                        Minor Surgical Operations Ledger
                      </h3>
                      <span className="text-[9px] bg-amber-50 text-amber-800 border-amber-200 border px-2 py-0.5 rounded-full font-bold uppercase">
                        Surgical consent logged
                      </span>
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-2">
                        Surgery Documentation (Suture specifics, local
                        anesthetic dosage lidocaine %, cautery used, excision
                        margins)
                      </label>
                      <textarea
                        rows={8}
                        value={minorSurgeryNotes}
                        onChange={(e) => setMinorSurgeryNotes(e.target.value)}
                        placeholder="Document aseptic scrub protocols, injection details, closure sizes, postoperative tissue viability notes..."
                        className="w-full bg-white border border-[#E2E8F0] rounded-md p-3 font-medium text-xs outline-none focus:border-[#0F172A] duration-150"
                      />
                      <button
                        disabled={isSavingEhr || isRecordLocked}
                        onClick={() => handleSaveEhrReport()}
                        className={`mt-3 font-semibold text-xs py-2 px-4 rounded-md shadow-sm transition-all duration-300 flex items-center gap-2 select-none cursor-pointer ${
                          justSaved
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-[0_0_12px_rgba(16,185,129,0.3)] scale-[1.02]"
                            : isSavingEhr
                              ? "bg-slate-700 text-slate-200 cursor-not-allowed"
                              : "bg-[#0F172A] hover:bg-slate-800 text-white"
                        }`}
                      >
                        {isSavingEhr ? (
                          <>
                            <RefreshCw className="w-3.5 h-3.5 animate-spin text-white" />
                            <span>Saving Surgery Record...</span>
                          </>
                        ) : justSaved ? (
                          <>
                            <CheckCircle className="w-3.5 h-3.5 text-white animate-bounce" />
                            <span>✓ Saved!</span>
                          </>
                        ) : (
                          "Submit Surgery Record"
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* 4. NURSING ORDERS TAB */}
                {tab === "nursing" && (
                  <div className="space-y-6">
                    <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 rounded-md space-y-3">
                      <h4 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider flex items-center gap-1.5">
                        <Activity className="w-4 h-4 text-emerald-500" /> Nurse
                        Orders
                      </h4>
                      <div className="relative">
                        <textarea
                          rows={2}
                          value={cpoeNurseInput}
                          onChange={(e) => setCpoeNurseInput(e.target.value)}
                          placeholder="Dressing, IVF, Injection..."
                          className="w-full bg-white border border-[#E2E8F0] rounded-md p-2 font-medium text-xs outline-none focus:border-[#0F172A]"
                        />
                        {cpoeNurseInput.length >= 3 && (
                          <div className="absolute z-10 w-full bg-white border border-slate-200 shadow-lg rounded-md mt-1 mb-2 max-h-40 overflow-y-auto">
                            {clinicalCatalog["NURSING"]
                              .filter((item) =>
                                item
                                  .toLowerCase()
                                  .includes(cpoeNurseInput.toLowerCase()),
                              )
                              .map((item, idx) => (
                                <div
                                  key={idx}
                                  className="px-2 py-1.5 text-xs hover:bg-slate-50 cursor-pointer border-b last:border-0 border-slate-100 font-medium text-slate-700"
                                  onClick={() => setCpoeNurseInput(item)}
                                >
                                  {item}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          handleDispatchSpecificOrder(
                            "NURSING",
                            cpoeNurseInput,
                            () => setCpoeNurseInput(""),
                          )
                        }
                        disabled={!cpoeNurseInput}
                        className="w-full md:w-auto h-8 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-xs px-6 rounded-md flex items-center justify-center gap-1.5 transition select-none cursor-pointer"
                      >
                        <Send className="w-3.5 h-3.5" /> Dispatch to Nurse
                      </button>
                    </div>
                  </div>
                )}

                {/* 5. LAB / X-RAY ORDERS TAB */}
                {tab === "lab" && (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                    {/* Left Column: Form and Draft */}
                    <div className="lg:col-span-6 space-y-6">
                      <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 rounded-md space-y-3">
                        <h4 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider flex items-center gap-1.5">
                          <Activity className="w-4 h-4 text-indigo-500" />{" "}
                          Investigations
                        </h4>
                        <select
                          value={cpoeInvDept}
                          onChange={(e) =>
                            setCpoeInvDept(e.target.value as any)
                          }
                          className="w-full bg-white border border-[#E2E8F0] text-xs text-[#1E293B] font-semibold px-2 py-1.5 rounded-md outline-none"
                        >
                          <option value="LAB">Laboratory</option>
                          <option value="X_RAY">Radiology & Imaging</option>
                        </select>
                        <div className="relative">
                          <textarea
                            rows={2}
                            value={cpoeInvInput}
                            onChange={(e) => setCpoeInvInput(e.target.value)}
                            placeholder="Search LAB / XRAY..."
                            className="w-full bg-white border border-[#E2E8F0] rounded-md p-2 font-medium text-xs outline-none focus:border-[#0F172A]"
                          />
                          {cpoeInvInput.length >= 3 &&
                            !clinicalCatalog[cpoeInvDept].includes(
                              cpoeInvInput,
                            ) && (
                              <div className="absolute z-10 w-full bg-white border border-slate-200 shadow-lg rounded-md mt-1 mb-2 max-h-40 overflow-y-auto font-sans">
                                {clinicalCatalog[cpoeInvDept]
                                  .filter((item) =>
                                    item
                                      .toLowerCase()
                                      .includes(cpoeInvInput.toLowerCase()),
                                  )
                                  .map((item, idx) => (
                                    <div
                                      key={idx}
                                      onClick={() => setCpoeInvInput(item)}
                                    >
                                      {item}
                                    </div>
                                  ))}
                              </div>
                            )}
                        </div>
                        {/* Smart Lab Suggestions */}
                        {(() => {
                          const searchLower = cpoeInvInput.toLowerCase();
                          const matchedDisease = cpoeInvInput.length >= 3 && cpoeInvDept === "LAB" 
                            ? Object.entries(smartLabSuggestions).find(([disease]) => searchLower.includes(disease) || disease.includes(searchLower)) 
                            : null;
                          
                          if (!matchedDisease) return null;
                          
                          const [diseaseName, tests] = matchedDisease;
                          
                          return (
                            <div className="mt-2 flex flex-col gap-2 p-2 bg-indigo-50/50 rounded border border-indigo-100/50">
                              <div className="flex flex-wrap gap-2 items-center justify-between">
                                <span className="text-[11px] font-bold text-indigo-800 uppercase flex items-center gap-1">
                                  <span>🧬</span> Suggested for {diseaseName}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setInvestigations((prev) => [
                                      ...prev,
                                      ...tests.map(test => ({ dept: "LAB" as const, name: test }))
                                    ]);
                                    setCpoeInvInput("");
                                  }}
                                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-[11px] py-1.5 px-4 rounded-md shadow-sm transition-all flex items-center gap-1.5"
                                >
                                  + Add All ({tests.length} Tests)
                                </button>
                              </div>
                              <div className="text-[10px] text-indigo-600/70 font-medium leading-relaxed">
                                {tests.join(", ")}
                              </div>
                            </div>
                          );
                        })()}
                        <button
                          type="button"
                          onClick={() => {
                            setInvestigations((prev) => [...prev, { dept: cpoeInvDept, name: cpoeInvInput.trim() }]);
                            setCpoeInvInput("");
                          }}
                          disabled={!cpoeInvInput}
                          className="w-full md:w-auto h-8 bg-slate-800 hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-xs px-4 rounded-md flex items-center justify-center gap-1.5 transition select-none cursor-pointer mt-2"
                        >
                          + Add to Requests
                        </button>
                      </div>

                      {/* Current Batch Preview */}
                      <div className="bg-white border text-sm border-slate-200 rounded-md p-4">
                        <h4 className="font-bold text-slate-700 text-xs mb-3 flex items-center justify-between">
                          Draft Investigations ({investigations.length} items)
                          {investigations.length > 0 && (
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  // Group by department
                                  const labs = investigations.filter(
                                    (i) => i.dept === "LAB",
                                  );
                                  const xrays = investigations.filter(
                                    (i) => i.dept === "X_RAY",
                                  );

                                  if (labs.length > 0) {
                                    handleDispatchSpecificOrder(
                                      "LAB",
                                      labs
                                        .map(
                                          (l, idx) => `${idx + 1}. ${l.name}`,
                                        )
                                        .join("\n"),
                                      () => {},
                                    );
                                  }
                                  if (xrays.length > 0) {
                                    handleDispatchSpecificOrder(
                                      "X_RAY",
                                      xrays
                                        .map(
                                          (x, idx) => `${idx + 1}. ${x.name}`,
                                        )
                                        .join("\n"),
                                      () => {},
                                    );
                                  }
                                  triggerSuccessToast(
                                    "All investigations submitted successfully.",
                                  );
                                  setInvestigations([]);
                                }}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded flex items-center gap-1 text-[10px] transition shadow-sm"
                              >
                                <Send className="w-3 h-3" /> Submit ALL to
                                Lab/X-Ray
                              </button>
                              {/* Standalone Lab Printing Action */}
                              <button
                                type="button"
                                onClick={() => {
                                  try {
                                    setPrintDocType("INVESTIGATION");
                                    setSelectedOrderToPrint(null);
                                    setPrintOpen(true);
                                  } catch (err: any) {
                                    console.error(
                                      "[Laboratory Print Error] ",
                                      err,
                                    );
                                  }
                                }}
                                className="bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 px-3 py-1.5 rounded flex items-center gap-1 text-[10px] transition shadow-sm font-bold cursor-pointer"
                              >
                                <Printer className="w-3 h-3" /> Print Lab
                                Requisition
                              </button>
                            </div>
                          )}
                        </h4>

                        {investigations.length === 0 ? (
                          <p className="text-slate-400 italic text-[10px]">
                            No investigations added to the draft yet.
                          </p>
                        ) : (
                          <ul className="space-y-2">
                            {investigations.map((inv, i) => (
                              <li
                                key={i}
                                className="flex items-center justify-between bg-slate-50 border border-slate-100 p-2 rounded"
                              >
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-widest ${
                                      inv.dept === "LAB"
                                        ? "bg-purple-100 text-purple-700"
                                        : "bg-sky-100 text-sky-700"
                                    }`}
                                  >
                                    {inv.dept}
                                  </span>
                                  <span className="font-medium text-slate-800 text-xs">
                                    {inv.name}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setInvestigations(
                                      investigations.filter(
                                        (_, idx) => idx !== i,
                                      ),
                                    )
                                  }
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash className="w-3.5 h-3.5" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        {investigations.length > 0 && selectedPatient && (
                          <div className="mt-2 text-right flex items-center justify-end gap-2">
                            <SendWhatsAppButton
                              patientPhone={selectedPatient.phone}
                              messageContent={`Investigation Request for ${selectedPatient.name}:\n${investigations.map((inv) => `- ${inv.name} (${inv.dept})`).join("\n")}`}
                              label="Investigations"
                            />
                            <SaveToDriveButton
                              fileName={`Investigation-${selectedPatient.name}-${new Date().toISOString()}.txt`}
                              content={`Investigation Request for ${selectedPatient.name}:\n${investigations.map((inv) => `- ${inv.name} (${inv.dept})`).join("\n")}`}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Column: Trusted Diagnostics Directory */}
                    <div className="lg:col-span-6 bg-white border border-slate-200 shadow-sm rounded-md p-4 flex flex-col h-[525px]">
                      <div className="border-b border-indigo-50 pb-3 mb-3">
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-5 h-5 text-indigo-600" />
                          <div>
                            <h4 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider">
                              Trusted Diagnostics Reference Directory
                            </h4>
                            <p className="text-[10px] font-medium text-slate-400">
                              Accredited Clinical Assay & Imaging Protocol
                              Library
                            </p>
                          </div>
                        </div>

                        {/* Search Database */}
                        <div className="mt-3 relative">
                          <input
                            type="text"
                            value={invSearchText}
                            onChange={(e) => setInvSearchText(e.target.value)}
                            placeholder="Search clinical labs (e.g. CBC, CMP, LFTs, TSH) & reference databases..."
                            className="w-full bg-white border border-[#E2E8F0] rounded-md p-2 font-medium text-xs outline-none focus:border-[#0F172A] transition shadow-sm"
                          />
                          {invSearchText.length >= 3 && (
                            <div className="absolute z-20 w-full bg-white border border-slate-200 shadow-xl rounded-md mt-1 max-h-64 overflow-y-auto">
                              {(() => {
                                const matched = universalSearchData.filter(
                                  (item) =>
                                    item.name
                                      .toLowerCase()
                                      .includes(invSearchText.toLowerCase()) ||
                                    item.subText
                                      .toLowerCase()
                                      .includes(invSearchText.toLowerCase()),
                                );
                                if (matched.length === 0) {
                                  return (
                                    <div className="p-3 text-xs text-slate-400 text-center">
                                      No results found in clinical directory.
                                    </div>
                                  );
                                }
                                return matched.slice(0, 50).map((item, idx) => (
                                  <div
                                    key={idx}
                                    className="px-3 py-2 text-xs hover:bg-slate-50 cursor-pointer border-b last:border-0 border-slate-100 flex justify-between items-center text-right"
                                    onClick={() => {
                                      handleSelectUniversalSuggestion(item);
                                      setInvSearchText("");
                                    }}
                                  >
                                    <div className="flex items-center gap-1.5">
                                      <span
                                        className={`text-[8px] uppercase font-bold px-1.5 py-0.5 rounded ${
                                          item.type === "DIAGNOSIS"
                                            ? "bg-orange-50 text-orange-600"
                                            : item.type === "INVESTIGATION"
                                              ? "bg-teal-50 text-teal-600"
                                              : "bg-purple-50 text-purple-600"
                                        }`}
                                      >
                                        {item.type}
                                      </span>
                                      <span className="font-semibold text-slate-700">
                                        {item.name}
                                      </span>
                                    </div>
                                    <span className="text-[10px] font-mono text-slate-400">
                                      {item.subText}
                                    </span>
                                  </div>
                                ));
                              })()}
                            </div>
                          )}
                        </div>

                        {/* Category Selector Tabs */}
                        <div className="flex gap-1 overflow-x-auto pr-1 mt-2 pb-1 scrollbar-thin scrollbar-thumb-slate-200">
                          {[
                            "All",
                            "LAB/Hematology",
                            "LAB/Biochemistry",
                            "LAB/Endocrinology",
                            "LAB/Immunology",
                            "LAB/Microbiology",
                            "Radiology",
                          ].map((cat) => {
                            let display = cat;
                            if (cat === "LAB/Hematology")
                              display = "Hematology";
                            else if (cat === "LAB/Biochemistry")
                              display = "Biochemistry";
                            else if (cat === "LAB/Endocrinology")
                              display = "Endocrinology";
                            else if (cat === "LAB/Immunology")
                              display = "Immunology";
                            else if (cat === "LAB/Microbiology")
                              display = "Microbiology";
                            else if (cat === "Radiology")
                              display = "Imaging & Scans";

                            return (
                              <button
                                key={cat}
                                type="button"
                                onClick={() => setInvSelectedCategory(cat)}
                                className={`text-[9.5px] font-semibold px-2.5 py-1 rounded transition select-none flex-shrink-0 cursor-pointer ${
                                  invSelectedCategory === cat
                                    ? "bg-indigo-600 text-white font-bold"
                                    : "bg-slate-100 hover:bg-slate-200 text-slate-600"
                                }`}
                              >
                                {display}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Filtered Investigations Container */}
                      <div className="flex-1 overflow-y-auto pr-1 space-y-2.5 min-h-0">
                        {(() => {
                          const queried = fullInvestigationCatalog.filter(
                            (inv) => {
                              const matchCat = (() => {
                                if (invSelectedCategory === "All") return true;
                                if (invSelectedCategory === "LAB/Hematology")
                                  return (
                                    inv.category === "Hematology & Coagulation"
                                  );
                                if (invSelectedCategory === "LAB/Biochemistry")
                                  return (
                                    inv.category ===
                                    "Biochemistry & Organ Panels"
                                  );
                                if (invSelectedCategory === "LAB/Endocrinology")
                                  return (
                                    inv.category === "Endocrinology & Hormones"
                                  );
                                if (invSelectedCategory === "LAB/Immunology")
                                  return (
                                    inv.category === "Immunology & Serology"
                                  );
                                if (invSelectedCategory === "LAB/Microbiology")
                                  return (
                                    inv.category === "Microbiology & Biofluids"
                                  );
                                if (invSelectedCategory === "Radiology")
                                  return (
                                    inv.category ===
                                    "Imaging & Plain Radiography"
                                  );
                                return true;
                              })();
                              const matchSearch =
                                !invSearchText ||
                                inv.name
                                  .toLowerCase()
                                  .includes(invSearchText.toLowerCase()) ||
                                inv.category
                                  .toLowerCase()
                                  .includes(invSearchText.toLowerCase()) ||
                                inv.indication
                                  .toLowerCase()
                                  .includes(invSearchText.toLowerCase()) ||
                                inv.clinicalUtility
                                  .toLowerCase()
                                  .includes(invSearchText.toLowerCase());
                              return matchCat && matchSearch;
                            },
                          );

                          if (queried.length === 0) {
                            return (
                              <div className="flex flex-col items-center justify-center h-full text-slate-400 text-xs italic py-8 font-sans">
                                <Info className="w-8 h-8 text-slate-300 mb-2" />
                                No investigation protocols match this query.
                              </div>
                            );
                          }

                          return queried.map((med, idx) => {
                            const isSelected =
                              cpoeInvInput.toLowerCase() ===
                              med.name.toLowerCase();
                            return (
                              <div
                                key={idx}
                                className={`border rounded p-3 transition flex flex-col gap-1.5 ${
                                  isSelected
                                    ? "bg-indigo-50/50 border-indigo-400"
                                    : "bg-slate-50/40 hover:bg-slate-50 border-slate-150"
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center xl:gap-2 gap-1 flex-wrap">
                                    <span className="font-serif font-extrabold text-slate-800 text-xs">
                                      {med.name}
                                    </span>
                                    <span
                                      className={`text-[8.2px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                                        med.dept === "LAB"
                                          ? "bg-purple-100 text-purple-700"
                                          : "bg-sky-100 text-sky-700"
                                      }`}
                                    >
                                      {med.dept}
                                    </span>
                                    {med.dept === "LAB" &&
                                      med.referenceRange && (
                                        <span className="text-[7.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200/60 leading-none">
                                          ✓ Ref DB Loaded
                                        </span>
                                      )}
                                  </div>
                                </div>

                                <div className="text-[11px] text-slate-600 leading-normal">
                                  <span className="font-semibold text-slate-500 uppercase text-[9px]">
                                    Indication:{" "}
                                  </span>
                                  {med.indication}
                                </div>

                                <div className="text-[10.5px] bg-white p-2 rounded border border-slate-100 space-y-1">
                                  {med.dept === "LAB" && (
                                    <div className="flex items-center gap-1">
                                      <span className="text-[8px] font-bold uppercase tracking-wider text-slate-400 shrink-0">
                                        Specimen:
                                      </span>
                                      <span className="font-mono text-slate-700 font-semibold truncate text-[9.5px]">
                                        {med.specimen}
                                      </span>
                                    </div>
                                  )}
                                  <div className="text-slate-500 leading-tight">
                                    <span className="text-[8px] font-bold uppercase tracking-wider text-slate-400 block mb-0.5">
                                      Clinical Utility:
                                    </span>
                                    <p className="text-[10px] text-slate-600 font-medium leading-relaxed">
                                      {med.clinicalUtility}
                                    </p>
                                  </div>

                                  {/* CLINICAL SECURITY: Interactive Lab Reference Database Integration */}
                                  {med.dept === "LAB" && med.referenceRange && (
                                    <div
                                      className="mt-2.5 pt-2.5 border-t border-dashed border-slate-150 space-y-2 bg-slate-50/70 p-2.5 rounded text-[10px]"
                                      dir="ltr"
                                    >
                                      <div className="flex flex-col gap-0.5">
                                        <span className="text-[8px] font-extrabold uppercase tracking-wider text-indigo-600">
                                          Normal Reference Ranges:
                                        </span>
                                        <span className="font-mono text-slate-900 font-extrabold leading-normal text-[10px]">
                                          {med.referenceRange}
                                        </span>
                                      </div>
                                      {med.criticalValue && (
                                        <div className="flex flex-col gap-0.5">
                                          <span className="text-[8px] font-extrabold uppercase tracking-wider text-rose-600">
                                            Critical Alert Limits:
                                          </span>
                                          <span className="font-mono text-rose-700 font-bold leading-normal text-[9.5px]">
                                            {med.criticalValue}
                                          </span>
                                        </div>
                                      )}
                                      {med.clinicalSignificance && (
                                        <div className="flex flex-col gap-0.5">
                                          <span className="text-[8px] font-extrabold uppercase tracking-wider text-slate-500">
                                            Diagnostic Value Interpretation:
                                          </span>
                                          <p className="text-slate-600 font-medium leading-relaxed text-[9.5px]">
                                            {med.clinicalSignificance}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>

                                <div className="flex gap-2 mt-0.5 pt-1.5 justify-end border-t border-slate-100">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // pre-populate form
                                      setCpoeInvInput(med.name);
                                      setCpoeInvDept(med.dept);
                                      triggerSuccessToast(
                                        `Pre-populated details for ${med.name}.`,
                                      );
                                    }}
                                    className="text-[9.5px] font-bold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 px-2.5 py-1 rounded transition flex items-center gap-1 cursor-pointer"
                                  >
                                    <Edit className="w-3" /> Pre-fill Form
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setInvestigations((prev) => [
                                        ...prev,
                                        {
                                          name: med.name,
                                          dept: med.dept,
                                        },
                                      ]);
                                      triggerSuccessToast(
                                        `Added ${med.name} to active requests.`,
                                      );
                                    }}
                                    className="text-[9.5px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 px-2.5 py-1 rounded shadow-sm transition flex items-center gap-1 cursor-pointer"
                                  >
                                    <Plus className="w-3 h-3" /> Quick Add
                                  </button>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {/* 6. PHARMACY RX TAB */}
                {tab === "pharmacy" && (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                    {/* Left Column: Input and Draft */}
                    <div className="lg:col-span-6 space-y-6">
                      <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 rounded-md space-y-3">
                        <h4 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider flex items-center gap-1.5">
                          <Activity className="w-4 h-4 text-purple-500" /> e-Rx
                          Prescription
                        </h4>

                        <div className="relative">
                          <div className="flex justify-between items-center">
                            <label className="block text-[10px] font-semibold text-slate-500 uppercase">
                              Drug Name (BNF Catalog)
                            </label>
                            {bnfCatalog.find(
                              (d) =>
                                (d.name || "").toLowerCase() ===
                                cpoeDrugInput.toLowerCase(),
                            ) && (
                              <button
                                type="button"
                                onClick={() =>
                                  setShowDrugInfoModal(
                                    bnfCatalog.find(
                                      (d) =>
                                        (d.name || "").toLowerCase() ===
                                        cpoeDrugInput.toLowerCase(),
                                    )!,
                                  )
                                }
                                className="text-[9px] bg-sky-100 hover:bg-sky-200 text-sky-700 px-1.5 py-0.5 rounded border border-sky-300 font-bold uppercase transition flex items-center gap-1"
                              >
                                <Info className="w-3 h-3" /> Info
                              </button>
                            )}
                          </div>
                          <input
                            type="text"
                            value={cpoeDrugInput}
                            onChange={(e) => setCpoeDrugInput(e.target.value)}
                            onFocus={() => setIsDrugInputFocused(true)}
                            onBlur={() =>
                              setTimeout(
                                () => setIsDrugInputFocused(false),
                                200,
                              )
                            }
                            placeholder="Search medicine..."
                            className="w-full bg-white border border-[#E2E8F0] rounded-md p-2 font-medium text-xs outline-none focus:border-[#0F172A] mt-1"
                          />
                          {isDrugInputFocused &&
                            !bnfCatalog.some((d) => d.name === cpoeDrugInput) &&
                            cpoeDrugInput.length > 0 && (
                              <div className="absolute z-10 w-full bg-white border border-slate-200 shadow-lg rounded-md mt-1 mb-2 max-h-52 flex flex-col">
                                <div className="overflow-y-auto flex-1">
                                  {(() => {
                                    const inputLower = cpoeDrugInput.toLowerCase();
                                    
                                    const matchedFavs = favoriteDiagnoses.filter(fav => 
                                      fav.type === "MEDICATION" && fav.original &&
                                      (fav.name.toLowerCase().includes(inputLower) ||
                                      (fav.subText && fav.subText.toLowerCase().includes(inputLower)))
                                    ).map(fav => ({ ...fav.original, isFav: true }));

                                    const matchedCatalog = bnfCatalog.filter((item) => {
                                      const nameMatch = (item.name || "").toLowerCase().includes(inputLower);
                                      const indMatch = item.indication ? item.indication.toLowerCase().includes(inputLower) : false;
                                      const tradeNames = iraqiTradeNamesMap[item.name] || [];
                                      const tradeMatch = tradeNames.some((tn) => tn && tn.toLowerCase().includes(inputLower));
                                      return (nameMatch || indMatch || tradeMatch) && !matchedFavs.some(fav => fav.name === item.name);
                                    }).map(item => ({ ...item, isFav: false }));

                                    const matchedMeds = [...matchedFavs, ...matchedCatalog];

                                    return matchedMeds.map((item, idx) => {
                                      const tradeNames = iraqiTradeNamesMap[item.name] || [];
                                      return (
                                        <div
                                          key={"bnf-" + idx}
                                          className={`px-3 py-2 text-xs hover:bg-slate-50 cursor-pointer border-b last:border-0 border-slate-100 flex flex-col ${item.isFav ? 'bg-amber-50/30' : ''}`}
                                          onClick={() => {
                                            setCpoeDrugInput(item.name);
                                            setCpoeDrugDose(item.defaultDose);
                                            setCpoeDrugFreq(item.defaultFreq);
                                            setCpoeDrugDuration(item.defaultDuration);
                                          }}
                                        >
                                          <div className="flex justify-between items-center">
                                            <span className="font-bold text-slate-800 flex items-center gap-1.5">
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const uItem = { type: "MEDICATION", name: item.name, subText: `${item.category || "Medication"} - ${item.defaultDose || ""} ${item.defaultFreq || ""}`, original: item };
                                                  toggleFavoriteDiagnosis(uItem);
                                                }}
                                                className="text-slate-300 hover:text-amber-400 transition"
                                                title="Toggle Favorite"
                                              >
                                                <Star className={`w-3.5 h-3.5 ${item.isFav ? "fill-amber-400 text-amber-400" : ""}`} />
                                              </button>
                                              {item.name}
                                              <span className="text-[9px] font-normal text-slate-400 border border-slate-200 px-1 rounded bg-slate-50 ml-1">
                                                Local Index
                                              </span>
                                            </span>
                                          </div>
                                          {tradeNames.length > 0 && (
                                            <span
                                              className="text-[9.5px] text-slate-500 font-medium italic mt-0.5 ml-5"
                                              title="Available Trade Names in Iraq"
                                            >
                                              Trade names: {tradeNames.join(", ")}
                                            </span>
                                          )}
                                        </div>
                                      );
                                    });
                                  })()}



                                  {bnfCatalog.filter((item) => {
                                    const inputLower =
                                      cpoeDrugInput.toLowerCase();
                                    const nameMatch = item.name
                                      .toLowerCase()
                                      .includes(inputLower);
                                    const tradeNames =
                                      iraqiTradeNamesMap[item.name] || [];
                                    const tradeMatch = tradeNames.some((tn) =>
                                      tn && tn.toLowerCase().includes(inputLower),
                                    );
                                    return nameMatch || tradeMatch;
                                  }).length === 0 && (
                                      <div className="p-3 text-center text-slate-400 text-xs italic">
                                        No matching medicine found.
                                      </div>
                                    )}
                                </div>

                              </div>
                            )}
                        </div>

                        {(() => {
                          const matchedMed = bnfCatalog.find(
                            (d) =>
                              d.name.toLowerCase() ===
                              cpoeDrugInput.toLowerCase(),
                          );
                          const drugCategory = matchedMed?.category;

                          const isTopicalMed =
                            drugCategory === "Dermatology" ||
                            cpoeDrugInput.toLowerCase().includes("cream") ||
                            cpoeDrugInput.toLowerCase().includes("ointment") ||
                            cpoeDrugInput.toLowerCase().includes("gel") ||
                            cpoeDrugInput.toLowerCase().includes("lotion") ||
                            cpoeDrugInput.toLowerCase().includes("spray") ||
                            cpoeDrugInput.toLowerCase().includes("shampoo") ||
                            cpoeDrugInput.toLowerCase().includes("emulsion") ||
                            cpoeDrugInput.toLowerCase().includes("baume");

                          const isEyeMed =
                            drugCategory === "Ophthalmology" ||
                            cpoeDrugInput.toLowerCase().includes("eye drops") ||
                            cpoeDrugInput.toLowerCase().includes("eye drop") ||
                            cpoeDrugInput
                              .toLowerCase()
                              .includes("eye ointment") ||
                            cpoeDrugInput.toLowerCase().includes("ophthalmic");

                          const isEntMed =
                            drugCategory === "ENT Specialists" ||
                            cpoeDrugInput.toLowerCase().includes("nasal") ||
                            cpoeDrugInput.toLowerCase().includes("ear drops") ||
                            cpoeDrugInput.toLowerCase().includes("ear drop") ||
                            cpoeDrugInput.toLowerCase().includes("otic") ||
                            cpoeDrugInput
                              .toLowerCase()
                              .includes("xylometazoline") ||
                            cpoeDrugInput
                              .toLowerCase()
                              .includes("fluticasone") ||
                            cpoeDrugInput.toLowerCase().includes("mometasone");

                          const quickDoseOptions = [
                            "15g Tube",
                            "30g Tube",
                            "50g Tube",
                            "100g Tube",
                            "125g Tub",
                            "150g Tube",
                            "500g Tub",
                            "40ml Tube",
                            "60ml Spray",
                            "100ml Bottle",
                            "120ml Bottle",
                            "150ml Bottle",
                            "200ml Bottle",
                          ];

                          const quickFreqOptions = [
                            "Apply thin layer to affected area BD (Twice daily)",
                            "Apply thin layer to affected area OD (Once daily) at night",
                            "Apply liberally as a moisturizer frequently (4-6 times daily)",
                            "Wash scalp and leave for 3-5 mins, then rinse",
                            "Apply to the entire body from neck down, wash after 8-12 hours",
                            "Spray onto the affected areas of the scalp twice daily",
                            "Apply in thick layers 2-4 times daily and massage gently",
                          ];

                          const quickEyeDoseOptions = [
                            "5ml Bottle",
                            "10ml Bottle",
                            "3.5g Tube",
                            "4g Tube",
                            "5g Tube",
                          ];
                          const quickEyeFreqOptions = [
                            "Instill 1 drop in affected eye(s) OD in the evening",
                            "Instill 1 drop in affected eye(s) BD (Twice daily)",
                            "Instill 1 drop in affected eye(s) TDS (Three times daily)",
                            "Instill 1 drop in affected eye(s) QDS (Four times daily)",
                            "Instill 1 drop in both eyes PRN for dry eyes",
                            "Apply 1 cm ribbon inside lower eyelid OD at bedtime",
                            "Apply 1 cm ribbon inside lower eyelid TDS (Three times daily)",
                          ];

                          const quickEntDoseOptions = [
                            "10ml Spray",
                            "15ml Spray",
                            "120 actuations",
                            "7.5ml Bottle",
                            "10ml Bottle",
                          ];
                          const quickEntFreqOptions = [
                            "Instill 2 sprays in each nostril OD in the morning",
                            "Instill 2 sprays in each nostril BD (Twice daily)",
                            "Instill 1-2 sprays in each nostril TDS (Max 5 days)",
                            "Instill 4 drops in affected ear canal BD (Twice daily)",
                            "Instill 3-4 drops in affected ear BD to soften wax",
                            "Instill 2-3 drops in affected ear/eye TDS",
                          ];

                          return (
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-[10px] font-semibold text-slate-500 uppercase">
                                    {isTopicalMed
                                      ? "Quantity & Pack Size (الكمية والحجم)"
                                      : isEyeMed || isEntMed
                                        ? "Preparation & Size (الجرعة والعبوة)"
                                        : "Dose"}
                                  </label>
                                  <input
                                    type="text"
                                    value={cpoeDrugDose}
                                    onChange={(e) =>
                                      setCpoeDrugDose(e.target.value)
                                    }
                                    placeholder={
                                      isTopicalMed
                                        ? "e.g. 30g Tube"
                                        : isEyeMed || isEntMed
                                          ? "e.g. 5ml Bottle"
                                          : "e.g. 500mg"
                                    }
                                    className="w-full bg-white border border-[#E2E8F0] rounded-md p-2 font-medium text-xs outline-none focus:border-[#0F172A] mt-1 text-slate-800"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-semibold text-slate-500 uppercase">
                                    {isTopicalMed
                                      ? "Application / Frequency (الجرعة والاستخدام)"
                                      : isEyeMed || isEntMed
                                        ? "Directions & Frequency (الاستخدام والجرعة)"
                                        : "Frequency"}
                                  </label>
                                  {isTopicalMed || isEyeMed || isEntMed ? (
                                    <input
                                      type="text"
                                      value={cpoeDrugFreq}
                                      onChange={(e) =>
                                        setCpoeDrugFreq(e.target.value)
                                      }
                                      placeholder={
                                        isTopicalMed
                                          ? "e.g. Apply thin layer BD"
                                          : isEyeMed
                                            ? "e.g. Instill 1 drop BD"
                                            : "e.g. Instill 2 sprays OD"
                                      }
                                      className="w-full bg-white border border-[#E2E8F0] rounded-md p-2 font-medium text-xs outline-none focus:border-[#0F172A] mt-1 text-slate-800"
                                    />
                                  ) : (
                                    <select
                                      value={cpoeDrugFreq}
                                      onChange={(e) =>
                                        setCpoeDrugFreq(e.target.value)
                                      }
                                      className="w-full bg-white border border-[#E2E8F0] rounded-md p-2 text-xs font-semibold focus:border-[#0F172A] mt-1 text-slate-800"
                                    >
                                      <option value="">Select...</option>
                                      <option value="OD (Once daily)">
                                        OD (Once daily)
                                      </option>
                                      <option value="BD (Twice daily)">
                                        BD (Twice daily)
                                      </option>
                                      <option value="TDS (Three times / day)">
                                        TDS (Three/day)
                                      </option>
                                      <option value="QDS (Four times / day)">
                                        QDS (Four/day)
                                      </option>
                                      <option value="PRN (As needed)">
                                        PRN (As needed)
                                      </option>
                                    </select>
                                  )}
                                </div>
                              </div>

                              {/* Dermatology Helper Workspace */}
                              {isTopicalMed && (
                                <div className="bg-emerald-50/50 border border-emerald-100 rounded-md p-3.5 space-y-3 mt-1.5 animated fade-in-sm">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10.5px] font-bold text-emerald-800 flex items-center gap-1">
                                      ✨ Dermatology Quick Guide (تجهيز الجلدية
                                      السريع)
                                    </span>
                                  </div>

                                  {/* Quantity quick badges */}
                                  <div className="space-y-1">
                                    <span className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider items-center justify-between flex">
                                      <span>
                                        Select Pack Size (اختر حجم العبوة):
                                      </span>
                                      <span className="text-slate-400 normal-case">
                                        Dose / Size
                                      </span>
                                    </span>
                                    <div className="flex flex-wrap gap-1">
                                      {quickDoseOptions.map((opt) => (
                                        <button
                                          key={opt}
                                          type="button"
                                          onClick={() => setCpoeDrugDose(opt)}
                                          className={`text-[9.5px] px-2 py-1 rounded transition-all font-semibold select-none ${
                                            cpoeDrugDose === opt
                                              ? "bg-emerald-600 text-white border border-emerald-600"
                                              : "bg-white text-emerald-800 hover:bg-emerald-100/55 border border-emerald-200"
                                          }`}
                                        >
                                          {opt}
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Frequency / Instructions quick badges */}
                                  <div className="space-y-1">
                                    <span className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider">
                                      Select Directions of Use (اختر تعليمات
                                      الاستخدام):
                                    </span>
                                    <div className="space-y-1 max-h-40 overflow-y-auto">
                                      {quickFreqOptions.map((opt) => (
                                        <button
                                          key={opt}
                                          type="button"
                                          onClick={() => setCpoeDrugFreq(opt)}
                                          className={`w-full text-left text-[9.5px] px-2 py-1.5 rounded transition-all font-medium border text-slate-700 block ${
                                            cpoeDrugFreq === opt
                                              ? "bg-indigo-600 text-white border-indigo-600 font-semibold"
                                              : "bg-white hover:bg-slate-50 border-slate-200"
                                          }`}
                                        >
                                          {opt}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Ophthalmology Helper Workspace */}
                              {isEyeMed && (
                                <div className="bg-sky-50/50 border border-sky-100 rounded-md p-3.5 space-y-3 mt-1.5 animated fade-in-sm">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10.5px] font-bold text-sky-800 flex items-center gap-1">
                                      ✨ Ophthalmology Quick Guide (تجهيز علاجات
                                      العين السريع)
                                    </span>
                                  </div>

                                  {/* Quantity quick badges */}
                                  <div className="space-y-1">
                                    <span className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider items-center justify-between flex">
                                      <span>
                                        Select Bottle/Tube Size (حجم علبة
                                        العين):
                                      </span>
                                      <span className="text-slate-400 normal-case">
                                        Size
                                      </span>
                                    </span>
                                    <div className="flex flex-wrap gap-1">
                                      {quickEyeDoseOptions.map((opt) => (
                                        <button
                                          key={opt}
                                          type="button"
                                          onClick={() => setCpoeDrugDose(opt)}
                                          className={`text-[9.5px] px-2.5 py-1 rounded transition-all font-semibold select-none cursor-pointer ${
                                            cpoeDrugDose === opt
                                              ? "bg-sky-600 text-white border border-sky-600"
                                              : "bg-white text-sky-800 hover:bg-sky-100/55 border border-sky-200"
                                          }`}
                                        >
                                          {opt}
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Frequency / Instructions quick badges */}
                                  <div className="space-y-1">
                                    <span className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider">
                                      Select Eye Drop / Ointment Directions
                                      (طريقة الاستخدام والجرعة):
                                    </span>
                                    <div className="space-y-1 max-h-40 overflow-y-auto">
                                      {quickEyeFreqOptions.map((opt) => (
                                        <button
                                          key={opt}
                                          type="button"
                                          onClick={() => setCpoeDrugFreq(opt)}
                                          className={`w-full text-left text-[9.5px] px-2 py-1.5 rounded transition-all font-medium border text-slate-700 block cursor-pointer ${
                                            cpoeDrugFreq === opt
                                              ? "bg-indigo-600 text-white border-indigo-600 font-semibold"
                                              : "bg-white hover:bg-slate-50 border-slate-200"
                                          }`}
                                        >
                                          {opt}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* ENT Helper Workspace */}
                              {isEntMed && (
                                <div className="bg-purple-50/50 border border-purple-100 rounded-md p-3.5 space-y-3 mt-1.5 animated fade-in-sm">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10.5px] font-bold text-purple-800 flex items-center gap-1">
                                      ✨ ENT Quick Guide (تجهيز الأذن والأنف
                                      والحنجرة)
                                    </span>
                                  </div>

                                  {/* Quantity quick badges */}
                                  <div className="space-y-1">
                                    <span className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider items-center justify-between flex">
                                      <span>
                                        Select Pack Size (الحجم والعبوة):
                                      </span>
                                      <span className="text-slate-400 normal-case">
                                        Size
                                      </span>
                                    </span>
                                    <div className="flex flex-wrap gap-1">
                                      {quickEntDoseOptions.map((opt) => (
                                        <button
                                          key={opt}
                                          type="button"
                                          onClick={() => setCpoeDrugDose(opt)}
                                          className={`text-[9.5px] px-2.5 py-1 rounded transition-all font-semibold select-none cursor-pointer ${
                                            cpoeDrugDose === opt
                                              ? "bg-purple-600 text-white border border-purple-600"
                                              : "bg-white text-purple-800 hover:bg-purple-100/55 border border-purple-200"
                                          }`}
                                        >
                                          {opt}
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Frequency / Instructions quick badges */}
                                  <div className="space-y-1">
                                    <span className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider">
                                      Select Ear/Nose Spray Directions (طريقة
                                      الاستخدام والجرعة):
                                    </span>
                                    <div className="space-y-1 max-h-40 overflow-y-auto">
                                      {quickEntFreqOptions.map((opt) => (
                                        <button
                                          key={opt}
                                          type="button"
                                          onClick={() => setCpoeDrugFreq(opt)}
                                          className={`w-full text-left text-[9.5px] px-2 py-1.5 rounded transition-all font-medium border text-slate-700 block cursor-pointer ${
                                            cpoeDrugFreq === opt
                                              ? "bg-indigo-600 text-white border-indigo-600 font-semibold"
                                              : "bg-white hover:bg-slate-50 border-slate-200"
                                          }`}
                                        >
                                          {opt}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div>
                                <label className="block text-[10px] font-semibold text-slate-500 uppercase">
                                  Duration
                                </label>
                                <input
                                  type="text"
                                  value={cpoeDrugDuration}
                                  onChange={(e) =>
                                    setCpoeDrugDuration(e.target.value)
                                  }
                                  placeholder="e.g. 5 days, 1 month..."
                                  className="w-full bg-white border border-[#E2E8F0] rounded-md p-2 font-medium text-xs outline-none focus:border-[#0F172A] mt-1 text-slate-800"
                                />
                              </div>
                            </div>
                          );
                        })()}

                        {/* Drug Interaction Alerts */}
                        {getDrugInteractions(cpoeDrugInput).length > 0 && (
                          <div className="bg-rose-50 border border-rose-200 rounded-md p-2">
                            <h5 className="text-rose-800 font-bold text-[10px] uppercase flex items-center gap-1">
                              <Info className="w-3 h-3" /> Risky RX Alert
                            </h5>
                            <ul className="list-disc pl-4 mt-1">
                              {getDrugInteractions(cpoeDrugInput).map(
                                (alertStr, idx) => (
                                  <li
                                    key={idx}
                                    className="text-[9px] text-rose-700 font-medium leading-tight"
                                  >
                                    {alertStr}
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}

                        {/* CLINICAL SECURITY: Serious Drug-Drug Interaction Alert Screen Segment */}
                        {getDrugToDrugInteractions(prescriptions, cpoeDrugInput)
                          .length > 0 && (
                          <div className="bg-rose-50 border-2 border-rose-300 rounded-md p-3 mt-2">
                            <h5 className="text-rose-900 font-bold text-xs uppercase flex items-center gap-1.5 leading-none">
                              <span className="w-2 h-2 rounded-full bg-rose-600 animate-pulse inline-block"></span>
                              ⚠️ Serious Drug-Drug Interaction Alert
                            </h5>
                            <p className="text-[10px] text-rose-800 font-medium mt-1 leading-normal">
                              The medications selected present a serious
                              clinical interaction risk:
                            </p>
                            <ul className="space-y-2 mt-2">
                              {getDrugToDrugInteractions(
                                prescriptions,
                                cpoeDrugInput,
                              ).map((interaction, idx) => (
                                <li
                                  key={idx}
                                  className="bg-white border border-rose-100 p-2 rounded shadow-xs"
                                >
                                  <div
                                    className="flex flex-wrap justify-between items-center text-[10.5px] font-bold text-rose-950 gap-1"
                                    dir="ltr"
                                  >
                                    <span className="bg-rose-50 text-rose-900 px-1 rounded">
                                      {interaction.drugA}
                                    </span>
                                    <span className="text-rose-400 font-mono text-[9px] uppercase font-normal">
                                      interacts with
                                    </span>
                                    <span className="bg-rose-50 text-rose-900 px-1 rounded">
                                      {interaction.drugB}
                                    </span>
                                  </div>
                                  <p
                                    className="text-[10px] text-rose-700 font-normal leading-relaxed mt-1"
                                    dir="ltr"
                                  >
                                    <strong>Clinical Mechanism:</strong>{" "}
                                    {interaction.mechanism}
                                  </p>
                                  <div className="mt-1.5 flex flex-wrap justify-between items-center bg-rose-50 border border-rose-100/50 px-1.5 py-0.5 rounded text-[9px] gap-1">
                                    <span className="font-semibold text-rose-600 uppercase tracking-wider">
                                      {interaction.severity} Severity
                                    </span>
                                    <span
                                      className="text-rose-800 font-bold"
                                      dir="ltr"
                                    >
                                      Action: {interaction.action}
                                    </span>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            const allergyConflicts = getDrugInteractions(cpoeDrugInput);
                            const drugConflicts = getDrugToDrugInteractions(prescriptions, cpoeDrugInput);
                            
                            if (allergyConflicts.length > 0 || drugConflicts.length > 0) {
                              alert("🔴 تنبيه خطير (Hard Stop):\nيوجد تداخل دوائي أو حساسية مثبتة تجاه هذا الدواء. حفاظاً على سلامة المريض، يمنع النظام إضافة هذا الدواء للوصفة.");
                              return;
                            }

                            setPrescriptions((prev) => [
                              ...prev,
                              {
                                name: cpoeDrugInput,
                                dose: cpoeDrugDose || "N/A",
                                frequency: cpoeDrugFreq || "N/A",
                                duration: cpoeDrugDuration || "N/A",
                              },
                            ]);
                            setCpoeDrugInput("");
                            setCpoeDrugDose("");
                            setCpoeDrugFreq("");
                            setCpoeDrugDuration("");
                          }}
                          disabled={!cpoeDrugInput}
                          className="w-full md:w-auto h-8 bg-slate-800 hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-xs px-4 rounded-md flex items-center justify-center gap-1.5 transition select-none cursor-pointer mt-2"
                        >
                          + Add to Current Prescription
                        </button>
                      </div>

                      {/* Current Batch Preview */}
                      <div className="bg-white border text-sm border-slate-200 rounded-md p-4">
                        <h4 className="font-bold text-slate-700 text-xs mb-3 flex items-center justify-between">
                          Draft Prescription ({prescriptions.length} items)
                          {prescriptions.length > 0 && (
                            <div className="flex gap-2">
                              {/* Standalone Prescription Printing Action */}
                              <button
                                type="button"
                                onClick={() => {
                                  try {
                                    setPrintDocType("PRESCRIPTION");
                                    setSelectedOrderToPrint(null);
                                    setPrintOpen(true);
                                  } catch (err: any) {
                                    console.error(
                                      "[Prescription Print Error] ",
                                      err,
                                    );
                                  }
                                }}
                                className="bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 px-3 py-1.5 rounded flex items-center gap-1 text-[10px] transition shadow-sm font-bold cursor-pointer"
                              >
                                <Printer className="w-3 h-3" /> Print Rx
                                Prescription
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  // Compile all items into one mega text for pharmacy
                                  const compiled = prescriptions
                                    .map(
                                      (p, i) =>
                                        `${i + 1}. ${p.name} | Dose: ${p.dose} | Freq: ${p.frequency} | Dur: ${p.duration}`,
                                    )
                                    .join("\n");
                                  handleDispatchSpecificOrder(
                                    "PHARMACY",
                                    compiled,
                                    () => {
                                      triggerSuccessToast(
                                        "All items submitted to Pharmacy.",
                                      );
                                      setPrescriptions([]);
                                    },
                                  );
                                }}
                                className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded flex items-center gap-1 text-[10px] transition shadow-sm cursor-pointer"
                              >
                                <Send className="w-3 h-3" /> Submit ALL to
                                Pharmacy
                              </button>
                            </div>
                          )}
                        </h4>

                        {/* CLINICAL SECURITY: Batch-level Drug-Drug Serious Interaction Warning */}
                        {getDrugToDrugInteractions(prescriptions).length >
                          0 && (
                          <div className="bg-rose-50 border border-rose-300 rounded-md p-3 mb-3">
                            <h5 className="text-rose-900 font-bold text-xs uppercase flex items-center gap-1.5 leading-none">
                              <span className="w-2 h-2 rounded-full bg-rose-600 animate-ping inline-block"></span>
                              🚨 ACTIVE SERIOUS DRUG INTERACTION DETECTED
                            </h5>
                            <p className="text-[10px] text-rose-800 font-medium mt-1 leading-normal">
                              The current prescription list contains medications
                              with documented serious drug-drug reactions:
                            </p>
                            <div className="space-y-2 mt-2">
                              {getDrugToDrugInteractions(prescriptions).map(
                                (interaction, idx) => (
                                  <div
                                    key={idx}
                                    className="bg-white border border-rose-200 p-2.5 rounded shadow-xs text-left"
                                    dir="ltr"
                                  >
                                    <div className="flex justify-between items-center text-[10px] font-bold text-rose-950">
                                      <span className="bg-rose-50 text-rose-900 px-1.5 py-0.5 rounded">
                                        {interaction.drugA}
                                      </span>
                                      <span className="text-rose-400 font-mono text-[9px] uppercase font-normal">
                                        VS
                                      </span>
                                      <span className="bg-rose-50 text-rose-900 px-1.5 py-0.5 rounded">
                                        {interaction.drugB}
                                      </span>
                                    </div>
                                    <p className="text-[9.5px] text-rose-700 font-normal leading-relaxed mt-1 text-left">
                                      <strong>Clinical Mechanism:</strong>{" "}
                                      {interaction.mechanism}
                                    </p>
                                    <div className="mt-1.5 flex justify-between items-center bg-rose-50 px-1.5 py-0.5 rounded text-[8.5px] border border-rose-100/50">
                                      <span className="font-semibold text-rose-600 uppercase tracking-widest">
                                        {interaction.severity} LEVEL
                                      </span>
                                      <span className="text-rose-800 font-bold">
                                        Action Suggested: {interaction.action}
                                      </span>
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        )}

                        {/* Drug Interaction BNF Alert */}
                        {prescriptions.length >= 2 &&
                          getDrugToDrugInteractions(prescriptions).length ===
                            0 && (
                            <div className="bg-amber-50 border border-amber-300 rounded-md p-3 mb-3">
                              <h5 className="text-amber-900 font-bold text-xs uppercase flex items-center gap-1.5 leading-none">
                                <Info className="w-3 h-3 text-amber-600" />
                                Drug Interaction Alert
                              </h5>
                              <p className="text-[10px] text-amber-800 font-medium mt-1 leading-normal">
                                Multiple medications detected. Please check for
                                interactions by performing a{" "}
                                <a
                                  href="https://bnf.nice.org.uk/interactions/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-indigo-700 font-bold underline hover:text-indigo-900"
                                >
                                  search from trusted resources as BNF
                                </a>
                                .
                              </p>
                            </div>
                          )}

                        {prescriptions.length === 0 ? (
                          <p className="text-slate-400 italic text-[10px]">
                            No medicines added to the draft yet.
                          </p>
                        ) : (
                          <ul className="space-y-2">
                            {prescriptions.map((p, i) => (
                              <li
                                key={i}
                                className="flex justify-between items-center p-2 bg-slate-50 rounded border border-slate-100"
                              >
                                <div>
                                  <p className="font-bold text-slate-800 text-[11px]">
                                    {p.name}
                                  </p>
                                  <p className="text-[10px] text-slate-500">
                                    {p.dose} • {p.frequency} • {p.duration}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPrescriptions(
                                      prescriptions.filter(
                                        (_, idx) => idx !== i,
                                      ),
                                    )
                                  }
                                  className="text-rose-500 hover:text-rose-700 p-1"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        {prescriptions.length > 0 && selectedPatient && (
                          <div className="mt-2 text-right flex items-center justify-end gap-2">
                            <SendWhatsAppButton
                              patientPhone={selectedPatient.phone}
                              messageContent={`Prescription for ${selectedPatient.name}:\n${prescriptions.map((p) => `- ${p.name} (${p.dose}, ${p.frequency}, ${p.duration})`).join("\n")}`}
                              label="Prescription"
                            />
                            <SaveToDriveButton
                              fileName={`Prescription-${selectedPatient.name}-${new Date().toISOString()}.txt`}
                              content={`Prescription for ${selectedPatient.name}:\n${prescriptions.map((p) => `- ${p.name} (${p.dose}, ${p.frequency}, ${p.duration})`).join("\n")}`}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Column: BNF & Drugs.com Clinically Trusted Reference and Search Databases */}
                    <div className="lg:col-span-6 bg-white border border-slate-200 shadow-sm rounded-md p-4 flex flex-col h-[520px]">
                      <div className="border-b border-indigo-50 pb-3 mb-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-indigo-600" />
                            <div>
                              <h4 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider">
                                Clinical Rx Reference
                              </h4>
                              <p className="text-[10px] font-medium text-slate-400 font-sans">
                                Official BNF Catalog & External Databases
                              </p>
                            </div>
                          </div>

                          {/* Rx Database Segment Tab Control */}
                          <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-250">
                            <button
                              type="button"
                              onClick={() => {
                                setActiveRxDatabase("bnf");
                                triggerSuccessToast(
                                  "Switched to Local BNF Medicine Catalog.",
                                );
                              }}
                              className={`px-3 py-1 rounded-md text-[10px] font-bold transition-all cursor-pointer ${
                                activeRxDatabase === "bnf"
                                  ? "bg-indigo-600 text-white shadow-sm"
                                  : "text-slate-600 hover:text-slate-800"
                              }`}
                            >
                              BNF Catalog
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setActiveRxDatabase("drugsdotcom");
                                triggerSuccessToast(
                                  "Switched to Drugs.com Reference Portal.",
                                );
                              }}
                              className={`px-3 py-1 rounded-md text-[10px] font-bold transition-all cursor-pointer ${
                                activeRxDatabase === "drugsdotcom"
                                  ? "bg-emerald-600 text-white shadow-sm"
                                  : "text-slate-600 hover:text-slate-800"
                              }`}
                            >
                              Drugs.com Portal
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setActiveRxDatabase("emc");
                                triggerSuccessToast(
                                  "Switched to emc UK Catalog.",
                                );
                              }}
                              className={`px-3 py-1 rounded-md text-[10px] font-bold transition-all cursor-pointer ${
                                activeRxDatabase === "emc"
                                  ? "bg-rose-600 text-white shadow-sm"
                                  : "text-slate-600 hover:text-slate-800"
                              }`}
                            >
                              emc UK Catalog
                            </button>
                          </div>
                        </div>

                        {activeRxDatabase === "bnf" && (
                          <>
                            {/* Search Database */}
                            <div className="mt-3 relative">
                              <input
                                type="text"
                                value={bnfSearchText}
                                onChange={(e) =>
                                  setBnfSearchText(e.target.value)
                                }
                                placeholder="Filter drug by name, instruction, active ingredient..."
                                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5 font-medium text-xs outline-none focus:bg-white focus:border-indigo-600 transition"
                              />
                            </div>

                            {/* Category Selector Tabs */}
                            <div className="flex gap-1 overflow-x-auto pr-1 mt-2 pb-1 scrollbar-thin scrollbar-thumb-slate-200">
                              {[
                                "All",
                                "Ophthalmology",
                                "ENT Specialists",
                                "Gastro-intestinal",
                                "Cardiovascular",
                                "Respiratory",
                                "Nervous System & Pain",
                                "CNS & Psychiatry",
                                "Infections",
                                "Endocrine",
                                "Genitourinary",
                                "Dermatology",
                                "Immunology & Serology",
                              ].map((cat) => (
                                <button
                                  key={cat}
                                  type="button"
                                  onClick={() => setBnfSelectedCategory(cat)}
                                  className={`text-[9.5px] font-semibold px-2.5 py-1 rounded transition select-none flex-shrink-0 cursor-pointer ${
                                    bnfSelectedCategory === cat
                                      ? "bg-indigo-600 text-white font-bold"
                                      : "bg-slate-100 hover:bg-slate-200 text-slate-600"
                                  }`}
                                >
                                  {cat}
                                </button>
                              ))}
                            </div>

                            {/* NICE & Clinical Guidelines Box */}
                            <div className="bg-emerald-50/50 border border-emerald-100 rounded-md p-3 mt-2 text-left flex flex-col">
                              <div className="flex items-center justify-between gap-2 mb-2">
                                <h5 className="font-bold text-[10.5px] text-emerald-800 flex items-center gap-1 shrink-0">
                                  📘 NICE & Trusted Guidelines
                                </h5>
                                <input
                                  type="text"
                                  placeholder="Search guidelines..."
                                  value={guidelineSearchTerm}
                                  onChange={(e) =>
                                    setGuidelineSearchTerm(e.target.value)
                                  }
                                  className="bg-white border border-emerald-200 text-[9px] px-2 py-1 rounded w-full outline-none focus:border-emerald-400"
                                />
                              </div>
                              <div className="text-[9.5px] text-slate-700 space-y-1.5 leading-relaxed max-h-32 overflow-y-auto pr-1 select-text flex-1">
                                {NICE_GUIDELINES.filter(
                                  (g) =>
                                    g.disease
                                      .toLowerCase()
                                      .includes(
                                        guidelineSearchTerm.toLowerCase(),
                                      ) ||
                                    g.management
                                      .toLowerCase()
                                      .includes(
                                        guidelineSearchTerm.toLowerCase(),
                                      ),
                                ).map((g, idx) => (
                                  <div
                                    key={idx}
                                    className={
                                      idx > 0
                                        ? "border-t border-emerald-100/50 pt-1.5"
                                        : ""
                                    }
                                  >
                                    <strong className="text-emerald-900">
                                      {g.disease}:
                                    </strong>{" "}
                                    {g.management}
                                  </div>
                                ))}
                                {NICE_GUIDELINES.filter(
                                  (g) =>
                                    g.disease
                                      .toLowerCase()
                                      .includes(
                                        guidelineSearchTerm.toLowerCase(),
                                      ) ||
                                    g.management
                                      .toLowerCase()
                                      .includes(
                                        guidelineSearchTerm.toLowerCase(),
                                      ),
                                ).length === 0 && (
                                  <div className="text-emerald-600/70 italic p-1 text-center">
                                    No matching guidelines found.
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Toggled Prescription Reference Interfaces */}
                      {activeRxDatabase === "emc" ? (
                        <div className="flex-1 flex flex-col justify-between space-y-3.5 min-h-0 mt-3">
                          <div className="bg-rose-500/10 border border-rose-500/20 p-3.5 rounded-lg text-right space-y-1">
                            <h5 className="font-bold text-[11px] text-rose-600 flex items-center gap-1 justify-end">
                              🇬🇧 بوابة المرجع البريطاني للأدوية (emc)
                            </h5>
                            <p className="text-[10px] text-slate-500 leading-normal font-sans">
                              يمكنك البحث عن تفاصيل الأدوية والجرعات المتوفرة في بريطانيا عبر البوابة الرسمية:{" "}
                              <strong>medicines.org.uk/emc</strong> مباشرة.
                            </p>
                          </div>

                          <div className="space-y-3 flex-1 overflow-y-auto pr-1 min-h-0">
                            {/* Dynamic Online Search Input bar */}
                            <div className="border border-slate-150 p-3 rounded-lg bg-slate-50 text-right space-y-2">
                              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide">
                                ابحث في قاعدة بيانات emc المفتوحة:
                              </label>
                              <div className="flex gap-2 relative">
                                <input
                                  type="text"
                                  placeholder="اكتب اسم الدواء (مثال: Amoxicillin, Ibuprofen)..."
                                  value={bnfSearchText}
                                  onChange={(e) =>
                                    setBnfSearchText(e.target.value)
                                  }
                                  className="flex-1 bg-white border border-slate-250 rounded px-2.5 py-1.5 text-xs text-slate-800 outline-none focus:border-rose-500 text-left font-medium"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    window.open(
                                      `https://www.medicines.org.uk/emc/search?q=${encodeURIComponent(bnfSearchText || "paracetamol")}`,
                                      "_blank",
                                    )
                                  }
                                  className="bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-white font-extrabold text-[11px] px-3.5 py-1.5 rounded transition cursor-pointer"
                                >
                                  ابحث الآن
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : activeRxDatabase === "drugsdotcom" ? (
                        <div className="flex-1 flex flex-col justify-between space-y-3.5 min-h-0 mt-3">
                          <div className="bg-sky-500/10 border border-sky-500/20 p-3.5 rounded-lg text-right space-y-1">
                            <h5 className="font-bold text-[11px] text-sky-400 flex items-center gap-1 justify-end">
                              🌐 بوابة معلومات مرجع Drugs.com الطبي
                            </h5>
                            <p className="text-[10px] text-zinc-300 leading-normal font-sans">
                              يمكنك البحث عن النشرات الدوائية والتركيبات
                              وتفاعلات الأدوية المتداخلة عبر البوابة الرسمية:{" "}
                              <strong>drugs.com/drug_information.html</strong>{" "}
                              مباشرة.
                            </p>
                          </div>

                          <div className="space-y-3 flex-1 overflow-y-auto pr-1 min-h-0">
                            {/* Dynamic Online Search Input bar */}
                            <div className="border border-slate-150 p-3 rounded-lg bg-slate-50 text-right space-y-2">
                              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide">
                                ابحث في قاعدة بيانات Drugs.com العالمية:
                              </label>
                              <div className="flex gap-2 relative">
                                <input
                                  type="text"
                                  placeholder="اكتب اسم الدواء (مثال: Amoxicillin, Ibuprofen)..."
                                  value={bnfSearchText}
                                  onChange={(e) =>
                                    setBnfSearchText(e.target.value)
                                  }
                                  className="flex-1 bg-white border border-slate-250 rounded px-2.5 py-1.5 text-xs text-slate-800 outline-none focus:border-emerald-500 text-left font-medium"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    window.open(
                                      `https://www.drugs.com/search.php?searchterm=${encodeURIComponent(bnfSearchText || "paracetamol")}`,
                                      "_blank",
                                    )
                                  }
                                  className="bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-750 text-white font-extrabold text-[11px] px-3.5 py-1.5 rounded transition cursor-pointer"
                                >
                                  ابحث الآن
                                </button>
                              </div>
                            </div>

                            {/* Grid of Specialty Tools inside drugs.com */}
                            <div className="grid grid-cols-2 gap-2 text-right">
                              <a
                                href="https://www.drugs.com/drug_interactions.html"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2.5 bg-blue-50/40 hover:bg-blue-50 border border-blue-100 rounded-lg text-slate-700 hover:text-blue-800 transition flex flex-col justify-between"
                              >
                                <span className="font-bold text-[10.5px]">
                                  💊 Interactions Checker
                                </span>
                                <span className="text-[9px] text-slate-400 mt-0.5">
                                  فحص تداخلات الأدوية
                                </span>
                              </a>
                              <a
                                href="https://www.drugs.com/pill_identification.html"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2.5 bg-indigo-50/40 hover:bg-indigo-50 border border-indigo-100 rounded-lg text-slate-700 hover:text-indigo-800 transition flex flex-col justify-between"
                              >
                                <span className="font-bold text-[10.5px]">
                                  🔍 Pill Identifier
                                </span>
                                <span className="text-[9px] text-slate-400 mt-0.5">
                                  معرف الحبوب والكبسولات
                                </span>
                              </a>
                              <a
                                href="https://www.drugs.com/dosage/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2.5 bg-emerald-50/40 hover:bg-emerald-50 border border-emerald-100 rounded-lg text-slate-700 hover:text-emerald-800 transition flex flex-col justify-between"
                              >
                                <span className="font-bold text-[10.5px]">
                                  🩺 Clinical Dosage
                                </span>
                                <span className="text-[9px] text-slate-400 mt-0.5">
                                  الجرعات الطبية المعتمدة
                                </span>
                              </a>
                              <a
                                href="https://www.drugs.com/drug_information.html"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2.5 bg-purple-50/40 hover:bg-purple-50 border border-purple-100 rounded-lg text-slate-700 hover:text-purple-800 transition flex flex-col justify-between"
                              >
                                <span className="font-bold text-[10.5px]">
                                  📚 Drug Information Info
                                </span>
                                <span className="text-[9px] text-slate-400 mt-0.5">
                                  النشرات والاعتمادات الرسمية
                                </span>
                              </a>
                            </div>

                            {/* Direct launch references pills */}
                            <div className="bg-slate-50 border border-slate-150 p-3 rounded-lg space-y-1.5">
                              <span className="block text-[9.5px] uppercase font-extrabold text-slate-400 text-right">
                                بحث سريع للأدوية الشائعة على Drugs.com:
                              </span>
                              <div className="flex flex-wrap gap-1.5 justify-end">
                                {[
                                  "Amoxicillin",
                                  "Ibuprofen",
                                  "Atorvastatin",
                                  "Metformin",
                                  "Omeprazole",
                                  "Paracetamol",
                                  "Aspirin",
                                  "Prednisone",
                                  "Gabapentin",
                                ].map((name) => (
                                  <a
                                    key={name}
                                    href={`https://www.drugs.com/search.php?searchterm=${encodeURIComponent(name)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[10px] font-bold bg-white hover:bg-slate-50 text-slate-700 px-2 py-1 rounded border border-slate-200 flex items-center gap-1 transition-all"
                                  >
                                    <ExternalLink className="w-2.5 h-2.5 text-slate-400" />
                                    {name}
                                  </a>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="pt-2 border-t border-slate-100">
                            <a
                              href="https://www.drugs.com/drug_information.html"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full bg-[#1E3A8A] hover:bg-blue-900 active:bg-blue-950 text-white font-extrabold text-[11px] py-2 px-4 rounded shadow-md text-center flex items-center justify-center gap-1.5 transition-all outline-none"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              افتح الموقع الرسمي لـ Drugs.com
                            </a>
                          </div>
                        </div>
                      ) : (
                        /* Filtered Drug List Container (Integrated BNF) */
                        <div className="flex-1 overflow-y-auto pr-1 space-y-2.5 min-h-0 mt-3">
                          {(() => {
                            const queried = bnfCatalog.filter((med) => {
                              const matchCat =
                                bnfSelectedCategory === "All" ||
                                med.category === bnfSelectedCategory;
                              const matchSearch =
                                !bnfSearchText ||
                                med.name
                                  .toLowerCase()
                                  .includes(bnfSearchText.toLowerCase()) ||
                                med.indication
                                  .toLowerCase()
                                  .includes(bnfSearchText.toLowerCase()) ||
                                (med.info &&
                                  med.info
                                    .toLowerCase()
                                    .includes(bnfSearchText.toLowerCase()));
                              return matchCat && matchSearch;
                            });

                            if (queried.length === 0) {
                              return (
                                <div className="flex flex-col items-center justify-center h-full text-slate-400 text-xs italic py-8">
                                  <Info className="w-8 h-8 text-slate-300 mb-2" />
                                  No clinical matches found in the BNF database.
                                  Try a different query.
                                </div>
                              );
                            }

                            return queried.map((med, idx) => {
                              const isSelected =
                                cpoeDrugInput.toLowerCase() ===
                                med.name.toLowerCase();
                              return (
                                <div
                                  key={idx}
                                  className={`border rounded p-3 transition flex flex-col gap-1.5 ${
                                    isSelected
                                      ? "bg-indigo-50/50 border-indigo-400"
                                      : "bg-slate-50/40 hover:bg-slate-50 border-slate-150"
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className="font-serif font-extrabold text-slate-800 text-xs">
                                        {med.name}
                                      </span>
                                      <span className="text-[8.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-slate-200/70 text-slate-600 rounded">
                                        {med.category || "General"}
                                      </span>
                                    </div>
                                    <div className="flex gap-1">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setShowDrugInfoModal(med)
                                        }
                                        className="text-[9.5px] bg-sky-100 hover:bg-sky-200 text-sky-700 px-1.5 py-0.5 rounded border border-sky-200 font-bold uppercase flex items-center gap-1 transition-colors"
                                      >
                                        <Info className="w-3 h-3" /> View BNF
                                        Info
                                      </button>
                                      <a
                                        href={`https://www.drugs.com/search.php?searchterm=${encodeURIComponent(med.name)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[9.5px] bg-emerald-100 hover:bg-emerald-200 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200 font-bold uppercase flex items-center gap-1 transition-colors"
                                      >
                                        <ExternalLink className="w-3 h-3" />{" "}
                                        Drugs.com
                                      </a>
                                    </div>
                                  </div>

                                  <div className="text-[11px] text-slate-600 leading-normal">
                                    <span className="font-semibold text-slate-500 uppercase text-[9px]">
                                      Indication:{" "}
                                    </span>
                                    {med.indication}
                                  </div>

                                  <div className="grid grid-cols-3 gap-2 bg-white/80 rounded border border-slate-100 p-1.5 text-[10px]">
                                    <div>
                                      <span className="block text-[8px] uppercase tracking-wider font-semibold text-slate-400">
                                        Typical Dose
                                      </span>
                                      <span className="font-mono font-medium text-slate-700">
                                        {med.defaultDose}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="block text-[8px] uppercase tracking-wider font-semibold text-slate-400">
                                        Default Freq
                                      </span>
                                      <span className="font-mono font-medium text-slate-700 truncate block">
                                        {med.defaultFreq}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="block text-[8px] uppercase tracking-wider font-semibold text-slate-400">
                                        Duration
                                      </span>
                                      <span className="font-mono font-medium text-slate-700">
                                        {med.defaultDuration}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="flex gap-2 mt-0.5 border-t border-slate-100 pt-2 justify-end">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        // Clinical Security: Sanitize and load variables into CPOE state
                                        setCpoeDrugInput(med.name);
                                        setCpoeDrugDose(med.defaultDose);
                                        setCpoeDrugFreq(med.defaultFreq);
                                        setCpoeDrugDuration(
                                          med.defaultDuration,
                                        );
                                        triggerSuccessToast(
                                          `Pre-populated details for ${med.name}.`,
                                        );
                                      }}
                                      className="text-[9.5px] font-bold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 px-2.5 py-1 rounded transition flex items-center gap-1 cursor-pointer"
                                    >
                                      <Pill className="w-3 h-3 text-slate-500" />{" "}
                                      Pre-fill Form
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        // Clinical Security: Seamless add directly to active prescription array
                                        setPrescriptions((prev) => [
                                          ...prev,
                                          {
                                            name: med.name,
                                            dose: med.defaultDose,
                                            frequency: med.defaultFreq,
                                            duration: med.defaultDuration,
                                          },
                                        ]);
                                        triggerSuccessToast(
                                          `Added ${med.name} directly to the draft.`,
                                        );
                                      }}
                                      className="text-[9.5px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 px-2.5 py-1 rounded shadow-sm transition flex items-center gap-1 cursor-pointer"
                                    >
                                      <Plus className="w-3 h-3" /> Quick
                                      Prescribe
                                    </button>
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Dispatched Orders Queue */}
                {(tab === "nursing" || tab === "lab" || tab === "pharmacy") && (
                  <div className="space-y-2 mt-6">
                    <h4 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider text-[#64748B]">
                      Patient's Historical CPOE Orders Ledger
                    </h4>
                    <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                      {dispatchedOrders
                        .filter((o) => o.patientId === selectedPatient.id)
                        .map((order) => (
                          <div
                            key={order.id}
                            className="bg-white border border-[#E2E8F0] rounded-md p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-sm"
                          >
                            <div>
                              <div className="flex items-center gap-2">
                                <span
                                  className={`font-bold text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                                    order.department === "LAB"
                                      ? "bg-purple-50 text-purple-700 border-purple-200"
                                      : order.department === "X_RAY"
                                        ? "bg-sky-50 text-sky-700 border-sky-200"
                                        : "bg-teal-50 text-teal-700 border-teal-200"
                                  }`}
                                >
                                  {order.department} Panel
                                </span>
                                <span className="text-[10px] text-[#64748B] font-mono">
                                  ID: {order.id}
                                </span>
                              </div>
                              <p className="text-xs text-[#1E293B] font-semibold mt-1.5">
                                {order.instruction}
                              </p>
                              {order.results && (
                                <div className="mt-2.5 bg-[#F8FAFC] border border-[#E2E8F0] p-2.5 rounded-md text-[10.5px] font-mono text-[#64748B] whitespace-pre-line">
                                  <strong className="text-[#1E293B] font-bold block mb-1">
                                    Results Payload:
                                  </strong>
                                  {order.results}
                                </div>
                              )}
                            </div>
                            <div className="flex-shrink-0 flex flex-col gap-2 items-end">
                              <span
                                className={`font-bold text-[9px] uppercase px-2 py-0.5 rounded border ${
                                  order.status === "PENDING"
                                    ? "bg-amber-50 text-amber-700 border-amber-200"
                                    : "bg-emerald-50 text-emerald-700 border-emerald-200"
                                }`}
                              >
                                {order.status}
                              </span>
                              <button
                                onClick={() => {
                                  if (order.department === "PHARMACY") {
                                    setPrintDocType("PRESCRIPTION");
                                  } else {
                                    setPrintDocType("INVESTIGATION");
                                  }
                                  setSelectedOrderToPrint(order);
                                  setPrintOpen(true);
                                }}
                                className="text-[9px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 uppercase tracking-wider cursor-pointer"
                              >
                                <Printer className="w-3 h-3" />
                                Print
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* TIMELINE TAB */}
                {tab === "timeline" &&
                  (() => {
                    const items = [
                      ...appointments
                        .filter((a) => a.patientId === selectedPatient?.id)
                        .map((a) => ({
                          id: a.id,
                          type: "APPOINTMENT",
                          date: new Date(a.openedAt || a.dateTime),
                          title: `Appointment - ${a.department}`,
                          desc: `Status: ${a.status}`,
                          icon: Calendar,
                          color: "bg-blue-50 text-blue-600 border-blue-200",
                        })),
                      ...notesRecord.map((r) => ({
                        id: r.id,
                        type: "RECORD",
                        date: new Date(r.updatedAt || r.createdAt),
                        title: r.physicianName
                          ? `Clinical Encounter (${r.physicianName})`
                          : "Clinical Encounter",
                        desc:
                          r.soapAssessment ||
                          r.gpNotes ||
                          "Clinical details recorded.",
                        icon: FileText,
                        color:
                          "bg-emerald-50 text-emerald-600 border-emerald-200",
                      })),
                      ...dispatchedOrders
                        .filter((o) => o.patientId === selectedPatient?.id)
                        .map((o) => ({
                          id: o.id,
                          type: "ORDER",
                          date: new Date(o.createdAt),
                          title: `${o.department} Order`,
                          desc: o.instruction,
                          icon: Activity,
                          color: "bg-amber-50 text-amber-600 border-amber-200",
                        })),
                    ].sort((a, b) => b.date.getTime() - a.date.getTime());

                    return (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-bold text-[#1E293B]">
                            Chronological Patient Timeline
                          </h3>
                          <span className="text-[10px] text-[#0F172A] font-semibold bg-slate-100 border border-[#E2E8F0] px-3 py-1 rounded-md uppercase">
                            Unified EHR View
                          </span>
                        </div>

                        {selectedPatient?.dob &&
                        (new Date().getTime() -
                          new Date(selectedPatient.dob).getTime()) /
                          (1000 * 60 * 60 * 24 * 365.25) <=
                          5 ? (
                          <div className="space-y-4">
                            <PediatricDevelopment
                              patient={selectedPatient}
                              vitals={vitalsHistory}
                              apiToken={apiToken}
                              onRefresh={() =>
                                fetchPatientVitals(selectedPatient.id)
                              }
                            />
                            <VitalsChart
                              vitals={vitalsHistory}
                              targetBmi={selectedPatient?.targetBmi}
                              onTargetBmiChange={async (val) => {
                                if (!selectedPatient) return;
                                try {
                                  const res = await fetch(
                                    `/api/patients/${selectedPatient.id}`,
                                    {
                                      method: "PUT",
                                      headers: {
                                        "Content-Type": "application/json",
                                        Authorization: `Bearer ${apiToken}`,
                                      },
                                      body: JSON.stringify({ targetBmi: val }),
                                    },
                                  );
                                  if (res.ok) {
                                    const updatedPatient = await res.json();
                                    setSelectedPatient(updatedPatient);
                                  }
                                } catch (err) {
                                  console.error(err);
                                }
                              }}
                            />
                            <BmiTrendChart
                              vitals={vitalsHistory}
                              targetBmi={selectedPatient?.targetBmi}
                              onTargetBmiChange={async (val) => {
                                if (!selectedPatient) return;
                                try {
                                  const res = await fetch(
                                    `/api/patients/${selectedPatient.id}`,
                                    {
                                      method: "PUT",
                                      headers: {
                                        "Content-Type": "application/json",
                                        Authorization: `Bearer ${apiToken}`,
                                      },
                                      body: JSON.stringify({ targetBmi: val }),
                                    },
                                  );
                                  if (res.ok) {
                                    const updatedPatient = await res.json();
                                    setSelectedPatient(updatedPatient);
                                  }
                                } catch (err) {
                                  console.error(
                                    "Error setting target BMI in CDSS panel:",
                                    err,
                                  );
                                }
                              }}
                            />
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <VitalsChart
                              vitals={vitalsHistory}
                              targetBmi={selectedPatient?.targetBmi}
                              onTargetBmiChange={async (val) => {
                                if (!selectedPatient) return;
                                try {
                                  const res = await fetch(
                                    `/api/patients/${selectedPatient.id}`,
                                    {
                                      method: "PUT",
                                      headers: {
                                        "Content-Type": "application/json",
                                        Authorization: `Bearer ${apiToken}`,
                                      },
                                      body: JSON.stringify({ targetBmi: val }),
                                    },
                                  );
                                  if (res.ok) {
                                    const updatedPatient = await res.json();
                                    setSelectedPatient(updatedPatient);
                                  }
                                } catch (err) {
                                  console.error(err);
                                }
                              }}
                            />
                            <BmiTrendChart
                              vitals={vitalsHistory}
                              targetBmi={selectedPatient?.targetBmi}
                              onTargetBmiChange={async (val) => {
                                if (!selectedPatient) return;
                                try {
                                  const res = await fetch(
                                    `/api/patients/${selectedPatient.id}`,
                                    {
                                      method: "PUT",
                                      headers: {
                                        "Content-Type": "application/json",
                                        Authorization: `Bearer ${apiToken}`,
                                      },
                                      body: JSON.stringify({ targetBmi: val }),
                                    },
                                  );
                                  if (res.ok) {
                                    const updatedPatient = await res.json();
                                    setSelectedPatient(updatedPatient);
                                  }
                                } catch (err) {
                                  console.error(
                                    "Error setting target BMI in CDSS panel:",
                                    err,
                                  );
                                }
                              }}
                            />
                          </div>
                        )}

                        <div className="border border-slate-200 rounded-xl bg-white p-6 relative overflow-hidden">
                          <div className="absolute left-[39px] top-6 bottom-6 w-0.5 bg-slate-200" />
                          {items.length === 0 ? (
                            <div className="text-center py-10 font-medium text-xs text-slate-500">
                              No chronological events recorded for this patient.
                            </div>
                          ) : (
                            <div className="space-y-8 relative z-10">
                              {items.map((item) => {
                                const Icon = item.icon;
                                let formattedDate = "Unknown Date";
                                let formattedTime = "";
                                if (!isNaN(item.date.getTime())) {
                                  formattedDate = item.date.toLocaleDateString(
                                    "en-GB",
                                    {
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                    },
                                  );
                                  formattedTime = item.date.toLocaleTimeString(
                                    "en-US",
                                    { hour: "2-digit", minute: "2-digit" },
                                  );
                                }
                                return (
                                  <div
                                    key={item.id + item.type}
                                    className="flex gap-6 group"
                                  >
                                    {/* Icon / Line connecting points */}
                                    <div className="flex-shrink-0 flex flex-col items-center">
                                      <div
                                        className={`w-8 h-8 rounded-full border ${item.color} flex items-center justify-center font-bold relative z-10 bg-white group-hover:scale-110 transition-transform`}
                                      >
                                        <Icon className="w-4 h-4" />
                                      </div>
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4 hover:shadow-xs transition-shadow">
                                      <div className="flex items-start justify-between mb-2">
                                        <div>
                                          <h4 className="font-bold text-slate-800 text-sm uppercase tracking-wide">
                                            {item.title}
                                          </h4>
                                          <p className="text-[10px] font-mono text-slate-500 mt-1">
                                            {formattedDate} • {formattedTime}
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {item.type === "RECORD" && (
                                            <button
                                              onClick={() => {
                                                const record = notesRecord.find(
                                                  (r) => r.id === item.id,
                                                );
                                                if (record) {
                                                  setEditingRecordId(record.id);
                                                  setGpNotes(
                                                    record.gpNotes || "",
                                                  );
                                                  setSoapSubjective(
                                                    record.soapSubjective || "",
                                                  );
                                                  setSoapObjective(
                                                    record.soapObjective || "",
                                                  );
                                                  setSoapAssessment(
                                                    record.soapAssessment || "",
                                                  );
                                                  setSoapPlan(
                                                    record.soapPlan || "",
                                                  );
                                                  setFamilyHistory(
                                                    record.familyHistory === "N/A" ? "" : (record.familyHistory || "")
                                                  );
                                                  setSocialHistory(
                                                    record.socialHistory === "N/A" ? "" : (record.socialHistory || "")
                                                  );
                                                  setVitalTemp(record.vitalTemp || "");
                                                  setVitalPulse(record.vitalPulse || "");
                                                  setVitalBP(record.vitalBP || "");
                                                  setVitalWeight(record.vitalWeight || "");
                                                  setVitalHeight(record.vitalHeight || "");
                                                  setVitalBMI(record.vitalBMI || "");
                                                  setIcd10(
                                                    record.icd10Codes || "",
                                                  );
                                                  try {
                                                    if (record.prescriptions)
                                                      setPrescriptions(
                                                        JSON.parse(
                                                          record.prescriptions,
                                                        ),
                                                      );
                                                    else setPrescriptions([]);
                                                  } catch {
                                                    setPrescriptions([]);
                                                  }
                                                  setMinorSurgeryNotes(
                                                    record.minorSurgery || "",
                                                  );
                                                  try {
                                                    if (record.faceMapJson)
                                                      setLaserLogs(
                                                        JSON.parse(
                                                          record.faceMapJson,
                                                        ),
                                                      );
                                                    else setLaserLogs([]);
                                                  } catch {
                                                    setLaserLogs([]);
                                                  }
                                                  if (record.dermaData)
                                                    setSkinType(
                                                      record.dermaData,
                                                    );
                                                  else setSkinType("Type III");
                                                  try {
                                                    if (record.aiMetadata)
                                                      setAiAnalysisResult(
                                                        JSON.parse(
                                                          record.aiMetadata,
                                                        ),
                                                      );
                                                    else
                                                      setAiAnalysisResult(null);
                                                  } catch {
                                                    setAiAnalysisResult(null);
                                                  }
                                                  setTab("gp");
                                                  triggerSuccessToast(
                                                    "Loaded past record for editing.",
                                                  );
                                                }
                                              }}
                                              className="text-[10px] bg-white border border-indigo-200 text-indigo-700 font-bold px-2 py-0.5 rounded shadow-sm hover:bg-indigo-50"
                                            >
                                              Edit
                                            </button>
                                          )}
                                          <span
                                            className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${item.color}`}
                                          >
                                            {item.type}
                                          </span>
                                        </div>
                                      </div>
                                      <p className="text-xs text-slate-600 line-clamp-2 mt-2">
                                        {item.desc}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                {/* MEDICATION PROFILE TAB */}
                {tab === "med_profile" && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between border-b border-[#E2E8F0] pb-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-[#1E293B]">
                          Historical Medication Profile
                        </h3>
                        <span className="text-[10px] bg-slate-100 border border-[#E2E8F0] px-2 py-0.5 rounded-full text-[#64748B] font-mono">
                          Pharmacy Dispense Ledger
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setPrintDocType("MEDICATION_PROFILE");
                          setPrintOpen(true);
                        }}
                        className="bg-white border border-[#E2E8F0] hover:bg-slate-50 text-slate-700 font-bold text-[10px] uppercase px-3 py-1.5 rounded-md flex items-center gap-1.5 shadow-sm transition"
                      >
                        <Printer className="w-3.5 h-3.5" />
                        Print Medication Profile
                      </button>
                    </div>

                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {dispatchedOrders
                        .filter(
                          (o) =>
                            o.patientId === selectedPatient?.id &&
                            o.department === "PHARMACY",
                        )
                        .sort(
                          (a, b) =>
                            new Date(b.createdAt).getTime() -
                            new Date(a.createdAt).getTime(),
                        )
                        .map((order) => (
                          <div
                            key={order.id}
                            className="bg-white border border-[#E2E8F0] p-4 rounded-lg shadow-sm"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <span className="font-bold text-[#1E293B] text-xs">
                                  Prescription ID: {order.id.slice(0, 8)}
                                </span>
                                <div className="text-[10px] text-[#64748B] font-mono mt-0.5">
                                  {new Date(order.createdAt).toLocaleString()}
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                <span
                                  className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider border ${
                                    order.status === "COMPLETED" ||
                                    order.status === "DISPENSED"
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                      : "bg-amber-50 text-amber-700 border-amber-200"
                                  }`}
                                >
                                  {order.status === "COMPLETED"
                                    ? "DISPENSED"
                                    : order.status}
                                </span>
                                <button
                                  onClick={() => {
                                    setPrintDocType("PRESCRIPTION");
                                    setSelectedOrderToPrint(order);
                                    setPrintOpen(true);
                                  }}
                                  className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 uppercase tracking-wider cursor-pointer"
                                >
                                  <Printer className="w-3.5 h-3.5" />
                                  Print e-Rx
                                </button>
                              </div>
                            </div>
                            <div className="bg-slate-50 border border-slate-100 p-3 rounded-md mt-2 space-y-1">
                              <span className="block text-[10px] font-bold text-[#64748B] uppercase">
                                Dosage & Regimen
                              </span>
                              <p className="text-xs text-[#1E293B] uppercase whitespace-pre-wrap">
                                {order.instruction}
                              </p>
                            </div>
                          </div>
                        ))}

                      {dispatchedOrders.filter(
                        (o) =>
                          o.patientId === selectedPatient?.id &&
                          o.department === "PHARMACY",
                      ).length === 0 && (
                        <div className="text-center text-slate-400 text-xs italic py-10 bg-slate-50 rounded-md border border-slate-100">
                          No historical pharmacy prescriptions found for this
                          patient.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 5. MEDICAL HISTORY TAB */}
                {tab === "history" && (
                  <div className="space-y-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-slate-100 pb-3">
                      <div>
                        <h3 className="text-sm font-bold text-[#1E293B]">
                          Patient Medical History
                        </h3>
                        <p className="text-[10.5px] text-slate-500 mt-0.5">
                          IQ-Doctor Secure Clinical Registry Node
                        </p>
                      </div>
                      <div className="flex items-center gap-2 self-stretch sm:self-auto">
                        {/* Clinical Print Action: Triggers EHR Summarized PDF print flow */}
                        <button
                          type="button"
                          id="print-ehr-summary-btn"
                          onClick={() => {
                            try {
                              // Clinical Security: Validate that active patient profile exists and is populated
                              if (!selectedPatient) {
                                throw new Error(
                                  "No active patient patient-profile is currently loaded.",
                                );
                              }
                              // Set preview document type to full RECORD and display printable window
                              setPrintDocType("RECORD");
                              setPrintOpen(true);
                            } catch (error: any) {
                              // Robust Error Handling: Catch runtime exceptions and report gracefully
                              console.error(
                                "[Clinical Action Failure] Patient EHR summary generation blocked:",
                                error,
                              );
                              alert(
                                `Failed to load patient summary: ${error?.message || "Unknown validation error"}`,
                              );
                            }
                          }}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs px-4 py-2 rounded-lg shadow-sm hover:shadow-md transition active:translate-y-[0.5px] flex items-center gap-2 cursor-pointer grow sm:grow-0 justify-center"
                        >
                          <Printer className="w-3.5 h-3.5" />
                          <span>Print EHR Clinical Summary</span>
                        </button>
                        <span className="text-[10px] text-[#0F172A] font-semibold bg-slate-100 border border-[#E2E8F0] px-3 py-2 rounded-md uppercase hidden md:inline-block">
                          Dr. Watheq Al-Chalabi Clinic Archive
                        </span>
                      </div>
                    </div>

                    {historyLoading ? (
                      <div className="text-center py-10 font-medium text-xs text-slate-500">
                        Retrieving secure node records...
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                          {/* PAST SURGERIES COLUMN */}
                          <div className="space-y-4">
                            <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-2">
                              <Layers className="w-4 h-4 text-indigo-500" />
                              Past Surgeries / Procedures
                            </h4>

                            <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 rounded-xl space-y-4">
                              <h5 className="text-[11px] font-bold text-slate-800 uppercase tracking-wider">
                                Log New Procedure
                              </h5>
                              <div className="space-y-3">
                                <div className="grid grid-cols-3 gap-2">
                                  <div className="col-span-2">
                                    <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                      Procedure
                                    </label>
                                    <input
                                      type="text"
                                      value={newSurgery.procedure}
                                      onChange={(e) =>
                                        setNewSurgery({
                                          ...newSurgery,
                                          procedure: e.target.value,
                                        })
                                      }
                                      placeholder="e.g. Appendectomy"
                                      className="w-full bg-white border border-slate-200 text-xs px-2.5 py-1.5 rounded-md outline-none focus:border-indigo-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                      Year
                                    </label>
                                    <input
                                      type="text"
                                      value={newSurgery.year}
                                      onChange={(e) =>
                                        setNewSurgery({
                                          ...newSurgery,
                                          year: e.target.value,
                                        })
                                      }
                                      placeholder="2018"
                                      maxLength={4}
                                      className="w-full bg-white border border-slate-200 text-xs px-2.5 py-1.5 rounded-md outline-none text-center focus:border-indigo-500"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                    Surgical Notes
                                  </label>
                                  <input
                                    type="text"
                                    value={newSurgery.notes}
                                    onChange={(e) =>
                                      setNewSurgery({
                                        ...newSurgery,
                                        notes: e.target.value,
                                      })
                                    }
                                    placeholder="e.g. Post-op recovery normal..."
                                    className="w-full bg-[#FFFFFF] border border-slate-200 text-xs px-2.5 py-1.5 rounded-md outline-none focus:border-indigo-500"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!newSurgery.procedure) return;
                                    const updatedHistory: MedicalHistory = { ...medicalHistory,
                                      patientId: selectedPatient.id,
                                      surgeries: [
                                        ...(medicalHistory?.surgeries || []),
                                        {
                                          id: "s_" + Date.now().toString(36),
                                          procedure: newSurgery.procedure,
                                          year: newSurgery.year,
                                          notes: newSurgery.notes,
                                        },
                                      ],
                                      chronicConditions:
                                        medicalHistory?.chronicConditions || [],
                                      chronicMedications: medicalHistory?.chronicMedications || [],
                                          allergies: medicalHistory?.allergies || [],
                                    };
                                    handleUpdateMedicalHistory(updatedHistory);
                                    setNewSurgery({
                                      procedure: "",
                                      year: "",
                                      notes: "",
                                    });
                                  }}
                                  className="w-full bg-[#0F172A] hover:bg-slate-800 text-white font-semibold text-xs py-1.5 rounded-md shadow-xs transition cursor-pointer select-none text-center"
                                >
                                  Submit & Append Surgery
                                </button>
                              </div>
                            </div>

                            {/* LIST SURGERIES */}
                            <div className="space-y-2 max-h-[250px] overflow-y-auto pr-1">
                              {!(
                                medicalHistory?.surgeries &&
                                medicalHistory.surgeries.length > 0
                              ) ? (
                                <p className="text-slate-400 text-xs text-center py-6 border border-dashed border-[#E2E8F0] rounded-lg">
                                  No past surgeries reported.
                                </p>
                              ) : (
                                medicalHistory.surgeries.map((s) => (
                                  <div
                                    key={s.id}
                                    className="bg-white border border-[#E2E8F0] rounded-lg p-3.5 flex items-start justify-between shadow-xs"
                                  >
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-bold text-xs text-slate-800">
                                          {s.procedure}
                                        </span>
                                        <span className="text-[10px] font-mono bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded border border-indigo-150">
                                          {s.year}
                                        </span>
                                      </div>
                                      {s.notes && (
                                        <p className="text-slate-500 text-[11px] leading-relaxed">
                                          {s.notes}
                                        </p>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updatedHistory: MedicalHistory = { ...medicalHistory,
                                          patientId: selectedPatient.id,
                                          surgeries: (
                                            medicalHistory?.surgeries || []
                                          ).filter((item) => item.id !== s.id),
                                          chronicConditions:
                                            medicalHistory?.chronicConditions ||
                                            [],
                                          chronicMedications: medicalHistory?.chronicMedications || [],
                                          allergies: medicalHistory?.allergies || [],
                                        };
                                        handleUpdateMedicalHistory(
                                          updatedHistory,
                                        );
                                      }}
                                      className="p-1 text-slate-400 hover:text-rose-500 cursor-pointer transition"
                                      title="Delete/remove surgery entry"
                                    >
                                      <Trash className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>

                          {/* CHRONIC CONDITIONS COLUMN */}
                          <div className="space-y-4">
                            <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-2">
                              <Activity className="w-4 h-4 text-emerald-500" />
                              Chronic Conditions / Comorbidities
                            </h4>

                            <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 rounded-xl space-y-4">
                              <h5 className="text-[11px] font-bold text-slate-800 uppercase tracking-wider">
                                Log New Chronic Condition
                              </h5>
                              <div className="space-y-3">
                                <div className="grid grid-cols-3 gap-2">
                                  <div className="col-span-2">
                                    <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                      Condition
                                    </label>
                                    <input
                                      type="text"
                                      list={(newCondition.condition || "").length >= 2 ? "combined-medical-list" : undefined}
                                      value={newCondition.condition}
                                      onChange={(e) =>
                                        setNewCondition({
                                          ...newCondition,
                                          condition: e.target.value,
                                        })
                                      }
                                      placeholder="e.g. Hypertension"
                                      className="w-full bg-white border border-slate-200 text-xs px-2.5 py-1.5 rounded-md outline-none focus:border-emerald-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                      Diagnosed Year
                                    </label>
                                    <input
                                      type="text"
                                      value={newCondition.diagnosedYear}
                                      onChange={(e) =>
                                        setNewCondition({
                                          ...newCondition,
                                          diagnosedYear: e.target.value,
                                        })
                                      }
                                      placeholder="2019"
                                      maxLength={4}
                                      className="w-full bg-white border border-slate-200 text-xs px-2.5 py-1.5 rounded-md outline-none text-center focus:border-emerald-500"
                                    />
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 align-middle items-center">
                                  <div className="col-span-1">
                                    <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                      Status
                                    </label>
                                    <select
                                      value={newCondition.status}
                                      onChange={(e) =>
                                        setNewCondition({
                                          ...newCondition,
                                          status: e.target.value as any,
                                        })
                                      }
                                      className="w-full bg-white border border-slate-200 text-xs px-2 py-1.5 rounded-md outline-none text-[#1E293B]"
                                    >
                                      <option value="ACTIVE">Active</option>
                                      <option value="RESOLVED">Resolved</option>
                                      <option value="MANAGED">Managed</option>
                                    </select>
                                  </div>
                                  <div className="col-span-2">
                                    <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                      Condition Remarks / Rx
                                    </label>
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        list={(newCondition.tempRx || "").length >= 2 ? "bnf-meds-list" : undefined}
                                        value={newCondition.tempRx || ""}
                                        onChange={(e) =>
                                          setNewCondition({
                                            ...newCondition,
                                            tempRx: e.target.value,
                                          })
                                        }
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter" && (newCondition.tempRx || "").trim()) {
                                            e.preventDefault();
                                            setNewCondition(prev => ({
                                              ...prev,
                                              rxList: [...(prev.rxList || []), prev.tempRx.trim()],
                                              tempRx: ""
                                            }));
                                          }
                                        }}
                                        placeholder="Add medication..."
                                        className="flex-1 bg-white border border-slate-200 text-xs px-2.5 py-1.5 rounded-md outline-none focus:border-emerald-500"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if ((newCondition.tempRx || "").trim()) {
                                            setNewCondition(prev => ({
                                              ...prev,
                                              rxList: [...(prev.rxList || []), prev.tempRx.trim()],
                                              tempRx: ""
                                            }));
                                          }
                                        }}
                                        className="bg-emerald-50 hover:bg-emerald-100 text-emerald-600 px-3 py-1 rounded-md text-xs font-bold transition-colors border border-emerald-100 flex items-center justify-center"
                                      >
                                        <Plus className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                    {(newCondition.rxList || []).length > 0 && (
                                      <div className="flex flex-wrap gap-1.5 mt-2">
                                        {(newCondition.rxList || []).map((rx, idx) => (
                                          <span key={idx} className="bg-emerald-50 text-emerald-800 text-[10px] px-2 py-1 rounded flex items-center gap-1 border border-emerald-200">
                                            {rx}
                                            <button 
                                              type="button" 
                                              onClick={() => setNewCondition(prev => ({
                                                ...prev,
                                                rxList: (prev.rxList || []).filter((_, i) => i !== idx)
                                              }))}
                                              className="text-emerald-500 hover:text-emerald-900"
                                            >
                                              <X className="w-3 h-3" />
                                            </button>
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!newCondition.condition) return;
                                    const updatedHistory: MedicalHistory = { ...medicalHistory,
                                      patientId: selectedPatient.id,
                                      surgeries:
                                        medicalHistory?.surgeries || [],
                                      chronicMedications: medicalHistory?.chronicMedications || [],
                                          allergies: medicalHistory?.allergies || [],
                                      chronicConditions: [
                                        ...(medicalHistory?.chronicConditions ||
                                          []),
                                        {
                                          id: "cc_" + Date.now().toString(36),
                                          condition: newCondition.condition,
                                          diagnosedYear:
                                            newCondition.diagnosedYear,
                                          status: newCondition.status,
                                          notes: newCondition.notes + ((newCondition.rxList && newCondition.rxList.length > 0) ? "\nRX: " + newCondition.rxList.join(", ") : ""),
                                        },
                                      ],
                                    };
                                    handleUpdateMedicalHistory(updatedHistory);
                                    setNewCondition({
                                      condition: "",
                                      diagnosedYear: "",
                                      status: "ACTIVE",
                                      notes: "",
                                      rxList: [],
                                      tempRx: "",
                                    });
                                  }}
                                  className="w-full bg-emerald-600 hover:bg-[#059669] text-white font-semibold text-xs py-1.5 rounded-md shadow-xs transition cursor-pointer select-none text-center"
                                >
                                  Submit & Append Condition
                                </button>
                              </div>
                            </div>

                            {/* LIST CHRONIC CONDITIONS */}
                            <div className="space-y-2 max-h-[250px] overflow-y-auto pr-1">
                              {!(
                                medicalHistory?.chronicConditions &&
                                medicalHistory.chronicConditions.length > 0
                              ) ? (
                                <p className="text-slate-400 text-xs text-center py-6 border border-dashed border-[#E2E8F0] rounded-lg">
                                  No chronic conditions reported.
                                </p>
                              ) : (
                                medicalHistory.chronicConditions.map((cc) => (
                                  <div
                                    key={cc.id}
                                    className="bg-white border border-[#E2E8F0] rounded-lg p-3.5 flex items-start justify-between shadow-xs"
                                  >
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-bold text-xs text-slate-800">
                                          {cc.condition}
                                        </span>
                                        <span className="text-[9px] font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                                          Est: {cc.diagnosedYear}
                                        </span>
                                        <span
                                          className={`text-[8.5px] uppercase font-bold px-1.5 py-0.5 rounded border ${
                                            cc.status === "ACTIVE"
                                              ? "bg-rose-50 text-rose-700 border-rose-200"
                                              : cc.status === "MANAGED"
                                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                : "bg-slate-100 text-slate-600 border-slate-200"
                                          }`}
                                        >
                                          {cc.status}
                                        </span>
                                      </div>
                                      {cc.notes && (
                                        <p className="text-slate-500 text-[11px] leading-relaxed">
                                          {cc.notes}
                                        </p>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updatedHistory: MedicalHistory = { ...medicalHistory,
                                          patientId: selectedPatient.id,
                                          surgeries:
                                            medicalHistory?.surgeries || [],
                                          chronicMedications: medicalHistory?.chronicMedications || [],
                                          allergies: medicalHistory?.allergies || [],
                                          chronicConditions: (
                                            medicalHistory?.chronicConditions ||
                                            []
                                          ).filter((item) => item.id !== cc.id),
                                        };
                                        handleUpdateMedicalHistory(
                                          updatedHistory,
                                        );
                                      }}
                                      className="p-1 text-slate-400 hover:text-rose-500 cursor-pointer transition"
                                      title="Delete/remove chronic condition entry"
                                    >
                                      <Trash className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        </div>

                        {/* FAMILY AND SOCIAL HISTORY ROW */}
                        <div className="space-y-4 pt-4 border-t border-slate-200">
                          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                            <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                              <Activity className="w-4 h-4 text-emerald-500" />
                              Family & Social History
                            </h4>
                            <button
                              type="button"
                              onClick={() => {
                                if (medicalHistory) handleUpdateMedicalHistory(medicalHistory);
                              }}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition shadow-sm"
                            >
                              Save History
                            </button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                Family History
                              </label>
                              <textarea
                                rows={3}
                                value={medicalHistory?.familyHistory || ""}
                                onChange={(e) => {
                                  if (!medicalHistory) {
                                    setMedicalHistory({ patientId: selectedPatient?.id || "", surgeries: [], chronicConditions: [], allergies: [], familyHistory: e.target.value, socialHistory: "" });
                                  } else {
                                    setMedicalHistory({ ...medicalHistory, familyHistory: e.target.value });
                                  }
                                }}
                                placeholder="e.g., Diabetes, Heart Disease..."
                                className="w-full bg-white border border-[#E2E8F0] rounded-md p-3 font-medium text-xs outline-none focus:border-[#0F172A] transition-colors"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                Social History
                              </label>
                              <textarea
                                rows={3}
                                value={medicalHistory?.socialHistory || ""}
                                onChange={(e) => {
                                  if (!medicalHistory) {
                                    setMedicalHistory({ patientId: selectedPatient?.id || "", surgeries: [], chronicConditions: [], allergies: [], familyHistory: "", socialHistory: e.target.value });
                                  } else {
                                    setMedicalHistory({ ...medicalHistory, socialHistory: e.target.value });
                                  }
                                }}
                                placeholder="e.g., Smoking, Alcohol, Lifestyle..."
                                className="w-full bg-white border border-[#E2E8F0] rounded-md p-3 font-medium text-xs outline-none focus:border-[#0F172A] transition-colors"
                              />
                            </div>
                          </div>
                        </div>

                        {/* DRUG ALLERGIES ROW */}
                        <div className="space-y-4 pt-4 border-t border-slate-200">
                          <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-2">
                            <Info className="w-4 h-4 text-rose-500" />
                            Drug Allergies & Hypersensitivities
                          </h4>

                          <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 rounded-xl space-y-4">
                            <h5 className="text-[11px] font-bold text-slate-800 uppercase tracking-wider">
                              Log New Allergy
                            </h5>
                            <div className="space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 align-middle">
                                <div className="md:col-span-1">
                                  <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                    Severity
                                  </label>
                                  <select
                                    value={newAllergy.severity}
                                    onChange={(e) =>
                                      setNewAllergy({
                                        ...newAllergy,
                                        severity: e.target.value as any,
                                      })
                                    }
                                    className="w-full bg-white border border-slate-200 text-xs px-2 py-1.5 rounded-md outline-none text-[#1E293B]"
                                  >
                                    <option value="MILD">Mild</option>
                                    <option value="MODERATE">Moderate</option>
                                    <option value="SEVERE">Severe</option>
                                    <option value="CRITICAL">Critical</option>
                                  </select>
                                </div>
                                <div className="md:col-span-1">
                                  <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                    Drug / Allergen
                                  </label>
                                  <input
                                    type="text"
                                    list={(newAllergy.allergen || "").length >= 2 ? "bnf-meds-list" : undefined}
                                    value={newAllergy.allergen}
                                    onChange={(e) =>
                                      setNewAllergy({
                                        ...newAllergy,
                                        allergen: e.target.value,
                                      })
                                    }
                                    placeholder="e.g. Penicillin"
                                    className="w-full bg-white border border-slate-200 text-xs px-2.5 py-1.5 rounded-md outline-none focus:border-rose-500"
                                  />
                                </div>
                                <div className="md:col-span-2">
                                  <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                    Reaction Remarks
                                  </label>
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      value={newAllergy.reaction}
                                      onChange={(e) =>
                                        setNewAllergy({
                                          ...newAllergy,
                                          reaction: e.target.value,
                                        })
                                      }
                                      placeholder="e.g. Hives, anaphylaxis..."
                                      className="w-full bg-white border border-slate-200 text-xs px-2.5 py-1.5 rounded-md outline-none focus:border-rose-500"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (!newAllergy.allergen) return;
                                        const updatedHistory: MedicalHistory = { ...medicalHistory,
                                          patientId: selectedPatient.id,
                                          surgeries:
                                            medicalHistory?.surgeries || [],
                                          chronicConditions:
                                            medicalHistory?.chronicConditions ||
                                            [],
                                          allergies: [
                                            ...(medicalHistory?.allergies ||
                                              []),
                                            {
                                              id:
                                                "al_" + Date.now().toString(36),
                                              allergen: newAllergy.allergen,
                                              severity: newAllergy.severity,
                                              reaction: newAllergy.reaction,
                                            },
                                          ],
                                        };
                                        handleUpdateMedicalHistory(
                                          updatedHistory,
                                        );
                                        setNewAllergy({
                                          allergen: "",
                                          severity: "MILD",
                                          reaction: "",
                                        });
                                      }}
                                      className="bg-rose-600 hover:bg-rose-700 text-white font-semibold text-xs px-4 rounded-md shadow-xs transition cursor-pointer select-none whitespace-nowrap"
                                    >
                                      Add Allergy
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* LIST ALLERGIES */}
                          <div className="space-y-2 max-h-[250px] overflow-y-auto pr-1">
                            {!(
                              medicalHistory?.allergies &&
                              medicalHistory.allergies.length > 0
                            ) ? (
                              <p className="text-slate-400 text-xs text-center py-6 border border-dashed border-[#E2E8F0] rounded-lg">
                                No drug allergies reported.
                              </p>
                            ) : (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {medicalHistory.allergies.map((al) => (
                                  <div
                                    key={al.id}
                                    className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex items-start justify-between shadow-xs"
                                  >
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-bold text-xs text-slate-800">
                                          {al.allergen}
                                        </span>
                                        <span
                                          className={`text-[8px] uppercase font-bold px-1.5 py-0.5 rounded border ${
                                            al.severity === "CRITICAL"
                                              ? "bg-red-100 text-red-800 border-red-300"
                                              : al.severity === "SEVERE"
                                                ? "bg-rose-50 text-rose-700 border-rose-200"
                                                : al.severity === "MODERATE"
                                                  ? "bg-orange-50 text-orange-700 border-orange-200"
                                                  : "bg-yellow-50 text-yellow-700 border-yellow-200"
                                          }`}
                                        >
                                          {al.severity}
                                        </span>
                                      </div>
                                      {al.reaction && (
                                        <p className="text-slate-500 text-[10px] leading-relaxed">
                                          Reaction: {al.reaction}
                                        </p>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updatedHistory: MedicalHistory = { ...medicalHistory,
                                          patientId: selectedPatient.id,
                                          surgeries:
                                            medicalHistory?.surgeries || [],
                                          chronicConditions:
                                            medicalHistory?.chronicConditions ||
                                            [],
                                          allergies: (
                                            medicalHistory?.allergies || []
                                          ).filter((item) => item.id !== al.id),
                                        };
                                        handleUpdateMedicalHistory(
                                          updatedHistory,
                                        );
                                      }}
                                      className="p-1 text-slate-400 hover:text-rose-500 cursor-pointer transition"
                                      title="Delete/remove allergy entry"
                                    >
                                      <Trash className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}



                {/* 7. MEDICAL DOCUMENTS TAB */}
                {tab === "documents" && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-[#1E293B]">
                        المرفقات والمستندات الطبية
                      </h3>
                      <div>
                        <label className="bg-[#0F172A] hover:bg-slate-800 text-white font-bold text-xs py-2 px-4 rounded-md shadow-sm transition cursor-pointer flex items-center gap-2">
                          {isUploadingDoc ? "جاري الرفع..." : (
                            <>
                              <Plus className="w-3.5 h-3.5" /> إضافة مستند جديد
                            </>
                          )}
                          <input 
                            type="file" 
                            accept="image/*,application/pdf"
                            className="hidden" 
                            disabled={isUploadingDoc}
                            onChange={async (e) => {
                              if (!e.target.files || e.target.files.length === 0) return;
                              const file = e.target.files[0];
                              
                              setIsUploadingDoc(true);
                              try {
                                let finalData = "";
                                let finalType = file.type;

                                if (file.type.startsWith("image/")) {
                                  finalData = await new Promise<string>((resolve) => {
                                    const r = new FileReader();
                                    r.onload = (ev) => {
                                      const img = new Image();
                                      img.onload = () => {
                                        const canvas = document.createElement("canvas");
                                        let w = img.width;
                                        let h = img.height;
                                        // Compress if width > 1200px
                                        if (w > 1200) { h = Math.round((h * 1200) / w); w = 1200; }
                                        canvas.width = w; canvas.height = h;
                                        const ctx = canvas.getContext("2d");
                                        ctx?.drawImage(img, 0, 0, w, h);
                                        // 0.75 quality reduces size significantly while keeping clarity
                                        resolve(canvas.toDataURL("image/jpeg", 0.75));
                                      };
                                      img.src = ev.target?.result as string;
                                    };
                                    r.readAsDataURL(file);
                                  });
                                  finalType = "image/jpeg";
                                } else {
                                  finalData = await new Promise<string>((resolve) => {
                                    const r = new FileReader();
                                    r.onload = (ev) => resolve(ev.target?.result as string);
                                    r.readAsDataURL(file);
                                  });
                                }

                                const payload = {
                                  patientId: selectedPatient.id,
                                  title: file.name,
                                  fileType: finalType,
                                  fileData: finalData
                                };
                                const res = await fetch("/api/documents", {
                                  method: "POST",
                                  headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${apiToken}`
                                  },
                                  body: JSON.stringify(payload)
                                });
                                if (res.ok) {
                                  triggerSuccessToast("تم حفظ المستند بنجاح");
                                  fetchPatientDocuments(selectedPatient.id);
                                } else {
                                  const errorData = await res.json().catch(() => ({}));
                                  alert(errorData.error || "فشل رفع المستند. يرجى المحاولة مرة أخرى.");
                                }
                              } catch (err) {
                                console.error(err);
                              } finally {
                                setIsUploadingDoc(false);
                              }
                            }} 
                          />
                        </label>
                      </div>
                    </div>

                    {patientDocuments && patientDocuments.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {patientDocuments.map((doc, idx) => (
                          <div key={idx} className="bg-white border border-[#E2E8F0] rounded-lg p-3 shadow-sm hover:shadow-md transition group relative flex flex-col items-center text-center">
                            <button
                              onClick={async () => {
                                if (window.confirm("هل أنت متأكد من حذف هذا المستند؟")) {
                                  try {
                                    await fetch(`/api/documents/${doc.id}`, {
                                      method: "DELETE",
                                      headers: { Authorization: `Bearer ${apiToken}` }
                                    });
                                    fetchPatientDocuments(selectedPatient.id);
                                  } catch (err) { console.error(err); }
                                }
                              }}
                              className="absolute top-2 left-2 bg-white/80 p-1.5 rounded text-rose-500 opacity-0 group-hover:opacity-100 transition hover:bg-rose-50"
                            >
                              <Trash className="w-3 h-3" />
                            </button>
                            
                            {doc.fileType === "application/pdf" ? (
                              <div className="w-20 h-20 bg-rose-50 text-rose-500 rounded-md flex items-center justify-center mb-2">
                                <FileText className="w-8 h-8" />
                              </div>
                            ) : (
                              <img src={doc?.fileUrl ? (doc.fileUrl.startsWith('/api') ? doc.fileUrl : "/api" + doc.fileUrl) : ""} alt={doc?.title || ""} className="w-20 h-20 object-cover rounded-md mb-2 border border-slate-200" />
                            )}
                            <div className="w-full truncate text-[11px] font-bold text-slate-700" title={doc?.title || ""}>{doc?.title || ""}</div>
                            <div className="text-[9px] text-slate-500 mt-1">{doc?.uploadDate ? new Date(doc.uploadDate).toLocaleDateString("en-GB") : ""}</div>
                            
                            {doc?.fileUrl && (
                              <a href={doc.fileUrl.startsWith('/api') ? doc.fileUrl : "/api" + doc.fileUrl} target="_blank" rel="noreferrer" className="mt-2 text-[10px] bg-indigo-50 text-indigo-700 w-full py-1.5 rounded hover:bg-indigo-100 font-bold transition flex items-center justify-center">
                                {"\u0639\u0631\u0636"}
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-[#F8FAFC] border-2 border-dashed border-[#CBD5E1] rounded-xl p-8 text-center text-[#64748B]">
                        <FolderOpen className="w-10 h-10 mx-auto text-[#94A3B8] mb-3" />
                        <h4 className="font-bold text-[#1E293B] text-sm">{"\u0644\u0627 \u062A\u0648\u062C\u062F \u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0637\u0628\u064A\u0629"}</h4>
                        <p className="text-xs mt-1">{"\u0644\u0645 \u064A\u062A\u0645 \u0625\u0631\u0641\u0627\u0642 \u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0637\u0628\u064A\u0629 \u0644\u0647\u0630\u0627 \u0627\u0644\u0645\u0631\u064A\u0636 \u062D\u062A\u0649 \u0627\u0644\u0622\u0646."}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* 8. PATIENT FOLLOW-UP & LEAFLETS TAB */}
                {tab === "followup" && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-[#1E293B]">
                        Patient Follow-up & Leaflets
                      </h3>
                      <span className="text-[9px] bg-[#F8FAFC] border border-[#E2E8F0] px-2 py-0.5 rounded-full text-[#64748B] font-mono">
                        Patient Education Hub
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 rounded-xl space-y-4 md:col-span-2">
                        <h4 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-2">
                          <BookOpen className="w-4 h-4 text-emerald-500" />{" "}
                          Information & Follow-up Instructions (Arabic)
                        </h4>

                        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mt-6">
                          <h4 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-2 mb-4">
                            <Sparkles className="w-4 h-4 text-indigo-500" />{" "}
                            Generate Follow-up Instructions or Patient Leaflet
                          </h4>
                          <p className="text-[10px] text-slate-500 mb-3">
                            AI will generate a concise Arabic follow-up
                            instruction guide based on the patient's current
                            diagnosed conditions, plus any specific instructions
                            you write below.
                          </p>

                          {gpDiagnoses.length > 0 && (
                            <div className="mb-3 p-2 bg-indigo-50 border border-indigo-100 rounded text-[10px] text-indigo-800 font-medium flex items-center gap-1.5">
                              <Info className="w-3 h-3" /> Auto-detecting
                              current diagnoses:{" "}
                              {gpDiagnoses.map((d) => d.disorder).join(", ")}
                            </div>
                          )}

                          <div className="flex flex-col gap-3">
                            <textarea
                              value={doctorInstructions}
                              onChange={(e) =>
                                setDoctorInstructions(e.target.value)
                              }
                              placeholder="Write specific instructions to the patient here (optional). AI will combine this concisely with their diagnosed conditions."
                              className="w-full bg-slate-50 border border-slate-200 text-xs px-3 py-2 rounded-lg outline-none focus:border-indigo-500 focus:bg-white"
                              rows={3}
                            />
                            <div className="flex gap-2">
                              <select
                                value=""
                                onChange={(e) => {
                                  if (e.target.value && leafletTemplates[e.target.value]) {
                                    setCustomLeafletResult(leafletTemplates[e.target.value]);
                                  }
                                }}
                                className="bg-indigo-50 border border-indigo-200 text-indigo-900 font-bold text-xs px-3 py-2 rounded-lg outline-none cursor-pointer"
                              >
                                <option value="">-- تحميل قالب محفوظ --</option>
                                {Object.keys(leafletTemplates).map((name) => (
                                  <option key={name} value={name}>{name}</option>
                                ))}
                              </select>
                              <input
                                type="text"
                                value={customLeafletQuery}
                                onChange={(e) =>
                                  setCustomLeafletQuery(e.target.value)
                                }
                                placeholder="Or enter a generic topic here (e.g., Hypertension, Diabetes)..."
                                className="flex-1 bg-slate-50 border border-slate-200 text-xs px-3 py-2 rounded-lg outline-none focus:border-indigo-500 focus:bg-white"
                              />
                              <button
                                onClick={handleGenerateLeaflet}
                                disabled={customLeafletLoading}
                                className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-400 text-white font-semibold text-xs px-4 py-2 rounded-lg shadow-sm transition whitespace-nowrap cursor-pointer"
                              >
                                {customLeafletLoading
                                  ? "Generating..."
                                  : "Generate Instructions"}
                              </button>
                            </div>
                          </div>

                          {customLeafletResult && (
                            <div className="mt-4 p-4 bg-[#F8FAFC] border border-indigo-100 rounded-lg max-h-[300px] overflow-y-auto">
                              <textarea
                                className="w-full min-h-[250px] bg-transparent border-0 outline-none resize-none text-right font-sans text-sm text-slate-800 leading-relaxed"
                                dir="rtl"
                                value={customLeafletResult}
                                onChange={(e) => setCustomLeafletResult(e.target.value)}
                                spellCheck={false}
                              />
                              <div className="mt-4 flex justify-end gap-2">
                                <button
                                  onClick={() => {
                                    if (customLeafletResult) {
                                      const name = window.prompt("أدخل اسم القالب (مثال: تعليمات مرض السكري):");
                                      if (name) {
                                        const newTemplates = { ...leafletTemplates, [name]: customLeafletResult };
                                        setLeafletTemplates(newTemplates);
                                        localStorage.setItem("leaflet_templates", JSON.stringify(newTemplates));
                                        fetch("/api/leaflet-templates", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` }, body: JSON.stringify(newTemplates) }).catch(console.error);
                                        alert("تم حفظ القالب بنجاح.");
                                      }
                                    }
                                  }}
                                  className="bg-emerald-500 text-white hover:bg-emerald-600 font-bold px-3 py-1.5 text-[10px] uppercase tracking-wider rounded flex items-center gap-1.5"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                                  حفظ كقالب جديد
                                </button>
                                <button
                                  onClick={() => {
                                    const name = window.prompt("لحذف قالب محفوظ، أدخل اسمه هنا (أو اتركه فارغاً لمسح الشاشة فقط):");
                                    if (name) {
                                      if (leafletTemplates[name]) {
                                        if (window.confirm(`هل أنت متأكد من حذف قالب "${name}"؟`)) {
                                          const newTemplates = { ...leafletTemplates };
                                          delete newTemplates[name];
                                          setLeafletTemplates(newTemplates);
                                          localStorage.setItem("leaflet_templates", JSON.stringify(newTemplates));
                                          fetch("/api/leaflet-templates", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` }, body: JSON.stringify(newTemplates) }).catch(console.error);
                                          setCustomLeafletResult(null);
                                          alert("تم حذف القالب بنجاح.");
                                        }
                                      } else {
                                        alert("لم يتم العثور على قالب بهذا الاسم.");
                                      }
                                    } else if (name !== null) {
                                      setCustomLeafletResult(null);
                                    }
                                  }}
                                  className="bg-red-500 text-white hover:bg-red-600 font-bold px-3 py-1.5 text-[10px] uppercase tracking-wider rounded flex items-center gap-1.5"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                                  مسح / حذف قالب
                                </button>
                                <button
                                  onClick={() => {
                                    if (!selectedPatient?.phone) {
                                      alert("لا يوجد رقم هاتف مسجل لهذا المريض.");
                                      return;
                                    }
                                    const cleanPhone = selectedPatient.phone.replace(/\D/g, "");
                                    const waUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(customLeafletResult)}`;
                                    window.open(waUrl, "_blank");
                                  }}
                                  className="bg-[#25D366] text-white hover:bg-[#128C7E] font-bold px-3 py-1.5 text-[10px] uppercase tracking-wider rounded flex items-center gap-1.5"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                                  WhatsApp
                                </button>
                                <button
                                  onClick={() => {
                                    if (!selectedPatient?.phone) {
                                      alert("لا يوجد رقم هاتف مسجل لهذا المريض.");
                                      return;
                                    }
                                    const cleanPhone = selectedPatient.phone.replace(/\D/g, "");
                                    navigator.clipboard.writeText(customLeafletResult).then(() => {
                                      alert("تم نسخ التعليمات! سيتم فتح تليغرام الآن، قم بلصق النص وإرساله.");
                                      window.open(`https://t.me/+${cleanPhone}`, "_blank");
                                    });
                                  }}
                                  className="bg-[#0088cc] text-white hover:bg-[#0077b5] font-bold px-3 py-1.5 text-[10px] uppercase tracking-wider rounded flex items-center gap-1.5"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 24c6.627 0 12-5.373 12-12S18.627 0 12 0 0 5.373 0 12s5.373 12 12 12zm5.894-15.65c.427-.27.818-.08.665.48l-2.023 9.53c-.15.67-.532.83-.984.58l-2.72-2.01-1.31 1.26c-.15.15-.27.27-.55.27l.19-2.77 5.04-4.55c.22-.2.05-.31-.34-.05l-6.22 3.91-2.69-.84c-.58-.18-.59-.58.12-.86l10.49-4.04z"/></svg>
                                  Telegram
                                </button>
                                <button
                                  onClick={() => {
                                    setPrintDocType("LEAFLET_CUSTOM" as any);
                                    setPrintOpen(true);
                                  }}
                                  className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 font-bold px-3 py-1.5 text-[10px] uppercase tracking-wider rounded flex items-center gap-1.5"
                                >
                                  <Printer className="w-3.5 h-3.5" /> Print
                                  Custom Leaflet
                                </button>
                                <button
                                  onClick={() => setReferralOpen(true)}
                                  className="bg-indigo-600 text-white hover:bg-indigo-700 font-bold px-3 py-1.5 text-[10px] uppercase tracking-wider rounded flex items-center gap-1.5"
                                >
                                  <FileText className="w-3.5 h-3.5" /> Referral
                                  Letter
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
              {/* Left explanation card */}
              <div className="xl:col-span-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-6 flex flex-col justify-center items-center text-center shadow-sm">
                <ClipboardList className="w-12 h-12 text-slate-400 mb-4" />
                <h3 className="font-bold text-[#0F172A] text-xs uppercase tracking-wider text-[#64748B] mb-2">
                  No Active Patient Selected
                </h3>
                <p className="text-[#64748B] text-xs leading-relaxed max-w-sm">
                  الرجاء اختيار مريض من قائمة حجز العيادة على اليسار، أو استخدام
                  النموذج على اليمين لتسجيل مريض جديد مباشرة وبدء الاستشارة دون
                  تأخير.
                </p>
                <div className="mt-4 text-[10px] text-slate-400 border-t border-slate-200 pt-4 w-full font-mono">
                  الدكتور واثق الجلبي • Dr. Watheq Al-Chalabi Clinic Digital Platform
                </div>
              </div>

              {/* Quick Patient Registration Card */}
              <div className="xl:col-span-3 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-sm">
                {/* Search Existing Patient to Book Room directly */}
                <div className="mb-6 bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <h3 className="font-bold text-[#1E293B] text-xs flex items-center gap-2 mb-3">
                    <Search className="w-4 h-4 text-indigo-600" />
                    البحث السريع للمرضى المسجلين مسبقاً (Doctor Quick Search)
                  </h3>
                  <input
                    type="text"
                    placeholder="Search by Name or Phone (ابحث بالاسم أو رقم الهاتف)..."
                    value={quickSearchStr}
                    onChange={(e) => setQuickSearchStr(e.target.value)}
                    className="w-full bg-white border border-[#E2E8F0] text-xs text-[#1E293B] font-semibold px-3 py-2 rounded-md outline-none focus:border-indigo-600 transition"
                  />
                  {quickSearchStr.trim().length >= 3 && (
                    <div className="mt-2 flex flex-col gap-2 max-h-40 overflow-y-auto">
                      {patients
                        .filter((p) => {
                          const q =
                            normalizeArabic(quickSearchStr).toLowerCase();
                          const pName = normalizeArabic(
                            String(p.name || ""),
                          ).toLowerCase();
                          return (
                            pName.includes(q) ||
                            (p.phone && String(p.phone).includes(q)) ||
                            (p.nationalId && String(p.nationalId).includes(q))
                          );
                        })
                        .map((p) => (
                          <div
                            key={p.id}
                            className="flex items-center justify-between bg-white border border-slate-200 p-2 rounded-lg"
                          >
                            <div>
                              <div className="text-xs font-bold text-slate-800">
                                {p.name}
                              </div>
                              <div className="text-[10px] text-slate-500 font-mono">
                                {p.phone || (() => {
                                  if (!p.name) return "بدون رقم";
                                  const parts = p.name.trim().split(" ");
                                  if (parts.length < 2) return "بدون رقم";
                                  const lastName = parts[parts.length - 1];
                                  const familyMember = patients.find(other => other.id !== p.id && other.name && other.name.trim().endsWith(lastName) && other.phone);
                                  return familyMember ? `(مرتبط بـ ${familyMember.phone})` : "بدون رقم";
                                })()} | Age: {p.age} | {p.gender}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                handlePhysicianOneClickCheckIn(p);
                                setQuickSearchStr("");
                              }}
                              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-1 px-3 rounded text-[10px] cursor-pointer"
                            >
                              Book Visit & Launch EHR
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                <div className="border-b border-slate-100 pb-3 mb-4">
                  <h3 className="font-bold text-[#1E293B] text-sm flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-emerald-600 animate-pulse" />
                    <span>سجل مريضاً جديداً وافتح السجل الطبي فوراً</span>
                  </h3>
                  <p className="text-[10px] text-[#64748B] mt-0.5 font-mono">
                    Quick Register New Patient & Instantly Launch EHR Workspace
                  </p>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const normInputName = normalizeArabic(regName);
                    const normInputPhone = regPhone
                      .trim()
                      .replace(/[\s\-\(\)\+]/g, "");
                    const isDuplicate = patients.some((p) => {
                      const pName = normalizeArabic(String(p.name || ""));
                      const pPhone = String(p.phone || "").replace(
                        /[\s\-\(\)\+]/g,
                        "",
                      );
                      return (
                        (pName && pName === normInputName) ||
                        (pPhone &&
                          pPhone === normInputPhone &&
                          normInputPhone.length > 5)
                      );
                    });
                    if (isDuplicate) {
                      alert(
                        "⚠️ Critical Warning: This patient name or phone is already registered in the system. Use the Quick Search above to Book Visit/Launch EHR instead of creating a double registration.",
                      );
                      return;
                    }
                    handleQuickRegisterPatient(e);
                  }}
                  className="space-y-4"
                >
                  {(() => {
                    if (
                      !regName.trim() &&
                      !regPhone.trim() &&
                      !regNationalId.trim()
                    )
                      return null;
                    const normInputName = normalizeArabic(regName);
                    const normInputPhone = regPhone
                      .trim()
                      .replace(/[\s\-\(\)\+]/g, "");
                    const normInputNationalId = regNationalId
                      .trim()
                      .replace(/[\s-]/g, "");

                    const matched = patients.find((p) => {
                      if (!p) return false;
                      const normPName = normalizeArabic(String(p.name || ""));
                      const normPPhone = String(p.phone || "").replace(
                        /[\s\-\(\)\+]/g,
                        "",
                      );
                      const normPNat = String(p.nationalId || "").replace(
                        /[\s-]/g,
                        "",
                      );

                      const nameMatch =
                        normInputName.length >= 4 &&
                        (normPName.includes(normInputName) ||
                          normInputName.includes(normPName));
                      const phoneMatch =
                        normInputPhone.length >= 7 &&
                        normPPhone.includes(normInputPhone);
                      const nationalIdMatch =
                        normInputNationalId.length >= 5 &&
                        normPNat.includes(normInputNationalId);

                      return nameMatch || phoneMatch || nationalIdMatch;
                    });

                    if (!matched) return null;

                    return (
                      <div className="bg-amber-50 border border-amber-200 p-3.5 rounded-xl text-amber-900 text-xs flex flex-col gap-2 animate-fade-in text-right">
                        <div className="flex items-center gap-1.5 font-bold justify-end">
                          <span>
                            ⚠️ تنبيه من سجلات العيادة: المريض مسجل مسبقاً!
                          </span>
                        </div>
                        <p className="text-[10.5px] text-amber-800 leading-normal">
                          تم العثور على مريض مطابق مسجل باسم{" "}
                          <strong>{matched.name}</strong> ورقم الهاتف{" "}
                          {matched.phone}. لتفادي التكرار وسرعة الخدمة السريرية،
                          يمكنك تسجيل زيارة جديدة واستدعاء سجله فوراً بضغطة
                          واحدة مع تحديث عمره تلقائياً.
                        </p>
                        <button
                          type="button"
                          onClick={() =>
                            handlePhysicianOneClickCheckIn(matched)
                          }
                          className="w-full py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg text-xs cursor-pointer transition flex items-center justify-center gap-1 select-none"
                        >
                          ⚡ تسجيل زيارة جديدة وفتح ملف المريض بضغطة واحدة
                        </button>
                      </div>
                    );
                  })()}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-[#64748B] uppercase tracking-wider mb-1">
                        الاسم الكامل للمريض *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="مثل: أحمد علي محمود"
                        value={regName}
                        onChange={(e) => setRegName(e.target.value)}
                        className="w-full text-xs font-medium border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F8FAFC] text-[#0F172A] focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-[#64748B] uppercase tracking-wider mb-1">
                        رقم الهاتف للمريض *
                      </label>
                      <input
                        type="tel"
                        required
                        placeholder="مثل: +9647801234567"
                        value={regPhone}
                        onChange={(e) => setRegPhone(e.target.value)}
                        className="w-full text-xs font-mono border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F8FAFC] text-[#0F172A] focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-[#64748B] uppercase tracking-wider mb-1">
                        تاريخ الميلاد *
                      </label>
                      <input
                        type="date"
                        required
                        value={regDob}
                        onChange={(e) => {
                          const dobVal = e.target.value;
                          setRegDob(dobVal);
                          try {
                            /* Clinical Security & Validation: Defensively check date integrity to ensure correct bio-metrics output */
                            if (dobVal) {
                              const birthDate = new Date(dobVal);
                              if (!isNaN(birthDate.getTime())) {
                                const today = new Date();
                                let calculatedAge =
                                  today.getFullYear() - birthDate.getFullYear();
                                const monthDiff =
                                  today.getMonth() - birthDate.getMonth();

                                // Refine numerical years representation based on chronological timing
                                if (
                                  monthDiff < 0 ||
                                  (monthDiff === 0 &&
                                    today.getDate() < birthDate.getDate())
                                ) {
                                  calculatedAge--;
                                }

                                /* Expert Commentary: Automatically populate registration age field to speed up clinical workflow */
                                setRegAge(
                                  Math.max(0, calculatedAge).toString(),
                                );
                              }
                            }
                          } catch (error) {
                            /* Robust Error Handling: Contain date verification failures gracefully to prevent app crashes */
                            console.error(
                              "Clinical System Alert: Failed to auto-calculate billing / diagnostic age from birthdate.",
                              error,
                            );
                          }
                        }}
                        className="w-full text-xs font-mono border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F8FAFC] text-[#0F172A] focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-[#64748B] uppercase tracking-wider mb-1">
                        الرقم الوطني / الهوية المدنية
                      </label>
                      <input
                        type="text"
                        placeholder="اختياري"
                        value={regNationalId}
                        onChange={(e) => setRegNationalId(e.target.value)}
                        className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F8FAFC] text-[#0F172A] focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-[#64748B] uppercase tracking-wider mb-1">
                        الجنس
                      </label>
                      <select
                        value={regGender}
                        onChange={(e: any) => setRegGender(e.target.value)}
                        className="w-full text-xs border border-[#E2E8F0] rounded-lg px-2.5 py-1.5 bg-[#F8FAFC] text-[#0F172A] focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition"
                      >
                        <option value="">-- اختر --</option>
                        <option value="MALE">ذكر / Male</option>
                        <option value="FEMALE">أنثى / Female</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-[#64748B] uppercase tracking-wider mb-1">
                        العمر (سنوات)
                      </label>
                      <input
                        type="number"
                        placeholder="تلقائي"
                        value={regAge}
                        onChange={(e) => setRegAge(e.target.value)}
                        className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-1.5 bg-[#F8FAFC] text-[#0F172A] focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-[#64748B] uppercase tracking-wider mb-1">
                        فصيلة الدم
                      </label>
                      <input
                        type="text"
                        placeholder="A+ / B / etc"
                        value={regBloodType}
                        onChange={(e) => setRegBloodType(e.target.value)}
                        className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-1.5 bg-[#F8FAFC] text-[#0F172A] focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-[10px] font-bold text-[#64748B] uppercase tracking-wider mb-1">
                        الحساسيات المعروفة أو الأمراض المزمنة
                      </label>
                      <input
                        type="text"
                        placeholder="البنسلين، حساسية القمح، الغدة"
                        value={regAllergies}
                        onChange={(e) => setRegAllergies(e.target.value)}
                        className="w-full text-xs border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F8FAFC] text-[#0F172A] focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-[#64748B] uppercase tracking-wider mb-1">
                        وجهة الدخول
                      </label>
                      <select
                        value={regDept}
                        onChange={(e: any) => setRegDept(e.target.value)}
                        className="w-full text-xs border border-[#E2E8F0] rounded-lg px-2 text-center py-2 bg-[#F8FAFC] text-[#0F172A] focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition font-bold"
                      >
                        <option value="GP">الاستشارات العامة GP</option>
                        <option value="DERMATOLOGY">قسم الجلدية وبوتوكس</option>
                      </select>
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={isRegistering}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-bold text-xs py-3 rounded-lg transition shadow-sm flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {isRegistering ? (
                        <span className="animate-spin items-center justify-center mr-2 border-2 border-white border-t-transparent rounded-full w-4 h-4" />
                      ) : (
                        <UserPlus className="w-4 h-4" />
                      )}
                      <span>
                        إنشاء السجل الطبي والملف وفتح الاستشارة الآن | Create
                        EHR Record & Launch Workspace
                      </span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>

      
      {/* DATALISTS FOR MEDICAL HISTORY */}
      <datalist id="common-diseases-list">
        {COMMON_CHRONIC_DISEASES.map((d, i) => <option key={i} value={d} />)}
      </datalist>
      <datalist id="bnf-meds-list">
        {bnfCatalog.map((d, i) => <option key={i} value={d.name} />)}
      </datalist>
      <datalist id="combined-medical-list">
        {COMMON_CHRONIC_DISEASES.map((d, i) => <option key={'d'+i} value={d} />)}
        {bnfCatalog.map((d, i) => <option key={'m'+i} value={d.name} />)}
      </datalist>

      {/* GLOBAL CHECK MODAL */}
      {showGlobalCheckModal && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-200 animate-in fade-in zoom-in duration-200">
            <div className="bg-slate-900 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-indigo-400" />
                <h3 className="text-white font-bold tracking-wide">
                  Deep Global Check Report (AI CDSS)
                </h3>
              </div>
              <button
                onClick={() => setShowGlobalCheckModal(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto bg-slate-50 flex-1">
              {globalCheckResult && globalCheckResult.length > 0 ? (
                <div className="space-y-4">
                  {globalCheckResult.map((interaction, idx) => (
                    <div key={idx} className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-800 text-sm">
                            {interaction.drugA}
                          </span>
                          <span className="text-slate-400">↔</span>
                          <span className="font-bold text-slate-800 text-sm">
                            {interaction.drugB}
                          </span>
                        </div>
                        <span className={`text-[10px] font-extrabold uppercase px-2 py-1 rounded border ${
                          interaction.severity === 'CRITICAL' || interaction.severity === 'SERIOUS' ? 'bg-rose-100 text-rose-700 border-rose-300' :
                          interaction.severity === 'MODERATE' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                          'bg-blue-100 text-blue-700 border-blue-300'
                        }`}>
                          {interaction.severity}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mb-2 leading-relaxed">
                        <span className="font-semibold text-slate-700">Mechanism:</span> {interaction.mechanism}
                      </p>
                      <div className="bg-indigo-50 border border-indigo-100 p-3 rounded-md">
                        <p className="text-xs text-indigo-900 font-medium">
                          <span className="font-bold text-indigo-700 mr-1">Action:</span>
                          {interaction.action}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                    <Check className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-800 mb-2">No Serious Interactions Detected</h4>
                  <p className="text-slate-500 text-sm max-w-sm">
                    Based on global clinical databases, this combination appears safe to prescribe.
                  </p>
                </div>
              )}
            </div>
            
            <div className="border-t border-slate-200 p-4 bg-white flex justify-end">
              <button
                onClick={() => setShowGlobalCheckModal(false)}
                className="px-6 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-md font-bold text-sm transition-colors shadow-sm"
              >
                Close Report
              </button>
            </div>
          </div>
        </div>
      )}

      {showDrugInfoModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0F172A]/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden flex flex-col zoom-in-95 animate-in duration-200">
            <div className="flex items-center justify-between p-4 border-b border-indigo-100 bg-indigo-50">
              <div className="flex items-center gap-2 text-indigo-900">
                <Info className="w-5 h-5 text-indigo-600" />
                <h3 className="font-bold text-sm tracking-tight">
                  BNF Drug Information
                </h3>
              </div>
              <button
                onClick={() => setShowDrugInfoModal(null)}
                className="text-slate-500 hover:text-slate-900 transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4 text-sm text-slate-700 bg-white max-h-[460px] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                    Drug Name
                  </span>
                  <p className="font-bold text-slate-900">
                    {showDrugInfoModal.name}
                  </p>
                </div>
                <div>
                  <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                    BNF Chapter
                  </span>
                  <span className="inline-block px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[10px] font-serif font-bold rounded mt-0.5 border border-indigo-100 uppercase">
                    {(showDrugInfoModal as any).category || "General"}
                  </span>
                </div>
              </div>
              <div>
                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                  Indication
                </span>
                <p className="font-medium">{showDrugInfoModal.indication}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                    Typical Dose
                  </span>
                  <p className="font-mono text-xs font-semibold text-slate-800">
                    {showDrugInfoModal.defaultDose}
                  </p>
                </div>
                <div>
                  <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                    Typical Freq.
                  </span>
                  <p className="font-mono text-xs font-semibold text-slate-800">
                    {showDrugInfoModal.defaultFreq}
                  </p>
                </div>
              </div>
              {showDrugInfoModal.info && (
                <div className="pt-2 border-t border-slate-100">
                  <span className="block text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <Info className="w-3 h-3 text-blue-500" /> Mechanism of
                    Action & Notes
                  </span>
                  <p className="text-xs leading-relaxed text-slate-600 font-medium">
                    {showDrugInfoModal.info}
                  </p>
                </div>
              )}
              {(showDrugInfoModal as any).sideEffects && (
                <div className="pt-2 border-t border-slate-100">
                  <span className="block text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <Activity className="w-3 h-3 text-amber-500" /> Common Side
                    Effects (BNF)
                  </span>
                  <p className="text-xs leading-relaxed text-slate-600 font-medium">
                    {(showDrugInfoModal as any).sideEffects}
                  </p>
                </div>
              )}
              {(showDrugInfoModal as any).contraindications && (
                <div className="pt-2 border-t border-rose-100">
                  <span className="block text-[10px] font-bold text-rose-600 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <X className="w-3 h-3 text-rose-500" /> Key
                    Contraindications & safety
                  </span>
                  <p className="text-xs leading-relaxed text-rose-700 font-semibold bg-rose-50/50 p-1.5 rounded border border-rose-100">
                    {(showDrugInfoModal as any).contraindications}
                  </p>
                </div>
              )}
            </div>
            <div className="p-3 bg-slate-50 border-t border-slate-200 flex justify-end">
              <button
                onClick={() => setShowDrugInfoModal(null)}
                className="px-4 py-1.5 font-bold text-xs bg-slate-200 hover:bg-slate-300 text-slate-700 rounded transition cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {referralOpen && selectedPatient && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="font-bold text-sm">Referral Letter</h2>
              <button onClick={() => setReferralOpen(false)}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              <ReferralGenerator
                patientName={selectedPatient.name}
                patientAge={selectedPatient.age}
              />
            </div>
          </div>
        </div>
      )}



      {isAdminSettingsOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0F172A]/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col zoom-in-95 animate-in duration-200">
            <div className="flex items-center justify-between p-4 border-b border-[#E2E8F0] bg-[#F8FAFC] shrink-0">
              <div className="flex items-center gap-2 text-[#0F172A]">
                <Lock className="w-5 h-5 text-indigo-600" />
                <h3 className="font-bold text-sm tracking-tight text-[#1E293B]">
                  Admin Control Panel
                </h3>
              </div>
              <button
                onClick={() => setIsAdminSettingsOpen(false)}
                className="text-[#64748B] hover:text-[#0F172A] p-1 rounded-md hover:bg-[#E2E8F0] transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-xs font-bold text-[#1E293B] mb-1.5 uppercase tracking-wide">
                  Target User Account
                </label>
                <select
                  value={targetUserEmail}
                  onChange={(e) => setTargetUserEmail(e.target.value)}
                  className="w-full bg-white border border-[#E2E8F0] focus:border-indigo-500 text-sm px-3 py-2 rounded-lg outline-none transition"
                >
                  <option value="">-- اختر المستخدم (Select User) --</option>
                  {adminUsersList.map((u) => (
                    <option key={u.email} value={u.email}>
                      {u.name} - ({u.role}) - {u.email}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-500 mt-1">
                  Select the registered system account.
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#1E293B] mb-1.5 uppercase tracking-wide">
                  New Username
                </label>
                <input
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="Enter new alphanumeric username"
                  className="w-full bg-white border border-[#E2E8F0] focus:border-indigo-500 text-sm px-3 py-2 rounded-lg outline-none transition"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  For secure custom login (at least 3 characters).
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#1E293B] mb-1.5 uppercase tracking-wide">
                  New Password
                </label>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter secure new password"
                  className="w-full bg-white border border-[#E2E8F0] focus:border-indigo-500 text-sm px-3 py-2 rounded-lg outline-none transition"
                />
              </div>

              <div className="border-t border-[#E2E8F0] pt-4 mt-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={adminEditOverride}
                      onChange={async (e) => {
                        const val = e.target.checked;
                        setAdminEditOverride(val);
                        await fetch("/api/settings", {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${apiToken}`,
                          },
                          body: JSON.stringify({ adminEditOverride: val }),
                        });
                        triggerSuccessToast(
                          `Global Edit Override ${val ? "Enabled" : "Disabled"}`,
                        );
                      }}
                    />
                    <div
                      className={`block w-10 h-6 rounded-full transition ${adminEditOverride ? "bg-indigo-600" : "bg-slate-300"}`}
                    ></div>
                    <div
                      className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition transform ${adminEditOverride ? "translate-x-4" : ""}`}
                    ></div>
                  </div>
                  <div>
                    <span className="block text-xs font-bold text-[#1E293B] uppercase tracking-wide">
                      Allow Standard Users 24h+ Edits
                    </span>
                    <span className="block text-[10px] text-slate-500">
                      Overrides the default 24-hour lockout security policy
                      globally.
                    </span>
                  </div>
                </label>
              </div>

              {/* EHR LONG-TERM DATABASES PRESERVATION & RECOVERY PANEL */}
              <div className="border-t border-[#E2E8F0] pt-4 mt-2 space-y-3">
                <h4 className="text-xs font-bold text-[#1E293B] uppercase tracking-wide flex items-center gap-1.5">
                  🛡️ EHR Data Preservation (5+ Years Guard)
                </h4>
                <p className="text-[10px] text-slate-500 leading-relaxed font-semibold">
                  تصدير وحفظ أو استعادة السجلات الطبية السريرية للمرضى والزيارات
                  المتكاملة والروشتات لضمان بقائها دائمياً لـ 5 سنوات أو أكثر.
                </p>
                <div className="flex flex-col sm:flex-row gap-2.5 pt-1">
                  <button
                    onClick={handleExportBackup}
                    disabled={adminSettingLoading}
                    className="flex-1 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white border border-slate-900 font-bold text-[11px] px-3.5 py-2.5 rounded-lg transition active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer shadow-sm"
                  >
                    <Download className="w-3.5 h-3.5 text-indigo-300" />
                    <span>تصدير نسخة احتياطية (Export JSON)</span>
                  </button>
                  <label className="flex-1">
                    <span className="w-full bg-white hover:bg-slate-50 border border-[#E2E8F0] font-bold text-[11px] px-3.5 py-2.5 rounded-lg transition active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer shadow-sm text-slate-700 text-center">
                      <Upload className="w-3.5 h-3.5 text-slate-500" />
                      <span>استيراد السجلات (Import JSON)</span>
                    </span>
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImportBackup}
                      disabled={adminSettingLoading}
                      className="hidden"
                    />
                  </label>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <DataBackupPanel apiToken={apiToken} />
                </div>
              </div>
            </div>

            <div className="p-4 bg-[#F8FAFC] border-t border-[#E2E8F0] flex justify-end gap-3 shrink-0">
              <button
                onClick={() => setIsAdminSettingsOpen(false)}
                className="px-4 py-2 font-semibold text-xs text-slate-600 hover:text-slate-900 transition hover:bg-slate-100 rounded-lg cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleAdminChangePassword}
                disabled={
                  adminSettingLoading ||
                  !targetUserEmail ||
                  (!newPassword && !newUsername)
                }
                className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold text-xs px-6 py-2 rounded-lg shadow-sm transition flex items-center justify-center cursor-pointer"
              >
                {adminSettingLoading
                  ? "Applying..."
                  : "Apply Credential Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {printOpen && selectedPatient && (
        <PatientEhrPrintPreview
          patient={selectedPatient}
          vitals={editingRecordId ? {
            temp: vitalTemp,
            pulse: vitalPulse,
            bloodPressure: vitalBP,
            weight: vitalWeight,
            height: vitalHeight,
            bmi: vitalBMI,
            chiefComplaint: latestVitals?.chiefComplaint || ""
          } : latestVitals}
          clinicalRecords={notesRecord}
          orders={dispatchedOrders}
          medicalHistory={medicalHistory}
          docType={printDocType}
          selectedOrderToPrint={selectedOrderToPrint}
          activeSession={{
            editingRecordId,
            printableDocs: notesRecord.find(r => r.id === editingRecordId)?.printableDocs,
            physicianName: user?.name,
            gpNotes,
            soapSubjective,
            soapObjective,
            soapAssessment,
            soapPlan,
            icd10Codes: icd10,
            prescriptions,
            draftInvestigations: investigations,
            faceMarks: laserLogs,
            minorSurgery: minorSurgeryNotes,
            aiAnalysis: aiAnalysisResult,
            customLeafletResult,
          }}
          onClose={() => setPrintOpen(false)}
        />
      )}

      {/* IQ-Doctor Specialty Clinical Analysis Immediate Advisor POPUP (REMOVED) */}
      {false && isQiPopupOpen && qiAnalysisResult && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4 z-50 overflow-y-auto"
          style={{ direction: "ltr" }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh] border border-[#E2E8F0] animate-fadeIn">
            {/* Modal Header */}
            <div className="bg-[#1E1B4B] text-white px-6 py-4 flex justify-between items-center shrink-0 border-b border-indigo-950">
              <div className="flex items-center gap-2.5">
                <Sparkles className="w-5 h-5 text-indigo-300 animate-spin-slow" />
                <div className="text-left">
                  <h3 className="font-extrabold text-sm uppercase tracking-wider mb-0.5 text-white">
                    IQ-Doctor Professional Clinical Advisor (POPUP)
                  </h3>
                  <p className="text-[10px] text-indigo-200 font-semibold mb-0">
                    Live Specialty Professor Consult • Dermatology & General
                    Practice Evidence Triage
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsQiPopupOpen(false)}
                className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition cursor-pointer"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Modal Scrollable Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6 text-left bg-slate-50/50">
              {/* Alert Status */}
              {successMsg && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-3 rounded-lg text-xs font-bold leading-normal">
                  {successMsg}
                </div>
              )}

              {/* No suggestion returned */}
              {qiAnalysisResult.hasSuggestion === false ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-amber-800 space-y-2">
                  <p className="text-xs font-black flex items-center gap-2">
                    <Info className="w-5 h-5 text-amber-600 shrink-0" />
                    {qiAnalysisResult.message ||
                      "Insufficient medical record details."}
                  </p>
                  <p className="text-[11px] text-amber-700 font-medium leading-relaxed">
                    Make sure standard clinical indicators or chief symptoms are
                    outlined in Patient SOAP subjective (S), objective (O),
                    assessment (A), or plan (P) notes above, or you have
                    provided a descriptive dermatology lesion duration and
                    photo.
                  </p>
                </div>
              ) : (
                <>
                  {/* Top Level Highlight Banner: Best Suspected Specialty Diagnosis */}
                  <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-2 border-emerald-500/30 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm">
                    <div className="space-y-1">
                      <span className="text-[9px] uppercase font-black text-emerald-800 tracking-widest bg-emerald-500/20 px-2 py-0.5 rounded-full border border-emerald-400/20">
                        Primary Grounded Speculation
                      </span>
                      <h4 className="text-base font-black text-slate-950 tracking-tight leading-normal">
                        {qiAnalysisResult.mostAccurateDiagnosis}
                      </h4>
                      <p className="text-[10.5px] text-slate-500 font-bold">
                        Grounded with top clinical references & verified disease
                        registries.
                      </p>
                    </div>

                    {/* Quick Add Button inside Header */}
                    <button
                      onClick={handleAcceptQiSuggestedAnalysis}
                      disabled={isApplyingQiResult}
                      className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-extrabold text-[11px] uppercase px-5 py-3 rounded-xl flex items-center gap-2 shadow-md shrink-0 transition cursor-pointer border-b-2 border-emerald-800"
                    >
                      <CheckCircle className="w-4 h-4 text-emerald-100" />
                      {isApplyingQiResult
                        ? "Writing Chart..."
                        : "Accept & Populate Patient File"}
                    </button>
                  </div>

                  {/* Professor Discussion Analysis */}
                  <div className="bg-white border border-indigo-150 rounded-xl p-5 shadow-sm space-y-3">
                    <div className="flex items-center gap-1.5 border-b border-indigo-50/80 pb-2">
                      <Activity className="w-4 h-4 text-indigo-600" />
                      <span className="text-[11px] font-black text-indigo-950 uppercase tracking-widest">
                        Specialty Clinical Professor Evaluation Summary and
                        Logic
                      </span>
                    </div>
                    <div className="text-xs text-slate-700 leading-relaxed font-semibold bg-indigo-50/20 p-4 rounded-xl border border-indigo-50/50 whitespace-pre-wrap">
                      {qiAnalysisResult.professorAnalysis}
                    </div>
                  </div>

                  {/* Immediate Diagnostics Required Section */}
                  <div className="bg-rose-50/50 border border-rose-200/60 rounded-xl p-5 shadow-sm space-y-3">
                    <div className="flex items-center gap-1.5 border-b border-rose-100 pb-2">
                      <ClipboardList className="w-4 h-4 text-rose-600" />
                      <span className="text-[11px] font-black text-rose-950 uppercase tracking-widest text-left">
                        Immediate Investigations Assigned (CPOE Request Queue)
                      </span>
                    </div>
                    <p className="text-[10.5px] text-slate-500 font-bold">
                      The following tests will be automatically registered in
                      the laboratory/radiology order entry system on acceptance:
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-bold text-slate-800 list-disc list-inside bg-white p-3.5 rounded-xl border border-rose-100">
                      {qiAnalysisResult.immediateInvestigations &&
                      qiAnalysisResult.immediateInvestigations.length > 0 ? (
                        qiAnalysisResult.immediateInvestigations.map(
                          (inv: string, i: number) => {
                            const isXray =
                              /x-ray|cxr|ultrasound|scan|mri|ct|echo|radiology|imaging|mammogram/i.test(
                                inv,
                              );
                            return (
                              <li
                                key={i}
                                className="leading-relaxed flex items-center gap-2 text-slate-700"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"></span>
                                <span className="text-[11px] text-left">
                                  {inv}
                                </span>
                                <span
                                  className={`text-[8px] px-1.5 py-0.5 rounded font-black uppercase tracking-wider ${isXray ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}
                                >
                                  {isXray ? "Radiology / X-Ray" : "Lab Order"}
                                </span>
                              </li>
                            );
                          },
                        )
                      ) : (
                        <li className="text-slate-400 font-normal">
                          No immediate diagnostic tests recommended.
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* Differential Diagnosis Grid */}
                  <div className="space-y-3">
                    <span className="text-[10.5px] uppercase font-black text-slate-500 tracking-widest block text-left">
                      Speciality Differential Diagnostics Table (Triage Grid)
                    </span>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {qiAnalysisResult.differentialDiagnosis &&
                        qiAnalysisResult.differentialDiagnosis.map(
                          (diff: any, index: number) => (
                            <div
                              key={index}
                              className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm flex flex-col justify-between gap-3 hover:shadow-md transition duration-150"
                            >
                              <div className="space-y-2">
                                <div className="flex items-start justify-between gap-2.5">
                                  <span className="text-[11.5px] font-black text-slate-900 leading-snug tracking-tight">
                                    {diff.disorder}
                                  </span>
                                  <span className="text-[9px] bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 rounded-md font-black shrink-0 font-mono">
                                    {diff.probability}
                                  </span>
                                </div>
                                <p className="text-[10px] text-slate-600 font-bold leading-normal">
                                  {diff.reasoning}
                                </p>
                              </div>

                              <div className="border-t border-slate-100 pt-3 mt-1.5 space-y-2">
                                <div className="text-[9.5px] text-slate-500 font-bold leading-normal">
                                  <span className="text-indigo-950 font-extrabold uppercase text-[8px] tracking-wider block mb-0.5">
                                    Therapeutic Protocol:
                                  </span>
                                  {diff.recommendedManagement}
                                </div>

                                {diff.sourceUrl && (
                                  <a
                                    href={diff.sourceUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 text-[9px] font-extrabold text-indigo-600 hover:text-indigo-800 hover:underline hover:scale-101 duration-100 tracking-wide text-left"
                                  >
                                    <BookOpen className="w-3 h-3 text-indigo-500" />
                                    {diff.sourceTitle ||
                                      "Clinical Practice Guidelines Citation"}
                                  </a>
                                )}
                              </div>
                            </div>
                          ),
                        )}
                    </div>
                  </div>

                  {/* Grounding manual validation references */}
                  {qiAnalysisResult.sources &&
                    qiAnalysisResult.sources.length > 0 && (
                      <div className="border-t border-slate-200/80 pt-4 flex flex-wrap items-center gap-3.5 bg-slate-100/50 p-4 rounded-xl border border-slate-200">
                        <span className="text-[9px] uppercase font-black text-slate-400 tracking-wider">
                          Grounded Clinical Registries & Medical Societies
                          Consulted:
                        </span>
                        {qiAnalysisResult.sources.map(
                          (src: any, index: number) => (
                            <a
                              key={index}
                              href={src.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 text-[9.5px] font-bold text-slate-600 hover:text-slate-900 leading-tight bg-white px-2.5 py-1 rounded shadow-sm border border-slate-200/60"
                            >
                              <Clock className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                              {src.title}
                            </a>
                          ),
                        )}
                      </div>
                    )}
                </>
              )}
            </div>

            {/* Modal Actions Footer */}
            <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex justify-between items-center shrink-0">
              <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">
                * Clinical decision automation assist node.
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsQiPopupOpen(false)}
                  className="bg-slate-200 hover:bg-slate-300 active:bg-slate-400 text-slate-700 font-bold text-xs px-5 py-2.5 rounded-xl transition duration-100 shadow-sm cursor-pointer"
                >
                  Close & Ignore
                </button>
                {qiAnalysisResult?.hasSuggestion !== false && (
                  <button
                    onClick={handleAcceptQiSuggestedAnalysis}
                    disabled={isApplyingQiResult}
                    className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-extrabold text-xs px-6 py-2.5 rounded-xl transition duration-150 shadow-md flex items-center justify-center gap-2 cursor-pointer border-b-2 border-emerald-800"
                  >
                    <CheckCircle className="w-4 h-4 text-emerald-100" />
                    {isApplyingQiResult
                      ? "Writing Data..."
                      : "Apply to Patient SOAP, Diagnosis & Labs"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Online Consultation Rooms Manager Modal (Additive) */}
      {showOnlineConsultRooms && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto"
          dir="rtl"
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white px-6 py-4 flex justify-between items-center shrink-0">
              <div className="flex items-center gap-2.5">
                <Video className="w-5 h-5 animate-pulse" />
                <h3 className="font-bold text-base">
                  بوابة الاستشارات الطبية المرئية العاجلة
                </h3>
              </div>
              <button
                onClick={() => setShowOnlineConsultRooms(false)}
                className="bg-white/10 hover:bg-white/20 p-1.5 rounded-full transition"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 text-sm text-slate-600 leading-normal">
                👋 مرحباً دكتور. هنا يتم رصد الحالات التي طلبت استشارة مرئية حية
                تواً. يمكنك الانضمام فوراً وفتح غرف الفيديو الآمنة للتواصل
                المباشر مع المريض.
              </div>

              <div className="space-y-3">
                {urgentOnlineConsults.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                    <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                    <p className="font-bold text-slate-700 text-sm">
                      طابور الانتظار فارغ
                    </p>
                    <p className="text-slate-400 text-xs mt-1">
                      لا يوجد مرضى بانتظار الاستشارة الإلكترونية حالياً بشكل
                      مباشر.
                    </p>
                  </div>
                ) : (
                  urgentOnlineConsults.map((consult) => (
                    <div
                      key={consult.id}
                      className="bg-white border border-slate-200 hover:border-indigo-300 rounded-xl p-4 shadow-sm transition-all flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
                    >
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-ping"></span>
                          <span className="font-bold text-slate-800 text-sm">
                            {consult.name}
                          </span>
                          <span className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-500 font-medium">
                            العمر: {consult.age || "غير محدد"}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 font-mono">
                          رقم الهاتف: {consult.phone} | الهوية:{" "}
                          {consult.nationalId || "غير معلن"}
                        </p>
                        {consult.symptoms && (
                          <div className="text-xs bg-indigo-50/50 text-indigo-950 p-2 rounded border border-indigo-100/50 mt-2 font-serif text-right leading-relaxed">
                            <strong>الشكوى الرئيسية:</strong> {consult.symptoms}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setActiveOnlineConsult(consult);
                          setCustomMeetUrl(consult.meetUrl || "");
                          setShowOnlineConsultRooms(false);
                        }}
                        className="w-full md:w-auto shrink-0 bg-red-600 hover:bg-red-700 text-white font-bold text-xs py-2.5 px-4 rounded-lg shadow-md transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                      >
                        <Video className="w-3.5 h-3.5 ml-0.5" />
                        انضمام وبدء مكالمة الفيديو
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="bg-slate-50 px-6 py-4 shrink-0 flex justify-end border-t border-slate-100">
              <button
                onClick={() => setShowOnlineConsultRooms(false)}
                className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold px-4 py-2 rounded-lg cursor-pointer"
              >
                إغلاق النافذة
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Agora Video Consultation Overlay for Physician (Additive) */}
      {activeOnlineConsult && (
        <div
          className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4 z-50 overflow-y-auto"
          dir="rtl"
        >
          <div
            className={`bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl w-full ${showEmbeddedVideo ? "max-w-6xl" : "max-w-3xl"} overflow-hidden flex flex-col max-h-[92vh] transition-all duration-300`}
          >
            {/* Header */}
            <div className="bg-slate-850 border-b border-slate-850 px-6 py-4 flex justify-between items-center text-white shrink-0">
              <div className="flex items-center gap-3">
                <span className="bg-emerald-500/10 p-2 rounded-full border border-emerald-500/20">
                  <Video className="w-5 h-5 text-emerald-500 animate-pulse" />
                </span>
                <div>
                  <h3 className="font-bold text-base">
                    غرفة الاستشارة الطبية المباشرة (Google Meet)
                  </h3>
                  <p className="text-xs text-slate-400">
                    تواصل آمن ومشفّر مع المريض:{" "}
                    <strong className="text-white font-bold">
                      {activeOnlineConsult.name}
                    </strong>
                  </p>
                </div>
              </div>
              <button
                onClick={() => endPhysicianCall(false)}
                className="bg-slate-800 hover:bg-slate-700 p-2 rounded-full text-slate-400 hover:text-white transition cursor-pointer"
                title="خروج مؤقت دون إغلاق الجلسة"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Video Canvas Stage replacement (Google Meet launcher & Coordinator) */}
            {showEmbeddedVideo ? (
              <div className="flex-1 flex flex-col bg-slate-950 overflow-hidden relative min-h-[550px]">
                <div className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex justify-between items-center text-white shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="bg-indigo-500/10 p-1.5 rounded-full border border-indigo-500/20">
                      <Video className="w-4 h-4 text-indigo-400 animate-pulse" />
                    </span>
                    <span className="text-xs font-bold text-indigo-300">
                      مكالمة الفيديو الحية مع المراجع المفتوحة داخل الصفحة (بدون
                      الحاجة لتطبيقات) 📹
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowEmbeddedVideo(false)}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 rounded-lg text-[10px] font-extrabold transition cursor-pointer"
                  >
                    الرجوع واستعراض لوحة المريض / كتابة الوصفة 📄
                  </button>
                </div>
                <div className="w-full flex-1 relative bg-slate-900 min-h-[500px]">
                  <iframe
                    src={`https://meet.jit.si/IQDoctor-${activeOnlineConsult.id}#config.disableDeepLinking=true&config.prejoinPageEnabled=false&interfaceConfig.MOBILE_APP_PROMO=false&config.enableWelcomePage=false`}
                    allow="camera; microphone; display-capture; autoplay"
                    className="w-full h-full absolute inset-0 border-0"
                    title="Physician Video Call"
                  />
                </div>
              </div>
            ) : (
              <div className="p-6 flex-1 flex flex-col justify-start overflow-y-auto space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  {/* Left Side: Launch Room & Link controls */}
                  <div className="lg:col-span-7 space-y-5">
                    <div className="bg-slate-950 border border-slate-850 rounded-xl p-6 flex flex-col items-center justify-center text-center space-y-4 shadow-xl">
                      <div className="w-14 h-14 bg-indigo-500/10 rounded-xl flex items-center justify-center border border-indigo-500/20">
                        <Video className="w-8 h-8 text-indigo-400" />
                      </div>

                      <div className="space-y-1">
                        <h4 className="text-sm font-bold text-slate-200">
                          العيادة الطبية المرئية المباشرة
                        </h4>
                        <p className="text-[11px] text-slate-400 max-w-sm mx-auto leading-relaxed">
                          اختر طريقة الاتصال المفضلة للعيادة وبدء تشخيص المريض
                          وجهاً لوجه مباشرة:
                        </p>
                      </div>

                      {/* Highly-visible, 100% Guaranteed Jitsi Instant Link Option */}
                      <div className="w-full bg-indigo-950/40 border border-indigo-500/30 rounded-xl p-4 text-right space-y-2.5">
                        <div className="flex items-center justify-between">
                          <span className="bg-indigo-600 text-[10px] font-bold text-white px-2 py-0.5 rounded-full">
                            مستحسن وبدون مشاكل رموز
                          </span>
                          <h5 className="font-bold text-xs text-indigo-300">
                            الخيار الأول: اتصال طبي فوري مباشر (مستحسن)
                          </h5>
                        </div>
                        <p className="text-[10px] text-slate-400 leading-normal">
                          مكالمة فيديو سريعة فائقة النقاء لا تحتاج إلى إعداد أو
                          حسابات قوقل ميت. تفتح مباشرة داخل صفحتك الحالية لتلتقي
                          بالمراجع فوراً دون الحاجة لتنزيل تطبيقات.
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowEmbeddedVideo(true)}
                          className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold py-2 px-4 rounded-lg shadow-sm flex items-center justify-center gap-2 text-xs cursor-pointer transition"
                        >
                          <Video className="w-4 h-4" />
                          تشغيل مكالمة الفيديو الفورية داخل الصفحة 💻
                        </button>
                        <a
                          href={`https://meet.jit.si/IQDoctor-${activeOnlineConsult.id}#config.disableDeepLinking=true&config.prejoinPageEnabled=false&interfaceConfig.MOBILE_APP_PROMO=false&config.enableWelcomePage=false`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-center text-[10px] text-indigo-400 hover:text-indigo-300 font-sans mt-0.5 underline font-bold"
                        >
                          (أو اضغط لفتح المكالمة في نافذة خارجية كاحتياطي)
                        </a>
                      </div>

                      {/* Google Meet Option */}
                      <div className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-right space-y-2.5">
                        <h5 className="font-bold text-xs text-slate-300">
                          الخيار الثاني: اتصال Google Meet الرسمي
                        </h5>
                        <p className="text-[10px] text-slate-400 leading-normal">
                          يتطلب تزويد رابط اجتماع Google Meet صالح ومفعّل مسبقاً
                          من قبلك (يمكنك إعداده بالأسفل).
                        </p>

                        {activeOnlineConsult.meetUrl ? (
                          <div className="space-y-2.5">
                            <a
                              href={activeOnlineConsult.meetUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2.5 px-4 rounded-lg shadow-md hover:shadow-emerald-500/15 transition flex items-center justify-center gap-2 text-xs cursor-pointer"
                            >
                              <ExternalLink className="w-4 h-4" />
                              فتح رابط Google Meet المسجل
                            </a>
                            <div className="bg-black/40 border border-slate-800 p-2.5 rounded-lg">
                              <span className="block text-[8px] text-slate-500 font-bold mb-1 mb-r">
                                رابط Google Meet الموزع للمريض:
                              </span>
                              <span className="block text-[10px] text-emerald-400 font-mono select-all break-all direction-ltr text-center">
                                {activeOnlineConsult.meetUrl}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="p-3 bg-slate-950/60 rounded-lg text-[10px] text-amber-400 text-center">
                            ⚠️ لا يتوفر رابط بث قوقل حالياً. يرجى تهيئة الرابط
                            بالأسفل إذا رغبت به.
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Settings and Link Customizer Panel with Patient Room Resolution Instructions */}
                    <div className="bg-slate-950 border border-slate-800 rounded-xl p-5 space-y-4 shadow-inner">
                      <h5 className="font-extrabold text-xs text-slate-300 flex items-center gap-1.5">
                        <span className="text-indigo-400">⚙️</span>
                        إدارة روابط البث والربط الطبي الحقيقي
                      </h5>

                      {/* Clinical Warning Alert: Explain why clicking simulated rooms produces Google Meet errors */}
                      <div className="bg-amber-500/10 border border-amber-500/20 p-3.5 rounded-lg text-right space-y-1.5">
                        <h6 className="font-bold text-[11px] text-amber-400 flex items-center gap-1 justify-end">
                          ⚠️ تنبيه حول ظهور خطأ (Check your meeting code) للمريض
                        </h6>
                        <p className="text-[10px] text-zinc-400 leading-relaxed font-sans">
                          تم إنشاء الجلسة الحالية برابط عشوائي أولي لأغراض
                          المحاكاة والتجربة داخل التطبيق. إذا نقر المريض عليه
                          فستظهر له شاشة خطأ ميت الرسمية لأن الغرفة غير مفعّلة
                          في سيرفرات قوقل.
                        </p>
                        <p className="text-[10px] text-amber-200/90 font-bold leading-normal">
                          لحل ذلك وتأمين مكالمة حقيقية: الرجاء الضغط على الزر
                          أدناه لفتح غوغل ميت، ثم انسخ رابط الاجتماع الجديد
                          والصقه في المربع في الأسفل واضغط "حفظ الرابط".
                        </p>
                      </div>

                      {/* Google Meet Quick-Spawning Trigger button */}
                      <div className="pt-1">
                        <button
                          type="button"
                          onClick={() =>
                            window.open("https://meet.new", "_blank")
                          }
                          className="w-full bg-[#1E293B] hover:bg-indigo-950 border border-indigo-500/30 text-indigo-300 hover:text-white font-extrabold text-[11px] py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 transition-all duration-300 cursor-pointer shadow-sm"
                        >
                          <ExternalLink className="w-3.5 h-3.5 text-indigo-400" />
                          اضغط لإنشاء غرفة بث حقيقية مرخّصة (Google Meet)
                        </button>
                      </div>

                      <div className="space-y-2 pt-1 border-t border-slate-900">
                        <label className="block text-[10px] text-slate-400 font-bold">
                          تعديل أو تعيين رابط Google Meet مخصص للعيادة الحالية:
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={customMeetUrl}
                            onChange={(e) => setCustomMeetUrl(e.target.value)}
                            className="flex-1 bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-200 outline-none focus:border-indigo-505 font-mono direction-ltr"
                            placeholder="https://meet.google.com/abc-defg-hij"
                          />
                          <button
                            type="button"
                            onClick={() => saveCustomMeetUrl(customMeetUrl)}
                            disabled={isSavingMeetUrl || !customMeetUrl.trim()}
                            className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold text-xs px-4 rounded-lg transition shrink-0 cursor-pointer"
                          >
                            {isSavingMeetUrl ? "جاري الحفظ..." : "حفظ الرابط"}
                          </button>
                        </div>
                        <span className="block text-[9px] text-slate-500 leading-normal font-medium">
                          * بعد الحفظ، سيتم تحديث وتحديث زر الدخول في شاشة
                          المريض تلقائياً بالبث المباشر (Socket.IO Real-time).
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right Side: Medical symptoms / Patient Details Card */}
                  <div className="lg:col-span-5 space-y-4">
                    <div className="bg-slate-950 border border-slate-850 rounded-xl p-5 space-y-4 h-full flex flex-col justify-between">
                      <div>
                        <h4 className="font-bold text-xs text-rose-400 border-b border-slate-850 pb-2 mb-3">
                          📋 بطاقة المريض والتشخيص المبدئي
                        </h4>

                        <div className="space-y-3 text-xs">
                          <div>
                            <span className="text-slate-500 block text-[10px] font-bold">
                              الاسم الكامل:
                            </span>
                            <span className="text-slate-200 font-bold">
                              {activeOnlineConsult.name}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-slate-500 block text-[10px] font-bold">
                                العمر:
                              </span>
                              <span className="text-slate-200 font-bold">
                                {activeOnlineConsult.age || "غير محدد"} سنة
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-500 block text-[10px] font-bold">
                                رقم الهاتف:
                              </span>
                              <span className="text-slate-200 font-mono select-all font-bold">
                                {activeOnlineConsult.phone}
                              </span>
                            </div>
                          </div>
                          {activeOnlineConsult.nationalId && (
                            <div>
                              <span className="text-slate-500 block text-[10px] font-bold">
                                الرقم الوطني / الهوية الشخصية:
                              </span>
                              <span className="text-slate-300 font-mono">
                                {activeOnlineConsult.nationalId}
                              </span>
                            </div>
                          )}
                          {activeOnlineConsult.paymentMethod && (
                            <div className="bg-emerald-500/5 border border-emerald-500/10 p-2.5 rounded-lg text-right space-y-1">
                              <span className="text-emerald-400 block text-[10px] font-bold">
                                💳 تفاصيل الدفع والتحويل العراقي (١٠,٠٠٠ د.ع):
                              </span>
                              <span className="text-slate-300 font-sans text-xs font-semibold block">
                                {activeOnlineConsult.paymentReference}
                              </span>
                            </div>
                          )}
                          <div>
                            <span className="text-slate-500 block text-[10px] font-bold">
                              شكوى الأعراض الرئيسية:
                            </span>
                            <div className="bg-slate-900 p-2.5 rounded-lg text-[11px] text-slate-300 leading-relaxed border border-slate-850 max-h-40 overflow-y-auto">
                              {activeOnlineConsult.symptoms ||
                                "لم يتم إدراج شرح تفصيلي للأعراض."}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-slate-850 text-right">
                        <span className="inline-block bg-amber-500/10 text-amber-500 border border-amber-500/20 text-[10px] px-2.5 py-1 rounded font-bold">
                          الحالة الاستشارية: حية ونشطة
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Instant Document Transfer Panel (Physician Side) */}
                <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden w-full text-right shrink-0">
                  <div className="bg-slate-850 px-4 py-2 border-b border-slate-800 flex justify-between items-center text-right">
                    <h5 className="font-bold text-xs text-slate-300 flex items-center gap-1.5 basis-full justify-start">
                      <FileText className="w-4 h-4 text-emerald-500" />
                      تبادل المستندات مع المراجع (تقارير، فحوصات)
                    </h5>
                    <label className="bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 py-1 px-3 rounded shadow-sm text-xs font-bold cursor-pointer shrink-0 transition flex items-center gap-1">
                      <Upload className="w-3.5 h-3.5" />
                      إرسال ملف
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={handleLiveDocumentUpload}
                      />
                    </label>
                  </div>
                  <div className="p-4 flex flex-col gap-2 max-h-40 overflow-y-auto min-h-[60px]">
                    {liveDocuments.length === 0 ? (
                      <div className="text-center text-slate-500 text-xs italic my-auto">
                        لا توجد ملفات متبادلة في هذه الجلسة كجهة عيادة.
                      </div>
                    ) : (
                      liveDocuments.map((doc, i) => (
                        <a
                          key={i}
                          href={doc.url}
                          download={doc.name}
                          className="flex justify-between items-center p-2 rounded-lg bg-slate-950 border border-slate-800 hover:bg-slate-800 transition"
                        >
                          <span className="text-[11px] text-slate-400 font-bold">
                            {doc.sender === "العيادة (الطبيب)"
                              ? "أنت"
                              : "المراجع"}
                            :{" "}
                          </span>
                          <span className="text-xs font-bold text-emerald-400 text-left truncate ltr flex-1 max-w-[400px] mx-2">
                            {doc.name}
                          </span>
                          <span className="text-[10px] bg-slate-800 text-slate-300 px-2 py-1 flex items-center gap-1 rounded font-bold hover:bg-slate-700 shrink-0">
                            تحميل السجل
                          </span>
                        </a>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Media Controls Console / Actions Footer */}
            <div className="bg-slate-950 border-t border-slate-850 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4 shrink-0">
              <span className="text-[10px] text-slate-500 font-bold">
                🔒 تم مكاملته مع معايير الأمان الطبية وبروتوكول Google Meet
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => endPhysicianCall(false)}
                  className="bg-slate-800 hover:bg-slate-700 hover:text-white border border-slate-700 text-slate-300 text-xs font-bold px-5 h-10 rounded-lg transition cursor-pointer"
                  title="خروج مؤقت للائحة الحالات"
                >
                  حفظ الجلسة معلقة والرجوع
                </button>

                <button
                  onClick={() => endPhysicianCall(true)}
                  className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-5 h-10 rounded-lg shadow-lg flex items-center justify-center gap-1.5 transition cursor-pointer"
                >
                  <PhoneOff className="w-4 h-4 ml-1" />
                  إنهاء العيادة والاعتماد الكلي
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAnalytics && selectedPatient && (
        <PatientAnalytics 
          patientName={selectedPatient.name}
          vitalsHistory={vitalsHistory}
          labHistory={labHistory}
          notesHistory={notesRecord}
          onClose={() => setShowAnalytics(false)}
        />
      )}

      {/* Clinic Settings Modal */}
      {isClinicSettingsOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in slide-in-from-bottom-4 duration-300">
            <div className="bg-slate-50 border-b border-slate-200 p-5 flex justify-between items-center">
              <div>
                <h3 className="font-extrabold text-slate-800 text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-indigo-600" />
                  Clinic Profile & Branding
                </h3>
                <p className="text-xs text-slate-500 mt-1">Configure clinic identity, contact details, and default billing values.</p>
              </div>
              <button onClick={() => setIsClinicSettingsOpen(false)} className="text-slate-400 hover:text-slate-600 bg-white p-1.5 rounded-lg border border-slate-200 transition">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto" dir="rtl">
              <form 
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.target as HTMLFormElement;
                  const payload = {
                    clinicNameEn: (form.elements.namedItem("clinicNameEn") as HTMLInputElement).value,
                    clinicNameAr: (form.elements.namedItem("clinicNameAr") as HTMLInputElement).value,
                    doctorNameEn: (form.elements.namedItem("doctorNameEn") as HTMLInputElement).value,
                    doctorNameAr: (form.elements.namedItem("doctorNameAr") as HTMLInputElement).value,
                    specialization: (form.elements.namedItem("specialization") as HTMLInputElement).value,
                    phone: (form.elements.namedItem("phone") as HTMLInputElement).value,
                    email: (form.elements.namedItem("email") as HTMLInputElement).value,
                    address: (form.elements.namedItem("address") as HTMLInputElement).value,
                    consultationFee: parseInt((form.elements.namedItem("consultationFee") as HTMLInputElement).value) || 0,
                    clinicLogo: modalClinicLogo,
                    doctorSignature: modalDoctorSignature,
                    clinicStamp: modalClinicStamp,
                    footerNotes: (form.elements.namedItem("footerNotes") as HTMLTextAreaElement).value,
                    socialFacebook: (form.elements.namedItem("socialFacebook") as HTMLInputElement).value,
                    socialInstagram: (form.elements.namedItem("socialInstagram") as HTMLInputElement).value,
                    socialTelegram: (form.elements.namedItem("socialTelegram") as HTMLInputElement).value,
                    heroBannerEnabled: (form.elements.namedItem("heroBannerEnabled") as HTMLInputElement).checked,
                    heroBannerText: (form.elements.namedItem("heroBannerText") as HTMLInputElement).value,
                    heroBannerImages: modalHeroImages,
                    clinicNewsEnabled: clinicNewsEnabled,
                    clinicNewsItems: clinicNewsItems,
                  };
                  try {
                    const res = await fetch("/api/clinic/profile", {
                      method: "POST",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
                      body: JSON.stringify(payload)
                    });
                    if (res.ok) {
                      setWhiteLabel(payload);
                      localStorage.setItem("clinic_white_label_settings", JSON.stringify(payload));
                      triggerSuccessToast("تم حفظ إعدادات العيادة بنجاح وتزامنها مع قاعدة البيانات.");
                      setIsClinicSettingsOpen(false);
                    } else {
                      alert("حدث خطأ أثناء الحفظ. تأكد من صلاحياتك.");
                    }
                  } catch (err) {
                    alert("خطأ في الاتصال بالخادم.");
                  }
                }}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700">اسم العيادة (بالعربية)</label>
                    <input name="clinicNameAr" defaultValue={whiteLabel?.clinicNameAr || ""} className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                  </div>
                  <div className="space-y-1.5" dir="ltr">
                    <label className="text-xs font-bold text-slate-700 text-right block">Clinic Name (English)</label>
                    <input name="clinicNameEn" defaultValue={whiteLabel?.clinicNameEn || ""} className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                  </div>
                  
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700">اسم الطبيب (بالعربية)</label>
                    <input name="doctorNameAr" defaultValue={whiteLabel?.doctorNameAr || ""} className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                  </div>
                  <div className="space-y-1.5" dir="ltr">
                    <label className="text-xs font-bold text-slate-700 text-right block">Doctor Name (English)</label>
                    <input name="doctorNameEn" defaultValue={whiteLabel?.doctorNameEn || ""} className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                  </div>

                  <div className="space-y-1.5 md:col-span-2">
                    <label className="text-xs font-bold text-slate-700">التخصص الطبي / الشعار</label>
                    <input name="specialization" defaultValue={whiteLabel?.specialization || ""} placeholder="مثال: أخصائي طب الأسرة والتجميل" className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700">رقم هاتف العيادة</label>
                    <input name="phone" defaultValue={whiteLabel?.phone || ""} dir="ltr" placeholder="07XXXXXXXXX" className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none text-right" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700">البريد الإلكتروني للعيادة (اختياري)</label>
                    <input name="email" type="email" defaultValue={whiteLabel?.email || ""} dir="ltr" className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none text-right" />
                  </div>

                  <div className="space-y-1.5 md:col-span-2">
                    <label className="text-xs font-bold text-slate-700">عنوان العيادة</label>
                    <input name="address" defaultValue={whiteLabel?.address || ""} placeholder="المحافظة - المنطقة - الشارع الرئيسي" className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                  </div>

                  <div className="space-y-1.5 md:col-span-2 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <label className="text-xs font-bold text-emerald-800 flex items-center gap-1.5">
                      <DollarSign className="w-4 h-4" /> السعر الافتراضي للكشفية (Consultation Fee)
                    </label>
                    <div className="flex items-center gap-2 mt-2">
                      <input name="consultationFee" type="number" defaultValue={whiteLabel?.consultationFee || 0} dir="ltr" className="w-full bg-white border border-emerald-300 rounded-lg px-3 py-2 text-sm font-mono font-bold text-emerald-900 focus:ring-2 focus:ring-emerald-500 outline-none" />
                      <span className="text-emerald-700 font-bold text-sm shrink-0">دينار عراقي</span>
                    </div>
                    <p className="text-[10px] text-emerald-600 mt-1">يستخدم لملء الفواتير والإيصالات تلقائياً.</p>
                  </div>

                  {/* Banner Configuration */}
                  <div className="md:col-span-2 space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-xl mb-2">
                    <h4 className="font-bold text-blue-800 flex items-center gap-2">📸 بنر العيادة (Hero Banner) لصفحة الزوار</h4>
                    
                    <div className="flex items-center gap-2">
                      <input type="checkbox" name="heroBannerEnabled" id="heroBannerEnabled" defaultChecked={whiteLabel?.heroBannerEnabled || false} className="w-4 h-4 text-blue-600 rounded" />
                      <label htmlFor="heroBannerEnabled" className="text-sm font-bold text-slate-700">تفعيل ظهور البنر الإعلاني</label>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700">نص الإعلان أو الترحيب</label>
                      <input name="heroBannerText" defaultValue={whiteLabel?.heroBannerText || ""} placeholder="مثال: خصم 20% على جلسات الليزر هذا الأسبوع!" className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-700">صور العيادة والعروض (يمكن رفع عدة صور)</label>
                      <input 
                        type="file" 
                        accept="image/*"
                        multiple
                        onChange={async (e) => {
                          const files = e.target.files;
                          if (files) {
                            for (const file of Array.from(files)) {
                              const compressImage = (f: File): Promise<string> => {
                                return new Promise((resolve) => {
                                  const reader = new FileReader();
                                  reader.readAsDataURL(f);
                                  reader.onload = (event) => {
                                    const img = new Image();
                                    img.src = event.target?.result as string;
                                    img.onload = () => {
                                      const canvas = document.createElement('canvas');
                                      let width = img.width;
                                      let height = img.height;
                                      const MAX_DIMENSION = 1200;
                                      if (width > height && width > MAX_DIMENSION) {
                                        height *= MAX_DIMENSION / width;
                                        width = MAX_DIMENSION;
                                      } else if (height > MAX_DIMENSION) {
                                        width *= MAX_DIMENSION / height;
                                        height = MAX_DIMENSION;
                                      }
                                      canvas.width = width;
                                      canvas.height = height;
                                      const ctx = canvas.getContext('2d');
                                      ctx?.drawImage(img, 0, 0, width, height);
                                      resolve(canvas.toDataURL('image/jpeg', 0.6));
                                    };
                                  };
                                });
                              };
                              const compressedBase64 = await compressImage(file);
                              setModalHeroImages(prev => [...prev, compressedBase64]);
                            }
                          }
                        }}
                        className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
                      />
                      {modalHeroImages.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {modalHeroImages.map((img, idx) => (
                            <div key={idx} className="relative group">
                              <img src={img} className="w-20 h-20 object-cover rounded-lg shadow border border-slate-200" />
                              <button type="button" onClick={() => setModalHeroImages(prev => prev.filter((_, i) => i !== idx))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition shadow">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-end mt-3 border-t border-blue-200 pt-3">
                      <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-md hover:bg-blue-700 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                        حفظ ونشر البنر فوراً
                      </button>
                    </div>
                  </div>

                  {/* Clinic News Banner Configuration */}
                  <div className="md:col-span-2 space-y-4 p-4 bg-indigo-50 border border-indigo-200 rounded-xl mb-2">
                    <div className="flex items-center justify-between border-b border-indigo-200 pb-3">
                      <h4 className="font-bold text-indigo-900 flex items-center gap-2">📰 بنر أخبار وتنبيهات العيادة لصفحة الزوار</h4>
                      <div className="flex items-center gap-2">
                        <input 
                          type="checkbox" 
                          id="clinicNewsEnabled" 
                          checked={clinicNewsEnabled} 
                          onChange={(e) => setClinicNewsEnabled(e.target.checked)} 
                          className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer" 
                        />
                        <label htmlFor="clinicNewsEnabled" className="text-sm font-bold text-indigo-950 cursor-pointer">تفعيل بنر أخبار العيادة</label>
                      </div>
                    </div>

                    {clinicNewsEnabled && (
                      <div className="space-y-4">
                        <div className="flex flex-wrap justify-between items-center gap-2">
                          <span className="text-xs font-bold text-indigo-900">إدارة بطاقات التثقيف الصحي ونشرات معلومات المريض (BAD)</span>
                          <div className="flex items-center gap-2">
                            <button 
                              type="button" 
                              onClick={() => setClinicNewsItems(DEFAULT_BAD_LEAFLETS)}
                              className="bg-emerald-600 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-emerald-700 font-bold transition shadow flex items-center gap-1"
                              title="تحميل النشرات الـ 10 الافتراضية من الجمعية البريطانية لأطباء الجلدية"
                            >
                              📥 استعادة القائمة الافتراضية لنشرات (BAD) الـ 10
                            </button>
                            <button 
                              type="button" 
                              onClick={() => setClinicNewsItems(prev => [...prev, { id: Date.now().toString(), title: "عنوان النشرة الجديدة", description: "اكتب تفاصيل أو نصيحة طبية هنا", imageUrl: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&q=80&w=800", link: "" }])}
                              className="bg-indigo-600 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-indigo-700 font-bold transition shadow"
                            >
                              + إضافة بطاقة جديدة
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {clinicNewsItems.map((item, idx) => (
                            <div key={item.id || idx} className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm space-y-3 relative">
                              <button 
                                type="button" 
                                onClick={() => setClinicNewsItems(prev => prev.filter((_, i) => i !== idx))}
                                className="absolute top-3 left-3 bg-red-100 hover:bg-red-500 text-red-700 hover:text-white px-2 py-0.5 rounded text-xs font-bold transition"
                                title="حذف هذا الخبر"
                              >
                                ✕ حذف
                              </button>
                              <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-700 block">عنوان الخبر</label>
                                <input 
                                  value={item.title || ""} 
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setClinicNewsItems(prev => prev.map((it, i) => i === idx ? { ...it, title: val } : it));
                                  }} 
                                  className="w-full bg-slate-50 border border-slate-300 rounded px-2.5 py-1.5 text-sm font-bold outline-none focus:border-indigo-500" 
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-700 block">نص أو تفاصيل الخبر / النصيحة</label>
                                <textarea 
                                  value={item.description || ""} 
                                  rows={2}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setClinicNewsItems(prev => prev.map((it, i) => i === idx ? { ...it, description: val } : it));
                                  }} 
                                  className="w-full bg-slate-50 border border-slate-300 rounded px-2.5 py-1.5 text-xs outline-none focus:border-indigo-500 resize-none" 
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-700 block">رابط التوجيه (فيديو يوتيوب أو رابط مقال - اختياري)</label>
                                <input 
                                  value={item.link || ""} 
                                  dir="ltr"
                                  placeholder="https://youtube.com/..." 
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setClinicNewsItems(prev => prev.map((it, i) => i === idx ? { ...it, link: val } : it));
                                  }} 
                                  className="w-full bg-slate-50 border border-slate-300 rounded px-2.5 py-1.5 text-xs text-left outline-none focus:border-indigo-500" 
                                />
                              </div>
                              <div className="space-y-2 pt-1 border-t border-slate-100">
                                <label className="text-xs font-bold text-slate-700 block">صورة الغلاف (اختر من الجهاز أو ضع رابط الصورة)</label>
                                <div className="flex items-center gap-2">
                                  {item.imageUrl && <img src={item.imageUrl} alt="" className="w-12 h-12 object-cover rounded border shrink-0" />}
                                  <input 
                                    value={item.imageUrl || ""} 
                                    dir="ltr"
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setClinicNewsItems(prev => prev.map((it, i) => i === idx ? { ...it, imageUrl: val } : it));
                                    }} 
                                    className="w-full bg-slate-50 border border-slate-300 rounded px-2 py-1.5 text-[11px] text-left outline-none" 
                                    placeholder="رابط الصورة https://..." 
                                  />
                                </div>
                                <input 
                                  type="file" 
                                  accept="image/*" 
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      const reader = new FileReader();
                                      reader.readAsDataURL(file);
                                      reader.onload = (event) => {
                                        const img = new Image();
                                        img.src = event.target?.result as string;
                                        img.onload = () => {
                                          const canvas = document.createElement('canvas');
                                          let w = img.width; let h = img.height;
                                          if (w > h && w > 800) { h *= 800/w; w = 800; }
                                          else if (h > 800) { w *= 800/h; h = 800; }
                                          canvas.width = w; canvas.height = h;
                                          canvas.getContext('2d')?.drawImage(img, 0, 0, w, h);
                                          const b64 = canvas.toDataURL('image/jpeg', 0.7);
                                          setClinicNewsItems(prev => prev.map((it, i) => i === idx ? { ...it, imageUrl: b64 } : it));
                                        };
                                      };
                                    }
                                  }}
                                  className="block w-full text-[11px] text-slate-500 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-indigo-100 file:text-indigo-700 hover:file:bg-indigo-200 cursor-pointer"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end mt-3 border-t border-indigo-200 pt-3">
                      <button type="submit" className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-md hover:bg-indigo-700 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                        حفظ ونشر أحدث التغييرات على البنر
                      </button>
                    </div>
                  </div>

                  {/* Image Uploaders */}
                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-slate-100">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-700">شعار العيادة (Logo)</label>
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-3 text-center hover:bg-slate-50 transition cursor-pointer relative overflow-hidden h-24 flex items-center justify-center">
                        {modalClinicLogo ? <img src={modalClinicLogo} className="max-h-full max-w-full object-contain" /> : <div className="text-[10px] text-slate-400">انقر للرفع<br/>(صورة شفافة)</div>}
                        <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => setModalClinicLogo(reader.result as string);
                            reader.readAsDataURL(file);
                          }
                        }} />
                      </div>
                      {modalClinicLogo && <button type="button" onClick={() => setModalClinicLogo("")} className="text-[10px] text-rose-500 font-bold block w-full text-center mt-1">حذف الشعار</button>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-700">توقيع الطبيب (Signature)</label>
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-3 text-center hover:bg-slate-50 transition cursor-pointer relative overflow-hidden h-24 flex items-center justify-center">
                        {modalDoctorSignature ? <img src={modalDoctorSignature} className="max-h-full max-w-full object-contain" /> : <div className="text-[10px] text-slate-400">انقر للرفع<br/>(يفضل خلفية بيضاء/شفافة)</div>}
                        <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => setModalDoctorSignature(reader.result as string);
                            reader.readAsDataURL(file);
                          }
                        }} />
                      </div>
                      {modalDoctorSignature && <button type="button" onClick={() => setModalDoctorSignature("")} className="text-[10px] text-rose-500 font-bold block w-full text-center mt-1">حذف التوقيع</button>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-700">الختم الرسمي (Stamp)</label>
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-3 text-center hover:bg-slate-50 transition cursor-pointer relative overflow-hidden h-24 flex items-center justify-center">
                        {modalClinicStamp ? <img src={modalClinicStamp} className="max-h-full max-w-full object-contain" /> : <div className="text-[10px] text-slate-400">انقر للرفع<br/>(ختم العيادة)</div>}
                        <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => setModalClinicStamp(reader.result as string);
                            reader.readAsDataURL(file);
                          }
                        }} />
                      </div>
                      {modalClinicStamp && <button type="button" onClick={() => setModalClinicStamp("")} className="text-[10px] text-rose-500 font-bold block w-full text-center mt-1">حذف الختم</button>}
                    </div>
                  </div>

                  {/* Social & Notes */}
                  <div className="md:col-span-2 pt-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-3">
                      <h4 className="text-xs font-bold text-indigo-700 border-b border-indigo-100 pb-1">روابط السوشيال ميديا (للطباعة كـ QR)</h4>
                      <div className="space-y-2">
                        <input name="socialFacebook" defaultValue={whiteLabel?.socialFacebook || ""} dir="ltr" placeholder="Facebook Link" className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-2 focus:ring-indigo-500 outline-none text-right" />
                        <input name="socialInstagram" defaultValue={whiteLabel?.socialInstagram || ""} dir="ltr" placeholder="Instagram Link" className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-2 focus:ring-indigo-500 outline-none text-right" />
                        <input name="socialTelegram" defaultValue={whiteLabel?.socialTelegram || ""} dir="ltr" placeholder="Telegram Link" className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-2 focus:ring-indigo-500 outline-none text-right" />
                      </div>
                    </div>
                    
                    <div className="space-y-1.5">
                      <h4 className="text-xs font-bold text-indigo-700 border-b border-indigo-100 pb-1">ملاحظات أسفل الفاتورة والوصفة (Footer)</h4>
                      <textarea name="footerNotes" defaultValue={whiteLabel?.footerNotes || ""} placeholder="مثال: المراجعة مجانية خلال أسبوع. دوام العيادة من 4 عصراً إلى 10 مساءً." className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none h-24 resize-none"></textarea>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-5 mt-5 flex justify-end gap-3">
                  <button type="button" onClick={() => setIsClinicSettingsOpen(false)} className="px-5 py-2 text-sm font-bold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 cursor-pointer transition">Cancel</button>
                  <button type="submit" className="px-5 py-2 text-sm font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-2 shadow-md cursor-pointer transition">
                    <Save className="w-4 h-4" /> حفظ ومزامنة
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

